/* GENERATED FILE — do not edit directly. Source: src/b2b/ — build with `npm run build`. */
(function () {
/**
 * Case-pack parsing + per-unit price math.
 *
 * CANONICAL CONTRACT: `getCaseQtyFromString` is a faithful port of
 * `snippets/variant-option-case-qty.liquid` (the parser the product page uses),
 * and `dividePriceByCaseQty` mirrors `snippets/price.liquid` integer-cents
 * rounding. If a matching rule ever changes, change the Liquid and this module
 * TOGETHER — any difference makes B2B card prices diverge from the product page.
 *
 * Behaviour fixes vs the pre-extraction copy in legacy.js (each was a
 * divergence from the Liquid; the Liquid behaviour wins):
 * - ' X ' (uppercase) is NOT a case separator — only ' × ' and lowercase ' x '
 *   (old JS matched /\s[×x]\s/i, so "75cl X 6" showed per-bottle prices on B2B
 *   cards while the product page showed the case price).
 * - 'cl' in the ×/x branch is matched case-sensitively ("12 x 75CL" → 0).
 * - A delimiter CLAIMS the string like the Liquid elsif chain: if its tail
 *   doesn't parse, the result is 0 — no falling through to later branches
 *   ("6 par lot per 12" → 0, not 12; "caisse de 6 x magnum" → 0, not 6).
 * - 'case of' / 'caisse de' match as plain substrings of the downcased value,
 *   exactly like the Liquid (no word-boundary or digits-only requirement).
 *
 * Known benign extra vs the Liquid (pre-existing, shared with the
 * variant-format display-label code): U+2009 thin space is also normalized to
 * a regular space; the Liquid only normalizes U+00A0 and U+202F.
 *
 * Exported as `const` arrows on purpose: the bundle is served as a classic
 * script, and top-level `function` declarations would become window globals.
 */

/** NBSP / narrow NBSP / thin space → space, ' Par ' / ' Per ' → lowercase; trim. */
/** @param {unknown} str */
const normalizeFormatOptionValue = (str) => {
  if (typeof str !== 'string') return '';
  return str
    .replace(/ /g, ' ')
    .replace(/ /g, ' ')
    .replace(/ /g, ' ')
    .replace(/ Par /g, ' par ')
    .replace(/ Per /g, ' per ')
    .trim();
};

/**
 * First whitespace-or-hyphen token parsed as a positive integer, else 0.
 * Same extraction as the Liquid's `split: ' ' | first | split: '-' | first | plus: 0`
 * (parseInt shares Ruby to_i's leading-digits behaviour, e.g. "12abc" → 12).
 */
/** @param {string} s */
const firstIntToken = (s) => {
  const t = (s || '').trim().split(/[\s-]/)[0] || '';
  const n = parseInt(t, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Case unit count from one variant option value, e.g. "75cl par 12",
 * "75cl per 12", "75cl × 12", "12 x 75cl", "75cl/6", "Caisse de 6".
 * Branch order and case-sensitivity mirror the Liquid elsif chain exactly.
 */
/** @param {unknown} raw */
const getCaseQtyFromString = (raw) => {
  const str = normalizeFormatOptionValue(raw);
  if (!str) return 0;

  for (const sep of [' par ', ' per ', ' by ', ' for ']) {
    if (str.includes(sep)) return firstIntToken(str.split(sep).pop() || '');
  }

  // ' × ' before ' x ', both case-sensitive with literal single spaces.
  for (const sep of [' × ', ' x ']) {
    if (str.includes(sep)) {
      const parts = str.split(sep);
      const left = (parts[0] || '').trim();
      const right = (parts[parts.length - 1] || '').trim();
      if (right.includes('cl')) return firstIntToken(left);
      if (left.includes('cl')) return firstIntToken(right);
      return 0;
    }
  }

  if (str.includes('cl/')) {
    const parts = str.split('/');
    const left = (parts[0] || '').trim();
    const right = (parts[parts.length - 1] || '').trim();
    if (left.includes('cl') && right !== left) return firstIntToken(right);
    return 0;
  }

  const lower = str.toLowerCase();
  for (const sep of ['case of ', 'caisse de ']) {
    if (lower.includes(sep)) return firstIntToken(lower.split(sep).pop() || '');
  }

  return 0;
};

/** If variant is a case (e.g. "75cl par 6"), return the number of units; otherwise 0. */
/** Mirrors snippets/price.liquid: option1 → option2 → option3, then title fallback. */
/** @param {any} variant */
const getCaseQty = (variant) => {
  if (!variant) return 0;
  const check = (/** @type {unknown} */ s) => getCaseQtyFromString(typeof s === 'string' ? s : '');
  const opts = variant.selectedOptions || [];
  for (let i = 0; i < opts.length; i++) {
    const q = check(opts[i].value || '');
    if (q > 0) return q;
  }
  return check(variant.title || '');
};

/**
 * Per-unit amount from case-pack line total — matches snippets/price.liquid
 * integer cents rounding. Storefront `price.amount` is decimal major units.
 */
/** @param {number} amount */
/** @param {number} caseQty */
const dividePriceByCaseQty = (amount, caseQty) => {
  if (!Number.isFinite(amount) || caseQty <= 0) return amount;
  const cents = Math.round(Number(amount) * 100);
  const half = Math.floor(caseQty / 2);
  return Math.floor((cents + half) / caseQty) / 100;
};

/**
 * Shop money-format rendering (the `{{ amount }}` / `{{ amount_with_comma_separator }}`
 * template Shopify exposes as `shop.money_format`). Pure: moved verbatim out of
 * legacy.js. The Intl fallback and config lookup stay in legacy's `formatMoney`.
 *
 * Exported as `const` arrows on purpose: the bundle is served as a classic
 * script, and top-level `function` declarations would become window globals.
 */

const CURRENCY_DECIMALS = { BHD: 3, BIF: 0, BYR: 0, CLF: 4, CLP: 0, DJF: 0, GNF: 0, IQD: 3, ISK: 0, JOD: 3, JPY: 0, KMF: 0, KRW: 0, KWD: 3, LYD: 3, MRO: 5, OMR: 3, PYG: 0, RWF: 0, TND: 3, UGX: 0, UYI: 0, UYW: 4, VND: 0, VUV: 0, XAF: 0, XAG: 0, XAU: 0, XBA: 0, XBB: 0, XBC: 0, XBD: 0, XDR: 0, XOF: 0, XPD: 0, XPF: 0, XPT: 0, XSU: 0, XTS: 0, XUA: 0 };
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * @param {number} amount
 * @param {string} formatTemplate
 * @param {string} currency
 */
const formatMoneyWithShopFormat = (amount, formatTemplate, currency) => {
  if (!formatTemplate || typeof amount !== 'number' || !Number.isFinite(amount)) return '';
  const currencyKey = (currency || 'EUR').toUpperCase();
  const defaultPrecision = CURRENCY_DECIMALS[currencyKey] ?? DEFAULT_CURRENCY_DECIMALS;
  return formatTemplate.replace(/\{\{\s*(\w+)\s*\}\}/g, (/** @type {string} */ _, /** @type {string} */ placeholder) => {
    if (placeholder === 'currency') return currency || '';
    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = defaultPrecision;
    if (placeholder === 'amount') { /* use defaults */ } else if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.'; decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.'; precision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' '; precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' '; decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' '; decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'"; decimalSeparator = '.';
    }
    const rounded = Number(amount).toFixed(precision);
    let [intPart, decPart] = rounded.split('.');
    if (!intPart) intPart = '0';
    if (!decPart) decPart = '';
    intPart = intPart.replace(/\d(?=(\d{3})+(?!\d))/g, (d) => d + thousandsSeparator);
    return precision <= 0 ? intPart : intPart + decimalSeparator + decPart.padEnd(precision, '0');
  });
};

/**
 * Numeric (unformatted) price helpers for filtering, sorting and slider bounds.
 *
 * THE INVARIANT: every number in here is the price PER DISPLAYED UNIT — the
 * same number the wine cards show (`getDisplayPrice` = b2b_price-or-price
 * divided by the case quantity). Before this module, `getSortPrice` /
 * `getPriceRange` used the RAW case-pack price, so a €120 "75cl par 6" case
 * displayed €20 on its card but was sorted, filtered and bounded as €120.
 *
 * Exported as `const` arrows on purpose: the bundle is served as a classic
 * script, and top-level `function` declarations would become window globals.
 */

/** Raw numeric price: prefers the b2b_price metafield (JSON {amount,currencyCode}), else variant.price. */
/** @param {any} variant */
const getVariantPriceNum = (variant) => {
  if (!variant) return 0;
  const meta = variant.metafield?.value;
  if (meta) {
    try {
      const parsed = JSON.parse(meta);
      return parseFloat(parsed.amount) || 0;
    } catch { /* fall through */ }
  }
  return parseFloat(variant.price?.amount) || 0;
};

/** Price per displayed unit: raw price ÷ case quantity for case packs (matches the card display). */
/** @param {any} variant */
const getVariantUnitPriceNum = (variant) =>
  dividePriceByCaseQty(getVariantPriceNum(variant), getCaseQty(variant));

/** Numeric price for sorting (first variant, prefer b2b_price) — per displayed unit. */
/** @param {any} product */
const getSortPrice = (product) => {
  const v = product.variants?.edges?.[0]?.node;
  return getVariantUnitPriceNum(v);
};

/** Min/max per-unit price from all variants (for price filter UI). */
/** @param {any[]} products */
const getPriceRange = (products) => {
  if (!products.length) return { min: 0, max: 0 };
  let min = Infinity;
  let max = -Infinity;
  products.forEach((/** @type {any} */ p) => {
    const edges = p.variants?.edges;
    if (!Array.isArray(edges)) {
      const price = getSortPrice(p);
      if (price > 0) { if (price < min) min = price; if (price > max) max = price; }
      return;
    }
    edges.forEach((/** @type {any} */ e) => {
      const price = getVariantUnitPriceNum(e?.node);
      if (price > 0) { if (price < min) min = price; if (price > max) max = price; }
    });
  });
  return { min: Number.isFinite(min) ? min : 0, max: Number.isFinite(max) ? max : 0 };
};

/**
 * Storefront API GraphQL client. Function bodies are byte-for-byte from
 * legacy.js; the config lookup is injected so this module stays DOM-free and
 * unit-testable, and the old API_VERSION / MAX_RETRIES constants became the
 * `apiVersion` / `maxRetries` parameters below with identical defaults.
 *
 * Behavior contract (kept bit-for-bit from the original):
 * - 20s AbortController timeout per attempt.
 * - `storefrontFetch` retries up to `maxRetries` with linear backoff (1s, 2s, …)
 *   on ANY failure — including HTTP errors, GraphQL errors and timeouts (the
 *   known ~70s worst case is pre-existing and deliberately unchanged here).
 * - `storefrontFetchPartial` is single-attempt and returns `{ data, errors }`
 *   instead of throwing on GraphQL errors — so a cart containing an
 *   off-channel line (non-nullable `ProductVariant.product` is null) can
 *   still be read by the recovery paths.
 *
 * Exported as a `const` arrow on purpose: the bundle is served as a classic
 * script, and top-level `function` declarations would become window globals.
 */

/**
 * Map a BCP-47-ish locale (document.documentElement.lang, e.g. "fr", "fr-FR",
 * "sv-SE") to a Shopify Storefront `LanguageCode` enum value (e.g. FR, SV).
 * Only the primary subtag is used — the enum has no FR_FR, and FR alone yields
 * the French translations. Returns null when there's nothing usable so callers
 * skip the directive entirely (leaving the API's default language).
 * @param {string | null | undefined} locale
 * @returns {string | null}
 */
const languageCodeFromLocale = function languageCodeFromLocale(locale) {
  if (!locale || typeof locale !== 'string') return null;
  const primary = locale.trim().split(/[-_]/)[0].toUpperCase();
  return /^[A-Z]{2,3}$/.test(primary) ? primary : null;
};

/**
 * Insert an `@inContext(language: …)` directive into a named GraphQL *query*
 * operation so the Storefront API returns translated content (title,
 * description, …) for the shopper's language. Only `language` is set — never
 * `country` — so prices/availability are unchanged; B2B pricing comes from the
 * b2b_price metafield regardless. Mutations are left untouched (the regex only
 * matches the `query` keyword) so cart writes are unaffected.
 * @param {string} query
 * @param {string | null} language  Shopify LanguageCode enum value, or null to skip.
 * @returns {string}
 */
const withInContextLanguage = function withInContextLanguage(query, language) {
  if (!language || typeof query !== 'string' || query.includes('@inContext')) return query;
  return query.replace(
    /(query\s+[A-Za-z0-9_]+(?:\s*\([\s\S]*?\))?)\s*\{/,
    `$1 @inContext(language: ${language}) {`
  );
};

/**
 * @param {{ getConfig: () => any, apiVersion?: string, maxRetries?: number }} deps
 */
const createStorefrontClient = ({ getConfig, apiVersion = '2025-10', maxRetries = 2 }) => {
  /**
   * @param {string} query
   * @param {Record<string, any>} variables
   * @param {number} [retries]
   */
  async function storefrontFetch(query, variables, retries = 0) {
    const cfg = getConfig();
    if (!cfg?.token || !cfg?.domain) throw new Error('B2B Storefront config missing');

    const localizedQuery = withInContextLanguage(query, languageCodeFromLocale(cfg.locale));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);

    try {
      const res = await fetch(
        `https://${cfg.domain}/api/${apiVersion}/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': cfg.token,
          },
          body: JSON.stringify({ query: localizedQuery, variables }),
          signal: controller.signal,
        }
      );
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.errors) throw new Error(json.errors.map((/** @type {{message?: string}} */ e) => e.message).join('; '));
      return json.data;
    } catch (err) {
      clearTimeout(timeout);
      if (retries < maxRetries) {
        await new Promise((r) => setTimeout(r, 1000 * (retries + 1)));
        return storefrontFetch(query, variables, retries + 1);
      }
      throw err;
    }
  }

  /**
   * @param {string} query
   * @param {any} variables
   */
  async function storefrontFetchPartial(query, variables) {
    const cfg = getConfig();
    if (!cfg?.token || !cfg?.domain) throw new Error('B2B Storefront config missing');
    const localizedQuery = withInContextLanguage(query, languageCodeFromLocale(cfg.locale));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const res = await fetch(`https://${cfg.domain}/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token,
        },
        body: JSON.stringify({ query: localizedQuery, variables }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return { data: json.data ?? null, errors: json.errors ?? null };
    } catch (err) {
      clearTimeout(timeout);
      throw err;
    }
  }

  return { storefrontFetch, storefrontFetchPartial };
};

/**
 * Page config parsing: reads the [data-b2b-config] node Liquid injects (last one
 * wins — page sections come after theme.liquid's global VIP config). Moved
 * verbatim from legacy.js; exported as const-bound named function expressions so
 * the classic-script bundle gains no window globals.
 */

const taxToggleEnabledFromPage = function taxToggleEnabledFromPage() {
    const c = typeof window !== 'undefined' && window.__taxToggleConfig;
    return !!(c && c.taxToggleEnabled === true);
};

/** Build a storefront path including language/market subdirectory when Theme.routes.root_url is set. */
const localePrefixedStorePath = function localePrefixedStorePath(pathAfterRoot) {
    const ThemeObj = typeof Theme !== 'undefined' ? Theme : null;
    const raw = ThemeObj?.routes?.root_url != null ? String(ThemeObj.routes.root_url) : '/';
    const base = raw.replace(/\/+$/, '');
    const tail = pathAfterRoot.startsWith('/') ? pathAfterRoot : `/${pathAfterRoot}`;
    return base === '' ? tail : `${base}${tail}`;
};

/** @returns {B2BConfig | null} */
const getConfig = function getConfig() {
    /* Prefer the last [data-b2b-config]: page sections come after theme.liquid's global VIP config, so B2B catalog settings win for VIP customers browsing for-professionals. */
    const nodes = document.querySelectorAll('[data-b2b-config]');
    const el = nodes.length ? /** @type {HTMLElement} */ (nodes[nodes.length - 1]) : null;
    if (!el) return null;
    const scopeKey = (el.dataset.b2bScope || 'b2b').trim() || 'b2b';
    const vipScope = scopeKey.toLowerCase() === 'vip';
    return {
      token: el.dataset.b2bToken,
      domain: el.dataset.b2bDomain,
      productUrl:
        el.dataset.b2bProductUrl ||
        localePrefixedStorePath(vipScope ? 'pages/private-cellar-product' : 'pages/for-professionals-product'),
      draftOrderEndpoint: (el.dataset.b2bDraftOrderEndpoint || '').trim(),
      checkoutFlow: (el.dataset.b2bCheckoutFlow || 'checkout').trim() || 'checkout',
      orderRequestEmail: (el.dataset.b2bOrderRequestEmail || '').trim(),
      shopEmail: (el.dataset.b2bShopEmail || '').trim(),
      customerEmail: (el.dataset.b2bCustomerEmail || '').trim(),
      customerName: (el.dataset.b2bCustomerName || '').trim(),
      viewLabel: el.dataset.b2bViewLabel || 'View',
      purchaseLabel: el.dataset.b2bPurchaseLabel || 'Purchase',
      soldOutLabel: el.dataset.b2bSoldOutLabel || 'Sold out',
      noProductsLabel: el.dataset.b2bNoProducts || 'No products found.',
      loadingLabel: el.dataset.b2bLoading || 'Loading…',
      cardColorScheme: el.dataset.b2bCardColorScheme || 'scheme-3',
      sectionColorScheme: el.dataset.b2bSectionColorScheme || '',
      locale: document.documentElement.lang || 'fr-FR',
      moneyFormat: el.dataset.b2bMoneyFormat || '',
      currency: el.dataset.b2bCurrency || '',
      taxLabelTtc: el.dataset.b2bTaxLabelTtc || 'inc. VAT',
      taxLabelHt: el.dataset.b2bTaxLabelHt || 'Ex. VAT',
      taxToggleEnabled: taxToggleEnabledFromPage(),
      priceSaleLabel: el.dataset.b2bPriceSaleLabel || '',
      priceRegularLabel: el.dataset.b2bPriceRegularLabel || '',
      inStockLabel: el.dataset.b2bInStock || 'In stock',
      outOfStockLabel: el.dataset.b2bOutOfStock || 'Out of stock',
      limitedAvailabilityLabel: el.dataset.b2bLimitedAvailability || 'Limited availability',
      availableQuantityLabel: el.dataset.b2bAvailableQuantity || 'Available quantity: {{ count }}',
      otherVintagesLabel: el.dataset.b2bOtherVintages || 'Other Vintages:',
      addToCartLabel: el.dataset.b2bAddToCart || 'Add to Basket',
      cartTitleLabel: el.dataset.b2bCartTitle || (vipScope ? 'Cart' : 'B2B Cart'),
      cartEmptyLabel: el.dataset.b2bCartEmpty || (vipScope ? 'Your cart is empty.' : 'Your B2B cart is empty.'),
      continueShoppingLabel: el.dataset.b2bContinueShopping || 'Continue shopping',
      cartCheckoutLabel: el.dataset.b2bCartCheckout || 'Checkout',
      cartPlaceOrderLabel: el.dataset.b2bCartPlaceOrder || 'Place order',
      cartCloseLabel: el.dataset.b2bCartClose || 'Close',
      cartRemoveLabel: el.dataset.b2bCartRemove || 'Remove',
      productTag: (el.dataset.b2bProductTag || 'B2B').trim() || 'B2B',
      scopeKey: scopeKey,
      orderRequestTitle: el.dataset.b2bOrderRequestTitle || 'Order request',
      orderRequestIntro: el.dataset.b2bOrderRequestIntro || '',
      orderRequestSentTo: el.dataset.b2bOrderRequestSentTo || 'Sent to:',
      orderRequestNoEmail: el.dataset.b2bOrderRequestNoEmail || '',
      orderRequestSummary: el.dataset.b2bOrderRequestSummary || 'Summary',
      orderRequestCompany: el.dataset.b2bOrderRequestCompany || 'Company',
      orderRequestContactName: el.dataset.b2bOrderRequestContactName || 'Contact name',
      orderRequestContactEmail: el.dataset.b2bOrderRequestContactEmail || 'Contact email',
      orderRequestPhoneOptional: el.dataset.b2bOrderRequestPhoneOptional || 'Phone (optional)',
      orderRequestNotesOptional: el.dataset.b2bOrderRequestNotesOptional || 'Notes (optional)',
      orderRequestSend: el.dataset.b2bOrderRequestSend || 'Send order request',
      orderRequestCopy: el.dataset.b2bOrderRequestCopy || 'Copy request',
      orderRequestBack: el.dataset.b2bOrderRequestBack || 'Back to cart',
      orderRequestEmptyLine: el.dataset.b2bOrderRequestEmptyLine || '(empty)',
      orderRequestDiscountLabel: el.dataset.b2bOrderRequestDiscountLabel || 'Discount code:',
      cartSubtotalHt: el.dataset.b2bCartSubtotalHt || 'Subtotal ex. VAT',
      cartTotalEstimatedTtc: el.dataset.b2bCartTotalEstimatedTtc || 'Estimated total inc. VAT',
      cartTotalEstimated: el.dataset.b2bCartTotalEstimated || 'Estimated total',
      discountToggleLabel: el.dataset.b2bDiscountToggleLabel || 'Discount code',
      discountPlaceholder: el.dataset.b2bDiscountPlaceholder || 'Discount code',
      discountApply: el.dataset.b2bDiscountApply || 'Apply',
      discountFeedbackPattern: el.dataset.b2bDiscountFeedbackPattern || 'Code "__CODE__" will be applied at checkout.',
      discountRemove: el.dataset.b2bDiscountRemove || 'Remove',
      orderNoteToggleLabel: el.dataset.b2bOrderNoteToggleLabel || 'Order notes',
      taxNoteB2b: el.dataset.b2bTaxNoteB2b || '',
      taxNoteVip: el.dataset.b2bTaxNoteVip || '',
      privateCellarMinimumOrder:
        el.dataset.b2bPrivateCellarMinimumOrder || 'Minimum order value (total order): €120',
      bankTransferTitleDefault: el.dataset.b2bBankTransferTitle || 'Bank transfer required',
      bankTransferMessageDefault: el.dataset.b2bBankTransferMessage || '',
      referenceLabel: el.dataset.b2bReferenceLabel || 'Reference:',
      alertUnableOpenOrderRequest: el.dataset.b2bAlertUnableOpenOrderRequest || '',
      alertUnablePlaceOrderRequest: el.dataset.b2bAlertUnablePlaceOrderRequest || '',
      alertUnableCreateCheckout: el.dataset.b2bAlertUnableCreateCheckout || '',
      alertMissingVipChannel:
        el.dataset.b2bAlertMissingVipChannel ||
        'Oops — looks like this product is missing the VIP sales channel, so it can’t be added to your Private Cellar order:',
      feedbackCopied: el.dataset.b2bFeedbackCopied || '',
      feedbackCopyFailed: el.dataset.b2bFeedbackCopyFailed || '',
      feedbackEmailFailed: el.dataset.b2bFeedbackEmailFailed || '',
      feedbackNoRecipient: el.dataset.b2bFeedbackNoRecipient || '',
      feedbackOpeningEmail: el.dataset.b2bFeedbackOpeningEmail || '',
      feedbackSendingRequest: el.dataset.b2bFeedbackSendingRequest || '',
      feedbackRequestSentLine1: el.dataset.b2bFeedbackRequestSentLine1 || '',
      feedbackRequestSentLine2: el.dataset.b2bFeedbackRequestSentLine2 || '',
      feedbackRequestSentContinue: el.dataset.b2bFeedbackRequestSentContinue || '',
      feedbackRequestSentContinueLink: el.dataset.b2bFeedbackRequestSentContinueLink || '',
      quantityLabel: el.dataset.b2bQuantityLabel || 'Quantity:',
      closeOptionsLabel: el.dataset.b2bCloseOptionsLabel || 'Close',
      ariaDecreaseQuantity: el.dataset.b2bAriaDecreaseQuantity || 'Decrease quantity',
      ariaIncreaseQuantity: el.dataset.b2bAriaIncreaseQuantity || 'Increase quantity',
      ariaLineQuantity: el.dataset.b2bAriaLineQuantity || 'Quantity',
      cartBubblePrefix: el.dataset.b2bCartBubblePrefix || 'Cart:',
      addedToCart: el.dataset.b2bAddedToCart || 'Added',
      variantUnavailableSuffix: el.dataset.b2bVariantUnavailableSuffix || ' - Unavailable',
      orderEmailSubjectPrefix: el.dataset.b2bOrderEmailSubjectPrefix || 'B2B order request —',
      orderEmailBodyHeader: el.dataset.b2bOrderEmailBodyHeader || 'B2B ORDER REQUEST (bank transfer)',
      orderEmailCompany: el.dataset.b2bOrderEmailCompany || 'Company:',
      orderEmailContact: el.dataset.b2bOrderEmailContact || 'Contact:',
      orderEmailEmail: el.dataset.b2bOrderEmailEmail || 'Email:',
      orderEmailPhone: el.dataset.b2bOrderEmailPhone || 'Phone:',
      orderEmailLines: el.dataset.b2bOrderEmailLines || 'Lines:',
      orderEmailDiscount: el.dataset.b2bOrderEmailDiscount || 'Discount code:',
      orderEmailNotes: el.dataset.b2bOrderEmailNotes || 'Notes:',
      orderEmailThemeCartId: el.dataset.b2bOrderEmailThemeCartId || 'Theme cart id:',
      orderEmailPage: el.dataset.b2bOrderEmailPage || 'Page:',
      errorFormNotFound: el.dataset.b2bErrorFormNotFound || 'Form not found',
      conditionDefaultLabel: el.dataset.b2bConditionDefault || 'Perfect',
      colourFilterLabel: el.dataset.b2bColourFilterLabel || 'Colour',
      emptyProductImageUrl: (el.dataset.b2bEmptyProductImageUrl || '').trim(),
      cartItemsGap: (() => {
        const raw = parseInt(String(el.dataset.b2bCartItemsGap || '').trim(), 10);
        if (!Number.isFinite(raw)) return 24;
        return Math.min(36, Math.max(8, raw));
      })(),
      insuranceFeeVariantId: (el.dataset.b2bInsuranceFeeVariantId || '').trim(),
      insuranceFeeDenominations: (el.dataset.b2bInsuranceFeeDenominations || '').trim(),
      insuranceFeeDetails: (el.dataset.b2bInsuranceFeeDetails || '').trim(),
      feeDetailsLabel: el.dataset.b2bFeeDetailsLabel || 'Details:',
      insuranceFeePercent: (() => {
        const raw = parseFloat(el.dataset.b2bInsuranceFeePercent || '0');
        return Number.isFinite(raw) && raw > 0 ? raw : 0;
      })(),
      exciseFeeVariantId: (el.dataset.b2bExciseFeeVariantId || '').trim(),
      exciseFeeDenominations: (el.dataset.b2bExciseFeeDenominations || '').trim(),
      exciseSeFeeDenominations: (el.dataset.b2bExciseSeFeeDenominations || '').trim(),
      exciseFeeDetails: (el.dataset.b2bExciseFeeDetails || '').trim(),
      exciseUsPercent: (() => {
        const raw = parseFloat(el.dataset.b2bExciseUsPercent || '0');
        return Number.isFinite(raw) && raw > 0 ? raw : 0;
      })(),
      exciseSePerBottle: (() => {
        const raw = parseFloat(el.dataset.b2bExciseSePerBottle || '0');
        return Number.isFinite(raw) && raw > 0 ? raw : 0;
      })(),
      localizationCountry: (el.dataset.b2bLocalizationCountry || '').trim().toUpperCase(),
    };
};

/**
 * @param {B2BConfig | null | undefined} cfg
 * @param {string | null | undefined} code
 * @returns {string}
 */
const formatDiscountFeedback = function formatDiscountFeedback(cfg, code) {
    const raw = (cfg && cfg.discountFeedbackPattern) || 'Code "__CODE__" will be applied at checkout.';
    return String(raw).replace(/__CODE__/g, code == null ? '' : String(code));
};

/**
 * The shared Storefront API client instance — config is read from the page's
 * [data-b2b-config] node on every call. legacy.js and any module that needs to
 * talk to the Storefront API import { storefrontFetch, storefrontFetchPartial }
 * from here, so the whole bundle shares one client.
 */

const { storefrontFetch, storefrontFetchPartial } = createStorefrontClient({ getConfig });

/**
 * GraphQL documents for the B2B/VIP Storefront API — moved verbatim from
 * legacy.js (pure template literals, no interpolation).
 *
 * Field notes that matter elsewhere:
 * - CART_QUERY must keep `selectedOptions { name value }` on line merchandise:
 *   the Private Cellar excise fee counts bottles via getCaseQty(variant).
 * - SAFE_CART_LINES_QUERY is deliberately product-free so a cart poisoned by
 *   an off-channel line can still be read (ProductVariant.product is
 *   non-nullable and would null the whole response otherwise).
 */

const PRODUCTS_QUERY = `
    query B2BProducts($first: Int!, $cursor: String, $query: String, $sortKey: ProductSortKeys, $reverse: Boolean) {
      products(first: $first, after: $cursor, query: $query, sortKey: $sortKey, reverse: $reverse) {
        edges {
          node {
            id
            title
            handle
            description
            publishedAt
            vendor
            productType
            tags
            vintageMetafield: metafield(namespace: "custom", key: "vintage") { value }
            grapeColourMetafield: metafield(namespace: "custom", key: "grape_colour") { value }
            grapeColorMetafield: metafield(namespace: "custom", key: "grape_color") { value }
            millesimesRegionMetafield: metafield(namespace: "custom", key: "millesimes_region") { value }
            regionSubLastParentIdMetafield: metafield(namespace: "custom", key: "region_sub_last_parent_id") { value }
            millesimesProducteurMetafield: metafield(namespace: "custom", key: "millesimes_producteur") { value }
            producerNameMetafield: metafield(namespace: "custom", key: "producer_name") { value }
            millesimesCuveeMetafield: metafield(namespace: "custom", key: "millesimes_cuvee") { value }
            titlePartNameMetafield: metafield(namespace: "custom", key: "title_part_name") { value }
            millesimesAppellationMetafield: metafield(namespace: "custom", key: "millesimes_appellation") { value }
            millesimesClassificationMetafield: metafield(namespace: "custom", key: "millesimes_classification") { value }
            millesimesCouleurMetafield: metafield(namespace: "custom", key: "millesimes_couleur") { value }
            eventShortDescriptionMetafield: metafield(namespace: "custom", key: "event_short_description") { value }
            titlePartDesc1Metafield: metafield(namespace: "custom", key: "title_part_desc1") { value }
            titlePartDesc2Metafield: metafield(namespace: "custom", key: "title_part_desc2") { value }
            featuredImage { url altText width height }
            images(first: 5) {
              edges { node { url altText width height } }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  availableForSale
                  quantityAvailable
                  selectedOptions { name value }
                  metafield(namespace: "custom", key: "b2b_price") { value }
                  purchaseLimitUnitsMetafield: metafield(namespace: "custom", key: "purchase_limit_units") { value }
                  formatMetafield: metafield(namespace: "custom", key: "format") { value }
                  formatSizeMetafield: metafield(namespace: "custom", key: "size") { value }
                }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

const PRODUCTS_SEARCH_QUERY = `
    query B2BProductsSearch($first: Int!, $cursor: String, $query: String!, $productFilters: [ProductFilter!]) {
      search(first: $first, after: $cursor, query: $query, types: [PRODUCT], productFilters: $productFilters) {
        edges {
          node {
            ... on Product {
              id
              title
              handle
              description
              publishedAt
              vendor
              productType
              tags
              vintageMetafield: metafield(namespace: "custom", key: "vintage") { value }
              grapeColourMetafield: metafield(namespace: "custom", key: "grape_colour") { value }
              grapeColorMetafield: metafield(namespace: "custom", key: "grape_color") { value }
              millesimesRegionMetafield: metafield(namespace: "custom", key: "millesimes_region") { value }
              regionSubLastParentIdMetafield: metafield(namespace: "custom", key: "region_sub_last_parent_id") { value }
              millesimesProducteurMetafield: metafield(namespace: "custom", key: "millesimes_producteur") { value }
              producerNameMetafield: metafield(namespace: "custom", key: "producer_name") { value }
              millesimesCuveeMetafield: metafield(namespace: "custom", key: "millesimes_cuvee") { value }
              titlePartNameMetafield: metafield(namespace: "custom", key: "title_part_name") { value }
              millesimesAppellationMetafield: metafield(namespace: "custom", key: "millesimes_appellation") { value }
              millesimesClassificationMetafield: metafield(namespace: "custom", key: "millesimes_classification") { value }
              millesimesCouleurMetafield: metafield(namespace: "custom", key: "millesimes_couleur") { value }
              eventShortDescriptionMetafield: metafield(namespace: "custom", key: "event_short_description") { value }
              titlePartDesc1Metafield: metafield(namespace: "custom", key: "title_part_desc1") { value }
              titlePartDesc2Metafield: metafield(namespace: "custom", key: "title_part_desc2") { value }
              featuredImage { url altText width height }
              images(first: 5) {
                edges { node { url altText width height } }
              }
              variants(first: 100) {
                edges {
                  node {
                    id
                    title
                    price { amount currencyCode }
                    compareAtPrice { amount currencyCode }
                    availableForSale
                    quantityAvailable
                    selectedOptions { name value }
                    metafield(namespace: "custom", key: "b2b_price") { value }
                    purchaseLimitUnitsMetafield: metafield(namespace: "custom", key: "purchase_limit_units") { value }
                    formatMetafield: metafield(namespace: "custom", key: "format") { value }
                    formatSizeMetafield: metafield(namespace: "custom", key: "size") { value }
                  }
                }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

const PRODUCTS_SEARCH_CARD_QUERY = `
    query B2BProductsSearchCard($first: Int!, $cursor: String, $query: String!, $productFilters: [ProductFilter!]) {
      search(first: $first, after: $cursor, query: $query, types: [PRODUCT], productFilters: $productFilters) {
        edges {
          node {
            ... on Product {
              id
              title
              handle
              description
              tags
              vintageMetafield: metafield(namespace: "custom", key: "vintage") { value }
              grapeColourMetafield: metafield(namespace: "custom", key: "grape_colour") { value }
              grapeColorMetafield: metafield(namespace: "custom", key: "grape_color") { value }
              millesimesRegionMetafield: metafield(namespace: "custom", key: "millesimes_region") { value }
              regionSubLastParentIdMetafield: metafield(namespace: "custom", key: "region_sub_last_parent_id") { value }
              millesimesProducteurMetafield: metafield(namespace: "custom", key: "millesimes_producteur") { value }
              producerNameMetafield: metafield(namespace: "custom", key: "producer_name") { value }
              producerRefMetafield: metafield(namespace: "custom", key: "producer_ref") { value }
              producerWlIdMetafield: metafield(namespace: "custom", key: "producer_wl_id") { value }
              producerWinelabsIdMetafield: metafield(namespace: "custom", key: "producer_winelabs_id") { value }
              producerWineLabsIdMetafield: metafield(namespace: "custom", key: "producer_wine_labs_id") { value }
              millesimesCuveeMetafield: metafield(namespace: "custom", key: "millesimes_cuvee") { value }
              titlePartNameMetafield: metafield(namespace: "custom", key: "title_part_name") { value }
              millesimesAppellationMetafield: metafield(namespace: "custom", key: "millesimes_appellation") { value }
              millesimesClassificationMetafield: metafield(namespace: "custom", key: "millesimes_classification") { value }
              millesimesCouleurMetafield: metafield(namespace: "custom", key: "millesimes_couleur") { value }
              eventShortDescriptionMetafield: metafield(namespace: "custom", key: "event_short_description") { value }
              titlePartDesc1Metafield: metafield(namespace: "custom", key: "title_part_desc1") { value }
              titlePartDesc2Metafield: metafield(namespace: "custom", key: "title_part_desc2") { value }
              featuredImage { url altText width height }
              variants(first: 100) {
                edges {
                  node {
                    id
                    title
                    price { amount currencyCode }
                    compareAtPrice { amount currencyCode }
                    availableForSale
                    quantityAvailable
                    selectedOptions { name value }
                    metafield(namespace: "custom", key: "b2b_price") { value }
                    purchaseLimitUnitsMetafield: metafield(namespace: "custom", key: "purchase_limit_units") { value }
                    formatMetafield: metafield(namespace: "custom", key: "format") { value }
                    formatSizeMetafield: metafield(namespace: "custom", key: "size") { value }
                  }
                }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

const PRODUCTS_COUNT_QUERY = `
    query B2BProductsCount($query: String!, $productFilters: [ProductFilter!]) {
      search(first: 1, query: $query, types: [PRODUCT], productFilters: $productFilters) {
        totalCount
      }
    }
  `;

const PRODUCT_QUERY = `
    query B2BProduct($handle: String!) {
      product(handle: $handle) {
        id
        title
        handle
        description
        descriptionHtml
        vendor
        productType
        tags
        vintageMetafield: metafield(namespace: "custom", key: "vintage") { value }
        degustationMetafield: metafield(namespace: "custom", key: "degustation") { value }
        relatedVintagesMetafield: metafield(namespace: "custom", key: "related_vintages") {
          references(first: 20) {
            nodes {
              ... on Product {
                id
                handle
                title
                vintageMetafield: metafield(namespace: "custom", key: "vintage") { value }
              }
            }
          }
        }
        millesimesRegionMetafield: metafield(namespace: "custom", key: "millesimes_region") { value }
        millesimesProducteurMetafield: metafield(namespace: "custom", key: "millesimes_producteur") { value }
        millesimesCuveeMetafield: metafield(namespace: "custom", key: "millesimes_cuvee") { value }
        titlePartNameMetafield: metafield(namespace: "custom", key: "title_part_name") { value }
        millesimesAppellationMetafield: metafield(namespace: "custom", key: "millesimes_appellation") { value }
        millesimesClassificationMetafield: metafield(namespace: "custom", key: "millesimes_classification") { value }
        millesimesCouleurMetafield: metafield(namespace: "custom", key: "millesimes_couleur") { value }
        regionRefMetafield: metafield(namespace: "custom", key: "region_ref") { value }
        regionSubLastParentIdMetafield: metafield(namespace: "custom", key: "region_sub_last_parent_id") { value }
        producerNameMetafield: metafield(namespace: "custom", key: "producer_name") { value }
        producerRefMetafield: metafield(namespace: "custom", key: "producer_ref") { value }
        producerWlIdMetafield: metafield(namespace: "custom", key: "producer_wl_id") { value }
        producerWinelabsIdMetafield: metafield(namespace: "custom", key: "producer_winelabs_id") { value }
        producerWineLabsIdMetafield: metafield(namespace: "custom", key: "producer_wine_labs_id") { value }
        grapeColourMetafield: metafield(namespace: "custom", key: "grape_colour") { value }
        grapeColorMetafield: metafield(namespace: "custom", key: "grape_color") { value }
        grapeNameMetafield: metafield(namespace: "custom", key: "grape_name") { value }
        abvMetafield: metafield(namespace: "custom", key: "abv") { value type }
        conditionMetafield: metafield(namespace: "custom", key: "condition") { value }
        eventShortDescriptionMetafield: metafield(namespace: "custom", key: "event_short_description") { value }
        titlePartDesc1Metafield: metafield(namespace: "custom", key: "title_part_desc1") { value }
        titlePartDesc2Metafield: metafield(namespace: "custom", key: "title_part_desc2") { value }
        jebDunnuckScoreHighMetafield: metafield(namespace: "custom", key: "jeb_dunnuck_score_high") { value }
        jebDunnuckTastingNoteMetafield: metafield(namespace: "custom", key: "jeb_dunnuck_tasting_note") { value }
        wineSpectatorScoreHighMetafield: metafield(namespace: "custom", key: "wine_spectator_score_high") { value }
        wineSpectatorTastingNoteMetafield: metafield(namespace: "custom", key: "wine_spectator_tasting_note") { value }
        vinousScoreHighMetafield: metafield(namespace: "custom", key: "vinous_score_high") { value }
        vinousTastingNoteMetafield: metafield(namespace: "custom", key: "vinous_tasting_note") { value }
        jamesSucklingScoreHighMetafield: metafield(namespace: "custom", key: "james_suckling_score_high") { value }
        jamesSucklingTastingNoteMetafield: metafield(namespace: "custom", key: "james_suckling_tasting_note") { value }
        wineAdvocateScoreHighMetafield: metafield(namespace: "custom", key: "wine_advocate_score_high") { value }
        wineAdvocateTastingNoteMetafield: metafield(namespace: "custom", key: "wine_advocate_tasting_note") { value }
        featuredImage { url altText width height }
        images(first: 20) {
          edges { node { url altText width height } }
        }
        variants(first: 250) {
          edges {
            node {
              id
              title
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              availableForSale
              quantityAvailable
              selectedOptions { name value }
              metafield(namespace: "custom", key: "b2b_price") { value }
              purchaseLimitUnitsMetafield: metafield(namespace: "custom", key: "purchase_limit_units") { value }
              abvVariantMetafield: metafield(namespace: "custom", key: "abv") { value type }
            }
          }
        }
      }
    }
  `;

const REGIONS_QUERY = `
    query B2BRegions($cursor: String) {
      metaobjects(type: "region", first: 250, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id
          map_shown: field(key: "map_shown") { value }
          name: field(key: "name") { value }
          region_id: field(key: "region_id") { value }
          sub_last_parent_id: field(key: "sub_last_parent_id") { value }
          hierarchy: field(key: "hierarchy") { value }
        }
      }
    }
  `;

const PRODUCERS_QUERY = `
    query B2BProducers($cursor: String) {
      metaobjects(type: "producer", first: 250, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id
          handle
          producer_title: field(key: "producer_title") { value }
          title: field(key: "title") { value }
          details_domaine: field(key: "details_domaine") { value }
          producer_provided_overview: field(key: "producer_provided_overview") { value }
          producer_overview: field(key: "producer_overview") { value }
          producer_card_image: field(key: "producer_card_image") { value }
          main_image: field(key: "main_image") { value }
          producer_wl_id: field(key: "producer_wl_id") { value }
          producer_winelabs_id: field(key: "producer_winelabs_id") { value }
          producer_wine_labs_id: field(key: "producer_wine_labs_id") { value }
          producer_wl_labs_id: field(key: "producer_wl_labs_id") { value }
          producer_id: field(key: "producer_id") { value }
        }
      }
    }
  `;

const MEDIA_IMAGE_URL_QUERY = `
    query B2BMediaImageUrl($id: ID!) {
      node(id: $id) {
        ... on MediaImage {
          image { url altText }
        }
      }
    }
  `;

const CART_QUERY = `
    query B2BCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        totalQuantity
        cost {
          subtotalAmount { amount currencyCode }
          totalAmount { amount currencyCode }
        }
        lines(first: 50) {
          edges {
            node {
              id
              quantity
              cost { totalAmount { amount currencyCode } }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  quantityAvailable
                  selectedOptions { name value }
                  purchaseLimitUnitsMetafield: metafield(namespace: "custom", key: "purchase_limit_units") { value }
                  image { url altText width height }
                  product {
                    title
                    handle
                    description
                    tags
                    collections(first: 20) {
                      edges {
                        node { handle }
                      }
                    }
                    featuredImage { url altText width height }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

const SAFE_CART_LINES_QUERY = `
    query B2BCartSafeLines($cartId: ID!) {
      cart(id: $cartId) {
        id
        lines(first: 50) {
          edges { node { id merchandise { ... on ProductVariant { id } } } }
        }
      }
    }
  `;

const VARIANT_PRODUCT_AVAIL_QUERY = `
    query B2BVariantProductAvail($ids: [ID!]!) {
      nodes(ids: $ids) { ... on ProductVariant { id product { id } } }
    }
  `;

/**
 * Catalog value normalizers and product predicates shared by filters, facets and
 * rendering (accent/case-insensitive comparisons etc.). Moved verbatim from legacy.js.
 */

/**
 * @param {any} product
 * @param {string} fieldKey
 */
const getMetafieldValue = function getMetafieldValue(product, fieldKey) {
    const node = product[fieldKey];
    const v = node?.value ?? node;
    return v != null ? String(v).trim() : '';
};

/** Producer label for B2B filters — matches buildProductQuery (producer_name, else millesimes_producteur). */
/** @param {any} product */
const getProducerPrimaryLabel = function getProducerPrimaryLabel(product) {
    if (!product) return '';
    const name = (product.producerNameMetafield?.value || '').toString().trim();
    const alt = (product.millesimesProducteurMetafield?.value || '').toString().trim();
    return name || alt;
};

/** Stable comparison for producer filter (facet labels vs metafield strings vs checkbox values). */
/** @param {unknown} s */
const normalizeProducerFilterValue = function normalizeProducerFilterValue(s) {
    if (s == null) return '';
    return String(s)
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
};

/** Normalize vintage strings so "2015", "2015.0", " 2015 " match filter checkboxes. */
/** @param {unknown} raw */
const normalizeVintageYearForFilter = function normalizeVintageYearForFilter(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    const n = parseInt(s, 10);
    if (Number.isFinite(n) && n >= 1700 && n <= 2100) return String(n);
    return s;
};

/**
 * Normalize region_sub_last_parent_id values (metaobject + product metafield) so "7", "7.0", 7 match.
 * Integer metafields are indexed in Storefront search as numbers — quoted "7" in the query often returns 0 hits.
 */
/** @param {unknown} raw */
const normalizeRegionFilterValue = function normalizeRegionFilterValue(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (/^\d+(\.\d+)?$/.test(s)) {
      const n = parseFloat(s);
      if (Number.isFinite(n) && Math.floor(n) === n) return String(Math.floor(n));
    }
    return s;
};

/** True if product has at least one variant available for sale. */
/** @param {any} product */
const isProductInStock = function isProductInStock(product) {
    const edges = product.variants?.edges;
    if (!Array.isArray(edges)) return false;
    return edges.some((e) => e?.node?.availableForSale === true);
};

/**
 * Formatted (display) prices: shop money-format via config, b2b_price metafield
 * override, and per-unit division for case packs. The card, picker and cart all
 * read prices through these. Moved verbatim from legacy.js.
 */

/**
 * @param {number} amount
 * @param {string | undefined} currencyCode
 */
const formatMoney = function formatMoney(amount, currencyCode) {
    const cfg = getConfig();
    const currency = currencyCode || cfg?.currency || 'EUR';
    if (cfg?.moneyFormat) {
      const formatted = formatMoneyWithShopFormat(Number(amount), cfg.moneyFormat, currency);
      if (formatted) return formatted;
    }
    try {
      return new Intl.NumberFormat(cfg?.locale || 'fr-FR', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${currency} ${Number(amount).toFixed(2)}`;
    }
};

/** @param {any} variant */
const getDisplayPrice = function getDisplayPrice(variant) {
    let amount = null;
    let currencyCode = variant.price?.currencyCode;
    const meta = variant.metafield?.value;
    if (meta) {
      try {
        const parsed = JSON.parse(meta);
        amount = parseFloat(parsed.amount);
        currencyCode = parsed.currencyCode || currencyCode;
      } catch { /* fall through */ }
    }
    if (amount == null || !Number.isFinite(amount)) {
      amount = parseFloat(variant.price?.amount) || 0;
    }
    const caseQty = getCaseQty(variant);
    if (caseQty > 0) {
      amount = dividePriceByCaseQty(amount, caseQty);
    }
    return formatMoney(amount, currencyCode);
};

/** Formatted compare-at price; when variant is a case, returns unit compare-at price. */
/** @param {any} variant */
const getDisplayCompareAtPrice = function getDisplayCompareAtPrice(variant) {
    const comp = variant.compareAtPrice;
    if (!comp || !Number.isFinite(parseFloat(comp.amount))) return null;
    let amount = parseFloat(comp.amount);
    const caseQty = getCaseQty(variant);
    if (caseQty > 0) {
      amount = dividePriceByCaseQty(amount, caseQty);
    }
    return formatMoney(amount, comp.currencyCode);
};

/**
 * Major-unit sale or compare amount after B2B metafield (sale only) + case division — for compare visibility.
 */
/** @param {any} variant */
/** @param {'sale' | 'compare'} role */
const getVariantMajorAmountPerDisplayedUnit = function getVariantMajorAmountPerDisplayedUnit(variant, role) {
    let amount = null;
    if (role === 'sale') {
      const meta = variant.metafield?.value;
      if (meta) {
        try {
          amount = parseFloat(JSON.parse(meta).amount);
        } catch (_) {
          /* ignore */
        }
      }
    }
    const node = role === 'sale' ? variant.price : variant.compareAtPrice;
    if (amount == null || !Number.isFinite(amount)) {
      amount = parseFloat(node?.amount) || 0;
    }
    const cq = getCaseQty(variant);
    if (cq > 0) amount = dividePriceByCaseQty(amount, cq);
    return amount;
};

/**
 * Fee-line predicates and computations for the cents-as-quantity placeholder fees
 * (non-Plus store: no Cart Transform). Insurance = percent of product subtotal;
 * excise/duty by destination (US percent, Sweden per-bottle) where the destination
 * comes from the Magical Fees confirmed location, its __pc_shipping_destination
 * mirror, or the localization country — in that order. Pure reads only: the cart
 * mutation/sync/watcher code stays in legacy.js. Moved verbatim.
 */

/** @param {string | number | null | undefined} id */
const normalizeInsuranceFeeVariantGid = function normalizeInsuranceFeeVariantGid(id) {
    if (!id) return '';
    id = String(id).trim();
    if (id.startsWith('gid://')) return id;
    return `gid://shopify/ProductVariant/${id}`;
};

/** @param {B2BConfig | null | undefined} cfg */
const getConfiguredInsuranceFeeGid = function getConfiguredInsuranceFeeGid(cfg) {
    return normalizeInsuranceFeeVariantGid((cfg || {}).insuranceFeeVariantId || '');
};

/**
 * @param {any} line
 * @param {string | string[] | Set<string>} feeGids One insurance fee variant GID, or a list/set
 *   of them (the denomination ladder — see {@link getInsuranceFeeGids}). Matches the line's
 *   merchandise against any of them.
 */
const isInsuranceFeeLine = function isInsuranceFeeLine(line, feeGids) {
    if (!line || !feeGids) return false;
    const id = normalizeInsuranceFeeVariantGid(line?.merchandise?.id || '');
    if (!id) return false;
    if (typeof feeGids === 'string') return id === feeGids;
    if (Array.isArray(feeGids)) return feeGids.indexOf(id) !== -1;
    if (feeGids instanceof Set) return feeGids.has(id);
    return false;
};

/**
 * First numeric variant id from a token that may be a bare id, a `gid://…/ProductVariant/123`,
 * or a full Shopify admin URL (`…/products/…/variants/123`). Returns '' when no id is present.
 * @param {string} token
 */
const extractVariantIdToken = function extractVariantIdToken(token) {
    const s = String(token || '').trim();
    if (!s) return '';
    if (s.startsWith('gid://')) return s;
    const m = s.match(/(\d{3,})\D*$/);
    return m ? m[1] : s;
};

/**
 * Parse a per-unit amount in major units to integer cents. Accepts dot or comma decimals and a
 * leading currency symbol, e.g. "0.10", "0,10", "€1.00", "100". Returns 0 when invalid.
 * @param {string} raw
 */
const amountStringToCents = function amountStringToCents(raw) {
    const n = parseFloat(String(raw == null ? '' : raw).replace(',', '.').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
};

/**
 * Parse a fee denomination ladder config string into `[{ gid, cents }]`, sorted by `cents` DESC.
 * The string is a newline- or semicolon-separated list of "<variantId> <amount>" rows (amount in
 * major units; the variant's REAL per-unit price, so the decomposition stays exact even under B2B
 * catalog price overrides). Each row's id may be a bare id, a GID, or an admin URL; the amount may
 * use a dot or comma decimal. Falls back to `fallbackGid` at 1 cent (€0.01) when no ladder rows
 * parse, so an unset ladder behaves byte-for-byte like the old single-variant sync. Shared by the
 * insurance and excise getters so the two can never diverge.
 * @param {string | null | undefined} rawString
 * @param {string} fallbackGid
 * @returns {Array<{ gid: string, cents: number }>}
 */
const parseFeeDenominations = function parseFeeDenominations(rawString, fallbackGid) {
    const raw = String(rawString || '').trim();
    /** @type {Array<{ gid: string, cents: number }>} */
    const out = [];
    if (raw) {
      for (const row of raw.split(/[\n;]+/)) {
        const text = row.trim();
        if (!text) continue;
        const parts = text.match(/^(\S+)[\s:=]+(.+)$/);
        if (!parts) continue;
        const gid = normalizeInsuranceFeeVariantGid(extractVariantIdToken(parts[1]));
        const cents = amountStringToCents(parts[2]);
        if (gid && cents > 0) out.push({ gid, cents });
      }
    }
    if (!out.length && fallbackGid) out.push({ gid: fallbackGid, cents: 1 });
    const seen = new Set();
    const uniq = out.filter((d) => (seen.has(d.gid) ? false : (seen.add(d.gid), true)));
    uniq.sort((a, b) => b.cents - a.cents);
    return uniq;
};

/**
 * Insurance fee denomination ladder (`cfg.insuranceFeeDenominations`, else the single legacy
 * `insuranceFeeVariantId` @ €0.01). See {@link parseFeeDenominations}.
 * @param {B2BConfig | null | undefined} cfg
 * @returns {Array<{ gid: string, cents: number }>}
 */
const getInsuranceFeeDenominations = function getInsuranceFeeDenominations(cfg) {
    return parseFeeDenominations((cfg || {}).insuranceFeeDenominations, getConfiguredInsuranceFeeGid(cfg));
};

/**
 * All insurance fee variant GIDs (the denomination ladder, or the single legacy variant).
 * Pass this anywhere a cart line must be recognised as "an insurance fee line".
 * @param {B2BConfig | null | undefined} cfg
 * @returns {string[]}
 */
const getInsuranceFeeGids = function getInsuranceFeeGids(cfg) {
    return getInsuranceFeeDenominations(cfg).map((d) => d.gid);
};

/**
 * Max units allowed on a single fee line (insurance OR excise) before stepping up to the
 * next-larger denomination. The €0.01 variant therefore covers fees up to this many cents EXACTLY
 * (default €3.00); above that the fee uses a bigger denomination and is rounded to it. Raise it for
 * finer rounding at the cost of larger line quantities; lower it for smaller quantities and coarser
 * rounding (keep it ≥ the largest everyday €0.01 fee you want shown to the cent).
 */
const FEE_MAX_UNITS_PER_LINE = 300;

/**
 * Plan the insurance fee as a SINGLE cart line: the smallest denomination whose round-to-nearest
 * quantity is ≤ `maxUnits`. The €0.01 variant is used exactly for everyday fees; larger fees step
 * up to €0.10 / €1 / €10 / €100 and the fee is rounded to that denomination (a few cents at most,
 * shrinking as the order grows). This keeps ONE clean line rather than fragmenting the fee across
 * several variants. Returns [] for a zero/negative fee.
 *
 * Falls back to the largest denomination (uncapped) only if even €100 units would exceed `maxUnits`
 * — i.e. a fee over €30,000 at the default cap, which can't occur on a real order — so the fee is
 * never silently dropped.
 * @param {number} targetCents
 * @param {Array<{ gid: string, cents: number }>} denominations
 * @param {number} [maxUnits]
 * @returns {Array<{ gid: string, cents: number, quantity: number }>} 0 or 1 entries
 */
const decomposeFeeToDenominations = function decomposeFeeToDenominations(targetCents, denominations, maxUnits) {
    const cents = Math.max(0, Math.round(Number(targetCents) || 0));
    if (cents <= 0) return [];
    const cap = Number(maxUnits) > 0 ? Number(maxUnits) : FEE_MAX_UNITS_PER_LINE;
    const ladder = (denominations || [])
      .filter((d) => d && d.cents > 0)
      .slice()
      .sort((a, b) => a.cents - b.cents); // smallest denomination first
    if (!ladder.length) return [];
    for (const d of ladder) {
      const quantity = Math.round(cents / d.cents);
      if (quantity <= cap) return quantity > 0 ? [{ gid: d.gid, cents: d.cents, quantity }] : [];
    }
    const top = ladder[ladder.length - 1];
    return [{ gid: top.gid, cents: top.cents, quantity: Math.max(1, Math.round(cents / top.cents)) }];
};

/** @param {B2BConfig | null | undefined} cfg */
const getConfiguredExciseFeeGid = function getConfiguredExciseFeeGid(cfg) {
    return normalizeInsuranceFeeVariantGid((cfg || {}).exciseFeeVariantId || '');
};

/**
 * US / default excise ladder (`cfg.exciseFeeDenominations`, else the single legacy
 * `exciseFeeVariantId` @ €0.01). Same ladder mechanism as insurance — see {@link parseFeeDenominations}.
 * @param {B2BConfig | null | undefined} cfg
 * @returns {Array<{ gid: string, cents: number }>}
 */
const getExciseUsFeeDenominations = function getExciseUsFeeDenominations(cfg) {
    return parseFeeDenominations((cfg || {}).exciseFeeDenominations, getConfiguredExciseFeeGid(cfg));
};

/**
 * Sweden excise ladder (`cfg.exciseSeFeeDenominations`) — a SEPARATE fee product so the order reads
 * "Sweden Alcohol Duty" rather than the US product's name. Falls back to the US/shared ladder when no
 * dedicated Sweden product is configured, so Sweden keeps working on the shared product until one is added.
 * @param {B2BConfig | null | undefined} cfg
 * @returns {Array<{ gid: string, cents: number }>}
 */
const getExciseSeFeeDenominations = function getExciseSeFeeDenominations(cfg) {
    const se = parseFeeDenominations((cfg || {}).exciseSeFeeDenominations, '');
    return se.length ? se : getExciseUsFeeDenominations(cfg);
};

/**
 * Destination-aware excise ladder for the SYNC — which product's variants to actually add: the Sweden
 * product for an SE destination, else the US / default product.
 * @param {B2BConfig | null | undefined} cfg
 * @returns {Array<{ gid: string, cents: number }>}
 */
const getExciseFeeDenominations = function getExciseFeeDenominations(cfg) {
    const c = cfg || {};
    if (getPrivateCellarDestinationCountry(c) === 'SE') return getExciseSeFeeDenominations(c);
    return getExciseUsFeeDenominations(c);
};

/**
 * EVERY excise variant GID across destinations (US + Sweden ladders), deduped — for recognising a cart
 * line as an excise line regardless of which destination's product it came from. Lets a destination
 * switch (US⇄SE) correctly remove the old product's line and add the new one.
 * @param {B2BConfig | null | undefined} cfg
 * @returns {string[]}
 */
const getExciseFeeGids = function getExciseFeeGids(cfg) {
    const seen = new Set();
    /** @type {string[]} */
    const gids = [];
    for (const d of getExciseUsFeeDenominations(cfg).concat(getExciseSeFeeDenominations(cfg))) {
      if (!seen.has(d.gid)) {
        seen.add(d.gid);
        gids.push(d.gid);
      }
    }
    return gids;
};

/** localStorage key the Magical Fees app writes the customer's confirmed shipping destination to. */
const MA_FEES_LOCATION_STORAGE_KEY = '__ma_fees_selected_location';

/**
 * VIP mirror of the customer's CONFIRMED Magical Fees destination. The MF app deletes
 * `__ma_fees_selected_location` whenever it sees the native cart empty (its post-checkout
 * reset — both at init and after every observed /cart/* POST), but the VIP merge empties the
 * native cart after every regular-product add. Without a mirror the confirmed destination is
 * wiped on every add/page change, which re-opens the MF location popup and silently drops the
 * Private Cellar excise destination.
 */
const PC_DESTINATION_MIRROR_STORAGE_KEY = '__pc_shipping_destination';

/**
 * Parse a Magical Fees location JSON string, returning it only when it is a CONFIRMED
 * destination with a country code (the only state worth mirroring/restoring).
 * @param {string | null | undefined} raw
 * @returns {any | null}
 */
const parseConfirmedMaFeesLocation = function parseConfirmedMaFeesLocation(raw) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(String(raw));
      if (!parsed || typeof parsed !== 'object') return null;
      if (parsed.confirmed !== true) return null;
      if (!String(parsed.countryCode || '').trim()) return null;
      return parsed;
    } catch (_) {
      return null;
    }
};

/** @returns {any | null} Mirrored confirmed destination object, or null. */
const getMirroredPcDestination = function getMirroredPcDestination() {
    try {
      return parseConfirmedMaFeesLocation(localStorage.getItem(PC_DESTINATION_MIRROR_STORAGE_KEY));
    } catch (_) {
      return null;
    }
};

/** @returns {string} Raw JSON of the mirrored confirmed destination, or ''. */
const getMirroredPcDestinationRaw = function getMirroredPcDestinationRaw() {
    try {
      const raw = localStorage.getItem(PC_DESTINATION_MIRROR_STORAGE_KEY);
      return parseConfirmedMaFeesLocation(raw) ? String(raw) : '';
    } catch (_) {
      return '';
    }
};

/**
 * Destination country for CHANGE DETECTION in the location watcher: confirmed MF key →
 * mirror → raw MF key → ''. A Magical Fees wipe followed by our restore leaves this value
 * unchanged, so the watcher can tell a real destination change from MF's reset noise.
 * @returns {string}
 */
const getEffectiveMaFeesDestinationCountry = function getEffectiveMaFeesDestinationCountry() {
    try {
      const confirmedMf = parseConfirmedMaFeesLocation(localStorage.getItem(MA_FEES_LOCATION_STORAGE_KEY));
      if (confirmedMf) return String(confirmedMf.countryCode).trim().toUpperCase();
    } catch (_) {
      /* ignore */
    }
    const mirrored = getMirroredPcDestination();
    if (mirrored) return String(mirrored.countryCode).trim().toUpperCase();
    return getMaFeesSelectedCountry();
};

/**
 * Destination the customer picked in the Magical Fees popup (b2c), e.g. `{ countryCode: 'US' }`.
 * The Private Cellar drawer has no popup of its own, so it reuses this selection. Returns the
 * uppercased ISO country code, or '' if nothing is stored / unparseable.
 * @returns {string}
 */
const getMaFeesSelectedCountry = function getMaFeesSelectedCountry() {
    try {
      const raw = localStorage.getItem(MA_FEES_LOCATION_STORAGE_KEY);
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      const code = parsed && typeof parsed === 'object' ? parsed.countryCode : '';
      return String(code || '').trim().toUpperCase();
    } catch (_) {
      return '';
    }
};

/**
 * Resolved destination country for Private Cellar fees: the Magical Fees selection first (that is
 * the actual shipping destination the customer confirmed), then `localization.country` as a
 * fallback for customers who never saw the popup.
 * @param {B2BConfig | null | undefined} cfg
 * @returns {string}
 */
const getPrivateCellarDestinationCountry = function getPrivateCellarDestinationCountry(cfg) {
    /* Prefer a CONFIRMED destination: the live MF key first, else our mirror (which survives
     * MF's wipe-on-empty-cart reset). Then MF's unconfirmed ip-detected guess, then the theme
     * localization country. */
    try {
      const confirmedMf = parseConfirmedMaFeesLocation(localStorage.getItem(MA_FEES_LOCATION_STORAGE_KEY));
      if (confirmedMf) return String(confirmedMf.countryCode).trim().toUpperCase();
    } catch (_) {
      /* ignore */
    }
    const mirrored = getMirroredPcDestination();
    if (mirrored) return String(mirrored.countryCode).trim().toUpperCase();
    const fromMaFees = getMaFeesSelectedCountry();
    if (fromMaFees) return fromMaFees;
    return String((cfg || {}).localizationCountry || '').toUpperCase();
};

/** True when the customer has not yet confirmed a Magical Fees shipping destination. */
const isMaFeesDestinationUnset = function isMaFeesDestinationUnset() {
    return !getMaFeesSelectedCountry();
};

/**
 * @param {any} line
 * @param {string | string[] | Set<string>} exciseGids One excise fee variant GID, or a list/set of
 *   them (the denomination ladder — see {@link getExciseFeeGids}). Matches against any of them.
 */
const isExciseFeeLine = function isExciseFeeLine(line, exciseGids) {
    if (!line || !exciseGids) return false;
    const id = normalizeInsuranceFeeVariantGid(line?.merchandise?.id || '');
    if (!id) return false;
    if (typeof exciseGids === 'string') return id === exciseGids;
    if (Array.isArray(exciseGids)) return exciseGids.indexOf(id) !== -1;
    if (exciseGids instanceof Set) return exciseGids.has(id);
    return false;
};

/** Any auto-managed fee line (insurance OR excise/duty) — excluded from product subtotals/counts. */
/**
 * @param {any} line
 * @param {B2BConfig | null | undefined} [cfg]
 */
const isAnyFeeLine = function isAnyFeeLine(line, cfg) {
    const c = cfg || getConfig() || {};
    return (
      isInsuranceFeeLine(line, getInsuranceFeeGids(c)) ||
      isExciseFeeLine(line, getExciseFeeGids(c))
    );
};

/** Sum of product-line totals (major units), excluding the insurance + excise fee lines. */
/**
 * @param {any} cart
 * @param {B2BConfig | null | undefined} [cfg]
 */
const getB2BCartProductSubtotal = function getB2BCartProductSubtotal(cart, cfg) {
    const c = cfg || getConfig() || {};
    const edges = cart?.lines?.edges || [];
    return edges.reduce((/** @type {number} */ sum, /** @type {any} */ e) => {
      const node = e?.node;
      if (isAnyFeeLine(node, c)) return sum;
      return sum + (parseFloat(node?.cost?.totalAmount?.amount || '0') || 0);
    }, 0);
};

/** Total bottles across product lines: line quantity × bottles-per-unit (case packs via {@link getCaseQty}). */
/**
 * @param {any} cart
 * @param {B2BConfig | null | undefined} [cfg]
 */
const getB2BCartBottleCount = function getB2BCartBottleCount(cart, cfg) {
    const c = cfg || getConfig() || {};
    const edges = cart?.lines?.edges || [];
    let bottles = 0;
    for (const e of edges) {
      const node = e?.node;
      if (isAnyFeeLine(node, c)) continue;
      const qty = Number(node?.quantity) || 0;
      if (qty < 1) continue;
      const perUnit = getCaseQty(node?.merchandise) || 1;
      bottles += qty * perUnit;
    }
    return bottles;
};

/**
 * Excise/duty fee in cents (= placeholder quantity, since the fee variant is €0.01/unit) for the
 * destination country. US: `excise_us_percent`% of product subtotal. Sweden: `excise_se_per_bottle`
 * € × bottle count. Other countries: 0 (no fee). Mirrors the insurance integer-percent convention.
 * @param {number} productSubtotal
 * @param {number} bottleCount
 * @param {B2BConfig | null | undefined} cfg
 * @returns {number}
 */
const getExciseFeeTargetQty = function getExciseFeeTargetQty(productSubtotal, bottleCount, cfg) {
    const c = cfg || {};
    const country = getPrivateCellarDestinationCountry(c);
    if (country === 'US') {
      const pct = Number(c.exciseUsPercent) || 0;
      if (pct <= 0 || productSubtotal <= 0) return 0;
      return Math.max(1, Math.round(productSubtotal * pct));
    }
    if (country === 'SE') {
      const perBottle = Number(c.exciseSePerBottle) || 0;
      if (perBottle <= 0 || bottleCount <= 0) return 0;
      return Math.max(1, Math.round(bottleCount * perBottle * 100));
    }
    return 0;
};

/** Read-only drawer label for the excise/duty fee line, by destination country. */
/** @param {B2BConfig | null | undefined} cfg */
const getExciseFeeLabel = function getExciseFeeLabel(cfg) {
    const c = cfg || {};
    const country = getPrivateCellarDestinationCountry(c);
    if (country === 'US') {
      const pct = Number(c.exciseUsPercent) || 0;
      return c.exciseLabelUs || `US Excise Tax (${pct}%)`;
    }
    if (country === 'SE') {
      const perBottle = Number(c.exciseSePerBottle) || 0;
      const amt = perBottle ? formatMoney(perBottle, c.currency || 'EUR') : '';
      return c.exciseLabelSe || (amt ? `Sweden Alcohol Duty (${amt}/bottle)` : 'Sweden Alcohol Duty');
    }
    return c.exciseLabelDefault || 'Excise / Duty';
};

/**
 * Drawer title + details for the excise/duty fee line. For a Sweden (SE) destination the fee is its
 * OWN dedicated Shopify product, so prefer that product's real title + description (merchant-managed
 * in the admin) over the computed {@link getExciseFeeLabel} label and the `excise_fee_details`
 * setting. Falls back to the label/setting when the product fields are blank or the destination is
 * not Sweden (US keeps "US Excise Tax (15%)" etc.), so non-SE behaviour is unchanged.
 * @param {any} line cart line whose `merchandise.product` carries the fee product's title/description
 * @param {B2BConfig | null | undefined} cfg
 * @returns {{ title: string, details: string }}
 */
const getExciseFeeLineDisplay = function getExciseFeeLineDisplay(line, cfg) {
    const c = cfg || {};
    const isSe = getPrivateCellarDestinationCountry(c) === 'SE';
    const product = (line && line.merchandise && line.merchandise.product) || {};
    const productTitle = String(product.title || '').trim();
    const productDesc = String(product.description || '').trim();
    return {
      title: isSe && productTitle ? productTitle : getExciseFeeLabel(c),
      details: isSe && productDesc ? productDesc : String(c.exciseFeeDetails || '').trim(),
    };
};

/**
 * Magical Fees app fee lines in the native cart (product_type "Fee" / "Percentage Fee" /
 * "Fee Placeholder"). Never merge these into the VIP Storefront cart: the PC excise/insurance
 * sync owns fees there (merging would double-charge), and MF removes its own orphaned fee
 * lines from the theme cart once the product lines are gone.
 * @param {any} item Ajax `/cart.js` line
 */
const isMagicalFeesAjaxLine = function isMagicalFeesAjaxLine(item) {
    const ptype = String((item && item.product_type) || '').trim().toLowerCase();
    return ptype === 'fee' || ptype === 'percentage fee' || ptype === 'fee placeholder';
};

/** True when a confirmed destination exists (confirmed MF key or our mirror). */
const isPcDestinationConfirmed = function isPcDestinationConfirmed() {
    try {
      if (parseConfirmedMaFeesLocation(localStorage.getItem(MA_FEES_LOCATION_STORAGE_KEY))) return true;
    } catch (_) { /* ignore */ }
    return !!getMirroredPcDestination();
};

/**
 * Countries with destination-based fees, mirroring the Magical Fees rules (option values are
 * country codes, names in English — exactly what MF's own popup renders and stores). A country
 * added to MF's rules but missing here just routes the customer to "Other destinations"; MF
 * re-asks once on their next b2c add, so nothing breaks — keep the two lists in sync anyway.
 */
const PC_DESTINATION_COUNTRIES = [
    ['US', 'United States'],
    ['SE', 'Sweden'],
  ];

/** US states/territories for the US fee rule — MF requires a state for the US. */
const PC_DESTINATION_US_REGIONS = [
    ['AL', 'Alabama'], ['AK', 'Alaska'], ['AS', 'American Samoa'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
    ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
    ['FL', 'Florida'], ['GA', 'Georgia'], ['GU', 'Guam'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
    ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
    ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
    ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'],
    ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'],
    ['MP', 'Northern Mariana Islands'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'],
    ['PR', 'Puerto Rico'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'],
    ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['VI', 'U.S. Virgin Islands'],
    ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
  ];

/**
 * Insurance fee in cents (= placeholder quantity, fee variant is €0.01/unit):
 * integer-percent convention, e.g. 1% of a €209.40 subtotal → 209 (€2.09).
 * Mirrors the inline computation syncInsuranceFeeLine uses; minimum 1 cent once
 * a percent is configured (even for a zero subtotal — caller guards empty carts).
 * @param {number} productSubtotal
 * @param {B2BConfig | null | undefined} cfg
 * @returns {number}
 */
const getInsuranceFeeTargetQty = function getInsuranceFeeTargetQty(productSubtotal, cfg) {
  const feePercent = Number((cfg || {}).insuranceFeePercent) || 0;
  if (feePercent <= 0) return 0;
  return Math.max(1, Math.round(productSubtotal * feePercent));
};

/**
 * VIP merge policy: which native /cart.js lines may be merged into the Private
 * Cellar Storefront cart. Magical Fees fee products are NEVER merged (the PC
 * excise/insurance sync owns fees there — merging would double-charge), vip- and
 * b2b-catalog-tagged lines are excluded, and untagged lines are included.
 * Also home to the catalog-tag resolvers the policy (and the legacy query
 * builder) read from config. Moved verbatim from legacy.js.
 */

const getProductTag = function getProductTag() {
    const cfg = getConfig();
    return ((cfg && cfg.productTag) ? String(cfg.productTag) : 'B2B').trim() || 'B2B';
};

/**
 * Product tag for B2B catalog lines (e.g. stripping non-B2B items). Prefer the last
 * `[data-b2b-scope="b2b"]` config so a theme VIP block + b2b section does not use "VIP" here.
 */
const getB2bCatalogProductTagLower = function getB2bCatalogProductTagLower() {
    try {
      const nodes =
        typeof document !== 'undefined' ? document.querySelectorAll('[data-b2b-config][data-b2b-scope="b2b"]') : null;
      if (nodes && nodes.length) {
        const last = /** @type {HTMLElement} */ (nodes[nodes.length - 1]);
        const t = last.dataset && last.dataset.b2bProductTag;
        if (t && String(t).trim()) return String(t).trim().toLowerCase();
      }
    } catch (_) {
      /* ignore */
    }
    return (getProductTag() || 'B2B').trim().toLowerCase();
};

/** @param {any} item */
const getAjaxLineProductTagsLower = function getAjaxLineProductTagsLower(item) {
    if (!item) return [];
    if (Array.isArray(item.product_tags)) return item.product_tags.map((/** @type {any} */ t) => String(t).toLowerCase());
    if (typeof item.product_tags === 'string') {
      return item.product_tags.split(',').map((/** @type {string} */ t) => t.trim().toLowerCase()).filter(Boolean);
    }
    if (Array.isArray(item.tags)) return item.tags.map((/** @type {any} */ t) => String(t).toLowerCase());
    if (typeof item.tags === 'string') {
      return item.tags.split(',').map((/** @type {string} */ t) => t.trim().toLowerCase()).filter(Boolean);
    }
    return [];
};

/**
 * Tags for a cart line: prefer Storefront (accurate product tags); fall back to Ajax fields when present.
 */
/**
 * @param {any} item
 * @param {Map<string, string[]>} tagMap
 */
const tagsForAjaxLineItem = function tagsForAjaxLineItem(item, tagMap) {
    const pid = String(item.product_id || '').replace(/\D/g, '');
    if (pid && tagMap.has(pid)) return tagMap.get(pid) || [];
    return getAjaxLineProductTagsLower(item);
};

/** True when the line should be merged into the VIP Storefront cart (regular boutique lines). */
/**
 * @param {any} item
 * @param {Map<string, string[]>} tagMap
 */
const isRegularOnlineStoreLineForVipMerge = function isRegularOnlineStoreLineForVipMerge(item, tagMap) {
    if (isMagicalFeesAjaxLine(item)) return false;
    const tags = tagsForAjaxLineItem(item, tagMap);
    if (!tags.length) return true;
    if (tags.includes('vip')) return false;
    const b2b = getB2bCatalogProductTagLower();
    if (b2b && tags.includes(b2b)) return false;
    return true;
};

/**
 * Scope-keyed client persistence: catalog scope inference (vip vs b2b), the
 * *_KEY_BASE storage-key constants, and the stored cart-id / discount-code /
 * cart-note accessors. vip and b2b carts are deliberately separate (no key
 * adoption across scopes). Moved verbatim from legacy.js.
 */

const CACHE_KEY_BASE = 'b2b_products_v14';

const STOREFRONT_CART_ID_KEY_BASE = 'b2b_storefront_cart_id_v1';

/** Set when we send the customer to cart.checkoutUrl; used to interpret an empty Storefront cart after return. */
const SESSION_KEY_B2B_CHECKOUT_REDIRECT = 'b2b_sf_checkout_redirect_ts';

const SESSION_KEY_B2B_ORDER_REQUEST_SENT = 'b2b_sf_order_request_sent';

/** Lines JSON saved right before Storefront checkout redirect; used to rebuild cart if Shopify empties it after checkout. */
const LOCAL_KEY_B2B_PRE_CHECKOUT_SNAPSHOT_BASE = 'b2b_pre_checkout_cart_snapshot_v1';

const STOREFRONT_DISCOUNT_CODE_KEY_BASE = 'b2b_storefront_discount_code_v1';

const STOREFRONT_CART_NOTE_KEY_BASE = 'b2b_storefront_cart_note_v1';

/**
 * Optimistic replay snapshot: the last authoritatively-painted Storefront cart (item count + the
 * cart JSON + a timestamp). Lets the header bubble and the drawer body paint instantly on the next
 * page load instead of waiting on the Storefront network round-trip; the post-load sync always
 * reconciles. Written on every successful sync; cleared together with the cart id (see
 * setStoredStorefrontCartId), so an emptied/abandoned cart never replays stale lines.
 */
const STOREFRONT_CART_SNAPSHOT_KEY_BASE = 'b2b_storefront_cart_snapshot_v1';

/** Backstop max age for the optimistic snapshot replay; the live sync reconciles regardless. */
const STOREFRONT_CART_SNAPSHOT_MAX_MS = 24 * 60 * 60 * 1000;

/**
 * Known-empty marker. An emptied Storefront cart still EXISTS (cart id retained, 0 lines), so removing
 * the items snapshot leaves no positive "this cart is empty" signal — the mount replay can't tell
 * "empty" from "never seen" and the drawer paints nothing until the network sync resolves (blank panel
 * on open). This timestamp marker, kept mutually exclusive with the items snapshot, lets mount paint the
 * empty state instantly. Unlike the items snapshot it deliberately does NOT expire — "empty" carries no
 * stale-price risk, and the marker is cleared the moment any non-empty cart is seen — so a cart emptied
 * long ago still opens instantly; the live sync reconciles regardless.
 */
const STOREFRONT_CART_EMPTY_KEY_BASE = 'b2b_storefront_cart_empty_v1';

/** Set/clear the known-empty marker (module-local; the cart-id + snapshot setters keep it in sync). */
/** @param {boolean} isEmpty */
const markStoredStorefrontCartEmpty = function markStoredStorefrontCartEmpty(isEmpty) {
    try {
      const key = withCartPersistenceKey(STOREFRONT_CART_EMPTY_KEY_BASE);
      if (isEmpty) localStorage.setItem(key, String(Date.now()));
      else localStorage.removeItem(key);
    } catch (_) {
      /* non-critical: optimistic marker only */
    }
};

/**
 * True when the cart was last seen empty. Does NOT expire (empty has no staleness risk; the marker is
 * cleared the moment a non-empty cart is seen). Lets mount paint the empty state instantly instead of
 * leaving the drawer blank until the sync round-trip. The stored value is a write timestamp, validated
 * here only as a well-formed positive number (guards corrupted/garbage values).
 * @returns {boolean}
 */
const getStoredStorefrontCartEmpty = function getStoredStorefrontCartEmpty() {
    try {
      const raw = localStorage.getItem(withCartPersistenceKey(STOREFRONT_CART_EMPTY_KEY_BASE));
      if (!raw) return false;
      const ts = Number(raw);
      return Number.isFinite(ts) && ts > 0;
    } catch (_) {
      return false;
    }
};

const getScopeKeySuffix = function getScopeKeySuffix() {
    const cfg = getConfig();
    const raw = (cfg && cfg.scopeKey) ? String(cfg.scopeKey) : 'b2b';
    const safe = raw.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    return safe || 'b2b';
};

/** @param {string} base */
const withScopeKey = function withScopeKey(base) {
    return base + '_' + getScopeKeySuffix();
};

/**
 * Scope suffix for Storefront cart id / discount / checkout snapshot in localStorage.
 * Prefer `[data-b2b-cart-ui]`'s stamped `data-b2b-catalog-scope` so keys stay stable when a later
 * `[data-b2b-config]` block forces `getScopeKeySuffix()` to `b2b` while the drawer remains VIP
 * (theme VIP + section B2B — otherwise the second add reads `_b2b`, sees no id, and opens a new cart).
 */
const getCartPersistenceScopeSuffix = function getCartPersistenceScopeSuffix() {
    try {
      const cartRoot = document.querySelector('[data-b2b-cart-ui]');
      const raw = cartRoot?.getAttribute('data-b2b-catalog-scope');
      if (raw != null && String(raw).trim() !== '') {
        const s = String(raw).toLowerCase().replace(/[^a-z0-9_-]/g, '');
        if (s) return s;
      }
    } catch (_) {
      /* ignore */
    }
    return getScopeKeySuffix();
};

/** @param {string} base */
const withCartPersistenceKey = function withCartPersistenceKey(base) {
    return base + '_' + getCartPersistenceScopeSuffix();
};

/**
 * Single value for `data-b2b-catalog-scope` on the cart UI root. If any config block is VIP,
 * stamp `vip` even when the last `[data-b2b-config]` is B2B so cart isolation matches Private Cellar.
 */
const inferStampedCatalogScopeForCartUi = function inferStampedCatalogScopeForCartUi() {
    try {
      const nodes = document.querySelectorAll('[data-b2b-config]');
      for (let i = 0; i < nodes.length; i++) {
        const sk = String((/** @type {HTMLElement} */ (nodes[i]).dataset.b2bScope || '')).trim().toLowerCase();
        if (sk === 'vip') return 'vip';
      }
    } catch (_) {
      /* ignore */
    }
    return getScopeKeySuffix();
};

const REGIONS_CACHE_KEY_BASE = 'b2b_regions_map_shown_v1';

const REGIONS_ALL_CACHE_KEY_BASE = 'b2b_regions_all_v1';

const PRODUCERS_CACHE_KEY_BASE = 'b2b_producers_all_v1';

/**
 * Storefront cart id is keyed by `withScopeKey` (vip vs b2b). We do **not** copy or adopt the
 * other scope's id — customers with both Private Cellar and B2B access must keep two carts.
 */
const getStoredStorefrontCartId = function getStoredStorefrontCartId() {
    try {
      const primaryKey = withCartPersistenceKey(STOREFRONT_CART_ID_KEY_BASE);
      return localStorage.getItem(primaryKey) || '';
    } catch (_) {
      return '';
    }
};

/** @param {string | null | undefined} cartId */
const setStoredStorefrontCartId = function setStoredStorefrontCartId(cartId) {
    try {
      const primaryKey = withCartPersistenceKey(STOREFRONT_CART_ID_KEY_BASE);
      if (!cartId) {
        localStorage.removeItem(primaryKey);
        /* Cart is gone — drop the optimistic replay snapshot so a later load can't paint stale lines, and
         * mark known-empty so mount paints the empty state instantly instead of a blank drawer. */
        localStorage.removeItem(withCartPersistenceKey(STOREFRONT_CART_SNAPSHOT_KEY_BASE));
        markStoredStorefrontCartEmpty(true);
      } else {
        localStorage.setItem(primaryKey, cartId);
        /* We have a cart again — clear the known-empty marker (the next sync sets the real snapshot). */
        markStoredStorefrontCartEmpty(false);
      }
    } catch (_) {
      /* non-critical */
    }
};

/**
 * Optimistic cart snapshot for instant first paint. Stored alongside the cart id (and cleared with
 * it): `count` feeds the header bubble and `cart` replays the drawer body before the network sync.
 * Returns null when absent or older than STOREFRONT_CART_SNAPSHOT_MAX_MS.
 * @returns {{ count: number, cart: any } | null}
 */
const getStoredStorefrontCartSnapshot = function getStoredStorefrontCartSnapshot() {
    try {
      const raw = localStorage.getItem(withCartPersistenceKey(STOREFRONT_CART_SNAPSHOT_KEY_BASE));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const ts = Number(parsed && parsed.t);
      if (!Number.isFinite(ts) || ts <= 0) return null;
      const age = Date.now() - ts;
      if (!(age >= 0) || age > STOREFRONT_CART_SNAPSHOT_MAX_MS) return null;
      const count = Number(parsed.count);
      return { count: Number.isFinite(count) ? count : 0, cart: parsed.cart || null };
    } catch (_) {
      return null;
    }
};

/**
 * Persist the just-painted Storefront cart for optimistic replay. A non-positive count or missing
 * cart clears the snapshot (an emptied cart must not replay stale lines).
 * @param {number} count
 * @param {any} cart
 */
const setStoredStorefrontCartSnapshot = function setStoredStorefrontCartSnapshot(count, cart) {
    try {
      const key = withCartPersistenceKey(STOREFRONT_CART_SNAPSHOT_KEY_BASE);
      const n = Number(count) || 0;
      if (n <= 0 || !cart) {
        /* Confirmed empty: drop the items snapshot and set the known-empty marker (mutually exclusive). */
        localStorage.removeItem(key);
        markStoredStorefrontCartEmpty(true);
        return;
      }
      localStorage.setItem(key, JSON.stringify({ count: n, cart: cart, t: Date.now() }));
      markStoredStorefrontCartEmpty(false);
    } catch (_) {
      /* non-critical: optimistic snapshot only, the live sync is the source of truth */
    }
};

const getStoredB2BDiscountCode = function getStoredB2BDiscountCode() {
    try {
      return (localStorage.getItem(withCartPersistenceKey(STOREFRONT_DISCOUNT_CODE_KEY_BASE)) || '').trim();
    } catch (_) {
      return '';
    }
};

/** @param {string | null | undefined} code */
const setStoredB2BDiscountCode = function setStoredB2BDiscountCode(code) {
    try {
      const normalized = (code || '').trim();
      if (!normalized) {
        localStorage.removeItem(withCartPersistenceKey(STOREFRONT_DISCOUNT_CODE_KEY_BASE));
      } else {
        localStorage.setItem(withCartPersistenceKey(STOREFRONT_DISCOUNT_CODE_KEY_BASE), normalized);
      }
    } catch (_) {
      /* non-critical */
    }
};

/** Customer's order note for the Private Cellar (VIP) cart. Stored locally so it survives drawer
 *  repaints/reloads; pushed onto the Storefront cart at checkout so it reaches the order. */
const getStoredB2BCartNote = function getStoredB2BCartNote() {
    try {
      return localStorage.getItem(withCartPersistenceKey(STOREFRONT_CART_NOTE_KEY_BASE)) || '';
    } catch (_) {
      return '';
    }
};

/** @param {string | null | undefined} note */
const setStoredB2BCartNote = function setStoredB2BCartNote(note) {
    try {
      const raw = note == null ? '' : String(note);
      if (!raw.trim()) {
        localStorage.removeItem(withCartPersistenceKey(STOREFRONT_CART_NOTE_KEY_BASE));
      } else {
        localStorage.setItem(withCartPersistenceKey(STOREFRONT_CART_NOTE_KEY_BASE), raw);
      }
    } catch (_) {
      /* non-critical */
    }
};

/**
 * HTML escaping + inline SVG icon strings (mirrors of assets/icon-*.svg).
 * Moved verbatim from legacy.js.
 */

const ICON_CLOSE = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width,1)" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width,1)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const ICON_MINUS = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width,1)" stroke-linecap="round"/></svg>';

const ICON_PLUS = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>';

/** Same markup as assets/icon-filters-close.svg (cart discount pill remove). */
const ICON_FILTERS_CLOSE =
    '<svg width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g opacity="var(--close-icon-opacity)"><path d="M6 1.5L1 6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path d="M1 1.5L6 6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></g></svg>';

const ICON_DELETE = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width,1)" stroke-linecap="round"/><path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width,1)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Match assets/icon-cart.svg (fill uses currentColor for button theming) */
const ICON_CART = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="-3.455 0 65.56 65.56" fill="none" aria-hidden="true"><path fill="currentColor" d="M55.69,23.81c-.3-3.48-3.16-6.11-6.65-6.11h-3.9c.07-.59.12-1.18.12-1.78,0-8.78-7.15-15.93-15.93-15.93s-15.93,7.15-15.93,15.93c0,.6.06,1.19.12,1.78h-3.9c-3.5,0-6.35,2.63-6.65,6.11L.02,58.32c-.16,1.86.47,3.71,1.73,5.08,1.26,1.37,3.05,2.16,4.92,2.16h45.3c1.86,0,3.65-.79,4.91-2.16,1.26-1.37,1.89-3.22,1.74-5.08l-2.94-34.51ZM17.44,15.93c0-6.56,5.33-11.89,11.89-11.89s11.89,5.33,11.89,11.89c0,.6-.15,1.18-.23,1.78h-23.3c-.09-.59-.24-1.17-.24-1.78ZM53.91,60.67c-.5.55-1.19.85-1.94.85H6.67c-.74,0-1.43-.3-1.94-.85-.51-.55-.75-1.26-.68-2l2.94-34.5c.12-1.37,1.24-2.41,2.62-2.41h39.42c1.38,0,2.51,1.03,2.62,2.41l2.94,34.5c.06.74-.18,1.45-.68,2Z"/></svg>';

const ICON_CHECKMARK = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** @param {unknown} str */
const esc = function esc(str) {
    const d = document.createElement('div');
    d.textContent = String(str ?? '');
    return d.innerHTML;
};

/**
 * Product display text: computed card title/subtitle (millesimes metafields,
 * mirrors product-title-value.liquid / product-subtitle-value.liquid), variant
 * format/size display labels (Liquid-injected labels JSON, memoized), and the
 * wine-card description HTML. Moved verbatim from legacy.js.
 */

/** @type {false | null | Record<string, unknown>} */
let variantFormatLabelsMemo = null;

/** Labels JSON from Liquid (snippets/b2b-variant-format-labels-json.liquid); false when absent or invalid. */
const getVariantFormatLabels = function getVariantFormatLabels() {
    if (variantFormatLabelsMemo !== null) return variantFormatLabelsMemo === false ? null : variantFormatLabelsMemo;
    const el = document.querySelector('script[type="application/json"][data-b2b-variant-format-labels]');
    if (!el) {
      variantFormatLabelsMemo = false;
      return null;
    }
    try {
      const parsed = JSON.parse(String(el.textContent || '').trim());
      variantFormatLabelsMemo =
        parsed && typeof parsed === 'object' ? /** @type {Record<string, unknown>} */ (parsed) : false;
      return variantFormatLabelsMemo === false ? null : variantFormatLabelsMemo;
    } catch {
      variantFormatLabelsMemo = false;
      return null;
    }
};

/** @param {string} template */
const interpolateCaseOfTemplate = function interpolateCaseOfTemplate(template, count) {
    const t = String(template || '').trim() || 'Case of {{ count }}';
    return t.replace(/\{\{\s*count\s*\}\}/gi, String(count));
};

/** @param {string} sizeKey fragment before "cl" in lowercased raw value */
const sizeDisplayNameForKey = function sizeDisplayNameForKey(sizeKey, labels) {
    const sk = (sizeKey || '').trim().toLowerCase();
    const useNames = !!(labels && labels.useSizeDisplayNames);
    if (!useNames || !labels || !labels.sizes) return `${sk}cl`;
    const map = /** @type {Record<string, string>} */ (labels.sizes);
    const jsonKey = sk.replace(/\./g, '_');
    return map[jsonKey] || `${sk}cl`;
};

/** @param {Record<string, unknown> | null} labels */
const lookupConditionLabel = function lookupConditionLabel(labels, code) {
    const c = (code || '').toLowerCase().trim();
    if (!c || !labels || !labels.conditions) return '';
    const map = /** @type {Record<string, string>} */ (labels.conditions);
    return map[c] || map[c.replace(/\s+/g, '_')] || '';
};

/**
 * Same rules as snippets/variant-format-display-label.liquid (PDP / wine-card).
 * @param {string} rawInput
 * @param {Record<string, unknown> | null} labels
 */
const formatFormatStyleDisplayLabel = function formatFormatStyleDisplayLabel(rawInput, labels) {
    const raw_value = normalizeFormatOptionValue(String(rawInput || '').trim());
    if (!raw_value) return '';
    let display_label = raw_value;
    let variant_suffix = '';
    const raw_lower = raw_value.toLowerCase();

    const case_qty_fmt = getCaseQtyFromString(raw_value);
    if (case_qty_fmt > 0) {
      const tmpl =
        labels && typeof labels.caseOfTemplate === 'string' ? labels.caseOfTemplate : 'Case of {{ count }}';
      display_label = interpolateCaseOfTemplate(tmpl, case_qty_fmt);
      const variant_parts_case = raw_value.split(' - ');
      if (variant_parts_case.length > 1) {
        const suffix_candidate = variant_parts_case[variant_parts_case.length - 1].trim();
        if (suffix_candidate) variant_suffix = suffix_candidate;
      }
    } else if (raw_lower.includes('cl')) {
      const size_key = (raw_lower.split('cl')[0] || '').trim();
      display_label = labels ? sizeDisplayNameForKey(size_key, labels) : `${size_key}cl`;

      const variant_parts = raw_value.split(' - ');
      const condition_code = variant_parts.length ? variant_parts[variant_parts.length - 1].trim().toLowerCase() : '';
      let condition_label = '';
      if (variant_parts.length > 1 && condition_code) {
        condition_label = labels ? lookupConditionLabel(labels, condition_code) : '';
      }
      if (condition_label) {
        variant_suffix = condition_label;
      } else if (variant_parts.length > 1) {
        const suffix_candidate = variant_parts[variant_parts.length - 1].trim();
        if (suffix_candidate) variant_suffix = suffix_candidate;
      }
    } else if (raw_lower === 'billet') {
      display_label = labels && typeof labels.billet === 'string' ? labels.billet : raw_value;
    }

    if (variant_suffix) {
      display_label = `${display_label} ${variant_suffix}`.trim();
    }
    return display_label;
};

/**
 * B2B PDP format radios — maps variant titles like "75cl par 6" to translated case lines (e.g. fr: "Caisse de 6").
 * Uses {@link getVariantFormatLabels} from snippets/b2b-variant-format-labels-json.liquid (variant-format-display-label rules).
 * @param {unknown} rawTitle
 */
const formatB2BVariantPickerLabel = function formatB2BVariantPickerLabel(rawTitle) {
    const s = String(rawTitle || '').trim();
    if (!s) return '';
    const out = formatFormatStyleDisplayLabel(s, getVariantFormatLabels());
    return out || s;
};

/** @param {any} variant */
const collectVariantLabelCandidates = function collectVariantLabelCandidates(variant) {
    /** @type {string[]} */
    const list = [];
    const opts = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
    for (let i = 0; i < opts.length; i++) {
      const v = (opts[i]?.value || '').toString().trim();
      if (v) list.push(v);
    }
    const title = (variant?.title || '').toString().trim();
    if (title && title.toLowerCase() !== 'default title') list.push(title);
    return list;
};

/**
 * Raw string for card price suffix — mirrors snippets/price.liquid candidate picking.
 * When case_qty > 0, prefer an option/title fragment that contains "cl" (per-bottle label).
 */
/** @param {any} variant */
/** @param {number} caseQty */
const pickRawValueForVariantLabel = function pickRawValueForVariantLabel(variant, caseQty) {
    const candidates = collectVariantLabelCandidates(variant);
    if (caseQty > 0) {
      for (let i = 0; i < candidates.length; i++) {
        const cnorm = normalizeFormatOptionValue(candidates[i]);
        if (cnorm.includes('cl')) return candidates[i];
      }
    }
    for (let i = 0; i < candidates.length; i++) {
      const candidate_stripped = candidates[i];
      const cnorm = normalizeFormatOptionValue(candidate_stripped);
      const candidate_lower = cnorm.toLowerCase();
      if (
        cnorm.includes('cl') ||
        cnorm.includes(' par ') ||
        cnorm.includes(' per ') ||
        cnorm.includes(' by ') ||
        cnorm.includes(' for ') ||
        /\s[×x]\s/.test(cnorm) ||
        cnorm.includes('cl/') ||
        /\s(par|per|by|for)\s/i.test(candidate_stripped) ||
        /\bcase\s+of\s+\d+/i.test(candidate_lower) ||
        /\bcaisse\s+de\s+\d+/i.test(candidate_lower) ||
        candidate_lower === 'billet'
      ) {
        return candidate_stripped;
      }
    }
    return candidates[0] || '';
};

/**
 * Text next to divided card price — mirrors snippets/price.liquid variant_label.
 * Uses i18n from getVariantFormatLabels(); falls back to formatFormatStyleDisplayLabel when needed.
 */
/** @param {string} raw_value */
/** @param {number} caseQty */
/** @param {Record<string, unknown> | null} labels */
const formatPriceCardVariantSuffix = function formatPriceCardVariantSuffix(raw_value, caseQty, labels) {
    if (!raw_value) return '';
    const raw_lower = raw_value.toLowerCase().trim();

    if (raw_value.includes('cl')) {
      if (raw_value.includes(' par ')) {
        const parts = raw_value.split(' par ');
        const head = (parts[0] || '').trim();
        const size_key = (head.split('cl')[0] || '').trim();
        return sizeDisplayNameForKey(size_key, labels);
      }
      if (raw_value.includes(' per ')) {
        const parts = raw_value.split(' per ');
        const head = (parts[0] || '').trim();
        const size_key = (head.split('cl')[0] || '').trim();
        return sizeDisplayNameForKey(size_key, labels);
      }
      const size_key = (raw_value.split('cl')[0] || '').trim();
      return sizeDisplayNameForKey(size_key, labels);
    }

    if (raw_lower === 'billet') {
      return labels && typeof labels.billet === 'string' ? labels.billet : raw_value;
    }

    if (caseQty > 0 && (raw_lower.includes('case of ') || raw_lower.includes('caisse de '))) {
      const tmpl =
        labels && typeof labels.caseOfTemplate === 'string' ? labels.caseOfTemplate : 'Case of {{ count }}';
      return interpolateCaseOfTemplate(tmpl, caseQty);
    }

    return formatFormatStyleDisplayLabel(raw_value, labels);
};

/** Card price suffix / variant-selector consistency helper (not full PDP format picker). */
/** @param {any} variant */
const getVariantDisplayLabel = function getVariantDisplayLabel(variant) {
    if (!variant) return '';
    const caseQty = getCaseQty(variant);
    const raw = pickRawValueForVariantLabel(variant, caseQty);
    if (!raw) return '';
    return formatPriceCardVariantSuffix(raw, caseQty, getVariantFormatLabels());
};

/** Computed product title from millesimes metafields (matches product-title-value.liquid). */
/** @param {any} product */
const getProductTitleValue = function getProductTitleValue(product) {
    if (!product) return '';
    const title = (product.title || '').trim();
    const regionRaw = getMetafieldValue(product, 'millesimesRegionMetafield');
    const isBordeaux = regionRaw === 'Bordeaux';
    const producteur = getMetafieldValue(product, 'millesimesProducteurMetafield');
    const cuvee = getMetafieldValue(product, 'millesimesCuveeMetafield');
    const titlePartName = getMetafieldValue(product, 'titlePartNameMetafield');
    const titleCuvee = cuvee || titlePartName;
    const vintage = getMetafieldValue(product, 'vintageMetafield');
    const vintageStr = vintage;
    const showVintage = vintageStr !== '0';

    let titleBlankCount = 0;
    if (!isBordeaux) {
      if (!producteur) titleBlankCount++;
      if (!cuvee) titleBlankCount++;
      if (!vintage) titleBlankCount++;
    } else {
      if (!cuvee) titleBlankCount++;
      if (!vintage) titleBlankCount++;
    }

    const namePart = titlePartName || title;

    if (titleBlankCount >= 2) {
      if (showVintage) return (namePart + ' ' + vintage).trim();
      return namePart;
    }

    let builtTitle;
    if (isBordeaux) {
      builtTitle = showVintage ? (titleCuvee + ' ' + vintage).trim() : titleCuvee;
    } else {
      builtTitle = showVintage
        ? (producteur + ' ' + titleCuvee + ' ' + vintage).trim()
        : (producteur + ' ' + titleCuvee).trim();
    }

    if (builtTitle === vintageStr || !builtTitle) {
      return showVintage ? (namePart + ' ' + vintage).trim() : namePart;
    }
    return builtTitle;
};

/** True if product has an option value "billet" (event product). */
/** @param {any} product */
const isEventProduct = function isEventProduct(product) {
    const edges = product.variants?.edges;
    if (!Array.isArray(edges)) return false;
    for (const e of edges) {
      const opts = e?.node?.selectedOptions;
      if (!Array.isArray(opts)) continue;
      for (const o of opts) {
        const v = (o?.value || o?.name || '').toString().toLowerCase().trim();
        if (v === 'billet') return true;
      }
    }
    return false;
};

/** Computed product subtitle from millesimes metafields (matches product-subtitle-value.liquid). */
/** @param {any} product */
const getProductSubtitleValue = function getProductSubtitleValue(product) {
    if (!product) return '';
    const regionRaw = getMetafieldValue(product, 'millesimesRegionMetafield');
    const isBordeaux = regionRaw === 'Bordeaux';
    const isChampagne = regionRaw === 'Champagne';

    if (isEventProduct(product)) {
      return getMetafieldValue(product, 'eventShortDescriptionMetafield');
    }

    const appellation = getMetafieldValue(product, 'millesimesAppellationMetafield');
    const regionVal = getMetafieldValue(product, 'millesimesRegionMetafield');
    const classification = getMetafieldValue(product, 'millesimesClassificationMetafield');
    const couleur = getMetafieldValue(product, 'millesimesCouleurMetafield');

    let metafieldDesc = '';
    if (isBordeaux) {
      metafieldDesc = [classification, appellation, regionVal, couleur].filter(Boolean).join(' ').trim();
    } else if (isChampagne) {
      metafieldDesc = [regionVal, classification, couleur].filter(Boolean).join(' ').trim();
    } else {
      metafieldDesc = [appellation, regionVal, couleur].filter(Boolean).join(' ').trim();
    }

    const desc1 = getMetafieldValue(product, 'titlePartDesc1Metafield');
    const desc2 = getMetafieldValue(product, 'titlePartDesc2Metafield');
    const customSubtitle = (desc1 + ' ' + desc2).trim();

    if (metafieldDesc) return metafieldDesc;
    if (customSubtitle) return customSubtitle;
    return '';
};

/**
 * Critic scores line above the gallery (matches snippets/wine-card-description.liquid).
 * Requires Storefront API access on each score metafield definition.
 */
/**
 * @param {any} product
 * @param {string} fieldKey
 */
const scoreMetafieldRounded = function scoreMetafieldRounded(product, fieldKey) {
    const raw = getMetafieldValue(product, fieldKey);
    if (!raw) return 0;
    const n = parseFloat(String(raw).replace(',', '.'));
    if (!Number.isFinite(n)) return 0;
    return Math.round(n);
};

/** Critic scores below this threshold are hidden on the storefront. */
const MIN_CRITIC_SCORE = 90;

/** @param {any} product */
const getWineCardDescriptionHtml = function getWineCardDescriptionHtml(product) {
    if (!product) return '';
    const parts = [];
    const jd = scoreMetafieldRounded(product, 'jebDunnuckScoreHighMetafield');
    if (jd >= MIN_CRITIC_SCORE) parts.push('Jeb Dunnuck ' + jd);
    const ws = scoreMetafieldRounded(product, 'wineSpectatorScoreHighMetafield');
    if (ws >= MIN_CRITIC_SCORE) parts.push('Wine Spectator ' + ws);
    const vn = scoreMetafieldRounded(product, 'vinousScoreHighMetafield');
    if (vn >= MIN_CRITIC_SCORE) parts.push('Vinous ' + vn);
    const js = scoreMetafieldRounded(product, 'jamesSucklingScoreHighMetafield');
    if (js >= MIN_CRITIC_SCORE) parts.push('James Suckling ' + js);
    const wa = scoreMetafieldRounded(product, 'wineAdvocateScoreHighMetafield');
    if (wa >= MIN_CRITIC_SCORE) parts.push('Wine Advocate ' + wa);
    if (!parts.length) return '';
    const text = '\u2605 ' + parts.join(' \u2022 ');
    return (
      '<p style="font-size: 0.75rem; color: var(--color-foreground); opacity: 0.75; margin-bottom: 12px; text-align: center;">' +
      esc(text) +
      '</p>'
    );
};

/**
 * Client-side filtering, sorting, faceting and the Storefront search query
 * builders (tag query + productFilters for non-indexed metafields; colour is
 * emitted in both FR and EN because productMetafield matching is exact and
 * language-context dependent). Moved verbatim from legacy.js.
 */

const getDefaultProductQuery = function getDefaultProductQuery() {
    const tag = getProductTag();
    const base = 'tag:' + tag;
    const scope = getScopeKeySuffix();
    if (scope === 'vip' && tag.toLowerCase() === 'vip') {
      return base + ' -tag:B2B';
    }
    return base;
};

/** OR clause for one or more producer names (search query string). Used for multi-select and single-producer fallback. */
/** @param {string[] | null | undefined} producers */
const buildProducerQueryClause = function buildProducerQueryClause(producers) {
    if (!Array.isArray(producers) || producers.length === 0) return '';
    const orProducers = producers
      .map((/** @type {string} */ p) => {
        const raw = String(p || '').trim();
        const escaped = (/** @type {string} */ s) => String(s).replace(/"/g, '\\"');
        const normalized = raw
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, ' ');
        const vals = Array.from(new Set([raw, normalized].filter(Boolean)));
        const orParts = vals
          .map((val) => '(metafields.custom.producer_name:"' + escaped(val) + '" OR metafields.custom.millesimes_producteur:"' + escaped(val) + '")')
          .join(' OR ');
        return '(' + orParts + ')';
      })
      .join(' OR ');
    return '(' + orProducers + ')';
};

/**
 * Build Storefront API query string from filter/search state.
 * Uses Shopify search syntax: tag, title, metafields.custom.*
 * @param {{ includeProducerInString?: boolean }} [opts] - If true, include producer clause even when exactly one producer (fallback path).
 */
/**
 * @param {any} state
 * @param {{ includeProducerInString?: boolean }} [opts]
 */
const buildProductQuery = function buildProductQuery(state, opts) {
    const o = opts || {};
    const parts = [getDefaultProductQuery()];
    const q = (state && state.searchQuery) ? String(state.searchQuery).trim() : '';
    if (q) {
      parts.push('title:*' + q.replace(/"/g, '\\"') + '*');
    }
    // Region & vintage are applied via productFilters (see buildProductFiltersFromState) for ALL
    // selections — query-string metafield clauses are not search-indexed in this store and return 0.
    const producers = state && state.producer;
    if (Array.isArray(producers) && producers.length > 0) {
      const singleProducer = producers.length === 1;
      const useStringClause = !singleProducer || o.includeProducerInString === true;
      if (useStringClause) {
        const clause = buildProducerQueryClause(producers);
        if (clause) parts.push(clause);
      }
    }
    const producerRefs = state && state.producerRef;
    if (Array.isArray(producerRefs) && producerRefs.length > 0) {
      const orProducerRefs = producerRefs
        .map((r) => 'metafields.custom.producer_ref:"' + String(r).replace(/"/g, '\\"') + '"')
        .join(' OR ');
      parts.push('(' + orProducerRefs + ')');
    }
    return parts.join(' AND ');
};

/**
 * Storefront `search` productFilters (MetafieldFilter) — reliable for single region / single vintage when
 * query-string metafield clauses return 0 results. Multi-select still uses buildProductQuery OR clauses.
 * @param {'producer_name'|'millesimes_producteur'|'skipProducer'|undefined} [producerKeyOverride]
 *        skipProducer = do not add producer metafield (used with query-string producer fallback).
 */
/**
 * @param {any} state
 * @param {'producer_name'|'millesimes_producteur'|'skipProducer'|undefined} [producerKeyOverride]
 */
const buildProductFiltersFromState = function buildProductFiltersFromState(state, producerKeyOverride) {
    const st = state || {};
    const filters = [];
    // Region & vintage: the `region_sub_last_parent_id` / `vintage` metafields are NOT indexed for
    // Storefront query-string search (a `metafields.custom.*` clause returns 0 in this store), so they
    // must go through productFilters — for ALL selections, not just a single value. Multiple
    // productMetafield entries with the same key are OR-ed by the API; entries with different keys are
    // AND-ed, giving the desired "(region A OR B) AND (vintage X OR Y)" semantics.
    const regionIds = st.region;
    if (Array.isArray(regionIds) && regionIds.length > 0) {
      const seenR = new Set();
      regionIds.forEach((/** @type {any} */ id) => {
        const v = normalizeRegionFilterValue(id);
        if (v && !seenR.has(v)) {
          seenR.add(v);
          filters.push({ productMetafield: { namespace: 'custom', key: 'region_sub_last_parent_id', value: v } });
        }
      });
    }
    const vintages = st.vintage;
    if (Array.isArray(vintages) && vintages.length > 0) {
      const seenV = new Set();
      vintages.forEach((/** @type {any} */ vin) => {
        const v = normalizeVintageYearForFilter(String(vin));
        if (v && !seenV.has(v)) {
          seenV.add(v);
          filters.push({ productMetafield: { namespace: 'custom', key: 'vintage', value: v } });
        }
      });
    }
    const producers = st.producer;
    if (Array.isArray(producers) && producers.length === 1 && producerKeyOverride !== 'skipProducer') {
      const name = String(producers[0] || '').trim();
      if (name) {
        const key = producerKeyOverride === 'millesimes_producteur' ? 'millesimes_producteur' : 'producer_name';
        filters.push({
          productMetafield: { namespace: 'custom', key, value: name },
        });
      }
    }
    // Colour/style: the `custom.grape_colour` metafield is the only colour field configured as a
    // Storefront search filter (its query-string form is NOT indexed, so it must go through
    // productFilters, not buildProductQuery). productMetafield is an EXACT, case/accent-sensitive
    // match AND the metafield is translated — the Storefront API returns/filters on the FR value
    // (Rouge/Rosé/Blanc) or EN value (Red/Rose/White) depending on the buyer language context, which
    // this client does not pin. So emit BOTH language variants per colour; multiple productMetafield
    // entries with the same key are OR-ed by the API, so this matches in either context (and multi-
    // select colour works for free).
    const styles = st.style;
    if (Array.isArray(styles) && styles.length > 0) {
      /** @type {Record<string, string[]>} */
      const styleToMetafield = { Red: ['Rouge', 'Red'], Rose: ['Rosé', 'Rose'], White: ['Blanc', 'White'] };
      const seen = new Set();
      styles.forEach((/** @type {any} */ s) => {
        const key = String(s).trim();
        (styleToMetafield[key] || [key]).forEach((value) => {
          if (value && !seen.has(value)) {
            seen.add(value);
            filters.push({ productMetafield: { namespace: 'custom', key: 'grape_colour', value } });
          }
        });
      });
    }
    return filters.length > 0 ? filters : null;
};

/**
 * Filter products by state (searchQuery, vintage, style, format, priceMin, priceMax).
 */
/**
 * @param {any[]} products
 * @param {any} stateOrQuery
 */
const filterProducts = function filterProducts(products, stateOrQuery) {
    let list = products;
    const state = typeof stateOrQuery === 'string' ? { searchQuery: stateOrQuery } : stateOrQuery || {};

    /* Hide products where every variant is sold out; partial availability is unchanged. */
    list = list.filter(isProductInStock);

    const priceMin = state.priceMin != null && state.priceMin !== '' ? parseFloat(state.priceMin) : NaN;
    const priceMax = state.priceMax != null && state.priceMax !== '' ? parseFloat(state.priceMax) : NaN;
    if (!Number.isNaN(priceMin) || !Number.isNaN(priceMax)) {
      list = list.filter((/** @type {any} */ p) => {
        const price = getSortPrice(p);
        if (!Number.isNaN(priceMin) && price < priceMin) return false;
        if (!Number.isNaN(priceMax) && price > priceMax) return false;
        return true;
      });
    }
    if (state.searchQuery && String(state.searchQuery).trim() !== '') {
      const q = String(state.searchQuery).trim().toLowerCase();
      list = list.filter((/** @type {any} */ p) => {
        const title = (p.title || '').toLowerCase();
        const computedTitle = (getProductTitleValue(p) || '').toLowerCase();
        const subtitle = (getProductSubtitleValue(p) || '').toLowerCase();
        const vendor = (p.vendor || '').toLowerCase();
        const tags = (p.tags || []).join(' ').toLowerCase();
        return title.includes(q) || computedTitle.includes(q) || subtitle.includes(q) || vendor.includes(q) || tags.includes(q);
      });
    }
    const styles = state.style;
    if (Array.isArray(styles) && styles.length > 0) {
      // Map filter values (Red, Rose, White) to DB values: store may have EN or localized (Rouge, Rosé, Blanc).
      // Compare accent- and case-insensitively so "Rose"/"Rosé"/"rosé" (and any Unicode-normalization
      // mismatch between the literals below and the Shopify metafield) all match.
      const normColour = (/** @type {any} */ s) =>
        String(s == null ? '' : s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
      /** @type {Record<string, string[]>} */
      const styleAliases = { Red: ['Red', 'Rouge'], Rose: ['Rose', 'Rosé'], White: ['White', 'Blanc'] };
      const allowedValues = new Set();
      styles.forEach((/** @type {any} */ s) => {
        const key = String(s).trim();
        (styleAliases[key] || [key]).forEach((v) => allowedValues.add(normColour(v)));
      });
      list = list.filter((/** @type {any} */ p) => {
        // `grape_colour` is the populated, search-filterable colour field (French: Rouge/Rosé/Blanc).
        // `millesimes_couleur` is mostly empty for red/white, so it's only a last-resort fallback.
        const val = normColour(
          p.grapeColourMetafield?.value || p.grapeColorMetafield?.value || p.millesimesCouleurMetafield?.value || ''
        );
        return val && allowedValues.has(val);
      });
    }
    const formats = state.format;
    if (Array.isArray(formats) && formats.length > 0) {
      const formatSet = new Set(formats.map((f) => String(f).trim()));
      list = list.filter((/** @type {any} */ p) => {
        const edges = p.variants?.edges;
        if (!Array.isArray(edges)) return false;
        return edges.some((/** @type {any} */ e) => {
          const node = e?.node;
          const val = (node?.formatMetafield?.value || node?.formatSizeMetafield?.value || '').toString().trim();
          return val && formatSet.has(val);
        });
      });
    }
    const vintages = state.vintage;
    if (vintages != null && (Array.isArray(vintages) ? vintages.length : String(vintages).trim() !== '')) {
      const set = Array.isArray(vintages)
        ? new Set(vintages.map((/** @type {any} */ v) => normalizeVintageYearForFilter(v)))
        : new Set([normalizeVintageYearForFilter(String(vintages).trim())]);
      list = list.filter((/** @type {any} */ p) => {
        const pv = normalizeVintageYearForFilter((p.vintageMetafield?.value ?? '').toString());
        return set.has(pv);
      });
    }
    const regionIds = state.region;
    if (Array.isArray(regionIds) && regionIds.length > 0) {
      const regionSet = new Set(regionIds.map((id) => normalizeRegionFilterValue(id)));
      list = list.filter((/** @type {any} */ p) => {
        const val = normalizeRegionFilterValue((p.regionSubLastParentIdMetafield?.value ?? '').toString());
        return val && regionSet.has(val);
      });
    }
    const producers = state.producer;
    if (Array.isArray(producers) && producers.length > 0) {
      const producerSet = new Set(producers.map((n) => normalizeProducerFilterValue(n)).filter(Boolean));
      list = list.filter((/** @type {any} */ p) => {
        const val = normalizeProducerFilterValue(getProducerPrimaryLabel(p));
        return val && producerSet.has(val);
      });
    }
    return list;
};

/** Build vintage and producer counts from products. */
/** @param {any[]} products */
const getFacets = function getFacets(products) {
    /** @type {Record<string, number>} */
    const vintages = {};
    /** @type {Record<string, number>} */
    const producers = {};
    products.forEach((/** @type {any} */ p) => {
      const vin = normalizeVintageYearForFilter((p.vintageMetafield?.value ?? '').toString());
      if (vin) vintages[vin] = (vintages[vin] || 0) + 1;
      const prod = getProducerPrimaryLabel(p);
      if (prod) producers[prod] = (producers[prod] || 0) + 1;
    });
    const toList = (/** @type {Record<string, number>} */ obj) => Object.entries(obj).map(([value, count]) => ({ value, count })).sort((a, b) => a.value.localeCompare(b.value));
    return { vintages: toList(vintages), vintageCountByYear: vintages, producers: toList(producers), producerCountByName: producers };
};

/**
 * Numeric vintage YEAR for a product (custom.vintage), or null when it has no parseable
 * year (non-vintage / NV / blank). Same normalization as the vintage filter so values match.
 * @param {any} p
 * @returns {number | null}
 */
const getVintageYearValue = function getVintageYearValue(p) {
    const n = parseInt(normalizeVintageYearForFilter((p.vintageMetafield?.value ?? '').toString()), 10);
    return Number.isFinite(n) ? n : null;
};

/**
 * Compare two products by vintage year. `dir` = 1 ascending (old→new), -1 descending (new→old).
 * Products without a parseable vintage always sort to the END, in either direction.
 * @param {any} a
 * @param {any} b
 * @param {1 | -1} dir
 */
const compareByVintageYear = function compareByVintageYear(a, b, dir) {
    const ya = getVintageYearValue(a);
    const yb = getVintageYearValue(b);
    if (ya == null && yb == null) return 0;
    if (ya == null) return 1;
    if (yb == null) return -1;
    return (ya - yb) * dir;
};

/**
 * @param {any[]} products
 * @param {string} sortBy
 */
const sortProducts = function sortProducts(products, sortBy) {
    const list = [...products];
    switch (sortBy) {
      case 'price_asc':
        list.sort((a, b) => getSortPrice(a) - getSortPrice(b));
        break;
      case 'price_desc':
        list.sort((a, b) => getSortPrice(b) - getSortPrice(a));
        break;
      case 'title_asc':
        list.sort((a, b) => (getProductTitleValue(a) || a.title || '').localeCompare(getProductTitleValue(b) || b.title || ''));
        break;
      case 'title_desc':
        list.sort((a, b) => (getProductTitleValue(b) || b.title || '').localeCompare(getProductTitleValue(a) || a.title || ''));
        break;
      case 'created_asc':
        list.sort((a, b) => (a.publishedAt || '').localeCompare(b.publishedAt || ''));
        break;
      case 'created_desc':
        list.sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''));
        break;
      case 'vintage_asc':
        list.sort((a, b) => compareByVintageYear(a, b, 1));
        break;
      case 'vintage_desc':
        list.sort((a, b) => compareByVintageYear(a, b, -1));
        break;
      case 'best_selling':
        /* No sales data in Storefront API; keep default order */
      default:
        break;
    }
    return list;
};

/**
 * sessionStorage TTL cache for catalog fetches. Moved verbatim from legacy.js.
 */

const CACHE_TTL = 15 * 60 * 1000;

/** @param {string} key */
const getCached = function getCached(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts > CACHE_TTL) {
        sessionStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
};

/**
 * @param {string} key
 * @param {any} data
 */
const setCache = function setCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
    } catch { /* non-critical */ }
};

/**
 * Debug switches and unified logging. Stateless: flags live on window/sessionStorage
 * (?b2b_debug=1, window.__B2B_DEBUG__, __B2B_DEBUG_PRIVATE_CELLAR_MIN_UI__,
 * __B2B_DEBUG_CART_ROUTING__, __B2B_ON_ERROR__ host hook). Moved verbatim from legacy.js.
 */

/** Set `window.__B2B_DEBUG_PRIVATE_CELLAR_MIN_UI__ = true` then reload; open cart and copy console output / traces. */
const privateCellarMinUiDebugEnabled = function privateCellarMinUiDebugEnabled() {
    return typeof window !== 'undefined' && window.__B2B_DEBUG_PRIVATE_CELLAR_MIN_UI__ === true;
};

/**
 * @param {string} label
 * @param {unknown} payload
 */
const privateCellarMinUiLog = function privateCellarMinUiLog(label, payload) {
    if (!privateCellarMinUiDebugEnabled()) return;
    try {
      const ms = typeof performance !== 'undefined' ? performance.now().toFixed(1) : '?';
      console.log(`[b2b-storefront][private-cellar-min-ui][+${ms}ms] ${label}`, payload !== undefined ? payload : '');
    } catch (_) {
      /* ignore */
    }
};

/** @param {...unknown} args */
const b2bCartDebugLog = function b2bCartDebugLog(...args) {
    console.log('[b2b-storefront][cart-sync]', ...args);
};

/**
 * Lightweight debug switch: enable with `?b2b_debug=1` (session only) or set
 * `window.__B2B_DEBUG__ = true` in the console before reload.
 */
const b2bGlobalDebugEnabled = function b2bGlobalDebugEnabled() {
    try {
      if (typeof window !== 'undefined' && /** @type {any} */ (window).__B2B_DEBUG__ === true) return true;
      if (typeof window === 'undefined' || typeof URLSearchParams === 'undefined') return false;
      const sp = new URLSearchParams(window.location.search || '');
      if (sp.get('b2b_debug') === '1') {
        try {
          sessionStorage.setItem('b2b_debug_enabled', '1');
        } catch (_) {
          /* ignore */
        }
        return true;
      }
      try {
        return sessionStorage.getItem('b2b_debug_enabled') === '1';
      } catch (_) {
        return false;
      }
    } catch (_) {
      return false;
    }
};

/**
 * Unified logger for easier filtering in prod/staging.
 * @param {'debug'|'info'|'warn'|'error'} level
 * @param {string} scope
 * @param {string} message
 * @param {unknown} [payload]
 */
const b2bLog = function b2bLog(level, scope, message, payload) {
    const method = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
    if (level === 'debug' && !b2bGlobalDebugEnabled()) return;
    try {
      console[method](`[b2b-storefront][${scope}] ${message}`, payload !== undefined ? payload : '');
    } catch (_) {
      /* ignore */
    }
};

/**
 * Central error reporter:
 * - logs consistently
 * - optionally forwards to a host callback (`window.__B2B_ON_ERROR__`)
 * @param {string} scope
 * @param {unknown} error
 * @param {Record<string, unknown>} [meta]
 */
const b2bCaptureError = function b2bCaptureError(scope, error, meta) {
    const err = error instanceof Error ? error : new Error(String(error || 'Unknown B2B error'));
    b2bLog('error', scope, err.message, { meta: meta || {}, stack: err.stack || '' });
    try {
      const hook = typeof window !== 'undefined' ? /** @type {any} */ (window)['__B2B_ON_ERROR__'] : null;
      if (typeof hook === 'function') {
        hook({
          scope,
          message: err.message,
          stack: err.stack || '',
          meta: meta || {},
          ts: Date.now(),
        });
      }
    } catch (_) {
      /* ignore host hook failures */
    }
};

/**
 * Verbose `[b2b-storefront][cart-routing]` and `[cart-debug]` logs. Set to **false** when finished
 * debugging (e.g. collections/all only adding one regular product for VIP-global-config customers).
 * Console-only disable for one tab: `window.__B2B_DEBUG_CART_ROUTING__ = false` then reload.
 */
const B2B_DEBUG_CART_ROUTING_ENABLED = true;

const b2bCartRoutingDebugEnabled = function b2bCartRoutingDebugEnabled() {
    try {
      if (typeof window !== 'undefined' && /** @type {any} */ (window).__B2B_DEBUG_CART_ROUTING__ === false) {
        return false;
      }
      return B2B_DEBUG_CART_ROUTING_ENABLED;
    } catch (_) {
      return B2B_DEBUG_CART_ROUTING_ENABLED;
    }
};

/**
 * @param {string} label
 * @param {unknown} payload
 */
const b2bCartRoutingLog = function b2bCartRoutingLog(label, payload) {
    if (!b2bCartRoutingDebugEnabled()) return;
    console.log('[b2b-storefront][cart-routing]', label, payload || {});
};

/**
 * Catalog fetchers: paged + full product fetches (tag query or search API with
 * productFilters), multi-producer parallel pagination (self-contained b2bMpp
 * state), regions/producers metaobject lookups and the media-image URL resolver,
 * all with sessionStorage TTL caching. Talks to the API via the shared client.
 * Moved verbatim from legacy.js.
 */

const NEXT_PAGE_SIZE = 50;

const PER_PAGE = 48;

/** Storefront search `first` max is 250 — fewer round trips when scanning for OR producer matches. */
const MULTI_RAW_PAGE_SIZE = 250;

/**
 * Fetch total count for current query. Uses search API (tag + title only for reliability).
 * @returns {Promise<number>}
 */
/** @param {{ state?: any } | null | undefined} options */
const fetchProductsCount = async function fetchProductsCount(options) {
    const state = (options && options.state) || {};
    const singleProducer = Array.isArray(state.producer) && state.producer.length === 1;
    const tryCount = async (/** @type {string} */ query, /** @type {any} */ productFilters) => {
      const data = await storefrontFetch(PRODUCTS_COUNT_QUERY, { query, productFilters });
      return (data.search && typeof data.search.totalCount === 'number') ? data.search.totalCount : 0;
    };
    try {
      let q = buildProductQuery(state) || getDefaultProductQuery();
      let pf = buildProductFiltersFromState(state);
      let total = await tryCount(q, pf);
      if (total > 0 || !singleProducer) return Math.max(0, total);
      total = await tryCount(q, buildProductFiltersFromState(state, 'millesimes_producteur'));
      if (total > 0) return Math.max(0, total);
      total = await tryCount(buildProductQuery(state, { includeProducerInString: true }) || getDefaultProductQuery(), buildProductFiltersFromState(state, 'skipProducer'));
      return Math.max(0, total);
    } catch (_) {
      return 0;
    }
};

/** Multi-producer OR: parallel per-producer API fetch when possible; else scan without producer + filter. `seenIds` avoids duplicates across pages. */
/** @type {{ key: string, tail: unknown[], nextRawCursor: string|null, seenIds: Set<string> }} */
let b2bMpp = { key: '', tail: [], nextRawCursor: null, seenIds: new Set() };

/**
 * One search request with the same retries as fetchProductsPage (single-producer metafield fallbacks).
 */
/**
 * @param {any} searchState
 * @param {string | null} cursor
 * @param {(query: string, productFilters: any, cursorOverride?: string | null, firstSize?: number) => Promise<{products: any[], pageInfo: {hasNextPage: boolean, endCursor: string | null}}>} doFetch
 * @param {number} firstSize
 */
const fetchSearchPageWithRetry = async function fetchSearchPageWithRetry(searchState, cursor, doFetch, firstSize = PER_PAGE) {
    const singleProducer = Array.isArray(searchState.producer) && searchState.producer.length === 1;
    let query = buildProductQuery(searchState) || getDefaultProductQuery();
    let productFilters = buildProductFiltersFromState(searchState);
    let result = await doFetch(query, productFilters, cursor, firstSize);
    if (result.products.length === 0 && singleProducer) {
      const r2 = await doFetch(buildProductQuery(searchState) || getDefaultProductQuery(), buildProductFiltersFromState(searchState, 'millesimes_producteur'), cursor, firstSize);
      if (r2.products.length > 0) result = r2;
      else {
        const r3 = await doFetch(
          buildProductQuery(searchState, { includeProducerInString: true }) || getDefaultProductQuery(),
          buildProductFiltersFromState(searchState, 'skipProducer'),
          cursor,
          firstSize
        );
        if (r3.products.length > 0) result = r3;
      }
    }
    return result;
};

/**
 * Exactly two producers, first page: parallel API queries (one producer each) then merge union — avoids sparse OR sampling from one mixed search page.
 */
/**
 * @param {any} state
 * @param {(query: string, productFilters: any, cursorOverride?: string | null, firstSize?: number) => Promise<{products: any[], pageInfo: {hasNextPage: boolean, endCursor: string | null}}>} doFetch
 * @param {string} key
 */
const fetchTwoProducersParallelPageOne = async function fetchTwoProducersParallelPageOne(state, doFetch, key) {
    const [p1, p2] = state.producer;
    const st1 = { ...state, producer: [p1] };
    const st2 = { ...state, producer: [p2] };
    const stNoProd = { ...state, producer: [] };
    const fs = MULTI_RAW_PAGE_SIZE;
    const [r1, r2, rBroad] = await Promise.all([
      fetchSearchPageWithRetry(st1, null, doFetch, fs),
      fetchSearchPageWithRetry(st2, null, doFetch, fs),
      fetchSearchPageWithRetry(stNoProd, null, doFetch, fs),
    ]);
    const list1 = sortProducts(filterProducts(r1.products, state), state.sortBy || 'default');
    const list2 = sortProducts(filterProducts(r2.products, state), state.sortBy || 'default');
    const byId = new Map();
    list1.forEach((/** @type {any} */ p) => {
      if (p && p.id) byId.set(p.id, p);
    });
    list2.forEach((/** @type {any} */ p) => {
      if (p && p.id && !byId.has(p.id)) byId.set(p.id, p);
    });
    const merged = sortProducts(Array.from(byId.values()), state.sortBy || 'default');
    merged.forEach((/** @type {any} */ p) => {
      if (p && p.id) b2bMpp.seenIds.add(p.id);
    });
    const products = merged.slice(0, PER_PAGE);
    const tail = merged.slice(PER_PAGE);
    const nextRawCursor = rBroad.pageInfo.hasNextPage ? rBroad.pageInfo.endCursor : null;
    b2bMpp = { key, tail, nextRawCursor, seenIds: b2bMpp.seenIds };
    const hasNext =
      tail.length > 0 ||
      rBroad.pageInfo.hasNextPage ||
      r1.pageInfo.hasNextPage ||
      r2.pageInfo.hasNextPage;
    let endCursor = null;
    if (hasNext) {
      endCursor = nextRawCursor != null ? nextRawCursor : rBroad.pageInfo.endCursor;
    }
    return {
      products,
      pageInfo: {
        hasNextPage: hasNext,
        endCursor,
      },
    };
};

/** @param {any} state */
const mppStateKey = function mppStateKey(state) {
    const st = state || {};
    return JSON.stringify({
      region: st.region,
      vintage: st.vintage,
      searchQuery: st.searchQuery,
      style: st.style,
      format: st.format,
      priceMin: st.priceMin,
      priceMax: st.priceMax,
      producer: st.producer,
    });
};

/**
 * @param {any[]} collected
 * @param {{ hasNextPage: boolean, endCursor: string | null } | null} lastPageInfo
 * @param {any} state
 */
const finishMppPage = function finishMppPage(collected, lastPageInfo, state) {
    const sorted = sortProducts(collected, (state && state.sortBy) || 'default');
    const lp = lastPageInfo || { hasNextPage: false, endCursor: null };
    const hasNext = b2bMpp.tail.length > 0 || lp.hasNextPage || b2bMpp.nextRawCursor != null;
    let endCursor = null;
    if (hasNext) {
      if (b2bMpp.tail.length > 0) {
        endCursor = b2bMpp.nextRawCursor != null ? b2bMpp.nextRawCursor : lp.endCursor;
      } else {
        endCursor = lp.endCursor;
      }
    }
    return {
      products: sorted,
      pageInfo: {
        hasNextPage: hasNext,
        endCursor: hasNext ? endCursor : null,
      },
    };
};

/**
 * @param {string|null|undefined} incomingCursor - null = first page / filter reset.
 */
/**
 * @param {any} state
 * @param {string|null|undefined} incomingCursor
 * @param {(query: string, productFilters: any, cursorOverride?: string | null, firstSize?: number) => Promise<{products: any[], pageInfo: {hasNextPage: boolean, endCursor: string | null}}>} doFetch
 */
const fetchProductsPageMultiProducer = async function fetchProductsPageMultiProducer(state, incomingCursor, doFetch) {
    const stNoProd = { ...state, producer: [] };
    const q2 = buildProductQuery(stNoProd) || getDefaultProductQuery();
    const pf2 = buildProductFiltersFromState(stNoProd);
    const key = mppStateKey(state);

    if (incomingCursor == null || incomingCursor === undefined) {
      b2bMpp = { key, tail: /** @type {unknown[]} */ ([]), nextRawCursor: null, seenIds: new Set() };
    } else if (b2bMpp.key !== key) {
      b2bMpp = { key, tail: /** @type {unknown[]} */ ([]), nextRawCursor: null, seenIds: new Set() };
    }

    if (
      (incomingCursor == null || incomingCursor === undefined) &&
      Array.isArray(state.producer) &&
      state.producer.length === 2
    ) {
      return fetchTwoProducersParallelPageOne(state, doFetch, key);
    }

    /** @type {unknown[]} */
    const collected = [];
    while (b2bMpp.tail.length && collected.length < PER_PAGE) {
      const next = /** @type {any} */ (b2bMpp.tail.shift());
      if (next !== undefined) {
        const pid = next && next.id;
        if (pid && b2bMpp.seenIds.has(pid)) continue;
        if (pid) b2bMpp.seenIds.add(pid);
        collected.push(next);
      }
    }

    if (collected.length >= PER_PAGE) {
      return finishMppPage(collected, null, state);
    }

    let cur =
      incomingCursor != null && incomingCursor !== undefined ? incomingCursor : b2bMpp.nextRawCursor;

    /** @type {{ hasNextPage: boolean, endCursor: string | null }} */
    let lastPageInfo = { hasNextPage: false, endCursor: null };
    let safety = 0;

    while (collected.length < PER_PAGE && safety < 12) {
      safety++;
      const r = await doFetch(q2, pf2, cur, MULTI_RAW_PAGE_SIZE);
      lastPageInfo = r.pageInfo;
      const batch = filterProducts(r.products, state);
      for (let i = 0; i < batch.length; i++) {
        const p = batch[i];
        const pid = p && p.id;
        if (pid && b2bMpp.seenIds.has(pid)) continue;
        if (pid) b2bMpp.seenIds.add(pid);
        if (collected.length >= PER_PAGE) {
          b2bMpp.tail.push(p);
        } else {
          collected.push(p);
        }
      }
      if (collected.length >= PER_PAGE) {
        b2bMpp.nextRawCursor = r.pageInfo.hasNextPage ? r.pageInfo.endCursor : null;
        break;
      }
      if (!r.pageInfo.hasNextPage) {
        b2bMpp.nextRawCursor = null;
        break;
      }
      cur = r.pageInfo.endCursor;
    }

    return finishMppPage(collected, lastPageInfo, state);
};

/**
 * Fetch one page of B2B products (query-driven). Use this for collection:
 * search and filters are applied in the API query; only one page is in memory.
 * @param {{ state: Object, cursor?: string, cardsOnly?: boolean }} options
 * @returns {Promise<{ products: unknown[], pageInfo: { hasNextPage: boolean, endCursor: string|null } }>}
 */
/** @param {{ state?: any, cursor?: string | null, cardsOnly?: boolean } | null | undefined} options */
const fetchProductsPage = async function fetchProductsPage(options) {
    const state = (options && options.state) || {};
    const cursor = options?.cursor != null ? options.cursor : null;
    const cardsOnly = !!options?.cardsOnly;
    const singleProducer = Array.isArray(state.producer) && state.producer.length === 1;
    const multiProducers = Array.isArray(state.producer) && state.producer.length > 1;

    const doFetch = async (/** @type {string} */ query, /** @type {any} */ productFilters, /** @type {string | null | undefined} */ cursorOverride = cursor, /** @type {number | undefined} */ firstSize = PER_PAGE) => {
      const c = cursorOverride;
      const first = firstSize != null && firstSize > 0 ? firstSize : PER_PAGE;
      const variables = { first, cursor: c, query, productFilters };
      const data = await storefrontFetch(cardsOnly ? PRODUCTS_SEARCH_CARD_QUERY : PRODUCTS_SEARCH_QUERY, variables);
      const products = (data.search && data.search.edges) ? data.search.edges.map((/** @type {any} */ e) => e.node) : [];
      const pageInfo = (data.search && data.search.pageInfo) ? data.search.pageInfo : { hasNextPage: false, endCursor: null };
      return { products, pageInfo };
    };

    if (multiProducers) {
      return fetchProductsPageMultiProducer(state, cursor, doFetch);
    }

    let query = buildProductQuery(state) || getDefaultProductQuery();
    let productFilters = buildProductFiltersFromState(state);
    let result = await doFetch(query, productFilters);

    if (result.products.length === 0 && singleProducer) {
      const r2 = await doFetch(buildProductQuery(state) || getDefaultProductQuery(), buildProductFiltersFromState(state, 'millesimes_producteur'));
      if (r2.products.length > 0) return r2;
      const r3 = await doFetch(buildProductQuery(state, { includeProducerInString: true }) || getDefaultProductQuery(), buildProductFiltersFromState(state, 'skipProducer'));
      if (r3.products.length > 0) return r3;
    }

    return result;
};

/**
 * Fetches B2B products (all pages). Prefer fetchProductsPage for collection.
 * @param {{ firstPageSize?: number, onFirstPage?: (products: unknown[]) => void }} options
 * @returns {Promise<unknown[]>} Full product list.
 */
/** @param {{ firstPageSize?: number, onFirstPage?: (products: unknown[]) => void } | null | undefined} options */
const fetchAllProducts = async function fetchAllProducts(options) {
    const cacheKey = withScopeKey(CACHE_KEY_BASE);
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const firstPageSize = (options && options.firstPageSize) || NEXT_PAGE_SIZE;
    const onFirstPage = options && options.onFirstPage;
    const all = [];
    let cursor = null;
    let hasNext = true;
    let pages = 0;
    const query = getDefaultProductQuery();
    const variablesBase = { query, sortKey: 'ID', reverse: false };

    while (hasNext && pages < 50) {
      pages++;
      const pageSize = pages === 1 ? firstPageSize : NEXT_PAGE_SIZE;
      const data = await storefrontFetch(PRODUCTS_QUERY, { ...variablesBase, first: pageSize, cursor });
      const products = data.products.edges.map((/** @type {any} */ e) => e.node);
      all.push(...products);
      hasNext = data.products.pageInfo.hasNextPage;
      cursor = data.products.pageInfo.endCursor;

      if (pages === 1 && onFirstPage && products.length > 0) {
        onFirstPage(products);
      }
    }

    setCache(cacheKey, all);
    return all;
};

/** @param {string} handle */
const fetchProduct = async function fetchProduct(handle) {
    const data = await storefrontFetch(PRODUCT_QUERY, { handle });
    const p = data.product;
    if (!p) return null;
    const tags = (p.tags || []).map((/** @type {string} */ t) => t.toLowerCase());
    const requiredTag = getProductTag().toLowerCase();
    if (!tags.includes(requiredTag)) return null;
    return p;
};

const REGIONS_CACHE_TTL = 30 * 60 * 1000;

const REGIONS_ALL_CACHE_TTL = 30 * 60 * 1000;

/**
 * @param {string | number | null | undefined} parentId
 * @param {any[]} allRegions
 */
const resolveParentName = function resolveParentName(parentId, allRegions) {
    const idStr = (parentId || '').toString().trim();
    if (!idStr || idStr === '(none)') return '(none)';
    const parent = allRegions.find((r) => (r.region_id?.value || '').toString().trim() === idStr);
    if (parent?.name?.value) return parent.name.value;
    for (const r of allRegions || []) {
      const h = r.hierarchy?.value;
      if (!h) continue;
      try {
        const arr = JSON.parse(h);
        for (const item of arr) {
          if (item && (String(item.id) === idStr || item.id === parseInt(idStr, 10))) return item.name || '(unknown)';
        }
      } catch (_) {}
    }
    return '(unknown)';
};

const fetchB2BRegionsMapShown = async function fetchB2BRegionsMapShown() {
    const cacheKey = withScopeKey(REGIONS_CACHE_KEY_BASE);
    const cached = getCached(cacheKey);
    if (cached?.topLevelList) return cached;

    /** @type {any[]} */
    const allRegions = [];
    let cursor = null;
    let hasNext = true;

    while (hasNext) {
      const data = await storefrontFetch(REGIONS_QUERY, { cursor });
      const meta = data?.metaobjects;
      if (!meta) break;
      const nodes = meta.nodes || [];
      allRegions.push(...nodes);
      hasNext = meta.pageInfo?.hasNextPage === true;
      cursor = meta.pageInfo?.endCursor || null;
    }

    const regions = allRegions.filter((r) => {
      const v = r.map_shown?.value;
      return v === 'true' || v === true;
    });

    /** @type {Record<string, any[]>} */
    const byParent = {};
    for (const r of regions) {
      const subId = (r.sub_last_parent_id?.value || '').toString().trim();
      if (!subId || subId === '(none)') continue;
      if (!byParent[subId]) byParent[subId] = [];
      byParent[subId].push(r);
    }

    const topLevelList = Object.keys(byParent)
      .sort((a, b) => (resolveParentName(a, allRegions) || '').localeCompare(resolveParentName(b, allRegions) || ''))
      .map((key) => ({ id: key, name: resolveParentName(key, allRegions) }));

    const result = { topLevelList };
    setCache(cacheKey, result);
    return result;
};

const fetchAllB2BRegions = async function fetchAllB2BRegions() {
    const cacheKey = withScopeKey(REGIONS_ALL_CACHE_KEY_BASE);
    const cached = getCached(cacheKey);
    if (cached?.regions) return cached.regions;

    /** @type {any[]} */
    const allRegions = [];
    let cursor = null;
    let hasNext = true;

    while (hasNext) {
      const data = await storefrontFetch(REGIONS_QUERY, { cursor });
      const meta = data?.metaobjects;
      if (!meta) break;
      const nodes = meta.nodes || [];
      allRegions.push(...nodes);
      hasNext = meta.pageInfo?.hasNextPage === true;
      cursor = meta.pageInfo?.endCursor || null;
    }

    setCache(cacheKey, { regions: allRegions });
    return allRegions;
};

/** @type {Record<string, string>} */
const mediaImageUrlCache = {};

/** @param {unknown} v */
const normalizeId = function normalizeId(v) {
    if (v == null) return '';
    return String(v).trim();
};

const fetchAllB2BProducers = async function fetchAllB2BProducers() {
    const cacheKey = withScopeKey(PRODUCERS_CACHE_KEY_BASE);
    const cached = getCached(cacheKey);
    if (cached?.producers) return cached.producers;

    const allProducers = [];
    let cursor = null;
    let hasNext = true;

    while (hasNext) {
      const data = await storefrontFetch(PRODUCERS_QUERY, { cursor });
      const meta = data?.metaobjects;
      if (!meta) break;
      const nodes = meta.nodes || [];
      allProducers.push(...nodes);
      hasNext = meta.pageInfo?.hasNextPage === true;
      cursor = meta.pageInfo?.endCursor || null;
    }

    setCache(cacheKey, { producers: allProducers });
    return allProducers;
};

/** @param {string | number | null | undefined} mediaGid */
const fetchMediaImageUrlByGid = async function fetchMediaImageUrlByGid(mediaGid) {
    const gid = normalizeId(mediaGid);
    if (!gid) return '';
    if (mediaImageUrlCache[gid]) return mediaImageUrlCache[gid];

    const data = await storefrontFetch(MEDIA_IMAGE_URL_QUERY, { id: gid });
    const node = data?.node;
    const url = node?.image?.url ? String(node.image.url) : '';
    mediaImageUrlCache[gid] = url;
    return url;
};

/**
 * Match a producer metaobject by WL / Winelabs id or producer title string.
 * Shared by producer-details hydration and PDP title link.
 * @param {string|number|null|undefined} lookupRaw
 */
const findProducerMetaobjectByLookupId = async function findProducerMetaobjectByLookupId(lookupRaw) {
    const wl = normalizeId(lookupRaw);
    if (!wl) return null;

    const producers = await fetchAllB2BProducers();
    return (
      (producers || []).find((/** @type {any} */ p) => {
        const f1 = normalizeId(p?.producer_wl_id?.value);
        const f2 = normalizeId(p?.producer_winelabs_id?.value);
        const f3 = normalizeId(p?.producer_wine_labs_id?.value);
        const title = normalizeId(p?.producer_title?.value) || normalizeId(p?.title?.value);
        return f1 === wl || f2 === wl || f3 === wl || title === wl;
      }) || null
    );
};

/**
 * Storefront API product → producer metaobject handle for `/pages/producer/:handle`
 * (same lookup chain as `sections/b2b-product.liquid` producer-details).
 * @param {Record<string, unknown>} product
 */
const resolveProducerPageHandleForProduct = async function resolveProducerPageHandleForProduct(product) {
    if (!product || typeof product !== 'object') return '';
    const getVal = (/** @type {unknown} */ v) =>
      v && typeof v === 'object' && 'value' in /** @type {object} */ (v)
        ? /** @type {{ value?: unknown }} */ (v).value
        : v;
    const lookup =
      normalizeId(getVal(product.producerWlIdMetafield)) ||
      normalizeId(getVal(product.producerWinelabsIdMetafield)) ||
      normalizeId(getVal(product.producerWineLabsIdMetafield)) ||
      normalizeId(getVal(product.producerNameMetafield)) ||
      normalizeId(getVal(product.millesimesProducteurMetafield));
    if (!lookup) return '';
    const match = await findProducerMetaobjectByLookupId(lookup);
    const handle = match?.handle != null ? String(match.handle).trim() : '';
    return handle ? handle.toLowerCase() : '';
};

/**
 * Fetch producer metaobject + image URL for B2B producer-details.
 * @param {string|number} producerWlId
 */
const fetchProducerDetailsByWlId = async function fetchProducerDetailsByWlId(producerWlId) {
    const match = await findProducerMetaobjectByLookupId(producerWlId);
    if (!match) return null;

    const title = normalizeId(match?.producer_title?.value) || normalizeId(match?.title?.value) || '';
    const detailsDomaine = match?.details_domaine?.value || '';
    const providedOverview = String(match?.producer_provided_overview?.value || '').trim();
    const legacyOverview = match?.producer_overview?.value || '';
    const overviewRaw = providedOverview || legacyOverview;

    const imageGid = normalizeId(match?.producer_card_image?.value) || normalizeId(match?.main_image?.value) || '';
    const imageUrl = imageGid ? await fetchMediaImageUrlByGid(imageGid) : '';

    return {
      title,
      details_domaine: detailsDomaine,
      producer_overview: overviewRaw,
      imageUrl,
      handle: match?.handle || '',
    };
};

/**
 * Cart cost summaries and totals rendering: payable-total parsing, the effective
 * vip/b2b drawer scope, the €120 Private Cellar minimum (product-only, fee lines
 * subtracted), insurance/excise line finders, and the HT/TTC totals HTML/plain-
 * text blocks. Moved verbatim from legacy.js.
 */

/**
 * @param {any} cart
 * @param {string} feeGid
 * @returns {any | null}
 */
const findInsuranceFeeLine = function findInsuranceFeeLine(cart, feeGid) {
    if (!feeGid || !cart) return null;
    for (const e of (cart?.lines?.edges || [])) {
      if (isInsuranceFeeLine(e?.node, feeGid)) return e.node;
    }
    return null;
};

/**
 * @param {any} cart
 * @param {string} exciseGid
 * @returns {any | null}
 */
const findExciseFeeLine = function findExciseFeeLine(cart, exciseGid) {
    if (!exciseGid || !cart) return null;
    for (const e of cart?.lines?.edges || []) {
      if (isExciseFeeLine(e?.node, exciseGid)) return e.node;
    }
    return null;
};

/**
 * Shopify CartCost: subtotalAmount is typically before tax; totalAmount is the estimated
 * amount the customer pays (often TTC). When they differ (e.g. 20% VAT), VIP / private
 * cellar can show a single subtotal-before-tax line; B2B always displays ex-VAT amounts
 * only (no dual HT/TTC lines, no VAT wording in the cart).
 */
/**
 * @param {any} m
 * @returns {{ amount: number, currency: string } | null}
 */
const parseCartMoneyField = function parseCartMoneyField(m) {
    if (!m || m.amount == null || m.amount === '') return null;
    const amount = parseFloat(m.amount);
    if (!Number.isFinite(amount)) return null;
    return { amount, currency: m.currencyCode || 'EUR' };
};

/** Private cellar (VIP) minimum order in EUR — enforced when cart currency is EUR. */
const PRIVATE_CELLAR_MIN_ORDER_EUR = 120;

/**
 * Estimated order total for minimum check: Storefront `totalAmount` (payable), else subtotal.
 */
/** @param {any} cart */
const getB2BCartEstimatedTotalForMinimum = function getB2BCartEstimatedTotalForMinimum(cart) {
    const tot = parseCartMoneyField(cart?.cost?.totalAmount);
    const sub = parseCartMoneyField(cart?.cost?.subtotalAmount);
    if (tot && tot.amount > 0) return tot.amount;
    if (sub && sub.amount > 0) return sub.amount;
    return 0;
};

/** @param {B2BConfig | null | undefined} cfg */
const getEffectiveB2BCatalogScope = function getEffectiveB2BCatalogScope(cfg) {
    const fromLive = String((cfg && cfg.scopeKey) || 'b2b').toLowerCase();
    let fromUi = '';
    try {
      const cartRoot = document.querySelector('[data-b2b-cart-ui]');
      const raw = cartRoot && cartRoot.getAttribute('data-b2b-catalog-scope');
      if (raw != null && String(raw).trim() !== '') {
        fromUi = String(raw).toLowerCase().replace(/[^a-z0-9_-]/g, '') || 'b2b';
      }
    } catch (_) {
      /* ignore */
    }
    if (fromLive === 'vip' || fromUi === 'vip') return 'vip';
    if (fromUi) return fromUi;
    return fromLive;
};

/**
 * @param {any} cart
 * @param {B2BConfig | null | undefined} cfg
 * @returns {B2BCartCostSummary}
 */
const getB2BCartCostSummary = function getB2BCartCostSummary(cart, cfg) {
    const cfgSafe = cfg || {};
    const fallbackCur = cfg?.currency || 'EUR';
    const scope = getEffectiveB2BCatalogScope(cfg);
    const sub = parseCartMoneyField(cart?.cost?.subtotalAmount);
    const tot = parseCartMoneyField(cart?.cost?.totalAmount);
    const currency = sub?.currency || tot?.currency || fallbackCur;
    const subAmt = sub?.amount ?? 0;
    const totAmt = tot?.amount ?? 0;
    const differs = Math.abs(subAmt - totAmt) > 0.02;
    if (totAmt > 0 && differs) {
      /*
       * Private cellar / VIP follows the HT/TTC tax toggle, like the main storefront. Render the
       * ex-VAT base amount — tax-toggle.js converts it to inc-VAT when the toggle is set that way
       * (the cart money is stamped data-cart-taxes-included="false") — with a tax-NEUTRAL label, so
       * the left label can never contradict the inc/ex-VAT indicator. The tax-price-label indicator
       * (added in formatB2BCartTotalsHtml) carries the VAT wording, exactly like cart-summary.liquid.
       */
      if (scope === 'vip') {
        return { mode: 'single', amount: subAmt, currency, totalLabel: cfgSafe.cartTotalEstimated || 'Estimated total', htAmount: subAmt, ttcAmount: totAmt };
      }
      /* B2B: prices are always ex-VAT; do not show a second inc.-VAT line or VAT-specific labels. */
      return {
        mode: 'single',
        amount: subAmt,
        currency,
        totalLabel: cfgSafe.cartTotalEstimated || 'Estimated total',
        htAmount: subAmt,
        ttcAmount: totAmt,
      };
    }
    const single = totAmt > 0 ? totAmt : subAmt;
    return { mode: 'single', amount: single, currency, totalLabel: null, htAmount: subAmt, ttcAmount: totAmt };
};

/**
 * Sum of the auto-managed fee lines (insurance + excise/duty) in major units. Those variants have
 * "charge tax" off, so they carry NO VAT component: callers subtract them from the €120 product
 * minimum, and from the VAT base for the inc-VAT total (tax must never be applied to them).
 * @param {any} cart
 * @param {B2BConfig | null | undefined} cfg
 * @returns {number}
 */
const b2bCartFeeLineTotal = function b2bCartFeeLineTotal(cart, cfg) {
    return (cart?.lines?.edges || []).reduce((/** @type {number} */ sum, /** @type {any} */ e) => {
      const node = e?.node;
      if (!isAnyFeeLine(node, cfg)) return sum;
      return sum + (parseFloat(node?.cost?.totalAmount?.amount || '0') || 0);
    }, 0);
};

/**
 * €120 minimum must use the same money basis as the main total line in the VIP drawer. That line is HT
 * (subtotal) when subtotal and total differ; using Storefront `totalAmount` (TTC) here made the cart look
 * under €120 while TTC was already ≥ €120 — checkout became enabled after re-sync / opening the drawer.
 */
/**
 * @param {any} cart
 * @param {B2BConfig | null | undefined} cfg
 */
const amountForPrivateCellarMinimumOrder = function amountForPrivateCellarMinimumOrder(cart, cfg) {
    if (getEffectiveB2BCatalogScope(cfg) !== 'vip') {
      return getB2BCartEstimatedTotalForMinimum(cart);
    }
    const s = getB2BCartCostSummary(cart, cfg);
    let amount;
    if (s.mode === 'ht_ttc') amount = s.ttcAmount ?? s.amount;
    else if (s.mode === 'single') amount = s.amount;
    else amount = getB2BCartEstimatedTotalForMinimum(cart);
    /*
     * The €120 minimum is on PRODUCT value only. Subtract the auto-managed fee lines (insurance +
     * excise/duty) so a 15% US excise or per-bottle Sweden duty can't push a sub-€120 product cart
     * over the line.
     */
    return Math.max(0, amount - b2bCartFeeLineTotal(cart, cfg));
};

/**
 * Matches snippets/cart-summary.liquid: {@link TextComponent} + `[shimmer]` uses theme base.css animation.
 * @param {number} amount
 * @param {string} currency
 * @param {number} [exemptAmount] Non-taxable portion (insurance/excise fee lines) in the same ex-VAT
 *   basis as `amount`. When > 0 it is stamped as `data-tax-exempt` so tax-toggle.js applies VAT only
 *   to the taxable remainder: `(base − exempt) × rate + exempt`. Omit/0 for ordinary totals.
 */
const b2bCartTotalValueTextComponent = function b2bCartTotalValueTextComponent(amount, currency, exemptAmount) {
    const money = formatMoney(amount, currency);
    const moneyEsc = esc(money);
    const exempt = (typeof exemptAmount === 'number' && Number.isFinite(exemptAmount) && exemptAmount > 0.005)
      ? exemptAmount
      : 0;
    const exemptAttr = exempt ? ` data-tax-exempt="${exempt}"` : '';
    /* data-tax-raw carries the un-converted ex-VAT amount tax-toggle.js must use as the HT base. The
     * JS-rendered VIP drawer re-paints + re-applies the toggle asynchronously, and tax-toggle's
     * reset/re-apply cycle otherwise re-derives the base from text it had ALREADY multiplied by VAT —
     * compounding it (raw → ×rate → cached as base → ×rate again). An immutable raw attr the reset
     * leaves alone makes the conversion idempotent. data-tax-exempt (fee lines, no VAT) likewise
     * survives the reset so the inc-VAT total never taxes the insurance/excise fee. */
    return `<text-component value="${moneyEsc}" class="cart__total-value cart-secondary-typography" data-tax-raw="${amount}"${exemptAttr}>${moneyEsc}</text-component>`;
};

/** @param {any} cart */
const formatB2BCartTotalsHtml = function formatB2BCartTotalsHtml(cart) {
    const cfg = getConfig() || {};
    const s = getB2BCartCostSummary(cart, cfg);
    if (s.mode === 'ht_ttc') {
      return `
        <span class="cart__summary-item cart__total b2b-cart-total-ht" role="status" style="display:flex;flex-wrap:wrap;justify-content:space-between;gap:4px;width:100%;">
          <span class="cart__total-label cart-primary-typography">${esc(cfg.cartSubtotalHt || 'Subtotal ex. VAT')}</span>
          ${b2bCartTotalValueTextComponent(s.htAmount, s.currency)}
        </span>
        <span class="cart__summary-item cart__total b2b-cart-total-ttc" role="status" style="display:flex;flex-wrap:wrap;justify-content:space-between;gap:4px;width:100%;">
          <span class="cart__total-label cart-primary-typography">${esc(cfg.cartTotalEstimatedTtc || 'Estimated total inc. VAT')}</span>
          ${b2bCartTotalValueTextComponent(s.ttcAmount, s.currency)}
        </span>`;
    }
    const singleLabel = s.totalLabel || cfg.cartTotalEstimated || 'Estimated total';
    const isVip = getEffectiveB2BCatalogScope(cfg) === 'vip';
    const taxTtc = esc(cfg.taxLabelTtc || 'inc. VAT');
    const taxHt = esc(cfg.taxLabelHt || 'Ex. VAT');
    /* VIP follows the HT/TTC toggle and tax-toggle.js synthesises the inc-VAT figure client-side
     * (this market returns no Storefront tax estimate). The insurance/excise fee lines are non-taxable,
     * so pass their total as the exempt portion — the inc-VAT total then taxes only the products. */
    const vipFeeExempt = isVip ? b2bCartFeeLineTotal(cart, cfg) : 0;
    const vipTotalRowStyle = isVip
      ? ' style="display:flex;flex-wrap:wrap;justify-content:space-between;gap:8px;width:100%;align-items:baseline;"'
      : '';
    const priceAndLabelHtml = isVip
      ? `<span class="cart__total-amount-group" style="display:inline-flex;align-items:baseline;gap:0.35em;flex-wrap:wrap;justify-content:flex-end;">
          ${b2bCartTotalValueTextComponent(s.amount, s.currency, vipFeeExempt)}
          <span class="tax-price-label" data-cart-summary-tax-label data-tax-label-ttc="${taxTtc}" data-tax-label-ht="${taxHt}">${taxTtc}</span>
        </span>`
      : b2bCartTotalValueTextComponent(s.amount, s.currency);
    return `
      <span class="cart__summary-item cart__total" role="status"${vipTotalRowStyle}>
        <span class="cart__total-label cart-primary-typography">${esc(singleLabel)}</span>
        ${priceAndLabelHtml}
      </span>`;
};

/** @param {any} cart */
const formatB2BCartTotalsPlainLines = function formatB2BCartTotalsPlainLines(cart) {
    const cfg = getConfig() || {};
    const s = getB2BCartCostSummary(cart, cfg);
    if (s.mode === 'ht_ttc') {
      return [
        `${cfg.cartSubtotalHt || 'Subtotal ex. VAT'}: ${formatMoney(s.htAmount, s.currency)}`,
        `${cfg.cartTotalEstimatedTtc || 'Estimated total inc. VAT'}: ${formatMoney(s.ttcAmount, s.currency)}`,
      ];
    }
    const lbl = s.totalLabel || cfg.cartTotalEstimated || 'Estimated total';
    return [`${lbl}: ${formatMoney(s.amount, s.currency)}`];
};

/** @param {any} cart */
const formatB2BCartTotalsOrderRequestBlockHtml = function formatB2BCartTotalsOrderRequestBlockHtml(cart) {
    const cfg = getConfig() || {};
    const s = getB2BCartCostSummary(cart, cfg);
    if (s.mode === 'ht_ttc') {
      return `
          <p style="margin:10px 0 0; opacity:.85;"><strong>${esc(cfg.cartSubtotalHt || 'Subtotal ex. VAT')}:</strong> ${esc(formatMoney(s.htAmount, s.currency))}</p>
          <p style="margin:6px 0 0; opacity:.85;"><strong>${esc(cfg.cartTotalEstimatedTtc || 'Estimated total inc. VAT')}:</strong> ${esc(formatMoney(s.ttcAmount, s.currency))}</p>`;
    }
    const lbl = s.totalLabel || cfg.cartTotalEstimated || 'Estimated total';
    return `
          <p style="margin:10px 0 0; opacity:.85;"><strong>${esc(lbl)}:</strong> ${esc(formatMoney(s.amount, s.currency))}</p>`;
};

/** @param {any} payload */
const renderB2BBankTransferDraftOrderHtml = function renderB2BBankTransferDraftOrderHtml(payload) {
    const cfg = getConfig() || {};
    const title = (payload && payload.title) || cfg.bankTransferTitleDefault || 'Bank transfer required';
    const message = (payload && payload.message) || cfg.bankTransferMessageDefault || '';
    const reference = (payload && (payload.reference || payload.orderNumber || payload.name)) || '';
    const instructionsHtml = (payload && payload.instructionsHtml) || '';
    const nextStepsHtml = (payload && payload.nextStepsHtml) || '';
    return `
      <div class="cart-drawer__header b2b-cart-drawer__header">
        <span class="cart-drawer__heading h3"><span class="b2b-cart-drawer__title-cluster"><span class="b2b-cart-drawer__title-text h3">${esc(cfg.cartTitleLabel || 'B2B Cart')}</span></span></span>
        <button type="button" class="button close-button cart-drawer__close-button button-unstyled" data-b2b-cart-close aria-label="${esc(cfg.cartCloseLabel || 'Close')}">
          <span class="svg-wrapper">${ICON_CLOSE}</span>
        </button>
      </div>
      <div class="cart-drawer__content b2b-cart-drawer__content motion-reduce" style="padding: 16px;">
        <h3 class="h4" style="margin: 0 0 8px;">${esc(title)}</h3>
        <p style="margin: 0 0 12px; opacity: .85; line-height: 1.5;">${esc(message)}</p>
        ${reference ? `<p style="margin: 0 0 12px;"><strong>${esc(cfg.referenceLabel || 'Reference:')}</strong> ${esc(reference)}</p>` : ''}
        ${instructionsHtml ? `<div class="rte" style="margin: 0 0 12px;">${instructionsHtml}</div>` : ''}
        ${nextStepsHtml ? `<div class="rte" style="margin: 0 0 16px;">${nextStepsHtml}</div>` : ''}
        <div class="cart__ctas" style="display:grid;grid-template-columns:1fr;gap:10px;">
          <button type="button" class="button" data-b2b-cart-close>${esc(cfg.cartCloseLabel || 'Close')}</button>
        </div>
      </div>
    `;
};

/**
 * Small pure utilities shared across the b2b bundle.
 *
 * Exported as `const` arrows on purpose: the bundle is served as a classic
 * script, and top-level `function` declarations would become window globals.
 */

/**
 * Trailing-edge debounce. Arguments of the LAST call win. The returned
 * function carries `.cancel()` so a committing event (e.g. `change`) can drop
 * a pending trailing call before applying the same value immediately.
 * @template {(...args: any[]) => void} F
 * @param {F} fn
 * @param {number} wait
 */
const debounce = (fn, wait) => {
  /** @type {ReturnType<typeof setTimeout> | null} */
  let timer = null;
  const wrapped = (/** @type {any[]} */ ...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, wait);
  };
  wrapped.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return wrapped;
};

/**
 * Join email-body parts: `null`/`undefined` entries are omitted lines,
 * `''` entries are INTENTIONAL blank separator lines (kept, with runs
 * collapsed and the edges trimmed). Replaces a `.filter(Boolean).join('\n')`
 * that stripped the separators and produced one dense block.
 * @param {Array<string | null | undefined>} parts
 */
const joinBodyLines = (parts) => {
  const kept = (parts || []).filter((l) => l != null);
  /** @type {string[]} */
  const out = [];
  for (const line of kept) {
    if (line === '' && out[out.length - 1] === '') continue;
    out.push(line);
  }
  while (out.length && out[0] === '') out.shift();
  while (out.length && out[out.length - 1] === '') out.pop();
  return out.join('\n');
};

/**
 * B2B order-request (bank transfer) flow: the mailto email body (blank separator
 * lines preserved via joinBodyLines; insurance fee rendered as a priced line, not
 * x-cents), the contact-form field setter, and the order-request-sent session
 * flags. Moved verbatim from legacy.js.
 */

/**
 * @param {{ cart: any, formData: Record<string, any> }} param0
 * @returns {{ subject: string, body: string }}
 */
const buildOrderRequestEmail = function buildOrderRequestEmail({ cart, formData }) {
    const cfg = getConfig() || {};
    const discountCode = getStoredB2BDiscountCode();
    const gids = getInsuranceFeeGids(cfg);
    const totalLines = formatB2BCartTotalsPlainLines(cart);

    const lines = (cart?.lines?.edges || []).map((/** @type {any} */ e) => e?.node).filter(Boolean);
    const insuranceLines = lines.filter((/** @type {any} */ line) => isInsuranceFeeLine(line, gids));
    const lineTextParts = lines
      .filter((/** @type {any} */ line) => !isInsuranceFeeLine(line, gids))
      .map((/** @type {any} */ line) => {
        const qty = line?.quantity || 0;
        const productTitle = line?.merchandise?.product?.title || '';
        const variantTitle = line?.merchandise?.title || '';
        const title = variantTitle ? `${productTitle} — ${variantTitle}` : productTitle;
        return `- ${title} x ${qty}`;
      });
    /* Insurance is a denomination ladder (several €0.01/€1/€10… lines); show one summed amount,
     * not "x 3600". Its total is already inside the totals block below. */
    if (insuranceLines.length) {
      const feeTotal = insuranceLines.reduce(
        (/** @type {number} */ sum, /** @type {any} */ line) => sum + (parseFloat(line?.cost?.totalAmount?.amount || '0') || 0),
        0
      );
      const feeCur = insuranceLines[0]?.cost?.totalAmount?.currencyCode || cfg.currency || 'EUR';
      const feeTitle =
        insuranceLines[0]?.merchandise?.product?.title || insuranceLines[0]?.merchandise?.title || '1% Shipping Insurance';
      lineTextParts.push(`- ${feeTitle}: ${formatMoney(feeTotal, feeCur)}`);
    }
    const lineText = lineTextParts.join('\n');

    const subject = `${cfg.orderEmailSubjectPrefix || 'B2B order request —'} ${formData.company || ''}`.trim();
    /* Blank entries are intentional separator lines; joinBodyLines keeps them
       (collapsing runs) and only drops the null-ed optional lines — the old
       .filter(Boolean) stripped the separators and produced one dense block. */
    const body = joinBodyLines([
      cfg.orderEmailBodyHeader || 'B2B ORDER REQUEST (bank transfer)',
      '',
      `${cfg.orderEmailCompany || 'Company:'} ${formData.company || ''}`,
      `${cfg.orderEmailContact || 'Contact:'} ${formData.name || ''}`,
      `${cfg.orderEmailEmail || 'Email:'} ${formData.email || ''}`,
      formData.phone ? `${cfg.orderEmailPhone || 'Phone:'} ${formData.phone}` : null,
      '',
      cfg.orderEmailLines || 'Lines:',
      lineText || `- ${cfg.orderRequestEmptyLine || '(empty)'}`,
      '',
      ...totalLines,
      discountCode ? `${cfg.orderEmailDiscount || 'Discount code:'} ${discountCode}` : null,
      '',
      formData.notes ? `${cfg.orderEmailNotes || 'Notes:'}\n${formData.notes}` : null,
      '',
      `${cfg.orderEmailThemeCartId || 'Theme cart id:'} ${getStoredStorefrontCartId() || ''}`,
      `${cfg.orderEmailPage || 'Page:'} ${window.location.href}`,
    ]);

    return { subject, body };
};

/**
 * Same pipeline as snippets/wine-submission-form.liquid: real {% form 'contact' %} in the DOM,
 * then native submit via requestSubmit() (hCaptcha + POST) instead of fetch/iframe.
 */
/**
 * @param {HTMLFormElement} form
 * @param {string} fieldName
 * @param {unknown} value
 */
const setB2BOrderRequestContactField = function setB2BOrderRequestContactField(form, fieldName, value) {
    const v = value == null ? '' : String(value);
    const controls = form.querySelectorAll('input, textarea, select');
    for (let i = 0; i < controls.length; i++) {
      const control = /** @type {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} */ (controls[i]);
      if (control.name === fieldName) {
        control.value = v;
        return;
      }
    }
};

const markB2BOrderRequestSent = function markB2BOrderRequestSent() {
    try {
      sessionStorage.setItem(SESSION_KEY_B2B_ORDER_REQUEST_SENT, '1');
    } catch (_) {
      /* no-op */
    }
};

const clearB2BOrderRequestSentFlag = function clearB2BOrderRequestSentFlag() {
    try {
      sessionStorage.removeItem(SESSION_KEY_B2B_ORDER_REQUEST_SENT);
    } catch (_) {
      /* no-op */
    }
};

/**
 * Quantity selector: stock/purchase-limit resolution (min of inventory and
 * purchase_limit_units; negative = unlimited) and the quantity <select> options
 * with the custom-quantity dialog. Moved verbatim from legacy.js.
 */

/** Maximum quantity exposed before showing the '+' custom-entry option. */
const B2B_QUANTITY_MAX_OPTIONS = 24;

/** Sentinel option value for the '+' custom entry. */
const B2B_QUANTITY_CUSTOM_VALUE = '__custom__';

/**
 * @param {any} variant
 * @returns {number|null} purchase_limit_units when > 0; null when unset, blank, or 0 (no cap).
 */
const b2bParsePurchaseLimitUnits = function b2bParsePurchaseLimitUnits(variant) {
    if (!variant) return null;
    const mf = variant.purchaseLimitUnitsMetafield;
    const raw = mf?.value ?? mf;
    if (raw == null || raw === '') return null;
    const n = parseInt(String(raw), 10);
    if (isNaN(n) || n <= 0) return null;
    return n;
};

/**
 * Effective max purchasable quantity (Shopify units): min(inventory, purchase_limit_units).
 * purchase_limit_units 0 = no purchase cap. Negative return = unlimited.
 * @param {any} variant
 * @returns {number}
 */
const b2bGetVariantStock = function b2bGetVariantStock(variant) {
    if (!variant) return -1;
    if (variant.availableForSale === false) return 0;

    let invStock = -1;
    const rawQty = variant.quantityAvailable;
    if (rawQty != null) {
      const n = parseInt(rawQty, 10);
      if (!isNaN(n)) invStock = Math.max(0, n);
    }

    const purchaseLimit = b2bParsePurchaseLimitUnits(variant);
    if (purchaseLimit != null) {
      if (invStock < 0) return purchaseLimit;
      return Math.min(invStock, purchaseLimit);
    }

    return invStock;
};

/**
 * @param {number} stockForVariant
 * @returns {string}
 */
const b2bBuildQuantityOptionsHtml = function b2bBuildQuantityOptionsHtml(stockForVariant) {
    const unlimited = stockForVariant == null || stockForVariant < 0;
    let maxOptions;
    let showPlus = false;
    if (unlimited) {
      maxOptions = B2B_QUANTITY_MAX_OPTIONS;
      showPlus = true;
    } else if (stockForVariant > B2B_QUANTITY_MAX_OPTIONS) {
      maxOptions = B2B_QUANTITY_MAX_OPTIONS;
      showPlus = true;
    } else {
      maxOptions = Math.max(1, stockForVariant);
    }
    let html = '';
    for (let i = 1; i <= maxOptions; i++) {
      html += `<option value="${i}"${i === 1 ? ' selected' : ''}>${i}</option>`;
    }
    if (showPlus) {
      html += `<option value="${B2B_QUANTITY_CUSTOM_VALUE}">+</option>`;
    }
    return html;
};

/**
 * @param {HTMLSelectElement} quantitySelect
 * @param {number} stockForVariant
 */
const b2bRebuildQuantityOptions = function b2bRebuildQuantityOptions(quantitySelect, stockForVariant) {
    if (!quantitySelect) return;
    const previousValueNum = parseInt(quantitySelect.value, 10);
    const unlimited = stockForVariant == null || stockForVariant < 0;
    let maxOptions;
    let showPlus = false;
    if (unlimited) {
      maxOptions = B2B_QUANTITY_MAX_OPTIONS;
      showPlus = true;
    } else if (stockForVariant > B2B_QUANTITY_MAX_OPTIONS) {
      maxOptions = B2B_QUANTITY_MAX_OPTIONS;
      showPlus = true;
    } else {
      maxOptions = Math.max(1, stockForVariant);
    }

    while (quantitySelect.firstChild) {
      quantitySelect.removeChild(quantitySelect.firstChild);
    }
    for (let i = 1; i <= maxOptions; i++) {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = String(i);
      quantitySelect.appendChild(opt);
    }
    if (showPlus) {
      const plusOpt = document.createElement('option');
      plusOpt.value = B2B_QUANTITY_CUSTOM_VALUE;
      plusOpt.textContent = '+';
      quantitySelect.appendChild(plusOpt);
    }

    const maxAllowed = unlimited ? Infinity : Math.max(1, stockForVariant);
    if (!isNaN(previousValueNum) && previousValueNum >= 1 && previousValueNum <= maxOptions) {
      quantitySelect.value = String(previousValueNum);
    } else if (!isNaN(previousValueNum) && previousValueNum > maxOptions && previousValueNum <= maxAllowed) {
      const customOpt = document.createElement('option');
      customOpt.value = String(previousValueNum);
      customOpt.textContent = String(previousValueNum);
      customOpt.dataset.customQuantity = 'true';
      if (showPlus) {
        quantitySelect.insertBefore(customOpt, quantitySelect.lastElementChild);
      } else {
        quantitySelect.appendChild(customOpt);
      }
      quantitySelect.value = String(previousValueNum);
    } else {
      quantitySelect.value = '1';
    }
};

/**
 * @param {HTMLSelectElement} quantitySelect
 * @param {number} value
 */
const b2bSetCustomQuantity = function b2bSetCustomQuantity(quantitySelect, value) {
    if (!quantitySelect) return;
    const existingCustom = quantitySelect.querySelector('option[data-custom-quantity="true"]');
    if (existingCustom) existingCustom.remove();
    const matchingNumeric = quantitySelect.querySelector(`option[value="${String(value)}"]`);
    if (matchingNumeric) {
      quantitySelect.value = String(value);
      return;
    }
    const customOpt = document.createElement('option');
    customOpt.value = String(value);
    customOpt.textContent = String(value);
    customOpt.dataset.customQuantity = 'true';
    const plusOpt = quantitySelect.querySelector(`option[value="${B2B_QUANTITY_CUSTOM_VALUE}"]`);
    if (plusOpt) {
      quantitySelect.insertBefore(customOpt, plusOpt);
    } else {
      quantitySelect.appendChild(customOpt);
    }
    quantitySelect.value = String(value);
};

/**
 * Custom quantity dialog matching the existing primeurs-cart-guard dialog visuals.
 * @param {number} stockForVariant
 * @returns {Promise<number | null>}
 */
const b2bShowCustomQuantityDialog = function b2bShowCustomQuantityDialog(stockForVariant) {
    return new Promise((resolve) => {
      const translations = (typeof window !== 'undefined' && /** @type {any} */ (window).Theme && /** @type {any} */ (window).Theme.translations) || {};
      const titleText = translations.custom_quantity_title || 'Enter quantity';
      const messageText = translations.custom_quantity_message || 'How many bottles would you like to add?';
      const applyText = translations.custom_quantity_apply || 'Apply';
      const cancelText = translations.custom_quantity_cancel || 'Cancel';
      const hintTemplate = translations.custom_quantity_max_hint || 'Maximum available: __COUNT__';
      const exceedsTemplate = translations.custom_quantity_exceeds_stock || 'Allocation limit reached';
      const hasFiniteStock = stockForVariant != null && stockForVariant >= 0;
      const hintText = hasFiniteStock
        ? hintTemplate.replace('__COUNT__', String(stockForVariant))
        : '';

      // Positioned div overlay rather than native <dialog>+showModal(). The
      // B2B cart drawer renders inside its own modal stack and nested
      // top-layer dialogs intercept each other's clicks; a div overlay
      // sidesteps that and matches the regular cart implementation.
      const overlay = document.createElement('div');
      overlay.className = 'wine-custom-quantity-dialog-overlay';
      overlay.setAttribute('role', 'presentation');
      overlay.style.cssText = [
        'position: fixed',
        'inset: 0',
        'z-index: 2147483600',
        'background: rgba(0, 0, 0, 0.45)',
        'display: flex',
        'align-items: center',
        'justify-content: center',
        'padding: 1rem'
      ].join(';');

      const panel = document.createElement('div');
      panel.className = 'primeurs-cart-guard-dialog wine-custom-quantity-dialog';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-label', titleText);
      panel.style.cssText = [
        'background: var(--color-background, #fff)',
        'color: var(--color-foreground, #000)',
        'border: 1px solid rgb(var(--color-foreground-rgb) / 0.25)',
        'border-radius: 4px',
        'padding: 20px 22px',
        'max-width: 24rem',
        'width: 100%',
        'box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18)'
      ].join(';');
      panel.innerHTML = `
        <div class="primeurs-cart-guard-dialog__content">
          <h3 style="margin: 0 0 8px;">${esc(titleText)}</h3>
          <p style="margin: 0 0 6px;">${esc(messageText)}</p>
          ${hintText ? `<p style="opacity: 0.7; font-size: 0.85em; margin: 0 0 4px;">${esc(hintText)}</p>` : ''}
          <input type="number" min="1" ${hasFiniteStock ? `max="${stockForVariant}"` : ''} step="1" inputmode="numeric"
            data-custom-quantity-input
            style="width: 100%; padding: 8px 10px; font-size: 1rem; border: 1px solid rgb(var(--color-foreground-rgb) / 0.4); background: var(--color-background); color: var(--color-foreground); margin-top: 8px;" />
          <p data-custom-quantity-error style="color: #c0392b; font-size: 0.85em; margin: 6px 0 0 0; min-height: 1.2em;"></p>
          <div class="primeurs-cart-guard-dialog__actions" style="display:flex; align-items:center; gap:0.75rem; flex-wrap:nowrap; margin-top: 12px;">
            <button type="button" class="button button-secondary" data-action="cancel">${esc(cancelText)}</button>
            <button type="button" class="button button-primary" data-action="apply">${esc(applyText)}</button>
          </div>
        </div>
      `;

      overlay.appendChild(panel);

      const input = /** @type {HTMLInputElement | null} */ (panel.querySelector('[data-custom-quantity-input]'));
      const errorEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-custom-quantity-error]'));
      const cancelBtn = panel.querySelector('[data-action="cancel"]');
      const applyBtn = panel.querySelector('[data-action="apply"]');

      let resolved = false;
      /** @param {number | null} value */
      function finish(value) {
        if (resolved) return;
        resolved = true;
        document.removeEventListener('keydown', onEscKey, true);
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        resolve(value);
      }

      function tryApply() {
        const raw = (input && input.value || '').trim();
        const value = parseInt(raw, 10);
        if (!raw || isNaN(value) || value < 1) {
          if (errorEl) errorEl.textContent = messageText;
          return;
        }
        if (hasFiniteStock && value > stockForVariant) {
          if (errorEl) errorEl.textContent = exceedsTemplate.replace('__COUNT__', String(stockForVariant));
          return;
        }
        finish(value);
      }

      /** @param {KeyboardEvent} e */
      function onEscKey(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          finish(null);
        }
      }

      if (applyBtn) {
        applyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          tryApply();
        });
      }
      if (cancelBtn) {
        cancelBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          finish(null);
        });
      }
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) finish(null);
      });
      document.addEventListener('keydown', onEscKey, true);
      if (input) {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            tryApply();
          }
        });
      }

      document.body.appendChild(overlay);
      if (input) {
        input.value = hasFiniteStock ? String(Math.min(stockForVariant, B2B_QUANTITY_MAX_OPTIONS + 1)) : String(B2B_QUANTITY_MAX_OPTIONS + 1);
        try { input.focus(); input.select(); } catch (_) { /* noop */ }
      }
    });
};

/**
 * Catalog rendering: wine-card HTML (price block with compare-at, stock line,
 * quantity select, variant picker), the results grid, and cursor/numbered
 * pagination — all matching the theme's Liquid card/grid markup. Moved verbatim
 * from legacy.js.
 */

const isB2BScope = function isB2BScope() {
    const cfg = getConfig();
    return (cfg?.scopeKey || 'b2b') === 'b2b';
};

const getPaginationCaretHtml = function getPaginationCaretHtml() {
    const el = document.querySelector('[data-b2b-icon-caret]');
    return (el && el.innerHTML || '').trim();
};

/** Pagination for cursor-based (single-page) mode: only Prev / Next. */
/**
 * @param {boolean} hasNextPage
 * @param {number} currentPage
 * @param {boolean} hasPreviousPage
 */
const renderPaginationCursor = function renderPaginationCursor(hasNextPage, currentPage, hasPreviousPage) {
    const caret = getPaginationCaretHtml();
    const caretSpan = '<span class="svg-wrapper icon-caret icon-caret--forward">' + (caret || '‹') + '</span>';
    const prevDisabled = !hasPreviousPage;
    const nextDisabled = !hasNextPage;
    const prevPage = hasPreviousPage ? 'prev' : null;
    const nextPage = hasNextPage ? 'next' : null;
    return `
      <nav class="collection-pagination" aria-label="Pagination">
        <div class="collection-pagination__container">
          ${prevDisabled
      ? '<span class="collection-pagination__link collection-pagination__link--prev collection-pagination__link--disabled" aria-hidden="true">' + caretSpan + '</span>'
      : '<a href="#" class="collection-pagination__link collection-pagination__link--prev" data-b2b-page="' + prevPage + '" aria-label="Previous page">' + caretSpan + '</a>'
    }
          <span class="collection-pagination__number collection-pagination__number--active" aria-current="page">${currentPage}</span>
          ${nextDisabled
      ? '<span class="collection-pagination__link collection-pagination__link--next collection-pagination__link--disabled" aria-hidden="true">' + caretSpan + '</span>'
      : '<a href="#" class="collection-pagination__link collection-pagination__link--next" data-b2b-page="' + nextPage + '" aria-label="Next page">' + caretSpan + '</a>'
    }
        </div>
      </nav>`;
};

/**
 * @param {number} totalPages
 * @param {number} currentPage
 * @param {number} [clickablePageMax] - In cursor mode: only pages 1..clickablePageMax are links; rest disabled (no jump-to-last).
 */
const renderPagination = function renderPagination(totalPages, currentPage, clickablePageMax) {
    if (totalPages <= 1) return '';
    const caret = getPaginationCaretHtml();
    const caretSpan = '<span class="svg-wrapper icon-caret icon-caret--forward">' + (caret || '‹') + '</span>';
    const prevDisabled = currentPage <= 1;
    const nextDisabled = currentPage >= totalPages;
    const useCursorLimit = typeof clickablePageMax === 'number' && clickablePageMax > 0;
    const isClickable = (/** @type {number} */ p) => !useCursorLimit || (p <= clickablePageMax && p !== currentPage);
    let numbersHtml = '';
    if (totalPages <= 9) {
      for (let i = 1; i <= totalPages; i++) {
        if (i === currentPage) {
          numbersHtml += `<span class="collection-pagination__number collection-pagination__number--active" aria-current="page">${i}</span>`;
        } else if (isClickable(i)) {
          numbersHtml += `<a href="#" class="collection-pagination__number" data-b2b-page="${i}">${i}</a>`;
        } else {
          numbersHtml += `<span class="collection-pagination__number collection-pagination__number--disabled" aria-disabled="true">${i}</span>`;
        }
      }
    } else {
      /* Ellipsis logic (matches snippets/custom-pagination.liquid): first, …?, window of 5, …?, last */
      let startP = Math.max(2, currentPage - 2);
      let endP = Math.min(totalPages - 1, currentPage + 2);
      numbersHtml += currentPage === 1
        ? `<span class="collection-pagination__number collection-pagination__number--active" aria-current="page">1</span>`
        : isClickable(1)
          ? `<a href="#" class="collection-pagination__number" data-b2b-page="1">1</a>`
          : `<span class="collection-pagination__number collection-pagination__number--disabled" aria-disabled="true">1</span>`;
      if (currentPage > 3) {
        numbersHtml += `<span class="collection-pagination__ellipsis">…</span>`;
      }
      for (let p = 2; p < totalPages; p++) {
        if (p >= startP && p <= endP) {
          if (p === currentPage) {
            numbersHtml += `<span class="collection-pagination__number collection-pagination__number--active" aria-current="page">${p}</span>`;
          } else if (isClickable(p)) {
            numbersHtml += `<a href="#" class="collection-pagination__number" data-b2b-page="${p}">${p}</a>`;
          } else {
            numbersHtml += `<span class="collection-pagination__number collection-pagination__number--disabled" aria-disabled="true">${p}</span>`;
          }
        }
      }
      if (currentPage < totalPages - 2) {
        numbersHtml += `<span class="collection-pagination__ellipsis">…</span>`;
      }
      if (totalPages > 1) {
        if (currentPage === totalPages) {
          numbersHtml += `<span class="collection-pagination__number collection-pagination__number--active" aria-current="page">${totalPages}</span>`;
        } else if (isClickable(totalPages)) {
          numbersHtml += `<a href="#" class="collection-pagination__number" data-b2b-page="${totalPages}">${totalPages}</a>`;
        } else {
          numbersHtml += `<span class="collection-pagination__number collection-pagination__number--disabled" aria-disabled="true">${totalPages}</span>`;
        }
      }
    }
    return `
      <nav class="collection-pagination" aria-label="Pagination">
        <div class="collection-pagination__container">
          ${prevDisabled
      ? '<span class="collection-pagination__link collection-pagination__link--prev collection-pagination__link--disabled" aria-hidden="true">' + caretSpan + '</span>'
      : '<a href="#" class="collection-pagination__link collection-pagination__link--prev" data-b2b-page="' + (currentPage - 1) + '" aria-label="Previous page">' + caretSpan + '</a>'
    }
          <div class="collection-pagination__numbers">${numbersHtml}</div>
          ${nextDisabled
      ? '<span class="collection-pagination__link collection-pagination__link--next collection-pagination__link--disabled" aria-hidden="true">' + caretSpan + '</span>'
      : '<a href="#" class="collection-pagination__link collection-pagination__link--next" data-b2b-page="' + (currentPage + 1) + '" aria-label="Next page">' + caretSpan + '</a>'
    }
        </div>
      </nav>`;
};

/**
 * @param {any} variant
 * @param {B2BConfig} cfg
 */
const buildCardPriceInnerHtml = function buildCardPriceInnerHtml(variant, cfg) {
    const price = getDisplayPrice(variant);
    const comparePriceFormatted = getDisplayCompareAtPrice(variant);
    const saleMajor = getVariantMajorAmountPerDisplayedUnit(variant, 'sale');
    const compMajor = variant.compareAtPrice
      ? getVariantMajorAmountPerDisplayedUnit(variant, 'compare')
      : NaN;
    const hasCompare =
      comparePriceFormatted != null &&
      variant.compareAtPrice &&
      Number.isFinite(compMajor) &&
      compMajor > saleMajor;
    const taxTtc = esc(cfg.taxLabelTtc || '');
    const taxHt = esc(cfg.taxLabelHt || '');
    const priceSale = esc(cfg.priceSaleLabel || '');
    const priceRegular = esc(cfg.priceRegularLabel || '');
    const taxAttrs = ` data-tax-label-ttc="${taxTtc}" data-tax-label-ht="${taxHt}"`;
    const priceDisp = esc(String(price || ''));
    const compareDisp = esc(String(comparePriceFormatted || ''));
    const variantLabel = esc(String(getVariantDisplayLabel(variant) || ''));
    const variantLabelSpan = variantLabel ? `<span class="price-variant-label"> / ${variantLabel}</span>` : '';
    /* B2B catalog list prices are ex-VAT only; omit .tax-price-label so tax-toggle.js does not rewrite these cards. */
    const showTax = cfg.taxToggleEnabled === true && !isB2BScope();
    const taxSpan = showTax
      ? `<span class="tax-price-label"${taxAttrs}>${taxTtc}</span>`
      : '';
    if (hasCompare) {
      return (
        `<span role="group"><span class="visually-hidden">${priceSale}&nbsp;</span><span class="price">${priceDisp}</span>${variantLabelSpan}${taxSpan}</span>` +
        `<span role="group"><span class="visually-hidden">${priceRegular}&nbsp;</span><span class="compare-at-price">${compareDisp}</span></span>`
      );
    }
    return `<span class="price">${priceDisp}</span>${variantLabelSpan}${taxSpan}`;
};

/** Stock line for B2B cards: sold out, or limited when qty is 1–6 (matches PDP / collection threshold). */
const LIMITED_STOCK_MAX = 6;

/**
 * Build the variant -> stock map for a list of variants. Used to hydrate
 * the card with a `data-variant-stock` attribute the JS later reads.
 * @param {any[]} variants
 * @returns {string}
 */
const b2bBuildVariantStockMapJson = function b2bBuildVariantStockMapJson(variants) {
    /** @type {Record<string, number>} */
    const map = {};
    (variants || []).forEach((v) => {
      if (!v || !v.id) return;
      map[String(v.id)] = b2bGetVariantStock(v);
    });
    return JSON.stringify(map).replace(/'/g, '&#39;');
};

/**
 * @param {any} variant
 * @param {B2BConfig} cfg
 */
const buildCardStockLineHtml = function buildCardStockLineHtml(variant, cfg) {
    const inStock = variant.availableForSale;
    const stock = b2bGetVariantStock(variant);
    const qty = stock >= 0 ? stock : variant.quantityAvailable;
    let text = '';
    if (!inStock) {
      text = (cfg.outOfStockLabel || 'Out of stock').trim();
    } else if (qty != null && typeof qty === 'number' && qty > 0 && qty <= LIMITED_STOCK_MAX) {
      text = (cfg.limitedAvailabilityLabel || 'Limited availability').trim();
    }
    if (!text) return '';
    return `<br aria-hidden="true" /><span class="price-stock-status price-stock-status--below">${esc(text)}</span>`;
};

/** @param {any} product */
const renderCard = function renderCard(product) {
    const cfg = getConfig() || {};
    const edges = product.variants?.edges || [];
    const nodes = edges.map((/** @type {any} */ e) => e.node).filter(Boolean);
    const variant =
      nodes.find((/** @type {any} */ v) => !(v.title || '').toLowerCase().includes('billet')) || nodes[0];
    if (!variant) return '';

    const url = `${cfg.productUrl}?product=${encodeURIComponent(product.handle)}`;
    const img = product.featuredImage;

    const subtitleText = (getProductSubtitleValue(product) || (product.description || '').trim()) || '\u00A0';
    const subtitleHidden = subtitleText === '\u00A0';
    const titleText = (getProductTitleValue(product) || product.title || '').trim();
    const titleWords = titleText.split(/\s+/).filter(Boolean);
    const lastWord = titleWords[titleWords.length - 1];
    const isYear = lastWord?.length === 4 && /^[12]/.test(lastWord);
    let titleHtml = esc(titleText || product.title);
    if (titleWords.length === 2 && !isYear) {
      titleHtml = `${esc(titleWords[0])}<br>${esc(titleWords[1])}`;
    } else if (isYear && titleWords.length >= 3) {
      const wineCount = titleWords.length - 1;
      if (wineCount === 2) {
        titleHtml = `${esc(titleWords[0])} ${esc(titleWords[1])}<br>${esc(lastWord)}`;
      } else if (wineCount >= 4) {
        const line1 = [titleWords[0], titleWords[1]].map(esc).join(' ');
        const line2 = titleWords.slice(2, wineCount).map(esc).join(' ') + ' ' + esc(lastWord);
        titleHtml = `${line1}<br>${line2}`;
      } else {
        const wineName = titleWords.slice(0, wineCount).map(esc).join(' ');
        titleHtml = `${wineName}<br>${esc(lastWord)}`;
      }
    }
    const subtitleIsShort = subtitleText !== '\u00A0' && subtitleText.trim().split(/\s+/).length <= 3;
    const titleIsShort = titleWords.length <= 3;
    const subtitleExpand = subtitleIsShort && titleIsShort;

    const emptyImageUrl = (cfg.emptyProductImageUrl || '').trim();
    const emptyImageSrc = emptyImageUrl
      ? `${emptyImageUrl}${emptyImageUrl.includes('?') ? '&' : '?'}width=400`
      : '';

    const imageHtml = img
      ? `<a class="contents" href="${url}" aria-label="${esc(product.title)}">
           <div class="product-media-container media-fit constrain-height" style="aspect-ratio: 4/5;">
             <img src="${img.url}${img.url.includes('?') ? '&' : '?'}width=400"
                  alt="${esc(img.altText || product.title)}"
                  loading="lazy"
                  width="${img.width || 400}"
                  height="${img.height || 500}"
                  style="object-fit: contain; width: 100%; height: 100%;" />
           </div>
         </a>`
      : `<a class="contents" href="${url}" aria-label="${esc(product.title)}">
           <div class="product-media-container media-fit constrain-height" style="aspect-ratio: 4/5; background-color: var(--color-background);">
             ${
               emptyImageSrc
                 ? `<img src="${esc(emptyImageSrc)}"
                      alt="${esc(product.title)}"
                      loading="lazy"
                      width="400"
                      height="500"
                      style="display: block; object-fit: contain; width: 100%; height: 100%;" />`
                 : `<span class="visually-hidden">${esc(product.title)}</span>`
             }
           </div>
         </a>`;

    const containerId = `b2b-wine-purchase-${String(product.id).replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const purchaseVariants = nodes.filter((/** @type {any} */ v) => !(v.title || '').toLowerCase().includes('billet'));
    const firstListedPurchaseVariant = purchaseVariants[0] || variant;
    // Prefer a variant with effective stock (b2bGetVariantStock: -1 = unlimited, 0 = none) —
    // availableForSale alone can be true on oversell-policy variants whose stock map says 0.
    const firstInStockPurchaseVariant = purchaseVariants.find(
      (/** @type {any} */ v) => v.availableForSale && b2bGetVariantStock(v) !== 0
    );
    const firstAvailablePurchaseVariant =
      firstInStockPurchaseVariant || purchaseVariants.find((/** @type {any} */ v) => v.availableForSale);
    const defaultPurchaseVariant = firstAvailablePurchaseVariant || firstListedPurchaseVariant;
    const hasAnyPurchasableAvailable = purchaseVariants.some((/** @type {any} */ v) => v.availableForSale);
    const fmtLabels = getVariantFormatLabels();
    const showVariantBlock = purchaseVariants.length >= 1;
    const variantSelectorLocked = purchaseVariants.length <= 1;
    const variantOptionsHtml = purchaseVariants
      .map((/** @type {any} */ v) => {
        const selected = v.id === defaultPurchaseVariant.id ? ' selected' : '';
        const disabledOpt = v.availableForSale ? '' : ' disabled';
        const unavailableSuffix = v.availableForSale ? '' : (cfg.variantUnavailableSuffix || ' - Unavailable');
        const rawTitle = (v.title || '').toString();
        const optLabel = formatFormatStyleDisplayLabel(rawTitle, fmtLabels) || rawTitle;
        return `<option value="${esc(v.id)}"${selected}${disabledOpt}>${esc(optLabel + unavailableSuffix)}</option>`;
      })
      .join('');

    const variantStockMapJson = b2bBuildVariantStockMapJson(purchaseVariants);
    const initialVariantStock = b2bGetVariantStock(defaultPurchaseVariant);
    const quantityOptionsHtml = b2bBuildQuantityOptionsHtml(initialVariantStock);

    return `
      <product-card class="product-card" data-product-id="${product.id}">
        <a href="${url}" class="product-card__link" ref="productCardLink">
          <span class="visually-hidden">${esc(product.title)}</span>
        </a>
        <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style color-${esc(cfg.cardColorScheme)}"
             style="--border-width: 1px; --border-style: solid; --border-color: rgb(var(--color-border-rgb) / 1); --border-radius: 12px;
                    --padding-block-start: 12px; --padding-block-end: 12px; --padding-inline-start: 12px; --padding-inline-end: 12px;
                    --product-card-gap: 0px;">
          <div class="card-gallery spacing-style border-style"
               style="--border-style: none; --border-radius: 8px; overflow: hidden;
                      --padding-inline-start: max(20px, calc(var(--spacing-scale) * 24px));
                      --padding-inline-end: max(20px, calc(var(--spacing-scale) * 24px));
                      --gallery-aspect-ratio: 4 / 5;">
            ${imageHtml}
          </div>
          <div class="wine-card-title-container" style="min-height: 7rem; display: flex; flex-direction: column; justify-content: flex-start;">
            <p class="wine-card-main-title"
               style="font-size: 1.125rem; font-weight: 600; text-align: center; margin-bottom: 4px; margin-top: 0;
                      line-height: 1.4; min-height: 2.8em;${titleHtml.includes('<br>') ? '' : ' display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; text-wrap: balance;'}">
              <a class="contents" href="${url}" style="color: inherit; text-decoration: none;">${titleHtml}</a>
            </p>
            <p style="font-size: 0.875rem; color: var(--color-foreground); opacity: 0.7; text-align: center; margin-top: 0; line-height: 1.4;${subtitleExpand ? ' min-height: 2.8em; text-wrap: balance; max-width: 12ch; margin-inline: auto;' : ' display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;'} ${subtitleHidden ? 'visibility: hidden;' : ''}">
              <em>${esc(subtitleText)}</em>
            </p>
          </div>
          <product-price class="text-block text-block--align-center text-center h6 spacing-style"
                         data-product-id="${product.id}"
                         style="--width: 100%; --color: var(--color-foreground); --line-height: normal;
                                --letter-spacing: normal; --text-transform: none;
                                --padding-block-start: 15px; --padding-block-end: 15px;
                                --padding-inline-start: 0; --padding-inline-end: 0;">
            <div ref="priceContainer">
              ${buildCardPriceInnerHtml(defaultPurchaseVariant, cfg)}${buildCardStockLineHtml(defaultPurchaseVariant, cfg)}
            </div>
          </product-price>
          <div class="wine-purchase-container b2b-purchase" data-product-id="${product.id}" data-variant-stock='${variantStockMapJson}'>
            <div class="wine-purchase-options"
              data-purchase-options="${containerId}"
              style="max-height: 0; overflow: hidden; opacity: 0; margin-bottom: 0; pointer-events: none; transition: max-height 0.3s ease, opacity 0.3s ease, margin-bottom 0.3s ease;"
            >
              ${showVariantBlock ? `
              <div style="margin-bottom: 12px;">
                <div class="wine-variant-select-wrapper${variantSelectorLocked ? ' wine-variant-select-wrapper--locked' : ''}" style="position: relative; display: block; width: 100%; border: 1px solid rgb(var(--color-foreground-rgb) / 0.4); background-color: var(--color-background); overflow: hidden;">
                  <select class="wine-variant-select" data-b2b-variant-select="${containerId}"
                    ${variantSelectorLocked ? 'disabled aria-disabled="true"' : ''}
                    style="padding: ${variantSelectorLocked ? '4px 8px' : '4px 24px 4px 8px'}; font-size: 0.75rem; border: none; background-color: transparent; cursor: ${variantSelectorLocked ? 'default' : 'pointer'}; color: var(--color-foreground); appearance: none; width: 100%; outline: none;">
                    ${variantOptionsHtml}
                  </select>
                  ${variantSelectorLocked ? '' : `
                  <div style="position: absolute; right: 3px; top: 50%; transform: translateY(-50%); pointer-events: none; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 18px; height: 100%; gap: -2px;">
                    <svg style="width: 8px; height: 8px; opacity: 0.6; margin-bottom: -2px;" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 0L0 4H8L4 0Z" fill="currentColor"/></svg>
                    <svg style="width: 8px; height: 8px; opacity: 0.6; margin-top: -2px;" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 8L0 4H8L4 8Z" fill="currentColor"/></svg>
                  </div>`}
                </div>
              </div>` : ''}
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding: 0 4px;">
                <span style="font-size: 0.875rem; color: var(--color-foreground);">${esc(cfg.quantityLabel || 'Quantity:')}</span>
                <div class="wine-quantity-select-wrapper" style="position: relative; display: inline-block; min-width: 56px; border: 1px solid rgb(var(--color-foreground-rgb) / 0.4); background-color: var(--color-background); overflow: hidden;">
                  <select class="wine-quantity-select" id="${containerId}-qty" data-b2b-qty-select="${containerId}" style="padding: 4px 24px 4px 8px; font-size: 0.75rem; border: none; background-color: transparent; cursor: pointer; color: var(--color-foreground); appearance: none; width: 100%; outline: none;">
                    ${quantityOptionsHtml}
                  </select>
                  <div style="position: absolute; right: 3px; top: 50%; transform: translateY(-50%); pointer-events: none; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 18px; height: 100%; gap: -2px;">
                    <svg style="width: 8px; height: 8px; opacity: 0.6; margin-bottom: -2px;" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 0L0 4H8L4 0Z" fill="currentColor"/></svg>
                    <svg style="width: 8px; height: 8px; opacity: 0.6; margin-top: -2px;" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 8L0 4H8L4 8Z" fill="currentColor"/></svg>
                  </div>
                </div>
              </div>
            </div>
            <div class="wine-purchase-buttons">
              <a href="${url}" class="button button-secondary wine-purchase-buttons__btn">${esc(cfg.viewLabel)}</a>
              <button type="button"
                      class="button wine-purchase-button wine-purchase-buttons__btn"
                      data-b2b-purchase-button="${containerId}"
                      ${!hasAnyPurchasableAvailable ? 'disabled' : ''}>
                ${hasAnyPurchasableAvailable ? esc(cfg.purchaseLabel) : esc(cfg.soldOutLabel)}
              </button>
              <button type="button"
                      class="button wine-add-to-cart-button wine-purchase-buttons__btn"
                      data-b2b-buy="${defaultPurchaseVariant.id}"
                      data-b2b-add-button="${containerId}"
                      data-b2b-qty-source="${containerId}-qty"
                      style="display: none;"
                      ${!defaultPurchaseVariant.availableForSale ? 'disabled' : ''}>
                ${esc(cfg.purchaseLabel)}
              </button>
            </div>
            <button type="button"
                    class="wine-close-options"
                    data-b2b-close-options="${containerId}"
                    style="max-height: 0; overflow: hidden; opacity: 0; margin-top: 0; padding: 0; width: 100%; font-size: 0.75rem; border: 1px solid rgb(var(--color-foreground-rgb) / 0.4); background-color: transparent; color: var(--color-foreground); cursor: pointer; text-align: center; pointer-events: none; transition: max-height 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease, padding 0.3s ease;">
              ${esc(cfg.closeOptionsLabel || 'Close')}
            </button>
          </div>
        </div>
      </product-card>`;
};

/**
 * @param {HTMLElement} container
 * @param {any[]} products
 * @param {any} [state]
 * @param {any} [opts]
 */
const renderGrid = function renderGrid(container, products, state, opts) {
    const cfg = getConfig() || {};
    if (!container) return;

    const paginationInfo = (opts && opts.paginationInfo) || (state && state.paginationInfo);
    const singlePageMode = paginationInfo && paginationInfo.singlePageMode;

    // paginationOnly: refresh just the pagination nav in place (e.g. after the async total-count
    // resolves) without rebuilding the product cards — rebuilding them recreates every <img> and
    // makes the grid visibly flash. Leaves the existing card <ul> untouched.
    if (opts && opts.paginationOnly) {
      if (!paginationInfo) return;
      const cp = paginationInfo.currentPage || 1;
      const hnp = !!paginationInfo.hasNextPage;
      const hpp = !!paginationInfo.hasPreviousPage;
      const tp = (paginationInfo.totalCount != null && paginationInfo.perPage)
        ? Math.max(1, Math.ceil(paginationInfo.totalCount / paginationInfo.perPage))
        : 0;
      const cpm = tp > 0 ? cp + (hnp ? 1 : 0) : 0;
      const pagHtml = tp > 1 ? renderPagination(tp, cp, cpm) : renderPaginationCursor(hnp, cp, hpp);
      const existingNav = container.querySelector('.collection-pagination');
      if (existingNav) {
        existingNav.outerHTML = pagHtml;
      } else if (pagHtml && container.querySelector('ul')) {
        container.insertAdjacentHTML('beforeend', pagHtml);
      }
      return;
    }

    let list = products;
    let totalPages = 1;
    let currentPage = 1;
    let perPage = 48;
    let useCursorPagination = false;
    let hasNextPage = false;
    let hasPreviousPage = false;

    let clickablePageMax = 0;
    if (singlePageMode && paginationInfo) {
      useCursorPagination = true;
      currentPage = paginationInfo.currentPage || 1;
      hasNextPage = !!paginationInfo.hasNextPage;
      hasPreviousPage = !!paginationInfo.hasPreviousPage;
      totalPages = (paginationInfo.totalCount != null && paginationInfo.perPage) ? Math.max(1, Math.ceil(paginationInfo.totalCount / paginationInfo.perPage)) : 0;
      clickablePageMax = totalPages > 0 ? currentPage + (hasNextPage ? 1 : 0) : 0;
      list = filterProducts(list, state);
      list = sortProducts(list, state.sortBy || 'default');
    } else if (state) {
      list = filterProducts(list, state);
      list = sortProducts(list, state.sortBy || 'default');
      perPage = Math.max(1, state.perPage || 48);
      totalPages = Math.max(1, Math.ceil(list.length / perPage));
      currentPage = Math.max(1, Math.min(state.page || 1, totalPages));
      list = list.slice((currentPage - 1) * perPage, currentPage * perPage);
    }

    if (!list.length) {
      container.innerHTML = `
        <div class="main-collection-grid__empty">
          <h2 class="main-collection-grid__empty-title h2">${esc(cfg.noProductsLabel)}</h2>
        </div>`;
      /* No pagination when empty — nothing to paginate; avoids showing "1" with disabled arrows */
      return;
    }

    const items = list
      .map(
        (/** @type {any} */ p, i) => `
        <li class="product-grid__item product-grid__item--${i}"
            data-product-id="${p.id}"
            data-product-title="${esc(p.title)}"
            data-product-vendor="${esc(p.vendor || '')}"
            data-product-tags="${esc(JSON.stringify((p.tags || []).map((/** @type {any} */ tag) => String(tag || ''))))}">
          ${renderCard(p)}
        </li>`
      )
      .join('');

    const mobileLarge = container && container.getAttribute('data-mobile-card-size') === 'large';
    const gridClasses = 'product-grid product-grid--b2b product-grid--grid' + (mobileLarge ? ' product-grid-mobile--large' : '');
    let html = `
      <ul class="${gridClasses}"
          product-grid-view="default" role="list"
          data-product-card-size="${(container && container.getAttribute('data-product-card-size')) || 'medium'}">
        ${items}
      </ul>`;
    if (useCursorPagination) {
      if (totalPages > 1) {
        html += renderPagination(totalPages, currentPage, clickablePageMax);
      } else {
        html += renderPaginationCursor(hasNextPage, currentPage, hasPreviousPage);
      }
    } else if (state && totalPages > 1) {
      html += renderPagination(totalPages, currentPage);
    }
    container.innerHTML = html;
};

/**
 * Cart data access + low-level mutations: Storefront cart reads (with the
 * poisoned-cart heal for off-channel lines), native /cart.js reads, cart
 * create/update/remove mutations and the bank-transfer draft-order endpoint
 * call. Orchestration (sync, merge, optimistic paint) stays in legacy. Moved
 * verbatim from legacy.js.
 */

const createDraftOrderFromB2BCart = async function createDraftOrderFromB2BCart() {
    const cfg = getConfig() || {};
    if (!isB2BScope()) throw new Error('Draft orders are only enabled for B2B scope.');
    if (!cfg.draftOrderEndpoint) throw new Error('Draft order endpoint is not configured.');

    const cartId = getStoredStorefrontCartId();
    if (!cartId) throw new Error('Cart not found');

    const cart = await fetchStorefrontCart(cartId);
    const lines = (cart?.lines?.edges || []).map((/** @type {any} */ e) => {
      const n = e?.node;
      const v = n?.merchandise;
      return {
        lineId: n?.id || '',
        quantity: n?.quantity || 0,
        variantId: v?.id || '',
        productHandle: v?.product?.handle || '',
        productTitle: v?.product?.title || '',
        variantTitle: v?.title || '',
      };
    }).filter((/** @type {{variantId: string, quantity: number}} */ l) => l.variantId && l.quantity > 0);

    const discountCode = getStoredB2BDiscountCode();

    const res = await fetch(cfg.draftOrderEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        source: 'theme-b2b-storefront',
        scope: cfg.scopeKey || 'b2b',
        cartId,
        discountCode: discountCode || '',
        currency: cart?.cost?.subtotalAmount?.currencyCode || cfg.currency || '',
        lines,
        returnUrl: window.location.href,
      }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      /* ignore */
    }
    if (!res.ok) {
      const msg = data?.error || data?.message || `Draft order request failed (${res.status}).`;
      throw new Error(msg);
    }
    return data || {};
};

const getAjaxCartJsonUrl = function getAjaxCartJsonUrl() {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) {
      const base = String(Theme.routes.cart_url).replace(/\/$/, '');
      return `${base}.js`;
    }
    return '/cart.js';
};

/** @param {Array<string | number>} productIds */
const fetchStorefrontProductTagsMap = async function fetchStorefrontProductTagsMap(productIds) {
    const uniq = [
      ...new Set((productIds || []).map((id) => String(id || '').replace(/\D/g, '')).filter(Boolean)),
    ];
    const map = new Map();
    if (!uniq.length) return map;
    const ids = uniq.map((id) => `gid://shopify/Product/${id}`);
    const query = `
      query CartProductTags($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            tags
          }
        }
      }`;
    const data = await storefrontFetch(query, { ids });
    const nodes = data?.nodes || [];
    for (const n of nodes) {
      if (!n?.id) continue;
      const m = String(n.id).match(/Product\/(\d+)/);
      const numeric = m ? m[1] : '';
      if (!numeric) continue;
      const tags = (n.tags || []).map((/** @type {any} */ t) => String(t).toLowerCase());
      map.set(numeric, tags);
    }
    return map;
};

const fetchAjaxCartJson = async function fetchAjaxCartJson() {
    const res = await fetch(getAjaxCartJsonUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return res.json();
};

/** @param {Array<{ merchandiseId: string, quantity: number, sellingPlanId?: string }>} lineInputs */
const createStorefrontCartWithLines = async function createStorefrontCartWithLines(lineInputs) {
    if (!lineInputs?.length) throw new Error('No lines');
    const mutation = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart { id checkoutUrl totalQuantity }
          userErrors { field message }
        }
      }`;
    const variables = { input: { lines: lineInputs } };
    const data = await storefrontFetch(mutation, variables);
    const errors = data.cartCreate?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const cart = data.cartCreate?.cart;
    if (!cart?.id) throw new Error('Could not create cart');
    setStoredStorefrontCartId(cart.id);
    return cart;
};

/**
 * Variant gids whose product is NOT readable on the Storefront/Headless channel. Adding such a
 * variant to the Storefront cart poisons every later read (`Cannot return null for non-nullable
 * field ProductVariant.product`). `nodes(ids)` keeps the list shape and returns a null element
 * for an off-channel variant (the non-null `product` violation nulls only that nullable element),
 * so a partial fetch reveals exactly which ids are bad.
 * @param {string[]} variantGids
 * @returns {Promise<Set<string>>}
 */
const findUnavailableStorefrontVariantGids = async function findUnavailableStorefrontVariantGids(variantGids) {
    const unavailable = new Set();
    const ids = [...new Set((variantGids || []).filter(Boolean))];
    if (!ids.length) return unavailable;
    let result;
    try {
      result = await storefrontFetchPartial(VARIANT_PRODUCT_AVAIL_QUERY, { ids });
    } catch (_) {
      return unavailable; // network failure: do not assume anything is unavailable
    }
    const nodes = result?.data?.nodes;
    if (!Array.isArray(nodes)) return unavailable;
    for (let i = 0; i < ids.length; i++) {
      const node = nodes[i];
      if (!node || !node.product || !node.product.id) unavailable.add(ids[i]);
    }
    return unavailable;
};

/**
 * @param {string} cartId
 * @param {string[]} lineIds
 */
const removeStorefrontCartLinesByIds = async function removeStorefrontCartLinesByIds(cartId, lineIds) {
    const ids = [...new Set((lineIds || []).filter(Boolean))];
    if (!cartId || !ids.length) return;
    /* Response selects only `cart { id }` — never re-queries `product`, so it cannot itself poison. */
    const mutation = `
      mutation b2bCartRemoveLines($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { id }
          userErrors { field message }
        }
      }`;
    const data = await storefrontFetch(mutation, { cartId, lineIds: ids });
    const errors = data?.cartLinesRemove?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const out = data?.cartLinesRemove?.cart;
    if (out?.id) setStoredStorefrontCartId(out.id);
};

/**
 * Self-heal a Storefront cart poisoned by an off-channel line: identify the unreadable line(s)
 * via a `product`-free query + availability probe, remove them, and return the cleaned cart so
 * the drawer works again. Returns `undefined` when it cannot pin a culprit (caller rethrows the
 * original error); returns an empty/`null` cart when nothing readable remains.
 * @param {string} cartId
 * @returns {Promise<any | null | undefined>}
 */
const healPoisonedStorefrontCart = async function healPoisonedStorefrontCart(cartId) {
    let safe;
    try {
      safe = await storefrontFetchPartial(SAFE_CART_LINES_QUERY, { cartId });
    } catch (_) {
      return undefined;
    }
    const edges = safe?.data?.cart?.lines?.edges || [];
    if (!edges.length) return null;
    /** @type {Map<string, string[]>} */
    const lineIdsByVariant = new Map();
    const variantGids = [];
    const badLineIds = [];
    for (const e of edges) {
      const lineId = e?.node?.id;
      if (!lineId) continue;
      const vId = e?.node?.merchandise?.id;
      if (!vId) {
        badLineIds.push(lineId); // merchandise with no resolvable variant id — unreadable
        continue;
      }
      variantGids.push(vId);
      if (!lineIdsByVariant.has(vId)) lineIdsByVariant.set(vId, []);
      (lineIdsByVariant.get(vId) || []).push(lineId);
    }
    const unavailable = await findUnavailableStorefrontVariantGids(variantGids);
    for (const vId of unavailable) {
      for (const lineId of lineIdsByVariant.get(vId) || []) badLineIds.push(lineId);
    }
    b2bCartRoutingLog('heal_poisoned_storefront_cart', {
      cartIdTail: String(cartId).slice(-18),
      totalLines: edges.length,
      unavailableVariants: unavailable.size,
      removedLineCount: badLineIds.length,
    });
    if (!badLineIds.length) return undefined; // could not identify the offending line(s)
    try {
      await removeStorefrontCartLinesByIds(cartId, badLineIds);
    } catch (err) {
      console.warn('B2B cart: could not remove off-channel line(s) while healing cart:', err);
      return undefined;
    }
    try {
      const data = await storefrontFetch(CART_QUERY, { cartId });
      return data?.cart || null;
    } catch (_) {
      return null; // still unreadable after removal — surface as empty so the UI recovers
    }
};

/** @param {string | null | undefined} cartId */
const fetchStorefrontCart = async function fetchStorefrontCart(cartId) {
    if (!cartId) return null;
    try {
      const data = await storefrontFetch(CART_QUERY, { cartId });
      return data?.cart || null;
    } catch (err) {
      const msg = err && err.message ? String(err.message) : '';
      /* An off-channel line nulls the non-nullable `ProductVariant.product` and breaks the whole
       * cart read. Drop the offending line(s) so the drawer keeps working. */
      if (/ProductVariant\.product/i.test(msg) || /non-nullable field/i.test(msg)) {
        const healed = await healPoisonedStorefrontCart(cartId);
        if (healed !== undefined) return healed;
      }
      throw err;
    }
};

/**
 * @param {string} variantId
 * @param {number} quantity
 */
const createStorefrontCartWithLine = async function createStorefrontCartWithLine(variantId, quantity) {
    const mutation = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart { id checkoutUrl totalQuantity }
          userErrors { field message }
        }
      }`;
    const variables = {
      input: {
        lines: [{ merchandiseId: variantId, quantity }],
      },
    };
    const data = await storefrontFetch(mutation, variables);
    const errors = data.cartCreate?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const cart = data.cartCreate?.cart;
    if (!cart?.id) throw new Error('Could not create cart');
    setStoredStorefrontCartId(cart.id);
    return cart;
};

/**
 * @param {string} lineId
 * @param {number} quantity
 */
const updateStorefrontCartLine = async function updateStorefrontCartLine(lineId, quantity) {
    const cartId = getStoredStorefrontCartId();
    if (!cartId || !lineId) throw new Error('Cart not found');
    const mutation = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { id checkoutUrl totalQuantity }
          userErrors { field message }
        }
      }`;
    const variables = {
      cartId,
      lines: [{ id: lineId, quantity }],
    };
    const data = await storefrontFetch(mutation, variables);
    const errors = data.cartLinesUpdate?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const cart = data.cartLinesUpdate?.cart;
    if (!cart?.id) throw new Error('Could not update cart line');
    setStoredStorefrontCartId(cart.id);
    return cart;
};

/**
 * Removes every line from the B2B Storefront cart (after order request is ready to send).
 */
/** @param {any} cart */
const removeAllLinesFromB2BStorefrontCart = async function removeAllLinesFromB2BStorefrontCart(cart) {
    const cartId = getStoredStorefrontCartId();
    if (!cartId) return;
    const c = cart || (await fetchStorefrontCart(cartId));
    const edges = c?.lines?.edges || [];
    const lineIds = edges.map((/** @type {any} */ e) => e?.node?.id).filter(Boolean);
    if (!lineIds.length) {
      setStoredB2BDiscountCode('');
      return;
    }
    const mutation = `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { id totalQuantity }
          userErrors { field message }
        }
      }`;
    const data = await storefrontFetch(mutation, { cartId, lineIds });
    const errors = data.cartLinesRemove?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const outCart = data.cartLinesRemove?.cart;
    if (outCart?.id) setStoredStorefrontCartId(outCart.id);
    setStoredB2BDiscountCode('');
};

/**
 * Primeurs cart guard: Primeurs and non-Primeurs wines must not mix in one
 * order (separate logistics). Detects the composition of BOTH carts (native
 * /cart.js + B2B/VIP Storefront cart), and on conflict shows the clear-cart
 * dialog before allowing the add. Moved verbatim from legacy.js.
 */

const PRIMEURS_GUARD_TAG_PATTERN = /\bprimeur(?:s)?\b/;

const PRIMEURS_COLLECTION_HANDLE = 'categorie-primeurs';

/**
 * @param {unknown[]} tags
 * @returns {boolean}
 */
const hasPrimeursTag = function hasPrimeursTag(tags) {
    if (!Array.isArray(tags)) return false;
    return tags.some((tag) => {
      const lowered = String(tag || '').trim().toLowerCase();
      if (!lowered) return false;
      return (
        lowered === 'en primeur' ||
        lowered === 'en-primeur' ||
        lowered === 'primeur' ||
        lowered === 'primeurs' ||
        PRIMEURS_GUARD_TAG_PATTERN.test(lowered)
      );
    });
};

/**
 * @param {unknown[]} handles
 * @returns {boolean}
 */
const hasPrimeursCollection = function hasPrimeursCollection(handles) {
    if (!Array.isArray(handles)) return false;
    return handles.some((handle) => String(handle || '').trim().toLowerCase() === PRIMEURS_COLLECTION_HANDLE);
};

/**
 * @param {string} label
 * @param {unknown} payload
 */
const primeursGuardDebug = function primeursGuardDebug(label, payload) {
    b2bCartRoutingLog(`primeurs_guard_${label}`, payload);
};

/**
 * @param {any} cart
 * @returns {{ hasPrimeurs: boolean, hasRegular: boolean }}
 */
const getStorefrontCartPrimeursComposition = function getStorefrontCartPrimeursComposition(cart) {
    const edges = cart?.lines?.edges || [];
    let hasPrimeurs = false;
    let hasRegular = false;
    const cfg = getConfig() || {};
    for (let i = 0; i < edges.length; i++) {
      const node = edges[i]?.node;
      if (isAnyFeeLine(node, cfg)) continue;
      const tags = node?.merchandise?.product?.tags;
      const loweredTags = Array.isArray(tags) ? tags.map((t) => String(t || '').toLowerCase()) : [];
      const collections = node?.merchandise?.product?.collections?.edges || [];
      const collectionHandles = collections
        .map((/** @type {any} */ edge) => edge?.node?.handle)
        .map((/** @type {any} */ handle) => String(handle || '').trim().toLowerCase())
        .filter(Boolean);
      if (hasPrimeursTag(loweredTags) || hasPrimeursCollection(collectionHandles)) hasPrimeurs = true;
      else hasRegular = true;
      if (hasPrimeurs && hasRegular) break;
    }
    return { hasPrimeurs, hasRegular };
};

/**
 * @param {any} ajaxCart
 * @returns {Promise<{ hasPrimeurs: boolean, hasRegular: boolean }>}
 */
const getAjaxCartPrimeursComposition = async function getAjaxCartPrimeursComposition(ajaxCart) {
    const items = Array.isArray(ajaxCart?.items) ? ajaxCart.items : [];
    if (!items.length) {
      primeursGuardDebug('ajax_cart_empty', { itemCount: 0 });
      return { hasPrimeurs: false, hasRegular: false };
    }
    const skipStorefrontTagFetch = items.every((/** @type {any} */ item) => getAjaxLineProductTagsLower(item).length > 0);
    /** @type {Map<string, string[]>} */
    let tagMap = new Map();
    if (!skipStorefrontTagFetch) {
      try {
        tagMap = await fetchStorefrontProductTagsMap(items.map((/** @type {any} */ item) => item.product_id));
      } catch (_) {
        tagMap = new Map();
      }
    }
    primeursGuardDebug('ajax_cart_scan_start', {
      itemCount: items.length,
      skipStorefrontTagFetch,
      tagMapSize: tagMap.size,
      sample: items.slice(0, 8).map((/** @type {any} */ item) => ({
        product_id: item?.product_id,
        variant_id: item?.variant_id,
        quantity: item?.quantity,
        tags: getAjaxLineProductTagsLower(item),
      })),
    });
    let hasPrimeurs = false;
    let hasRegular = false;
    const decisions = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const tags = tagsForAjaxLineItem(item, tagMap);
      const isPrimeurs = hasPrimeursTag(tags);
      decisions.push({
        product_id: item?.product_id,
        variant_id: item?.variant_id,
        quantity: item?.quantity,
        tags,
        isPrimeurs,
      });
      if (isPrimeurs) hasPrimeurs = true;
      else hasRegular = true;
      if (hasPrimeurs && hasRegular) break;
    }
    primeursGuardDebug('ajax_cart_scan_result', {
      hasPrimeurs,
      hasRegular,
      decisions: decisions.slice(0, 12),
    });
    return { hasPrimeurs, hasRegular };
};

/**
 * @returns {Promise<{ hasPrimeurs: boolean, hasRegular: boolean }>}
 */
const getCombinedPrimeursComposition = async function getCombinedPrimeursComposition() {
    let hasPrimeurs = false;
    let hasRegular = false;
    const cartId = getStoredStorefrontCartId();
    if (cartId) {
      try {
        const sfCart = await fetchStorefrontCart(cartId);
        const sfComp = getStorefrontCartPrimeursComposition(sfCart);
        const sfEdges = sfCart?.lines?.edges || [];
        primeursGuardDebug('storefront_cart_scan_result', {
          cartIdTail: String(cartId).slice(-24),
          lineCount: sfEdges.length,
          hasPrimeurs: sfComp.hasPrimeurs,
          hasRegular: sfComp.hasRegular,
          sample: sfEdges.slice(0, 12).map((/** @type {any} */ edge) => {
            const node = edge?.node;
            const product = node?.merchandise?.product;
            const tags = Array.isArray(product?.tags) ? product.tags.map((/** @type {any} */ t) => String(t).toLowerCase()) : [];
            return {
              lineId: node?.id || '',
              quantity: node?.quantity || 0,
              productTitle: product?.title || '',
              productHandle: product?.handle || '',
              tags,
              collectionHandles:
                ((product?.collections?.edges || [])
                  .map((/** @type {any} */ edge) => edge?.node?.handle)
                  .map((/** @type {any} */ handle) => String(handle || '').trim().toLowerCase())
                  .filter(Boolean)),
              isPrimeurs:
                hasPrimeursTag(tags) ||
                hasPrimeursCollection(
                  ((product?.collections?.edges || [])
                    .map((/** @type {any} */ edge) => edge?.node?.handle)
                    .map((/** @type {any} */ handle) => String(handle || '').trim().toLowerCase())
                    .filter(Boolean))
                ),
            };
          }),
        });
        hasPrimeurs = hasPrimeurs || sfComp.hasPrimeurs;
        hasRegular = hasRegular || sfComp.hasRegular;
      } catch (_) {
        primeursGuardDebug('storefront_cart_scan_failed', {
          cartIdTail: String(cartId).slice(-24),
        });
        /* non-critical */
      }
    } else {
      primeursGuardDebug('storefront_cart_missing', {});
    }
    try {
      const ajaxCart = await fetchAjaxCartJson();
      const ajaxComp = await getAjaxCartPrimeursComposition(ajaxCart);
      hasPrimeurs = hasPrimeurs || ajaxComp.hasPrimeurs;
      hasRegular = hasRegular || ajaxComp.hasRegular;
    } catch (_) {
      primeursGuardDebug('ajax_cart_scan_failed', {});
      /* non-critical */
    }
    primeursGuardDebug('combined_composition', { hasPrimeurs, hasRegular });
    return { hasPrimeurs, hasRegular };
};

/**
 * @param {string} variantId
 * @returns {Promise<boolean | null>}
 */
const getVariantPrimeursStatus = async function getVariantPrimeursStatus(variantId) {
    const id = String(variantId || '').trim();
    if (!id) return null;
    const query = `
      query PrimeursVariant($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            product { tags }
          }
        }
      }`;
    try {
      const data = await storefrontFetch(query, { id });
      const tags = data?.node?.product?.tags;
      const lowered = Array.isArray(tags) ? tags.map((/** @type {any} */ t) => String(t || '').toLowerCase()) : [];
      if (!lowered.length) {
        primeursGuardDebug('variant_lookup_empty_tags', { variantId: id });
        return null;
      }
      const isPrimeurs = hasPrimeursTag(lowered);
      primeursGuardDebug('variant_lookup_result', {
        variantId: id,
        tags: lowered,
        isPrimeurs,
      });
      return isPrimeurs;
    } catch (_) {
      primeursGuardDebug('variant_lookup_failed', { variantId: id });
      return null;
    }
};

/**
 * @returns {Promise<boolean>}
 */
const showPrimeursClearCartModal = function showPrimeursClearCartModal() {
    return new Promise((resolve) => {
      const dialog = document.createElement('dialog');
      dialog.className = 'primeurs-cart-guard-dialog';
      dialog.style.border = '1px solid rgb(var(--color-foreground-rgb) / 0.25)';
      dialog.style.maxWidth = '30rem';
      const themeObj = typeof Theme !== 'undefined' ? Theme : (typeof window !== 'undefined' ? window.Theme : null);
      const title = themeObj?.translations?.primeurs_guard_title || 'En Primeur';
      const message =
        themeObj?.translations?.primeurs_guard_message ||
        'En Primeur orders are processed separately from in-stock wines and cannot be combined in the same cart.\n\nClear your cart to continue, or save this wine to your wishlist for later.';
      const cancelLabel = themeObj?.translations?.primeurs_guard_cancel || 'Cancel';
      const clearLabel = themeObj?.translations?.primeurs_guard_clear || 'Clear cart';
      dialog.innerHTML = `
        <form method="dialog" class="primeurs-cart-guard-dialog__content">
          <h3>${title}</h3>
          <p style="white-space: pre-line">${message}</p>
          <div class="primeurs-cart-guard-dialog__actions" style="display:flex; align-items:center; gap:0.75rem; flex-wrap:nowrap;">
            <button type="button" class="button button-secondary" data-action="cancel">${cancelLabel}</button>
            <button type="button" class="button button-primary" data-action="clear">${clearLabel}</button>
          </div>
        </form>
      `;

      const cleanup = () => {
        dialog.close();
        dialog.remove();
      };

      dialog.querySelector('[data-action="clear"]')?.addEventListener('click', () => {
        cleanup();
        resolve(true);
      });

      dialog.querySelector('[data-action="cancel"]')?.addEventListener('click', () => {
        cleanup();
        resolve(false);
      });

      dialog.addEventListener(
        'cancel',
        () => {
          cleanup();
          resolve(false);
        },
        { once: true }
      );

      document.body.appendChild(dialog);
      dialog.showModal();
    });
};

/**
 * @param {boolean} incomingIsPrimeurs
 * @returns {Promise<boolean>}
 */
const guardPrimeursCartConflict = async function guardPrimeursCartConflict(incomingIsPrimeurs) {
    const composition = await getCombinedPrimeursComposition();
    const cartAlreadyMixed = composition.hasPrimeurs && composition.hasRegular;
    const incomingConflicts =
      (incomingIsPrimeurs && composition.hasRegular) || (!incomingIsPrimeurs && composition.hasPrimeurs);
    primeursGuardDebug('decision', {
      incomingIsPrimeurs,
      hasPrimeurs: composition.hasPrimeurs,
      hasRegular: composition.hasRegular,
      cartAlreadyMixed,
      incomingConflicts,
    });
    if (!cartAlreadyMixed && !incomingConflicts) return true;
    const shouldClear = await showPrimeursClearCartModal();
    if (!shouldClear) return false;
    const cartId = getStoredStorefrontCartId();
    if (cartId) {
      const cart = await fetchStorefrontCart(cartId);
      await removeAllLinesFromB2BStorefrontCart(cart);
    }
    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch (_) {
      /* non-critical */
    }
    return true;
};

/** Heuristic: does this Ajax cart line look like an en-primeur product (by product tag or cart property)? */
/** @param {any} item Ajax `/cart.js` line */
const ajaxLineLooksPrimeurs = function ajaxLineLooksPrimeurs(item) {
    if (hasPrimeursTag(getAjaxLineProductTagsLower(item))) return true;
    const props = item && item.properties;
    if (props && typeof props === 'object') {
      for (const k of Object.keys(props)) {
        if (!/primeur/i.test(k)) continue;
        const v = props[k];
        if (v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true') return true;
      }
    }
    return false;
};

/**
 * Filters sidebar UI: renders facets (style, vintage, format, region, producer,
 * price) into the sidebar container, wires the change/input listeners once per
 * container (debounced price typing), and renders the active-filter pills.
 * Producer facet counts are cached per filter-context key. Moved verbatim from
 * legacy.js.
 */

/** Translate region names to French when locale is fr. */
/** @param {string} name */
const translateRegionName = function translateRegionName(name) {
    if (!name || typeof name !== 'string') return name || '';
    const cfg = getConfig();
    const locale = (cfg && cfg.locale) ? String(cfg.locale).toLowerCase() : (document.documentElement.lang || 'fr').toLowerCase();
    if (!locale.startsWith('fr')) return name;
    const trimmed = name.trim();
    const normalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    /** @type {Record<string, string>} */
    const map = { Burgundy: 'Bourgogne', Rhone: 'Rhône' };
    return map[trimmed] || map[normalized] || name;
};

/** When producer filter yields 0 API hits, getFacets([]) clears the list — cache options for same region/vintage/… context. */
/** @type {{ key: string, list: Array<{ value: string, count: number }> }} */
let b2bProducerFacetCache = { key: '', list: [] };

/** @param {any} state */
const producerFacetContextKey = function producerFacetContextKey(state) {
    const st = state || {};
    return JSON.stringify({
      region: st.region,
      vintage: st.vintage,
      searchQuery: st.searchQuery,
      style: st.style,
      format: st.format,
      priceMin: st.priceMin,
      priceMax: st.priceMax,
    });
};

/** Render vintage filter: only years present in the current product set (newest first). */
/**
 * @param {Record<string, number>} vintageCountByYear
 * @param {string[] | string | null | undefined} selectedYears
 * @param {string} idSuffix
 */
const renderVintageListItems = function renderVintageListItems(vintageCountByYear, selectedYears, idSuffix) {
    const suffix = idSuffix || '';
    const selected = Array.isArray(selectedYears) ? selectedYears : (selectedYears ? [String(selectedYears)] : []);
    const set = new Set(selected);
    const yearSet = new Set(Object.keys(vintageCountByYear || {}));
    for (const yearStr of selected) {
      if (yearStr) yearSet.add(String(yearStr));
    }
    const years = Array.from(yearSet)
      .map((y) => Number(y))
      .filter((y) => Number.isFinite(y) && y > 0)
      .sort((a, b) => b - a);
    let html = '';
    for (const y of years) {
      const yearStr = String(y);
      const checked = set.has(yearStr);
      const id = `b2b-filter-vintage-${y}${suffix}`;
      html += `<li class="facets__inputs-list-item" data-skip-node-update="true">
        <div class="checkbox" data-b2b-filter="vintage" data-b2b-value="${yearStr}">
          <input type="checkbox" class="checkbox__input" name="b2b-vintage" value="${yearStr}" id="${id}" ${checked ? 'checked' : ''} data-b2b-filter-input>
          <label class="checkbox__label" for="${id}" role="checkbox">
            ${ICON_CHECKMARK}
            <span class="checkbox__label-text">${yearStr}</span>
          </label>
        </div>
      </li>`;
    }
    return html;
};

/**
 * @param {any[]} products
 * @param {any} state
 * @param {HTMLElement} container
 * @param {(next: Record<string, any>) => void} onStateChange
 * @param {any} options
 */
const buildB2BFilters = function buildB2BFilters(products, state, container, onStateChange, options) {
    if (!container) return;
    const filterIdSuffix = (options && options.filterIdSuffix) || '';
    const facets = getFacets(products);
    const selectedVintages = state.vintage;
    const selectedArray = Array.isArray(selectedVintages) ? selectedVintages : (selectedVintages ? [String(selectedVintages)] : []);
    const selectedRegion = Array.isArray(state.region) ? state.region : (state.region ? [String(state.region)] : []);
    const selectedProducer = Array.isArray(state.producer) ? state.producer : (state.producer ? [String(state.producer)] : []);
    const regionsTopLevel = (options && options.regions && options.regions.topLevelList) ? options.regions.topLevelList : [];
    const pfKey = producerFacetContextKey(state);
    /** @type {any[]} */
    let producersList = facets.producers || [];
    /* Cache full producer list only when no producer filter — selecting one producer would otherwise replace cache with a single row. */
    if (producersList.length > 0 && selectedProducer.length === 0) {
      b2bProducerFacetCache = { key: pfKey, list: producersList.map((/** @type {any} */ x) => ({ value: String(x.value || ''), count: Number(x.count) || 0 })) };
    }
    /** @type {Record<string, number>} */
    const countByName = {};
    producersList.forEach((/** @type {{value: string, count: number}} */ item) => {
      const n = (item.value || '').toString().trim();
      if (n) countByName[normalizeProducerFilterValue(n)] = item.count || 0;
    });
    if (selectedProducer.length > 0 && b2bProducerFacetCache.key === pfKey && b2bProducerFacetCache.list.length > 0) {
      producersList = b2bProducerFacetCache.list.map((item) => {
        const n = (item.value || '').toString().trim();
        const nk = normalizeProducerFilterValue(n);
        return {
          value: n,
          count: Object.prototype.hasOwnProperty.call(countByName, nk) ? countByName[nk] : item.count || 0,
        };
      });
    }
    const ulVintage = /** @type {any} */ (container.querySelector('[data-b2b-vintage-list]') || container.querySelector('#b2b-facet-vintage'));
    if (ulVintage) ulVintage.innerHTML = renderVintageListItems(facets.vintageCountByYear || {}, selectedArray, filterIdSuffix);

    const ulProducer = /** @type {any} */ (container.querySelector('[data-b2b-producer-list]'));
    if (ulProducer && producersList.length > 0) {
      ulProducer.innerHTML = producersList
        .map((/** @type {any} */ item, /** @type {number} */ idx) => {
          const name = (item.value || '').toString().trim();
          const count = item.count || 0;
          const checked = selectedProducer.some((/** @type {any} */ p) => normalizeProducerFilterValue(p) === normalizeProducerFilterValue(name));
          const inputId = 'b2b-filter-producer-' + idx + '-' + name.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 40) + filterIdSuffix;
          return `
      <li class="facets__inputs-list-item" data-skip-node-update="true">
        <div class="checkbox" data-b2b-filter="producer" data-b2b-value="${esc(name)}">
          <input type="checkbox" class="checkbox__input" name="b2b-producer" value="${esc(name)}" id="${esc(inputId)}" data-b2b-filter-input data-b2b-value="${esc(name)}" ${checked ? 'checked' : ''}>
          <label class="checkbox__label" for="${esc(inputId)}" role="checkbox">
            ${ICON_CHECKMARK}
            <span class="checkbox__label-text">${esc(name)}</span>
          </label>
        </div>
      </li>`;
        })
        .join('');
    } else if (ulProducer) {
      ulProducer.innerHTML = '';
    }

    const ulRegion = /** @type {any} */ (container.querySelector('[data-b2b-region-list]'));
    if (ulRegion && regionsTopLevel.length > 0) {
      ulRegion.innerHTML = regionsTopLevel
        .map(
          (/** @type {any} */ item, /** @type {number} */ idx) => {
            const id = (item.id || '').toString().trim();
            const rawName = (item.name || 'Region ' + id).trim();
            const name = translateRegionName(rawName);
            const checked = selectedRegion.indexOf(id) !== -1;
            const inputId = 'b2b-filter-region-' + (idx + 1) + '-' + id.replace(/\s/g, '_') + filterIdSuffix;
            return `
      <li class="facets__inputs-list-item" data-skip-node-update="true">
        <div class="checkbox" data-b2b-filter="region" data-b2b-value="${esc(id)}">
          <input type="checkbox" class="checkbox__input" name="b2b-region" value="${esc(id)}" id="${esc(inputId)}" data-b2b-filter-input data-b2b-value="${esc(id)}" ${checked ? 'checked' : ''}>
          <label class="checkbox__label" for="${esc(inputId)}" role="checkbox">
            ${ICON_CHECKMARK}
            <span class="checkbox__label-text">${esc(name)}</span>
          </label>
        </div>
      </li>`;
          }
        )
        .join('');
    } else if (ulRegion) {
      ulRegion.innerHTML = '';
    }

    const statusVintage = /** @type {any} */ (container.querySelector('[data-b2b-status-vintage]') || container.querySelector('#b2b-status-vintage'));
    if (statusVintage) {
      if (selectedArray.length === 0) {
        statusVintage.textContent = '';
        statusVintage.style.display = 'none';
      } else {
        statusVintage.textContent = selectedArray.length > 2 ? selectedArray.length : selectedArray.join(', ');
        statusVintage.style.display = '';
      }
    }

    const statusRegion = /** @type {any} */ (container.querySelector('[data-b2b-status-region]'));
    const regionIdToNameForStatus = (options && options.regions && options.regions.topLevelList)
      ? options.regions.topLevelList.reduce((/** @type {Record<string, string>} */ acc, /** @type {any} */ r) => { acc[r.id] = translateRegionName(r.name || r.id); return acc; }, /** @type {Record<string, string>} */ ({}))
      : /** @type {Record<string, string>} */ ({});
    if (statusRegion) {
      if (selectedRegion.length === 0) {
        statusRegion.textContent = '';
        statusRegion.style.display = 'none';
      } else {
          const labels = selectedRegion.map((/** @type {any} */ id) => regionIdToNameForStatus[id] || id);
          statusRegion.textContent = labels.length > 2 ? String(labels.length) : labels.join(', ');
        statusRegion.style.display = '';
      }
    }

    const statusProducer = /** @type {any} */ (container.querySelector('[data-b2b-status-producer]'));
    if (statusProducer) {
      if (selectedProducer.length === 0) {
        statusProducer.textContent = '';
        statusProducer.style.display = 'none';
      } else {
        statusProducer.textContent = selectedProducer.length > 2 ? selectedProducer.length : selectedProducer.join(', ');
        statusProducer.style.display = '';
      }
    }

    const selectedStyle = Array.isArray(state.style) ? state.style : (state.style ? [String(state.style)] : []);
    const clearWrap = /** @type {any} */ (container.querySelector('[data-b2b-clear-wrap]') || container.querySelector('#b2b-filters-clear-wrap'));
    const cfg = getConfig();
    const colourFilterLabel = (
      clearWrap?.dataset?.b2bColourFilterLabel ||
      cfg?.colourFilterLabel ||
      'Colour'
    ).trim();
    container.querySelectorAll('[id^="Facet-Details-b2b-style"] .facets__summary > .facets__label').forEach((/** @type {any} */ el) => {
      el.textContent = colourFilterLabel;
    });
    container.querySelectorAll('[data-b2b-style-list]').forEach((/** @type {any} */ list) => {
      list.setAttribute('name', colourFilterLabel);
    });
    const styleInputs = container.querySelectorAll('input[name="b2b-style"][data-b2b-filter-input]');
    styleInputs.forEach((/** @type {any} */ input) => {
      const value = (input.dataset.b2bValue || input.value || '').trim();
      if (value) input.checked = selectedStyle.includes(value);
    });
    const statusStyle = /** @type {any} */ (container.querySelector('[data-b2b-status-style]'));
    if (statusStyle) {
      if (selectedStyle.length === 0) {
        statusStyle.textContent = '';
        statusStyle.style.display = 'none';
      } else {
        statusStyle.textContent = selectedStyle.length > 2 ? selectedStyle.length : selectedStyle.join(', ');
        statusStyle.style.display = '';
      }
    }

    const selectedFormat = Array.isArray(state.format) ? state.format : (state.format ? [String(state.format)] : []);
    const formatInputs = container.querySelectorAll('input[name="b2b-format"][data-b2b-filter-input]');
    formatInputs.forEach((/** @type {any} */ input) => {
      const value = (input.dataset.b2bValue || input.value || '').trim();
      if (value) input.checked = selectedFormat.includes(value);
    });
    const statusFormat = /** @type {any} */ (container.querySelector('[data-b2b-status-format]'));
    if (statusFormat) {
      if (selectedFormat.length === 0) {
        statusFormat.textContent = '';
        statusFormat.style.display = 'none';
      } else {
        statusFormat.textContent = selectedFormat.length > 2 ? selectedFormat.length : selectedFormat.join(', ');
        statusFormat.style.display = '';
      }
    }

    /* Price slider bounds + “highest price” line: only variants available for sale (matches sellable inventory). */
    const productsInStock = products.filter(isProductInStock);
    const priceRange = getPriceRange(productsInStock);
    const priceMin = state.priceMin != null && state.priceMin !== '' ? String(state.priceMin) : '';
    const priceMax = state.priceMax != null && state.priceMax !== '' ? String(state.priceMax) : '';
    const priceMinInput = /** @type {any} */ (container.querySelector('input[data-b2b-price-type="min"]'));
    const priceMaxInput = /** @type {any} */ (container.querySelector('input[data-b2b-price-type="max"]'));
    if (priceMinInput) {
      priceMinInput.value = priceMin;
      priceMinInput.placeholder = '';
    }
    if (priceMaxInput) {
      priceMaxInput.value = priceMax;
      priceMaxInput.placeholder = '';
    }
    const statusPrice = /** @type {any} */ (container.querySelector('[data-b2b-status-price]'));
    if (statusPrice) {
      if (priceMin === '' && priceMax === '') {
        statusPrice.textContent = '';
        statusPrice.style.display = 'none';
      } else {
        const currencyCode =
          (productsInStock[0] || products[0])?.variants?.edges?.[0]?.node?.price?.currencyCode || 'EUR';
        const minStr = priceMin !== '' ? formatMoney(parseFloat(priceMin), currencyCode) : '0';
        const maxStr = priceMax !== '' ? formatMoney(parseFloat(priceMax), currencyCode) : formatMoney(priceRange.max, currencyCode);
        statusPrice.textContent = `${minStr} – ${maxStr}`;
        statusPrice.style.display = '';
      }
    }

    const hasPrice = priceMin !== '' || priceMax !== '';
    const hasActive = selectedArray.length > 0 || selectedStyle.length > 0 || selectedFormat.length > 0 || selectedRegion.length > 0 || selectedProducer.length > 0 || hasPrice;
    if (clearWrap) {
      clearWrap.style.display = hasActive ? 'flex' : 'none';
      const pillsContainer = clearWrap.querySelector('[data-b2b-pills]');
      const iconEl = clearWrap.querySelector('[data-b2b-pill-close-icon]');
      const iconHtml = iconEl ? iconEl.innerHTML : '';
      /** @type {Record<string, string>} */
      const styleLabels = {
        Red: (clearWrap.dataset.b2bStyleLabelRed || 'Red').trim(),
        Rose: (clearWrap.dataset.b2bStyleLabelRose || 'Rose').trim(),
        White: (clearWrap.dataset.b2bStyleLabelWhite || 'White').trim()
      };
      const pills = [];
      selectedStyle.forEach((/** @type {any} */ v) => {
        pills.push({ key: 'style', value: v, label: styleLabels[v] || v });
      });
      selectedArray.forEach((/** @type {any} */ v) => {
        pills.push({ key: 'vintage', value: v, label: v });
      });
      selectedFormat.forEach((/** @type {any} */ v) => {
        pills.push({ key: 'format', value: v, label: v });
      });
      selectedRegion.forEach((/** @type {any} */ id) => {
        pills.push({ key: 'region', value: id, label: regionIdToNameForStatus[id] || id });
      });
      selectedProducer.forEach((/** @type {any} */ name) => {
        pills.push({ key: 'producer', value: name, label: name });
      });
      if (hasPrice) {
        const currencyCode = products[0]?.variants?.edges?.[0]?.node?.price?.currencyCode || 'EUR';
        const minStr = priceMin !== '' ? formatMoney(parseFloat(priceMin), currencyCode) : '0';
        const maxStr = priceMax !== '' ? formatMoney(parseFloat(priceMax), currencyCode) : formatMoney(priceRange.max, currencyCode);
        pills.push({ key: 'price', value: 'price', label: `Price: ${minStr} – ${maxStr}` });
      }
      const pillsHtml = pills
        .map(
          (p) =>
            `<button type="button" class="pills__pill pills__pill--desktop-small facets-remove__pill" data-b2b-remove-filter data-b2b-remove-key="${esc(p.key)}" data-b2b-remove-value="${esc(p.value)}" tabindex="0" role="button">${esc(p.label)}<span class="svg-wrapper svg-wrapper--smaller">${iconHtml}</span><span class="visually-hidden">Remove</span></button>`
        )
        .join('');
      if (pillsContainer) pillsContainer.innerHTML = pillsHtml;
    }

    if (!(/** @type {any} */ (container))._b2bFiltersBound) {
      (/** @type {any} */ (container))._b2bFiltersBound = true;
      container.addEventListener('change', (/** @type {any} */ e) => {
        const input = e.target.closest('input[data-b2b-filter-input]');
        if (!input) return;
        const box = /** @type {any} */ (input.closest('.checkbox[data-b2b-filter]'));
        if (!box) return;
        const key = box.dataset.b2bFilter;
        const value = (input.value || box.dataset.b2bValue || '').trim();
        if (key === 'style') {
          const next = Array.isArray(state.style) ? [...state.style] : (state.style ? [String(state.style)] : []);
          const idx = next.indexOf(value);
          if (input.checked) {
            if (idx === -1) next.push(value);
          } else {
            if (idx !== -1) next.splice(idx, 1);
          }
          onStateChange({ style: next });
          return;
        }
        if (key === 'format') {
          const next = Array.isArray(state.format) ? [...state.format] : (state.format ? [String(state.format)] : []);
          const idx = next.indexOf(value);
          if (input.checked) {
            if (idx === -1) next.push(value);
          } else {
            if (idx !== -1) next.splice(idx, 1);
          }
          onStateChange({ format: next });
          return;
        }
        if (key === 'region') {
          const next = Array.isArray(state.region) ? [...state.region] : (state.region ? [String(state.region)] : []);
          const idx = next.indexOf(value);
          if (input.checked) {
            if (idx === -1) next.push(value);
          } else {
            if (idx !== -1) next.splice(idx, 1);
          }
          onStateChange({ region: next });
          return;
        }
        if (key === 'producer') {
          const next = Array.isArray(state.producer) ? [...state.producer] : (state.producer ? [String(state.producer)] : []);
          const nv = normalizeProducerFilterValue(value);
          const idx = next.findIndex((p) => normalizeProducerFilterValue(p) === nv);
          if (input.checked) {
            if (idx === -1) next.push(value.trim());
          } else {
            if (idx !== -1) next.splice(idx, 1);
          }
          onStateChange({ producer: next });
          return;
        }
        if (key !== 'vintage') {
          onStateChange({ [key]: input.checked ? value : '' });
          return;
        }
        const next = Array.isArray(state.vintage) ? [...state.vintage] : (state.vintage ? [String(state.vintage)] : []);
        const idx = next.indexOf(value);
        if (input.checked) {
          if (idx === -1) next.push(value);
        } else {
          if (idx !== -1) next.splice(idx, 1);
        }
        next.sort((a, b) => Number(b) - Number(a));
        onStateChange({ vintage: next });
      });
      container.addEventListener('click', (/** @type {any} */ e) => {
        const clear = /** @type {any} */ (e.target.closest('[data-b2b-clear-filters]'));
        if (clear) {
          e.preventDefault();
          onStateChange({ vintage: [], style: [], format: [], region: [], producer: [], priceMin: '', priceMax: '' });
          return;
        }
        const pill = /** @type {any} */ (e.target.closest('[data-b2b-remove-filter]'));
        if (pill) {
          e.preventDefault();
          const key = pill.dataset.b2bRemoveKey;
          const value = (pill.dataset.b2bRemoveValue || '').trim();
          if (!key || !value) return;
          if (key === 'style') {
            const next = (Array.isArray(state.style) ? [...state.style] : state.style ? [String(state.style)] : []).filter((v) => v !== value);
            onStateChange({ style: next });
          } else if (key === 'vintage') {
            const next = (Array.isArray(state.vintage) ? [...state.vintage] : state.vintage ? [String(state.vintage)] : []).filter((v) => v !== value);
            onStateChange({ vintage: next });
          } else if (key === 'format') {
            const next = (Array.isArray(state.format) ? [...state.format] : state.format ? [String(state.format)] : []).filter((v) => v !== value);
            onStateChange({ format: next });
          } else if (key === 'region') {
            const next = (Array.isArray(state.region) ? [...state.region] : state.region ? [String(state.region)] : []).filter((v) => v !== value);
            onStateChange({ region: next });
          } else if (key === 'producer') {
            const nv = normalizeProducerFilterValue(value);
            const next = (Array.isArray(state.producer) ? [...state.producer] : state.producer ? [String(state.producer)] : []).filter(
              (v) => normalizeProducerFilterValue(v) !== nv
            );
            onStateChange({ producer: next });
          } else if (key === 'price') {
            onStateChange({ priceMin: '', priceMax: '' });
          }
        }
      });
      /* Price typing coalesces into one state change (each change triggers a
         Storefront refetch — previously one per keystroke); the change event
         (blur/Enter) commits immediately and drops any pending trailing call so
         the same value isn't applied twice. */
      const applyPriceInput = (/** @type {any} */ input) => {
        const type = input.dataset.b2bPriceType;
        const raw = (input.value || '').trim().replace(/,/g, '.');
        const val = raw === '' ? '' : raw;
        if (type === 'min') onStateChange({ priceMin: val });
        if (type === 'max') onStateChange({ priceMax: val });
      };
      const applyPriceInputDebounced = debounce(applyPriceInput, 300);
      container.addEventListener('input', (/** @type {any} */ e) => {
        const input = e.target.closest('input[data-b2b-price-input]');
        if (!input) return;
        applyPriceInputDebounced(input);
      });
      container.addEventListener('change', (/** @type {any} */ e) => {
        const input = e.target.closest('input[data-b2b-price-input]');
        if (input) {
          applyPriceInputDebounced.cancel();
          applyPriceInput(input);
        }
      });
    }
};

/** Reset seam for legacy's clearCache (an imported binding cannot be reassigned). */
const resetProducerFacetCache = function resetProducerFacetCache() {
  b2bProducerFacetCache = { key: '', list: [] };
};

/**
 * Shared cart-UI state and the sync hook — the first piece of the cart-core
 * redesign. These flags used to be module-scope `let`s inside the legacy IIFE,
 * shared by the sync orchestrator, the drawer, the €120 gate and the Magical
 * Fees watcher. They live on ONE mutable object so extracted modules and
 * legacy.js read/write the same state (imported bindings cannot be reassigned;
 * object properties can).
 *
 * requestCartSync() lets extracted modules ask for a cart re-sync without
 * importing the orchestrator (which still lives in legacy.js): legacy registers
 * syncB2BCartUI via setCartSyncHandler() during initial evaluation, before any
 * listener can fire.
 */

const cartState = {
  /** >0 while a cart line removal is in flight — sync keeps the optimistic view. */
  b2bLineRemoveInFlight: 0,
  /** Fingerprint of the last painted cart; '' forces the next paint. */
  lastRenderedCartFingerprint: '',
  /** Last Storefront cart seen by the €120 min-order UI (null = unknown). */
  b2bLastStorefrontCartForMinUi: null,
  /** Boutique lines still pending merge into the VIP cart (keeps checkout enabled). */
  b2bHasPendingMergeableThemeLines: false,
  /** True from the checkout click until redirect/early-exit — keeps the checkout button disabled
   * across the merge/sync delay (and any repaint or €120-gate re-apply in between). */
  b2bCheckoutInFlight: false,
  /** Budgeted diagnostic counters (transition logs only). */
  b2bGateLogBudget: 60,
  b2bPaintLogBudget: 40,
};

/** @type {(opts?: any) => any} */
let cartSyncHandler = () => {};

/** Legacy registers its syncB2BCartUI here at startup. */
/** @param {(opts?: any) => any} fn */
const setCartSyncHandler = function setCartSyncHandler(fn) {
  if (typeof fn === 'function') cartSyncHandler = fn;
};

/** Ask the orchestrator for a re-sync (no-op until the handler is registered). */
/** @param {any} [opts] */
const requestCartSync = function requestCartSync(opts) {
  return cartSyncHandler(opts);
};

/**
 * Cart drawer + header bubble UI: drawer body paint (fingerprint-gated via
 * cartState), drawer HTML, open/close with animation handling, the €120
 * minimum checkout gate (idempotent DOM writers so the paint observer cannot
 * loop), the Magical-Fees-proof header bubble guard, and the min-ui debug
 * probes. Moved verbatim from legacy.js.
 */

/** @type {MutationObserver | null} */
let b2bPrivateCellarCheckoutPaintObserver = null;

/** @type {MutationObserver | null} */
let b2bPrivateCellarCheckoutStyleProbe = null;

/** @type {HTMLElement | null} */
let b2bPrivateCellarCheckoutStyleProbeTarget = null;

const teardownPrivateCellarCheckoutStyleProbe = function teardownPrivateCellarCheckoutStyleProbe() {
    if (b2bPrivateCellarCheckoutStyleProbe) {
      try {
        b2bPrivateCellarCheckoutStyleProbe.disconnect();
      } catch (_) {
        /* ignore */
      }
      b2bPrivateCellarCheckoutStyleProbe = null;
    }
    b2bPrivateCellarCheckoutStyleProbeTarget = null;
};

/** @param {HTMLElement | null | undefined} btn */
const setupPrivateCellarCheckoutStyleProbe = function setupPrivateCellarCheckoutStyleProbe(btn) {
    if (!(btn instanceof HTMLElement)) {
      teardownPrivateCellarCheckoutStyleProbe();
      return;
    }
    if (!privateCellarMinUiDebugEnabled()) {
      teardownPrivateCellarCheckoutStyleProbe();
      return;
    }
    if (b2bPrivateCellarCheckoutStyleProbeTarget === btn && b2bPrivateCellarCheckoutStyleProbe) return;
    teardownPrivateCellarCheckoutStyleProbe();
    b2bPrivateCellarCheckoutStyleProbeTarget = btn;
    b2bPrivateCellarCheckoutStyleProbe = new MutationObserver((records) => {
      for (const m of records) {
        privateCellarMinUiLog('CHECKOUT_BUTTON_MUTATION', {
          mutationType: m.type,
          attributeName: m.attributeName,
          oldValue: m.oldValue != null ? String(m.oldValue).slice(0, 200) : null,
          newStyleAttr: btn.getAttribute('style'),
          computedOpacity: typeof getComputedStyle !== 'undefined' ? getComputedStyle(btn).opacity : null,
          disabledAttr: btn.hasAttribute('disabled'),
          disabledProp: 'disabled' in btn ? /** @type {{ disabled: boolean }} */ (btn).disabled : null,
        });
        try {
          console.trace('[b2b-storefront][private-cellar-min-ui] CHECKOUT_BUTTON_MUTATION trace');
        } catch (_) {
          /* ignore */
        }
      }
    });
    b2bPrivateCellarCheckoutStyleProbe.observe(btn, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['style', 'class', 'disabled'],
    });
    privateCellarMinUiLog('style_probe_attached', { tag: btn.tagName, className: btn.className });
};

/**
 * VIP vs B2B for cart rules: `[data-b2b-cart-ui]` gets `data-b2b-catalog-scope` once at mount; `getConfig().scopeKey`
 * always follows the *last* `[data-b2b-config]`. Those two can disagree (e.g. FAB stamped `b2b` while a lower
 * section makes live config `vip`). Cart sync then logs `scope: "vip"` but preferring only the mounted
 * attribute skipped the €120 minimum and left checkout enabled. If *either* live config or mounted UI is
 * `vip`, treat the drawer as VIP.
 */
/** @param {B2BConfig | null | undefined} cfg */
const getB2BCartCheckoutButtonLabel = function getB2BCartCheckoutButtonLabel(cfg) {
    if (getEffectiveB2BCatalogScope(cfg) === 'vip') {
      return (cfg && cfg.cartCheckoutLabel) || 'Checkout';
    }
    return (cfg && cfg.cartPlaceOrderLabel) || 'Place order';
};

/**
 * @param {any} cart
 * @param {B2BConfig | null | undefined} cfg
 */
const privateCellarMinimumOrderApplies = function privateCellarMinimumOrderApplies(cart, cfg) {
    if (getEffectiveB2BCatalogScope(cfg) !== 'vip') return false;
    const tot = parseCartMoneyField(cart?.cost?.totalAmount);
    const sub = parseCartMoneyField(cart?.cost?.subtotalAmount);
    const cur = (tot?.currency || sub?.currency || cfg?.currency || 'EUR').toUpperCase();
    return cur === 'EUR';
};

/**
 * @param {any} cart
 * @param {B2BConfig | null | undefined} cfg
 */
const isPrivateCellarMinimumMet = function isPrivateCellarMinimumMet(cart, cfg) {
    if (!privateCellarMinimumOrderApplies(cart, cfg)) return true;
    const amt = amountForPrivateCellarMinimumOrder(cart, cfg);
    return Math.round(amt * 100) >= Math.round(PRIVATE_CELLAR_MIN_ORDER_EUR * 100);
};

/* Idempotent DOM writers: only touch the attribute/style when the value actually differs, so the
 * drawer MutationObserver (which watches disabled/class/style/data-* on this very button) is not
 * re-triggered by the gate's own writes — that feedback loop spun forever (`pc_checkout_disabled`
 * logging endlessly) and caused the cart flicker. */
/** @param {Element} el @param {string} name @param {string} value */
const b2bSetAttrIfChanged = function b2bSetAttrIfChanged(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
};

/** @param {Element} el @param {string} name */
const b2bRemoveAttrIfPresent = function b2bRemoveAttrIfPresent(el, name) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
};

/** @param {HTMLElement} el @param {string} prop @param {string} value @param {string} [priority] */
const b2bSetStyleIfChanged = function b2bSetStyleIfChanged(el, prop, value, priority) {
    if (el.style.getPropertyValue(prop) !== value) el.style.setProperty(prop, value, priority || '');
};

/** @param {HTMLElement} el @param {string} prop */
const b2bRemoveStyleIfPresent = function b2bRemoveStyleIfPresent(el, prop) {
    if (el.style.getPropertyValue(prop) !== '') el.style.removeProperty(prop);
};

/**
 * Below-minimum checkout: root attribute (CSS) + inline opacity on the button. Inline `!important` survives
 * theme or late stylesheets; MutationObserver + delayed retries catch scripts that overwrite after sync.
 */
/**
 * @param {HTMLElement | null | undefined} root
 * @param {any} [cartFromSync]
 */
const applyPrivateCellarMinimumCheckoutUi = function applyPrivateCellarMinimumCheckoutUi(root, cartFromSync) {
    if (!root) return;
    if (arguments.length > 1) {
      cartState.b2bLastStorefrontCartForMinUi = cartFromSync || null;
    }
    const cart = cartState.b2bLastStorefrontCartForMinUi;
    const cfg = getConfig() || {};
    /*
     * No-arg re-applies (the t50/t250/t800 retries + the drawer mutation observer) decide from the
     * CACHED cart. If nothing real has been cached yet, there's nothing to base a decision on — bail
     * rather than disable, so a transient/stale re-apply can't gray out the checkout before the first
     * real sync. Explicit sync calls (arguments.length>1) always proceed; they know the true state,
     * including a genuinely empty cart (null) that must disable. Pending boutique lines also proceed
     * (they enable checkout regardless of the cached Storefront cart).
     */
    if (!cart && arguments.length <= 1 && !cartState.b2bHasPendingMergeableThemeLines) return;
    /*
     * Boutique lines still in the theme cart belong in this one Private Cellar cart. Treat them as
     * present (so an empty VIP cart with pending boutique items is still checkout-able) and never
     * dim while a merge is pending — clicking checkout forces the merge, then re-gates on the
     * unified total.
     */
    const pendingMergeLines = cartState.b2bHasPendingMergeableThemeLines;
    const hasItems = (cart?.lines?.edges || []).length > 0 || pendingMergeLines;
    const below =
      hasItems &&
      !pendingMergeLines &&
      cart &&
      privateCellarMinimumOrderApplies(cart, cfg) &&
      !isPrivateCellarMinimumMet(cart, cfg);
    if (below) b2bSetAttrIfChanged(root, 'data-b2b-private-cellar-below-min', 'true');
    else b2bRemoveAttrIfPresent(root, 'data-b2b-private-cellar-below-min');

    const btn = root.querySelector('[data-b2b-cart-checkout]');
    if (btn instanceof HTMLButtonElement) {
      privateCellarMinUiLog('apply', {
        below,
        hasItems,
        scopeUi: root.getAttribute('data-b2b-catalog-scope'),
        rootBelowMinAttr: root.getAttribute('data-b2b-private-cellar-below-min'),
        styleBefore: btn.getAttribute('style'),
        computedOpacityBefore: typeof getComputedStyle !== 'undefined' ? getComputedStyle(btn).opacity : null,
        cartIdTail: cart?.id ? String(cart.id).slice(-24) : null,
        syncArg: arguments.length > 1 ? (cartFromSync ? 'cart' : 'null') : 'omit',
      });
      /*
       * Never gate dimming on the button already having `disabled`: another script can clear it while the
       * cart is still under €120 — we would hit the else branch, strip opacity, and look "enabled".
       * When `below` is true, always force disabled + dim. Otherwise only enable when checkout is allowed.
       */
      if (below) {
        b2bSetAttrIfChanged(btn, 'disabled', '');
        b2bSetAttrIfChanged(btn, 'aria-disabled', 'true');
        if (btn.disabled !== true) btn.disabled = true;
        b2bSetAttrIfChanged(btn, 'data-b2b-private-cellar-checkout-blocked', 'true');
        b2bSetStyleIfChanged(btn, 'opacity', '0.45', 'important');
        b2bSetStyleIfChanged(btn, 'cursor', 'not-allowed', 'important');
        b2bSetStyleIfChanged(btn, 'pointer-events', 'none', 'important');
      } else {
        b2bRemoveAttrIfPresent(btn, 'data-b2b-private-cellar-checkout-blocked');
        b2bRemoveStyleIfPresent(btn, 'opacity');
        b2bRemoveStyleIfPresent(btn, 'cursor');
        b2bRemoveStyleIfPresent(btn, 'pointer-events');
        const allowCheckout =
          pendingMergeLines ||
          (hasItems &&
            cart &&
            (!privateCellarMinimumOrderApplies(cart, cfg) || isPrivateCellarMinimumMet(cart, cfg)));
        /* Never re-enable while a checkout click is in flight — the redirect is loading. */
        if (allowCheckout && !cartState.b2bCheckoutInFlight) {
          b2bRemoveAttrIfPresent(btn, 'disabled');
          b2bRemoveAttrIfPresent(btn, 'aria-disabled');
          if (btn.disabled !== false) btn.disabled = false;
        } else {
          /* Known cart that doesn't qualify (genuinely empty / non-EUR). Unknown carts already bailed above. */
          b2bSetAttrIfChanged(btn, 'disabled', '');
          b2bSetAttrIfChanged(btn, 'aria-disabled', 'true');
          if (btn.disabled !== true) btn.disabled = true;
        }
      }
      privateCellarMinUiLog('apply:after', {
        styleAfter: btn.getAttribute('style'),
        computedOpacityAfter: typeof getComputedStyle !== 'undefined' ? getComputedStyle(btn).opacity : null,
        disabled: btn.disabled,
      });
      /* Diagnostic (budget-capped): only log when MY gate is the one disabling — confirmed above it
       * isn't (disabledAfter:false), so this now stays quiet; an external script is the disabler. */
      if (btn.disabled && cartState.b2bGateLogBudget > 0) {
        cartState.b2bGateLogBudget -= 1;
        b2bCartRoutingLog('pc_gate', {
          disabledAfter: btn.disabled,
          below,
          hasItems,
          pendingMergeLines,
          cartPresent: !!cart,
          lineCount: (cart?.lines?.edges || []).length,
          applies: cart ? privateCellarMinimumOrderApplies(cart, cfg) : null,
          minMet: cart ? isPrivateCellarMinimumMet(cart, cfg) : null,
          minAmount: cart ? Math.round(amountForPrivateCellarMinimumOrder(cart, cfg) * 100) / 100 : null,
          syncArg: arguments.length > 1 ? (cartFromSync ? 'cart' : 'null') : 'omit',
        });
      }
      setupPrivateCellarCheckoutStyleProbe(btn);
    } else {
      teardownPrivateCellarCheckoutStyleProbe();
      privateCellarMinUiLog('apply:no_checkout_button', { below, hasItems });
    }
};

/** @param {HTMLElement | null | undefined} root */
const schedulePrivateCellarMinimumCheckoutUiRetries = function schedulePrivateCellarMinimumCheckoutUiRetries(root) {
    if (!root) return;
    const run = (/** @type {string} */ label) => {
      privateCellarMinUiLog('retry:' + label, {});
      applyPrivateCellarMinimumCheckoutUi(root);
    };
    try {
      if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(() => run('rAF'));
    } catch (_) {
      run('rAF_sync');
    }
    setTimeout(() => run('t50'), 50);
    setTimeout(() => run('t250'), 250);
    setTimeout(() => run('t800'), 800);
};

/** @param {HTMLElement | null | undefined} root */
const ensurePrivateCellarCheckoutPaintObserver = function ensurePrivateCellarCheckoutPaintObserver(root) {
    if (b2bPrivateCellarCheckoutPaintObserver || !root) return;
    const drawerBody = root.querySelector('[data-b2b-cart-drawer-body]');
    if (!drawerBody) return;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let t = null;
    b2bPrivateCellarCheckoutPaintObserver = new MutationObserver(() => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        t = null;
        privateCellarMinUiLog('drawer_body_mutation_debounced_reapply', {});
        applyPrivateCellarMinimumCheckoutUi(root);
      }, 0);
    });
    b2bPrivateCellarCheckoutPaintObserver.observe(drawerBody, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'style', 'data-b2b-private-cellar-checkout-blocked'],
    });
};

/** @param {any} renderOpts */
const isB2BOrderRequestJustSent = function isB2BOrderRequestJustSent(renderOpts) {
    if (renderOpts && renderOpts.orderJustSent) return true;
    try {
      return sessionStorage.getItem(SESSION_KEY_B2B_ORDER_REQUEST_SENT) === '1';
    } catch (_) {
      return false;
    }
};

/** @param {B2BConfig | null | undefined} cfg */
const hasOrderRequestSentFeedback = function hasOrderRequestSentFeedback(cfg) {
    const c = cfg || {};
    return !!(
      (c.feedbackRequestSentLine1 || '').trim()
      || (c.feedbackRequestSentLine2 || '').trim()
      || (c.feedbackRequestSentContinue || '').trim()
    );
};

/** @param {B2BConfig | null | undefined} cfg */
const formatOrderRequestSentFeedbackHtml = function formatOrderRequestSentFeedbackHtml(cfg) {
    const c = cfg || {};
    const line1 = (c.feedbackRequestSentLine1 || '').trim();
    const line2 = (c.feedbackRequestSentLine2 || '').trim();
    const continueTpl = (c.feedbackRequestSentContinue || '').trim();
    const linkText = (c.feedbackRequestSentContinueLink || c.continueShoppingLabel || 'Continue shopping').trim();
    const continueUrl = getB2BContinueShoppingUrl(c);
    /** @type {string[]} */
    const parts = [];
    if (line1) parts.push(`<p>${esc(line1)}</p>`);
    if (line2) parts.push(`<p>${esc(line2)}</p>`);
    if (continueTpl) {
      const linkHtml = `<a href="${esc(continueUrl)}" class="b2b-order-request-sent-continue">${esc(linkText)}</a>`;
      const linkIdx = continueTpl.indexOf('__LINK__');
      if (linkIdx >= 0) {
        const before = esc(continueTpl.slice(0, linkIdx));
        const after = esc(continueTpl.slice(linkIdx + 8));
        parts.push(`<p>${before}${linkHtml}${after}</p>`);
      } else {
        parts.push(`<p>${esc(continueTpl)}</p>`);
      }
    }
    return parts.join('');
};

/**
 * Mounts snippets/b2b-cart-tax-note-template.liquid (same as cart-summary tax note).
 * @param {ParentNode | null | undefined} scope
 */
const mountB2BCartDrawerTaxNoteFromTemplate = function mountB2BCartDrawerTaxNoteFromTemplate(scope) {
    if (!(scope instanceof Element)) return;
    const slot = scope.querySelector('[data-b2b-cart-tax-note-root]');
    if (!(slot instanceof HTMLElement)) return;
    const tpl = document.getElementById('b2b-storefront-cart-tax-note');
    const inner = tpl instanceof HTMLTemplateElement ? tpl.content.firstElementChild : null;
    if (inner instanceof Element) {
      slot.replaceWith(/** @type {Element} */ (inner.cloneNode(true)));
      return;
    }
    const fallbackCfg = getConfig() || {};
    const fallback = [fallbackCfg.taxNoteVip, fallbackCfg.taxNoteB2b].map((s) => String(s || '').trim()).find(Boolean);
    if (fallback) {
      const wrap = document.createElement('div');
      wrap.className = 'cart__summary-item tax-note cart-primary-typography b2b-tax-note';
      wrap.textContent = fallback;
      slot.replaceWith(wrap);
    } else {
      slot.remove();
    }
};

/**
 * @param {HTMLElement | null | undefined} drawerBody
 * @param {any} cart
 * @param {any} renderOpts
 */
const paintB2BCartDrawerBody = function paintB2BCartDrawerBody(drawerBody, cart, renderOpts) {
    if (!drawerBody) return;
    drawerBody.innerHTML = renderB2BCartDrawerHtml(cart, renderOpts);
    mountB2BCartDrawerTaxNoteFromTemplate(drawerBody);
    /*
     * Private Cellar (VIP) prices follow the HT/TTC tax toggle, but unlike the native cart this
     * drawer is rendered by JS and fires no `cart:update`, so tax-toggle.js never re-processes it —
     * the total would stay at its ex-VAT value with a mismatched inc/ex-VAT label. Ask tax-toggle to
     * re-apply the current mode now (it excludes the B2B trade cart itself, so this is a no-op outside
     * VIP, and absent entirely when the toggle is disabled).
     *
     * Convert SYNCHRONOUSLY first — in the same task as innerHTML, before the browser paints this
     * frame — so the ex-VAT base (e.g. 353,50 €) never flashes next to the "inc. VAT" label before
     * settling to 434,00 €. Safe to read/write textContent immediately: `text-component` has no
     * connectedCallback that rewrites its text (see TextComponent in utilities.js), so the just-
     * injected nodes need no upgrade tick. The rAF re-apply stays as a safety net for a later morph
     * or a paint that happened while the drawer body was still detached.
     */
    try {
      const refresh = typeof window !== 'undefined' && /** @type {any} */ (window).__taxToggleRefreshCartPrices;
      if (typeof refresh === 'function') {
        try { refresh(); } catch (_) { /* tax toggle is optional */ }
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => { try { refresh(); } catch (_) { /* tax toggle is optional */ } });
        }
      }
    } catch (_) {
      /* tax toggle is optional — never let it break the cart paint */
    }
};

/** Matches snippets/cart-bubble.liquid structure; styled in #b2b-cart-ui-styles (light B2B/VIP surface + theme text). */
/** @param {any} cart */
const renderB2BCartTitleBubble = function renderB2BCartTitleBubble(cart) {
    const lines = cart?.lines?.edges || [];
    const lineCount = lines.length;
    const qty = Number(cart?.totalQuantity);
    const displayCount = Number.isFinite(qty) && qty > 0 ? qty : lineCount;
    if (!lineCount || displayCount <= 0) return '';
    const display = displayCount > 99 ? '99+' : String(displayCount);
    const maintainRatio = displayCount <= 99 ? ' data-maintain-ratio' : '';
    const cfg = getConfig() || {};
    const bubblePrefix = (cfg.cartBubblePrefix || 'Cart:').trim();
    return `<div class="cart-bubble"${maintainRatio} aria-hidden="true"><span class="cart-bubble__background"></span><span class="cart-bubble__text"><span class="visually-hidden">${esc(bubblePrefix)} ${esc(display)}</span><span class="cart-bubble__text-count" data-b2b-cart-count>${esc(display)}</span></span></div>`;
};

/**
 * @param {any} cart
 * @param {any} renderOpts
 */
const renderB2BCartDrawerHtml = function renderB2BCartDrawerHtml(cart, renderOpts) {
    const cfg = getConfig() || {};
    const ro = renderOpts && typeof renderOpts === 'object' ? renderOpts : {};
    const discountCode = getStoredB2BDiscountCode();
    const exciseGids = getExciseFeeGids(cfg);
    const allLines = cart?.lines?.edges || [];
    // Exclude the fee placeholders (insurance + excise/duty) from "has items" and from list rendering order
    const lines = allLines;
    /* Insurance + excise are each spread across a denomination ladder (several €0.01/€1/€10… lines).
     * Collapse each into ONE drawer row: render on the first line of that fee, skip the rest, show the sum. */
    const insuranceGids = getInsuranceFeeGids(cfg);
    const insuranceNodes = allLines
      .map((/** @type {any} */ e) => e?.node)
      .filter((/** @type {any} */ n) => isInsuranceFeeLine(n, insuranceGids));
    const insuranceTotal = insuranceNodes.reduce(
      (/** @type {number} */ s, /** @type {any} */ n) => s + (parseFloat(n?.cost?.totalAmount?.amount || '0') || 0),
      0
    );
    const insuranceTotalCur = insuranceNodes[0]?.cost?.totalAmount?.currencyCode || cfg.currency || 'EUR';
    const firstInsuranceLineId = insuranceNodes[0]?.id || '';
    const exciseNodes = allLines
      .map((/** @type {any} */ e) => e?.node)
      .filter((/** @type {any} */ n) => isExciseFeeLine(n, exciseGids));
    const exciseTotal = exciseNodes.reduce(
      (/** @type {number} */ s, /** @type {any} */ n) => s + (parseFloat(n?.cost?.totalAmount?.amount || '0') || 0),
      0
    );
    const exciseTotalCur = exciseNodes[0]?.cost?.totalAmount?.currencyCode || cfg.currency || 'EUR';
    const firstExciseLineId = exciseNodes[0]?.id || '';
    const hasItems = allLines.some((/** @type {any} */ e) => !isAnyFeeLine(e?.node, cfg));
    const minMet = isPrivateCellarMinimumMet(cart, cfg);
    /* Pending boutique lines belong in this one cart; keep checkout enabled — the click forces the merge, then re-gates on the merged total. */
    const canCheckout = cartState.b2bHasPendingMergeableThemeLines || (hasItems && minMet);
    const cartItemsGapPx = typeof cfg.cartItemsGap === 'number' ? cfg.cartItemsGap : 24;

    const showOrderRequestSent = isB2BOrderRequestJustSent(ro) && !hasItems && hasOrderRequestSentFeedback(cfg);
    const orderRequestSentHtml = showOrderRequestSent ? formatOrderRequestSentFeedbackHtml(cfg) : '';

    const itemsSentBanner =
      showOrderRequestSent && orderRequestSentHtml
        ? `<div class="b2b-cart-items-sent-banner cart-primary-typography" role="status">${orderRequestSentHtml}</div>`
        : '';
    const continueShoppingUrl = getB2BContinueShoppingUrl(cfg);
    const listHtml = hasItems
      ? lines.map((/** @type {any} */ edge) => {
        const line = edge?.node;
        const lineId = line?.id || '';
        const qty = line?.quantity || 0;
        const productTitle = line?.merchandise?.product?.title || '';
        const productHandle = line?.merchandise?.product?.handle || '';
        const b2bProductBaseUrl = (cfg.productUrl || '').trim();
        /* Link into the B2B/VIP storefront catalog page (catalog-tagged products + fee placeholders). */
        const b2bCatalogHref =
          productHandle && b2bProductBaseUrl ? `${b2bProductBaseUrl}?product=${encodeURIComponent(productHandle)}` : '';
        /*
         * Regular boutique products get merged into the Private Cellar (VIP) cart but are NOT in the
         * VIP catalog, so their title must link to the standard storefront product page: the B2B/VIP
         * page (cfg.productUrl) only lists catalog-tagged products and would show nothing for them.
         * Route by catalog membership — a line whose product carries this scope's catalog tag
         * (cfg.productTag, e.g. "VIP") stays on the catalog page; everything else goes to /products/<handle>.
         */
        const productTagsLower = (Array.isArray(line?.merchandise?.product?.tags) ? line.merchandise.product.tags : [])
          .map((/** @type {any} */ t) => String(t || '').toLowerCase());
        const catalogTagLower = String(cfg.productTag || '').trim().toLowerCase();
        const isCatalogProduct = !!catalogTagLower && productTagsLower.includes(catalogTagLower);
        const cartLineTitleHref = !productHandle
          ? ''
          : isCatalogProduct
            ? b2bCatalogHref
            : localePrefixedStorePath(`products/${encodeURIComponent(productHandle)}`);
        const variantTitle = line?.merchandise?.title || '';
        const variantVolume = line?.merchandise?.selectedOptions?.find((/** @type {any} */ opt) => opt?.name === 'Volume')?.value || '';
        const imgUrl = line?.merchandise?.image?.url || line?.merchandise?.product?.featuredImage?.url || '';
        const imgSep = imgUrl.includes('?') ? '&' : '?';
        const imgUrl200 = imgUrl ? `${imgUrl}${imgSep}width=200` : '';
        const imgUrl320 = imgUrl ? `${imgUrl}${imgSep}width=320` : '';
        const imgUrl220 = imgUrl ? `${imgUrl}${imgSep}width=220` : '';
        const imgUrl440 = imgUrl ? `${imgUrl}${imgSep}width=440` : '';
        const imgAlt = line?.merchandise?.image?.altText || productTitle;
        const total = parseFloat(line?.cost?.totalAmount?.amount || '0') || 0;
        const totalCur = line?.cost?.totalAmount?.currencyCode || cfg.currency || 'EUR';

        // Fee lines (insurance + excise/duty): read-only, no quantity controls, no remove button
        if (isAnyFeeLine(line, cfg)) {
          const isExcise = isExciseFeeLine(line, exciseGids);
          const isInsurance = !isExcise && isInsuranceFeeLine(line, insuranceGids);
          /* Each fee's ladder is several lines; render it once (on the first) as one summed row. */
          if (isInsurance && lineId !== firstInsuranceLineId) return '';
          if (isExcise && lineId !== firstExciseLineId) return '';
          const feeDisplayTotal = isInsurance ? insuranceTotal : isExcise ? exciseTotal : total;
          const feeDisplayCur = isInsurance ? insuranceTotalCur : isExcise ? exciseTotalCur : totalCur;
          /* Excise/duty: for a Sweden destination the fee is its OWN Shopify product, so show that
           * product's real title + description (merchant-managed) instead of the computed label /
           * excise_fee_details setting; falls back to them when blank or for non-SE destinations. */
          const exciseDisplay = isExcise ? getExciseFeeLineDisplay(line, cfg) : null;
          const feeTitle = isExcise
            ? exciseDisplay.title
            : productTitle || '1% Shipping Insurance';
          /* Optional "Details:" sub-line, per fee type (insurance vs excise/duty). */
          const feeDetailsText = isInsurance
            ? String(cfg.insuranceFeeDetails || '').trim()
            : isExcise
              ? exciseDisplay.details
              : '';
          /*
           * Suppress the variant sub-line for fee placeholders when it's noise: a Details line is
           * already shown (a merchant who types a description into the variant name would otherwise
           * see it twice — once here, once as Details), the excise label already states the basis,
           * "Default Title", or a bare price/number like "0.01" (fee variants are often named after
           * their €0.01 unit price).
           */
          const variantTitleTrim = String(variantTitle || '').trim();
          const variantLooksLikePrice = /^[\d.,\s€$£]+$/.test(variantTitleTrim);
          const feeVariantLabel =
            !isExcise && !feeDetailsText && variantTitleTrim && variantTitleTrim !== 'Default Title' && !variantLooksLikePrice
              ? variantTitle
              : '';
          /* Alt text uses the resolved fee title (feeTitle) — for excise that's the Sweden product's own
           * title on an SE order and the computed label otherwise (getExciseFeeLineDisplay), so a US order
           * never shows the Sweden product name or vice-versa. */
          return `<li class="b2b-cart-line b2b-cart-line--fee" data-b2b-line-id="${esc(lineId)}">
            <div class="cart-items__table-row b2b-cart-line__row">
              <div class="cart-items__media b2b-cart-line__thumb-wrap">
                ${imgUrl ? `<img class="cart-items__media-image b2b-cart-line__thumb border-style" src="${esc(imgUrl200)}" srcset="${esc(imgUrl200)} 200w" sizes="52px" width="200" height="200" alt="${esc(feeTitle)}" loading="lazy" style="--border-width: 1px; --border-style: none; --border-color: rgb(var(--color-border-rgb) / 0.5); --border-radius: 0px;">` : ''}
              </div>
              <div class="cart-items__details cart-primary-typography b2b-cart-line__content">
                <p>${b2bCatalogHref && !isExcise ? `<a href="${esc(b2bCatalogHref)}" class="cart-items__title">${esc(feeTitle)}</a>` : `<span class="cart-items__title">${esc(feeTitle)}</span>`}</p>
                ${feeVariantLabel ? `<p class="cart-items__variant">${esc(feeVariantLabel)}</p>` : ''}
                ${feeDetailsText ? `<p class="cart-items__variant b2b-cart-line__fee-details">${esc(cfg.feeDetailsLabel || 'Details:')} ${esc(feeDetailsText)}</p>` : ''}
              </div>
              <div class="cart-items__price cart-secondary-typography b2b-cart-line__price">
                ${esc(formatMoney(feeDisplayTotal, feeDisplayCur))}
              </div>
            </div>
          </li>`;
        }

        const lineVariant = line?.merchandise || null;
        const lineStock = b2bGetVariantStock(lineVariant);
        const lineUnlimited = lineStock == null || lineStock < 0;
        let lineMaxOptions;
        let lineShowPlus = false;
        if (lineUnlimited) {
          lineMaxOptions = B2B_QUANTITY_MAX_OPTIONS;
          lineShowPlus = true;
        } else if (lineStock > B2B_QUANTITY_MAX_OPTIONS) {
          lineMaxOptions = B2B_QUANTITY_MAX_OPTIONS;
          lineShowPlus = true;
        } else {
          lineMaxOptions = Math.max(1, lineStock);
        }
        let lineCurrentInOptions = false;
        let qtyOptionsHtml = '';
        for (let i = 1; i <= lineMaxOptions; i++) {
          if (i === qty) {
            lineCurrentInOptions = true;
            qtyOptionsHtml += `<option value="${i}" selected>${i}</option>`;
          } else {
            qtyOptionsHtml += `<option value="${i}">${i}</option>`;
          }
        }
        if (!lineCurrentInOptions && qty > lineMaxOptions) {
          qtyOptionsHtml += `<option value="${qty}" data-custom-quantity="true" selected>${qty}</option>`;
        }
        if (lineShowPlus) {
          qtyOptionsHtml += `<option value="${B2B_QUANTITY_CUSTOM_VALUE}">+</option>`;
        }

        return `<li class="b2b-cart-line" data-b2b-line-id="${esc(lineId)}" data-b2b-line-qty="${qty}" data-b2b-line-stock="${lineUnlimited ? -1 : lineStock}">
          <div class="cart-items__table-row b2b-cart-line__row">
            <div class="cart-items__media b2b-cart-line__thumb-wrap">
              ${imgUrl ? `<img class="cart-items__media-image b2b-cart-line__thumb" src="${esc(imgUrl320)}" srcset="${esc(imgUrl220)} 220w, ${esc(imgUrl440)} 440w" sizes="72px" width="320" height="320" alt="${esc(imgAlt || '')}" loading="lazy">` : ''}
            </div>
            <div class="cart-items__details cart-primary-typography b2b-cart-line__content">
              <p>${cartLineTitleHref ? `<a href="${esc(cartLineTitleHref)}" class="cart-items__title">${esc(productTitle)}</a>` : `<span class="cart-items__title">${esc(productTitle)}</span>`}</p>
              ${variantTitle ? `<p class="cart-items__variant">${esc(variantTitle)}${qty ? ` x ${qty}` : ''}</p>` : ''}
              ${variantVolume ? `<p class="cart-items__variant">${esc(variantVolume)}</p>` : ''}
            </div>
            <div class="b2b-cart-line__inline-price cart-primary-typography" aria-hidden="true" data-b2b-line-inline-price data-tax-raw="${total}">${esc(formatMoney(total, totalCur))}</div>
            <div class="cart-items__quantity b2b-cart-line__actions">
              <div class="quantity-selector cart-primary-typography b2b-cart-line__qty-selector">
                <button type="button" class="button quantity-minus button-unstyled" data-b2b-line-decrease aria-label="${esc(cfg.ariaDecreaseQuantity || 'Decrease quantity')}"><span class="svg-wrapper icon-plus">${ICON_MINUS}</span></button>
                <select class="quantity-selector__select" data-b2b-cart-qty-select aria-label="${esc(cfg.ariaLineQuantity || 'Quantity')}">${qtyOptionsHtml}</select>
                <button type="button" class="button quantity-plus button-unstyled" data-b2b-line-increase aria-label="${esc(cfg.ariaIncreaseQuantity || 'Increase quantity')}"><span class="svg-wrapper icon-plus">${ICON_PLUS}</span></button>
              </div>
              <button type="button" class="button button--tertiary cart-items__remove" data-b2b-line-remove aria-label="${esc(cfg.cartRemoveLabel || 'Remove')}">
                ${ICON_DELETE}
                <span class="visually-hidden">${esc(cfg.cartRemoveLabel || 'Remove')}</span>
              </button>
            </div>
            <div class="cart-items__price cart-secondary-typography b2b-cart-line__price" data-tax-raw="${total}">
              ${esc(formatMoney(total, totalCur))}
            </div>
          </div>
        </li>`;
      }).join('')
      : '';
    const emptyStateHtml = hasItems || showOrderRequestSent
      ? ''
      : `<div class="b2b-cart-empty-state cart-drawer__items">
          <span class="cart-drawer__heading h3 cart-drawer__heading--empty">${esc(cfg.cartEmptyLabel || 'Your cart is empty.')}</span>
          <a class="button cart-items__empty-button" href="${esc(continueShoppingUrl)}">${esc(cfg.continueShoppingLabel || 'Continue shopping')}</a>
        </div>`;
    const headerHtml = hasItems
      ? `<div class="cart-drawer__header b2b-cart-drawer__header">
          <span class="cart-drawer__heading h3"><span class="b2b-cart-drawer__title-cluster"><span class="b2b-cart-drawer__title-text h3">${esc(cfg.cartTitleLabel || 'B2B Cart')}</span>${renderB2BCartTitleBubble(cart)}</span></span>
          <button type="button" class="button close-button cart-drawer__close-button button-unstyled" data-b2b-cart-close aria-label="${esc(cfg.cartCloseLabel || 'Close')}">
            <span class="svg-wrapper">${ICON_CLOSE}</span>
          </button>
        </div>`
      : `<div class="cart-drawer__header b2b-cart-drawer__header b2b-cart-drawer__header--empty">
          <button type="button" class="button close-button cart-drawer__close-button button-unstyled" data-b2b-cart-close aria-label="${esc(cfg.cartCloseLabel || 'Close')}">
            <span class="svg-wrapper">${ICON_CLOSE}</span>
          </button>
        </div>`;
    const discountDisclosureExpanded = !!discountCode;
    const discountRemoveAria = `${cfg.discountRemove || 'Remove'}${discountCode ? `: ${discountCode}` : ''}`;
    const discountToggleAriaLabel = `${discountDisclosureExpanded ? 'Close ' : 'Open '}${cfg.discountToggleLabel || 'Discount code'}`;
    const discountCodesHtml = discountCode
      ? `<ul class="cart-discount__codes" role="list"><li class="cart-discount__pill" data-discount-code="${esc(discountCode)}"><p class="cart-discount__pill-code">${esc(discountCode)}</p><button type="button" class="cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled" data-b2b-discount-clear aria-label="${esc(discountRemoveAria)}">${ICON_FILTERS_CLOSE}<span class="visually-hidden">${esc(cfg.discountRemove || 'Remove')}</span></button></li></ul>`
      : '';
    /* Order note — Private Cellar (VIP) only. Professionals collect notes in the order-request form, so
     * adding it to their drawer too would double up. Reuses the discount disclosure's toggle/panel
     * classes for identical styling; the note persists locally and is pushed onto the Storefront cart
     * at checkout (data-b2b-note-input listener + the checkout handler). Sits above the discount below. */
    const showOrderNote = getEffectiveB2BCatalogScope(cfg) === 'vip';
    const orderNote = showOrderNote ? getStoredB2BCartNote() : '';
    const noteDisclosureExpanded = !!orderNote.trim();
    const noteToggleAriaLabel = `${noteDisclosureExpanded ? 'Close ' : 'Open '}${cfg.orderNoteToggleLabel || 'Order notes'}`;
    const orderNoteHtml = showOrderNote
      ? `<div class="cart-actions b2b-cart-note">
              <button type="button" class="b2b-cart-discount-toggle b2b-cart-note-toggle" data-b2b-note-toggle aria-expanded="${noteDisclosureExpanded ? 'true' : 'false'}" aria-label="${esc(noteToggleAriaLabel)}">
                <span class="disclosure-trigger__label h6">${esc(cfg.orderNoteToggleLabel || 'Order notes')}</span>
                <span class="svg-wrapper icon-plus">${ICON_PLUS}</span>
              </button>
              <div class="b2b-cart-discount-panel b2b-cart-note-panel" data-b2b-note-panel ${noteDisclosureExpanded ? '' : 'hidden'}>
                <label for="b2b-cart-note-input" class="visually-hidden">${esc(cfg.orderNoteToggleLabel || 'Order notes')}</label>
                <textarea id="b2b-cart-note-input" class="b2b-cart-note-input" data-b2b-note-input rows="3">${esc(orderNote)}</textarea>
              </div>
            </div>`
      : '';
    const footerHtml = hasItems
      ? `<div class="cart-drawer__summary b2b-cart-drawer__footer">
          <div class="cart__summary-totals">
            ${orderNoteHtml}
            <div class="cart-actions">
              <button type="button" class="b2b-cart-discount-toggle" data-b2b-discount-toggle aria-expanded="${discountDisclosureExpanded ? 'true' : 'false'}" aria-label="${esc(discountToggleAriaLabel)}">
                <span class="disclosure-trigger__label h6">${esc(cfg.discountToggleLabel || 'Discount code')}</span>
                <span class="svg-wrapper icon-plus">${ICON_PLUS}</span>
              </button>
              <div class="b2b-cart-discount-panel" data-b2b-discount-panel ${discountDisclosureExpanded ? '' : 'hidden'}>
                <div class="cart-discount__content">
                  <div class="cart-discount__form">
                    <label for="b2b-cart-discount-input" class="visually-hidden">${esc(cfg.discountPlaceholder || cfg.discountToggleLabel || 'Discount code')}</label>
                    <input id="b2b-cart-discount-input" type="text" name="discount" class="cart-discount__input" data-b2b-discount-input placeholder="${esc(cfg.discountPlaceholder || '')}" value="${esc(discountCode)}">
                    <button type="button" class="button button--primary cart-discount__button" data-b2b-discount-apply>${esc(cfg.discountApply || 'Apply')}</button>
                  </div>
                </div>
                ${discountCodesHtml}
                <div class="b2b-cart-discount-actions">
                  <span class="b2b-cart-discount-feedback" data-b2b-discount-feedback>${discountCode ? esc(formatDiscountFeedback(cfg, discountCode)) : ''}</span>
                </div>
              </div>
            </div>
            <div class="cart__total-container b2b-cart-total-container" style="display:flex;flex-direction:column;gap:8px;">
              ${formatB2BCartTotalsHtml(cart)}
              <div data-b2b-cart-tax-note-root></div>
            </div>
          </div>
          <div class="cart__ctas">
            <!-- Custom class (NOT the theme's cart__checkout-button): the Magical Fees app disables every
                 .cart__checkout-button to enforce its mandatory location fee, which fought our gate and
                 left this button greyed out on the Private Cellar cart. Renaming hides it from that app. -->
            <button type="button" class="b2b-pc-checkout-button button" data-b2b-cart-checkout ${canCheckout && !cartState.b2bCheckoutInFlight ? '' : 'disabled'}${cartState.b2bCheckoutInFlight ? ' aria-busy="true"' : ''}>${esc(getB2BCartCheckoutButtonLabel(cfg))}</button>
          </div>
        </div>`
      : '';

    return `
      ${headerHtml}
      <div class="cart-drawer__content b2b-cart-drawer__content motion-reduce">
        ${itemsSentBanner}
        ${hasItems ? `<scroll-hint class="cart-drawer__items b2b-cart-drawer__items"><ul class="b2b-cart-lines cart-items cart-items--dividers spacing-style" style="--cart-items-gap:${cartItemsGapPx}px;">${listHtml}</ul></scroll-hint>` : ''}
        ${emptyStateHtml}
        ${footerHtml}
      </div>
    `;
};

/** @param {B2BConfig} cfg */
const getB2BContinueShoppingUrl = function getB2BContinueShoppingUrl(cfg) {
    const localePrefix = getCurrentLocalePathPrefix();
    if (typeof document !== 'undefined' && document.body) {
      if (document.body.classList.contains('private-cellar-nav')) return `${localePrefix}/pages/private-cellar`;
      if (document.body.classList.contains('professionals-b2b-nav')) return `${localePrefix}/pages/for-professionals`;
    }
    return getEffectiveB2BCatalogScope(cfg) === 'vip'
      ? `${localePrefix}/pages/private-cellar`
      : `${localePrefix}/pages/for-professionals`;
};

const getCurrentLocalePathPrefix = function getCurrentLocalePathPrefix() {
    if (typeof window === 'undefined') return '';
    const path = String(window.location?.pathname || '');
    const m = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    return m ? `/${m[1]}` : '';
};

/** Last positive count we intend to show on the B2B header bubble (Storefront). */
let b2bLastPositiveHeaderCount = 0;

/** @type {MutationObserver | null} */
let b2bHeaderBubbleMo = null;

/** @type {Element | null} */
let b2bHeaderBubbleObservedEl = null;

/**
 * @param {any} textCountEl
 * @param {number} count
 */
const syncB2BHeaderBubbleIntent = function syncB2BHeaderBubbleIntent(textCountEl, count) {
    if (!(textCountEl instanceof HTMLElement) || typeof count !== 'number' || count <= 0) return;
    const want = String(count);
    if (textCountEl.textContent === want && !textCountEl.classList.contains('hidden')) return;
    textCountEl.textContent = want;
    textCountEl.classList.remove('hidden');
    const bubble = textCountEl.closest('.cart-bubble');
    if (bubble) {
      bubble.classList.remove('visually-hidden', 'cart-bubble--zero');
      bubble.setAttribute('aria-hidden', 'false');
    }
    const cartIcon = textCountEl.closest('cart-icon');
    if (cartIcon) {
      cartIcon.classList.add('header-actions__cart-icon--has-cart');
    }
};

const attachB2BHeaderBubbleGuard = function attachB2BHeaderBubbleGuard() {
    if (typeof MutationObserver === 'undefined') return;
    const el = document.querySelector('[data-b2b-header-cart] .cart-bubble__text-count');
    if (!el) return;
    if (b2bHeaderBubbleObservedEl === el && b2bHeaderBubbleMo) return;
    if (b2bHeaderBubbleMo) {
      try {
        b2bHeaderBubbleMo.disconnect();
      } catch (_) {
        /* ignore */
      }
    }
    b2bHeaderBubbleObservedEl = el;
    b2bHeaderBubbleMo = new MutationObserver(() => {
      if (b2bLastPositiveHeaderCount <= 0) return;
      const cur = document.querySelector('[data-b2b-header-cart] .cart-bubble__text-count');
      if (cur) {
        syncB2BHeaderBubbleIntent(cur, b2bLastPositiveHeaderCount);
      }
    });
    b2bHeaderBubbleMo.observe(el, { attributes: true, childList: true, characterData: true, subtree: true });
};

/**
 * @param {number} count
 * @param {string} debugReason
 */
const setB2BCartCount = function setB2BCartCount(count, debugReason) {
    /**
     * Include `.cart-bubble__text-count` under the B2B header link and `cart-icon[data-b2b-storefront-cart]`, not only
     * `[data-b2b-cart-count]` — avoids a Liquid/cache mismatch leaving the header with no attribute while JS still syncs.
     * Dedupe nodes: the same span can match multiple selectors.
     *
     * Call this again *after* `paintB2BCartDrawerBody(drawerBody, ...)` so nodes inside the drawer exist.
     */
    const selectorList = [
      '[data-b2b-header-cart] [data-b2b-cart-count]',
      '[data-b2b-cart-ui] [data-b2b-cart-count]',
      '[data-b2b-header-cart] .cart-bubble__text-count',
      'cart-icon[data-b2b-storefront-cart] .cart-bubble__text-count',
    ];
    const seen = new Set();
    /** @type {HTMLElement[]} */
    const countNodes = [];
    for (const sel of selectorList) {
      document.querySelectorAll(sel).forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (seen.has(el)) return;
        seen.add(el);
        countNodes.push(el);
      });
    }
    countNodes.forEach((node) => {
      const textCount = node.classList.contains('cart-bubble__text-count')
        ? node
        : node.closest('.cart-bubble__text-count');
      if (!textCount) return;
      textCount.textContent = String(count);
      textCount.classList.toggle('hidden', count <= 0);
      const bubble = textCount.closest('.cart-bubble');
      const cartIcon = textCount.closest('cart-icon');
      if (bubble) {
        bubble.classList.remove('visually-hidden');
        bubble.classList.toggle('cart-bubble--zero', count <= 0);
        bubble.setAttribute('aria-hidden', count <= 0 ? 'true' : 'false');
      }
      if (cartIcon) {
        cartIcon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
      }
    });
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(count),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      /* no-op */
    }

    if (typeof count === 'number') {
      b2bLastPositiveHeaderCount = count > 0 ? count : 0;
      if (count > 0) {
        attachB2BHeaderBubbleGuard();
        const rafFix = () => {
          const hel = document.querySelector('[data-b2b-header-cart] .cart-bubble__text-count');
          if (hel) {
            syncB2BHeaderBubbleIntent(hel, count);
          }
        };
        requestAnimationFrame(rafFix);
        requestAnimationFrame(() => requestAnimationFrame(rafFix));
      }
    }
};

const getCachedCartCount = function getCachedCartCount() {
    try {
      const raw = sessionStorage.getItem('cart-count');
      if (!raw) return -1;
      const parsed = JSON.parse(raw);
      const val = parseInt(String(parsed?.value || '0'), 10);
      return Number.isFinite(val) ? val : -1;
    } catch (_) {
      return -1;
    }
};

/**
 * Drives the open/close transition for the B2B cart drawer.
 *
 * Mirrors snippets/cart-drawer.liquid + assets/dialog.js: slide-in/out animation on
 * the drawer (slideInLeft/slideOutLeft from base.css), opacity fade on the backdrop,
 * body scroll lock while open, and Escape-to-close. The aside is not a real <dialog>
 * (no showModal()), so we coordinate the close via an `animationend` listener (with
 * a setTimeout fallback) before flipping `display:none` so the slide-out is visible.
 */
/** @type {((event: AnimationEvent) => void) | null} */
let b2bCartDrawerCloseAnimEndHandler = null;

/** @type {ReturnType<typeof setTimeout> | null} */
let b2bCartDrawerCloseFallbackTimer = null;

/** @param {KeyboardEvent} event */
const handleB2BCartDrawerEscape = function handleB2BCartDrawerEscape(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    setB2BCartDrawerOpen(false);
};

/** @param {boolean} open */
const setB2BCartDrawerOpen = function setB2BCartDrawerOpen(open) {
    const root = /** @type {any} */ (document.querySelector('[data-b2b-cart-ui]'));
    if (!root) return;
    const backdrop = /** @type {any} */ (root.querySelector('[data-b2b-cart-backdrop]'));
    const drawer = /** @type {any} */ (root.querySelector('[data-b2b-cart-drawer]'));
    if (!backdrop || !drawer) return;

    if (b2bCartDrawerCloseAnimEndHandler) {
      drawer.removeEventListener('animationend', b2bCartDrawerCloseAnimEndHandler);
      b2bCartDrawerCloseAnimEndHandler = null;
    }
    if (b2bCartDrawerCloseFallbackTimer) {
      clearTimeout(b2bCartDrawerCloseFallbackTimer);
      b2bCartDrawerCloseFallbackTimer = null;
    }

    if (open) {
      drawer.classList.remove('b2b-cart-drawer--closing');
      backdrop.classList.remove('b2b-cart-backdrop--closing');

      const wasHidden = drawer.style.display === 'none' || drawer.style.display === '';
      backdrop.style.display = 'block';
      drawer.style.display = 'flex';

      /* Same body-scroll-lock pattern as DialogComponent.showDialog in assets/dialog.js. */
      if (root.dataset.b2bCartScrollLock == null) {
        const scrollY = window.scrollY;
        root.dataset.b2bCartScrollLock = String(scrollY);
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      if (wasHidden) {
        /* Force reflow so slideInLeft re-runs from translateX(100%) when toggling display. */
        void drawer.offsetWidth;
      }

      drawer.classList.add('b2b-cart-drawer--open');
      backdrop.classList.add('b2b-cart-backdrop--open');

      document.addEventListener('keydown', handleB2BCartDrawerEscape);

      privateCellarMinUiLog('setB2BCartDrawerOpen(true)', {});
      applyPrivateCellarMinimumCheckoutUi(root);
      schedulePrivateCellarMinimumCheckoutUiRetries(root);
      return;
    }

    if (drawer.style.display === 'none' || drawer.style.display === '') return;

    drawer.classList.add('b2b-cart-drawer--closing');
    backdrop.classList.add('b2b-cart-backdrop--closing');
    drawer.classList.remove('b2b-cart-drawer--open');
    backdrop.classList.remove('b2b-cart-backdrop--open');

    const finishClose = () => {
      if (b2bCartDrawerCloseAnimEndHandler) {
        drawer.removeEventListener('animationend', b2bCartDrawerCloseAnimEndHandler);
        b2bCartDrawerCloseAnimEndHandler = null;
      }
      if (b2bCartDrawerCloseFallbackTimer) {
        clearTimeout(b2bCartDrawerCloseFallbackTimer);
        b2bCartDrawerCloseFallbackTimer = null;
      }
      drawer.classList.remove('b2b-cart-drawer--closing');
      backdrop.classList.remove('b2b-cart-backdrop--closing');
      drawer.style.display = 'none';
      backdrop.style.display = 'none';

      if (root.dataset.b2bCartScrollLock != null) {
        const scrollY = parseInt(root.dataset.b2bCartScrollLock || '0', 10);
        delete root.dataset.b2bCartScrollLock;
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }

      document.removeEventListener('keydown', handleB2BCartDrawerEscape);
    };

    b2bCartDrawerCloseAnimEndHandler = (/** @type {AnimationEvent} */ event) => {
      if (event.target === drawer) finishClose();
    };
    drawer.addEventListener('animationend', b2bCartDrawerCloseAnimEndHandler);
    /* Fallback: animationend is skipped if display flips to none mid-animation or prefers-reduced-motion disables it. */
    b2bCartDrawerCloseFallbackTimer = setTimeout(finishClose, 600);
};

/**
 * Magical Fees coexistence machinery (the 2026-06-11 popup-loop fix): the
 * Storage.prototype patch that mirrors/restores the confirmed destination, the
 * dialog auto-answer (showModal guard + MutationObserver fallback), the PC
 * destination prompt for VIP-only carts, and the destination-change watcher.
 * Re-syncs go through requestCartSync (cart/state.js); the sync orchestrator
 * itself still lives in legacy.js. Moved verbatim apart from that one seam.
 */

/** True once the Magical Fees destination-change watcher is wired (re-syncs the PC excise on change). */
let b2bMaFeesLocationWatchAttached = false;

/** True once the Magical Fees popup auto-confirm observer is wired (VIP: answers the location popup from the mirror). */
let b2bMaFeesAutoConfirmAttached = false;

/** One-shot guard for the Magical Fees trigger-API probe (logs once per page). */
let b2bMaFeesTriggerProbed = false;

/**
 * DIAGNOSTIC (temporary): the Private Cellar pages need to open the Magical Fees location popup
 * themselves (the app only auto-shows it on b2c add-to-cart). The app's JS isn't in the theme, so
 * this dumps its window globals + likely DOM triggers once, so we can wire the real open call.
 * Logs under [cart-routing] ma_fees_trigger_probe.
 */
const probeMaFeesTriggerApi = function probeMaFeesTriggerApi() {
    if (b2bMaFeesTriggerProbed) return;
    b2bMaFeesTriggerProbed = true;
    /** @type {any} */
    const out = { windowKeys: [], globalShapes: {}, domTriggers: [] };
    try {
      const keys = Object.keys(window).filter((k) => /ma[_-]?fees?|magical/i.test(k));
      out.windowKeys = keys;
      keys.forEach((k) => {
        try {
          const v = /** @type {any} */ (window)[k];
          const t = typeof v;
          if (t === 'function') out.globalShapes[k] = 'function';
          else if (v && t === 'object') out.globalShapes[k] = Object.keys(v).slice(0, 50);
          else out.globalShapes[k] = t;
        } catch (_) { /* ignore */ }
      });
    } catch (_) { /* ignore */ }
    try {
      const sel = '[class*="ma-fees"],[id*="ma-fees"],[id*="ma_fees"],[data-ma-fees],[class*="magical"]';
      out.domTriggers = Array.from(document.querySelectorAll(sel))
        .slice(0, 25)
        .map((el) => ({ tag: el.tagName, id: el.id || '', cls: typeof el.className === 'string' ? el.className : '', role: el.getAttribute('role') || '' }));
    } catch (_) { /* ignore */ }
    try {
      out.totalWindowKeys = Object.keys(window).length;
    } catch (_) { /* ignore */ }
    /* Log as a STRING so the console doesn't collapse it to "{…}" and we can read it in one copy. */
    try {
      b2bCartRoutingLog('ma_fees_trigger_probe', JSON.stringify(out));
    } catch (_) {
      b2bCartRoutingLog('ma_fees_trigger_probe', out);
    }
};

/**
 * When the customer confirms a destination in the Magical Fees popup, that app writes
 * `__ma_fees_selected_location` — but it doesn't know about our Private Cellar cart, so the excise
 * line wouldn't update until the next add. Watch for that change and re-sync immediately so the
 * US/Sweden fee appears (or clears) the moment the destination is chosen.
 */
const attachMaFeesLocationWatch = function attachMaFeesLocationWatch() {
    if (b2bMaFeesLocationWatchAttached) return;
    b2bMaFeesLocationWatchAttached = true;
    /* Change detection uses the EFFECTIVE destination (confirmed key → mirror → raw key): an MF
     * wipe-on-empty-cart followed by our restore leaves it unchanged, so no sync fires for MF's
     * reset noise. A re-entrant sync here is not just wasteful — it raced the in-flight merge
     * (MF re-evaluates after the merge's own /cart/update.js POST) and could hit the brief
     * "storefront cart and theme cart both empty" window, where the sync drops the stored cart
     * id and strands the just-merged lines in an orphaned cart. */
    let lastCountry = getEffectiveMaFeesDestinationCountry();
    const onMaybeChanged = () => {
      const now = getEffectiveMaFeesDestinationCountry();
      if (now === lastCountry) return;
      lastCountry = now;
      b2bCartRoutingLog('ma_fees_location_changed', { country: now || '(empty)' });
      /* Force a repaint: the excise line just changed, so never let the fingerprint optimisation skip
       * re-rendering the drawer (that left the fee in the total but not in the visible line list). */
      cartState.lastRenderedCartFingerprint = '';
      void requestCartSync(undefined);
    };
    /* Same tab: Magical Fees saves the selection via localStorage.setItem — observe that write.
     * Patch Storage.prototype, NOT the localStorage instance: Firefox routes property
     * assignment on a Storage object through its named-property setter (it literally STORES a
     * key called "setItem" instead of shadowing the method), so an instance patch silently
     * never installs there — no mirror, no restore, popup on every add. Prototype methods are
     * plain JavaScript and patch identically in every browser. */
    try {
      const store = window.localStorage;
      const proto = typeof Storage !== 'undefined' ? Storage.prototype : null;
      if (proto && typeof proto.setItem === 'function' && typeof proto.removeItem === 'function') {
        const origSetItem = proto.setItem;
        const origRemoveItem = proto.removeItem;
        proto.setItem = function (/** @type {string} */ k, /** @type {string} */ v) {
          origSetItem.apply(this, arguments);
          if (this === store && k === MA_FEES_LOCATION_STORAGE_KEY) {
            /* Mirror every CONFIRMED destination so it survives MF's wipe-on-empty-cart reset
             * (the VIP merge keeps the native cart empty, so that wipe fires after every add). */
            try {
              if (parseConfirmedMaFeesLocation(v)) origSetItem.call(store, PC_DESTINATION_MIRROR_STORAGE_KEY, String(v));
            } catch (_) { /* ignore */ }
            try { onMaybeChanged(); } catch (_) { /* ignore */ }
          }
        };
        proto.removeItem = function (/** @type {string} */ k) {
          if (!(this === store && k === MA_FEES_LOCATION_STORAGE_KEY)) return origRemoveItem.apply(this, arguments);
          origRemoveItem.apply(this, arguments);
          /* Magical Fees deletes its location key whenever it sees the native cart empty (its
           * post-checkout reset). In VIP scope the merge empties the native cart by design, so
           * restore the confirmed destination instead of letting every add/page-change wipe it.
           * Non-VIP customers keep MF's intended reset behavior. The restore is SILENT — the
           * effective destination is unchanged (the mirror still feeds it), and these wipes fire
           * right after the merge's own /cart POSTs, so kicking a sync from here would race the
           * in-flight merge. */
          try {
            const mirroredRaw = getMirroredPcDestinationRaw();
            if (mirroredRaw && getEffectiveB2BCatalogScope(getConfig() || {}) === 'vip') {
              origSetItem.call(store, MA_FEES_LOCATION_STORAGE_KEY, mirroredRaw);
              b2bCartRoutingLog('ma_fees_location_wipe_restored', { country: getMaFeesSelectedCountry() || '(empty)' });
              return;
            }
          } catch (_) { /* ignore */ }
          try { onMaybeChanged(); } catch (_) { /* ignore */ }
        };
      }
      /* Clean up the junk the previous instance patch left behind in Firefox: its named-property
       * setter stored the patch function's SOURCE under literal "setItem"/"removeItem" keys. */
      try {
        ['setItem', 'removeItem'].forEach((junkKey) => {
          const junk = store.getItem(junkKey);
          if (junk && /^\s*function/.test(junk)) store.removeItem(junkKey);
        });
      } catch (_) { /* ignore */ }
    } catch (_) { /* ignore — fallbacks below */ }
    /* Cross-tab changes. */
    try {
      window.addEventListener('storage', (/** @type {StorageEvent} */ e) => {
        if (!e || e.key == null || e.key === MA_FEES_LOCATION_STORAGE_KEY) onMaybeChanged();
      });
    } catch (_) { /* ignore */ }
    /* Belt-and-braces: clicking the popup's confirm button, in case the write path is missed. */
    try {
      document.addEventListener('click', (/** @type {any} */ e) => {
        const t = e && e.target instanceof Element ? e.target : null;
        if (t && t.closest('.ma-fees__button--primary')) {
          setTimeout(onMaybeChanged, 300);
          setTimeout(onMaybeChanged, 1200);
        }
      });
    } catch (_) { /* ignore */ }
    /* Heal a wipe (or an unconfirmed ip-detected overwrite) that landed before this watch
     * attached — MF's init re-check runs off an async /cart.js fetch, so ordering isn't
     * guaranteed. Effectively silent: lastCountry above was already seeded from the mirror,
     * so the patched setItem's change check no-ops and no sync is kicked from attach. */
    try {
      const mirroredRaw = getMirroredPcDestinationRaw();
      const currentRaw = window.localStorage.getItem(MA_FEES_LOCATION_STORAGE_KEY);
      if (
        mirroredRaw &&
        !parseConfirmedMaFeesLocation(currentRaw) &&
        getEffectiveB2BCatalogScope(getConfig() || {}) === 'vip'
      ) {
        window.localStorage.setItem(MA_FEES_LOCATION_STORAGE_KEY, mirroredRaw);
        b2bCartRoutingLog('ma_fees_location_healed_on_attach', { country: getMaFeesSelectedCountry() || '(empty)' });
      }
    } catch (_) { /* ignore */ }
};

/**
 * Fill the Magical Fees location dialog from the mirrored confirmed destination and click
 * MF's own confirm button, so MF rebuilds + saves its in-memory state exactly as if the
 * customer re-answered. Works on a dialog that is NOT (yet) open: MF wires every handler
 * before calling showModal, and `dialog.close()` on a non-open dialog is a spec no-op, so
 * the confirm flow resolves MF's location promise either way.
 * @param {HTMLElement} dialog
 * @returns {'confirmed' | 'switched' | 'skip' | 'failed'}
 */
const tryAutoAnswerMaFeesLocationDialog = function tryAutoAnswerMaFeesLocationDialog(dialog) {
    const mirror = getMirroredPcDestination();
    if (!mirror) return 'skip';
    if (getEffectiveB2BCatalogScope(getConfig() || {}) !== 'vip') return 'skip';

    const countrySelect = /** @type {HTMLSelectElement | null} */ (dialog.querySelector('#ma-fees_country-select'));
    const changeLocationBtn = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-action="change-location"]'));

    /* "Detected location" variants have no country select — switch MF to its manual flow.
     * MF re-renders the same <dialog> and calls showModal again, re-entering the caller. */
    if (!countrySelect && changeLocationBtn) {
      b2bCartRoutingLog('ma_fees_autoconfirm', { step: 'switch_to_manual' });
      changeLocationBtn.click();
      return 'switched';
    }

    const confirmBtn = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-action="confirm-manual-location"]'));
    if (!countrySelect || !confirmBtn) {
      b2bCartRoutingLog('ma_fees_autoconfirm', { step: 'bail_unknown_markup' });
      return 'failed';
    }

    const countryValue = String(mirror.countryCode || '').toUpperCase() === 'OTHER' ? 'other' : String(mirror.countryCode || '');
    if (!countrySelect.querySelector('option[value="' + countryValue + '"]')) {
      b2bCartRoutingLog('ma_fees_autoconfirm', { step: 'bail_country_not_listed', country: countryValue });
      return 'failed';
    }
    countrySelect.value = countryValue;
    countrySelect.dispatchEvent(new Event('change', { bubbles: true }));

    /* MF shows a province select when the chosen country's fee rules have provinces. */
    const provinceContainer = /** @type {HTMLElement | null} */ (dialog.querySelector('#ma-fees_province-container'));
    const provinceSelect = /** @type {HTMLSelectElement | null} */ (dialog.querySelector('#ma-fees_province-select-manual'));
    if (provinceSelect && provinceContainer && provinceContainer.style.display !== 'none') {
      const provinceValue = String(mirror.provinceCode || '').toUpperCase() === 'OTHER' ? 'other' : String(mirror.provinceCode || '');
      if (provinceValue && provinceSelect.querySelector('option[value="' + provinceValue + '"]')) {
        provinceSelect.value = provinceValue;
        provinceSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (confirmBtn.disabled) {
      /* Mirror lacks something MF now requires (e.g. a province) — let the customer answer;
       * their confirm refreshes the mirror for next time. */
      b2bCartRoutingLog('ma_fees_autoconfirm', { step: 'bail_confirm_disabled', country: countryValue });
      return 'failed';
    }
    confirmBtn.click();
    b2bCartRoutingLog('ma_fees_autoconfirm', {
      step: 'confirmed',
      country: countryValue,
      province: String(mirror.provinceCode || '') || null,
    });
    return 'confirmed';
};

/**
 * Intercept showModal for the Magical Fees location dialog: MF nulls its in-memory location
 * on EVERY page load that sees an empty native cart (always true in VIP scope — the merge
 * keeps the theme cart empty), so the popup re-opens on the first add of every page no matter
 * what localStorage holds. Answering it from the mirror BEFORE it opens means the customer
 * never sees it again after their first confirmation. Anything we can't answer falls through
 * to the original showModal — first-time customers and non-VIP pages are untouched.
 */
const installMaFeesShowModalGuard = function installMaFeesShowModalGuard() {
    try {
      if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype) return;
      const origShowModal = HTMLDialogElement.prototype.showModal;
      if (typeof origShowModal !== 'function') return;
      HTMLDialogElement.prototype.showModal = function () {
        try {
          if (this && this.classList && this.classList.contains('ma-fees__dialog--location')) {
            const outcome = tryAutoAnswerMaFeesLocationDialog(/** @type {HTMLElement} */ (this));
            if (outcome === 'confirmed' || outcome === 'switched') {
              /* confirmed: MF's promise already resolved and the dialog stays closed.
               * switched: MF synchronously re-rendered + re-called showModal (handled re-entrantly). */
              b2bCartRoutingLog('ma_fees_autoconfirm', { step: 'suppressed_show_modal', outcome });
              return;
            }
          }
        } catch (err) {
          try { b2bCaptureError('ma-fees-showmodal-guard', err, {}); } catch (_) { /* ignore */ }
        }
        return origShowModal.apply(this, arguments);
      };
    } catch (_) { /* ignore — the observer fallback below still applies */ }
};

/**
 * Belt-and-braces fallback to the showModal guard: answers a Magical Fees location dialog
 * that managed to OPEN anyway (e.g. it was already open before this script ran). Hides the
 * dialog while answering; reveals it on any failure so the customer can answer normally.
 */
const attachMaFeesAutoConfirm = function attachMaFeesAutoConfirm() {
    if (b2bMaFeesAutoConfirmAttached) return;
    b2bMaFeesAutoConfirmAttached = true;
    if (typeof document === 'undefined' || !document.body) return;

    try {
      const style = document.createElement('style');
      style.textContent =
        'dialog.ma-fees__dialog--location[data-pc-autoconfirming]{visibility:hidden !important;}' +
        'dialog.ma-fees__dialog--location[data-pc-autoconfirming]::backdrop{opacity:0 !important;}';
      document.head.appendChild(style);
    } catch (_) { /* ignore — worst case the dialog flashes briefly */ }

    /** @param {HTMLElement} dialog */
    const revealDialog = (dialog) => {
      try { dialog.removeAttribute('data-pc-autoconfirming'); } catch (_) { /* ignore */ }
    };

    const maybeAutoConfirm = () => {
      const dialog = /** @type {HTMLElement | null} */ (document.querySelector('dialog.ma-fees__dialog--location'));
      if (!dialog) return;
      if (!dialog.hasAttribute('open')) {
        /* Reset per-open state so the next open gets a fresh attempt. */
        if (dialog.dataset.pcAutoconfirmTried) delete dialog.dataset.pcAutoconfirmTried;
        revealDialog(dialog);
        return;
      }
      if (dialog.dataset.pcAutoconfirmTried === 'true') return;
      /* Cheap eligibility pre-checks so ineligible opens are not marked as tried. */
      if (!getMirroredPcDestination()) return;
      if (getEffectiveB2BCatalogScope(getConfig() || {}) !== 'vip') return;
      dialog.dataset.pcAutoconfirmTried = 'true';
      dialog.setAttribute('data-pc-autoconfirming', 'true');

      try {
        const outcome = tryAutoAnswerMaFeesLocationDialog(dialog);
        if (outcome === 'switched') {
          /* MF re-rendered the same <dialog> as the manual flow — re-attempt on the new markup. */
          delete dialog.dataset.pcAutoconfirmTried;
          setTimeout(maybeAutoConfirm, 0);
          return;
        }
        /* confirmed → the dialog is closed anyway; failed/skip → reveal it for the customer. */
        revealDialog(dialog);
      } catch (err) {
        b2bCaptureError('ma-fees-autoconfirm', err, {});
        revealDialog(dialog);
      }
    };

    try {
      const observer = new MutationObserver(maybeAutoConfirm);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    } catch (_) { /* ignore */ }
    maybeAutoConfirm();
};

/** Same copy as the Magical Fees popup relabelling in layout/theme.liquid. */
const getPcDestinationPromptCopy = function getPcDestinationPromptCopy() {
    const lang = String((document.documentElement && document.documentElement.getAttribute('lang')) || 'en')
      .slice(0, 2)
      .toLowerCase();
    if (lang === 'fr') {
      return {
        title: 'Où souhaitez-vous expédier votre commande ?',
        body: 'Certaines destinations peuvent entraîner des frais supplémentaires. Veuillez confirmer la destination de votre commande afin que le total soit calculé correctement.',
        label: 'Destination d’expédition',
        placeholder: 'Sélectionner la destination',
        confirm: 'Confirmer la destination',
        notListed: 'Autres destinations',
        stateLabel: 'État / province',
        statePlaceholder: 'Sélectionnez votre état/province...',
        stateNotListed: 'Mon état/province n’est pas listé',
      };
    }
    return {
      title: 'Where should we ship your order?',
      body: 'Shipping to certain destinations may include additional fees. Please confirm where this order is going so your total is calculated correctly.',
      label: 'Shipping destination',
      placeholder: 'Select shipping destination',
      confirm: 'Confirm destination',
      notListed: 'Other destinations',
      stateLabel: 'State / province',
      statePlaceholder: 'Select your state/province...',
      stateNotListed: 'My state/province is not listed',
    };
};

/**
 * Show the prompt after a Private Cellar add when no destination is confirmed yet. Escape
 * closes it without writing (the next add re-asks); confirming writes the MF location key,
 * which the patched Storage.prototype mirrors and the location watcher turns into an
 * immediate excise re-sync.
 */
const maybeShowPcDestinationPrompt = function maybeShowPcDestinationPrompt() {
    try {
      if (getEffectiveB2BCatalogScope(getConfig() || {}) !== 'vip') return;
      if (isPcDestinationConfirmed()) return;
      /* If the real MF popup is somehow open (b2c line transiting the native cart), defer to it. */
      const mfOpen = document.querySelector('dialog.ma-fees__dialog--location[open]');
      if (mfOpen) return;
      if (document.querySelector('dialog[data-b2b-pc-destination-prompt][open]')) return;
      showPcDestinationPrompt();
    } catch (err) {
      b2bCaptureError('pc-destination-prompt', err, {});
    }
};

const showPcDestinationPrompt = function showPcDestinationPrompt() {
    const c = getPcDestinationPromptCopy();
    const dialog = document.createElement('dialog');
    dialog.className = 'primeurs-cart-guard-dialog b2b-pc-destination-dialog';
    dialog.setAttribute('data-b2b-pc-destination-prompt', 'true');
    dialog.style.border = '1px solid rgb(var(--color-foreground-rgb) / 0.25)';
    dialog.style.maxWidth = '26rem';
    const selectStyle =
      'width: 100%; padding: 8px 10px; font-size: 0.95rem; border: 1px solid rgb(var(--color-foreground-rgb) / 0.4); background: var(--color-background); color: var(--color-foreground); margin-top: 4px;';
    const countryOptionsHtml = PC_DESTINATION_COUNTRIES
      .map(([code, name]) => `<option value="${esc(code)}">${esc(name)}</option>`)
      .join('');
    const regionOptionsHtml = PC_DESTINATION_US_REGIONS
      .map(([code, name]) => `<option value="${esc(code)}">${esc(name)}</option>`)
      .join('');
    dialog.innerHTML =
      '<form method="dialog" class="primeurs-cart-guard-dialog__content">' +
        '<h3>' + esc(c.title) + '</h3>' +
        '<p>' + esc(c.body) + '</p>' +
        '<label style="display:block; font-size: 0.85em; opacity: 0.8;">' + esc(c.label) +
          '<select data-pc-dest-country style="' + selectStyle + '">' +
            '<option value="">' + esc(c.placeholder) + '</option>' +
            countryOptionsHtml +
            '<option value="other">' + esc(c.notListed) + '</option>' +
          '</select>' +
        '</label>' +
        '<div data-pc-dest-region-wrap style="display:none; margin-top: 10px;">' +
          '<label style="display:block; font-size: 0.85em; opacity: 0.8;">' + esc(c.stateLabel) +
            '<select data-pc-dest-region style="' + selectStyle + '">' +
              '<option value="">' + esc(c.statePlaceholder) + '</option>' +
              regionOptionsHtml +
              '<option value="other">' + esc(c.stateNotListed) + '</option>' +
            '</select>' +
          '</label>' +
        '</div>' +
        '<div class="primeurs-cart-guard-dialog__actions" style="display:flex; align-items:center; justify-content:flex-end; gap:0.75rem; flex-wrap:nowrap; margin-top: 14px;">' +
          '<button type="button" class="button button-primary" data-pc-dest-confirm disabled>' + esc(c.confirm) + '</button>' +
        '</div>' +
      '</form>';

    const countrySelect = /** @type {HTMLSelectElement} */ (dialog.querySelector('[data-pc-dest-country]'));
    const regionWrap = /** @type {HTMLElement} */ (dialog.querySelector('[data-pc-dest-region-wrap]'));
    const regionSelect = /** @type {HTMLSelectElement} */ (dialog.querySelector('[data-pc-dest-region]'));
    const confirmBtn = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-pc-dest-confirm]'));

    function cleanup() {
      try { dialog.close(); } catch (_) { /* ignore */ }
      dialog.remove();
    }

    function refreshState() {
      const country = countrySelect.value;
      const needsRegion = country === 'US';
      regionWrap.style.display = needsRegion ? 'block' : 'none';
      if (!country) {
        confirmBtn.disabled = true;
      } else if (needsRegion) {
        confirmBtn.disabled = !regionSelect.value;
      } else {
        confirmBtn.disabled = false;
      }
    }

    countrySelect.addEventListener('change', refreshState);
    regionSelect.addEventListener('change', refreshState);

    confirmBtn.addEventListener('click', () => {
      const country = countrySelect.value;
      if (!country) return;
      /** @type {any} */
      let loc;
      if (country === 'other') {
        loc = { countryCode: 'OTHER', countryName: 'Location not listed', provinceCode: null, provinceName: null, source: 'user_selected', confirmed: true, timestamp: Date.now() };
      } else {
        const countryName = countrySelect.selectedOptions[0]?.textContent?.trim() || country;
        let provinceCode = null;
        let provinceName = null;
        let provinceNotListed = false;
        if (country === 'US') {
          if (!regionSelect.value) return;
          if (regionSelect.value === 'other') {
            provinceCode = 'OTHER';
            provinceName = 'Province not listed';
            provinceNotListed = true;
          } else {
            provinceCode = regionSelect.value;
            provinceName = regionSelect.selectedOptions[0]?.textContent?.trim() || regionSelect.value;
          }
        }
        loc = { countryCode: country, countryName, provinceCode, provinceName, source: 'user_selected', confirmed: true, timestamp: Date.now() };
        if (provinceNotListed) loc.provinceNotListed = true;
      }
      /* Same key + shape MF's own confirm writes: the patched Storage.prototype mirrors it and
       * the location watcher re-syncs the excise; MF reads it as confirmed on its next init. */
      try {
        window.localStorage.setItem(MA_FEES_LOCATION_STORAGE_KEY, JSON.stringify(loc));
      } catch (_) { /* ignore */ }
      b2bCartRoutingLog('pc_destination_prompt', { step: 'confirmed', country: loc.countryCode, province: loc.provinceCode || null });
      cleanup();
    });

    /* Escape closes without writing — the next Private Cellar add re-asks. */
    dialog.addEventListener('cancel', () => {
      b2bCartRoutingLog('pc_destination_prompt', { step: 'dismissed' });
      cleanup();
    }, { once: true });

    document.body.appendChild(dialog);
    b2bCartRoutingLog('pc_destination_prompt', { step: 'opened' });
    dialog.showModal();
};

/**
 * Checkout round-trip handling: the pre-checkout cart snapshot (rebuild the
 * Storefront cart if Shopify empties it after an abandoned checkout), the
 * post-checkout/thank-you detection, and the cart fingerprint + line-input
 * builders shared with the sync orchestrator. Moved verbatim from legacy.js.
 */

const CHECKOUT_ABANDON_MAX_MS = 24 * 60 * 60 * 1000;

const PRE_CHECKOUT_SNAPSHOT_MAX_MS = 4 * 60 * 60 * 1000;

/** If session checkout flag expired but snapshot is newer than this, still try restore (referrer often empty). */
const PRE_CHECKOUT_SNAPSHOT_RESTORE_GRACE_MS = 45 * 60 * 1000;

const clearB2BCheckoutRedirectSessionFlag = function clearB2BCheckoutRedirectSessionFlag() {
    try {
      sessionStorage.removeItem(SESSION_KEY_B2B_CHECKOUT_REDIRECT);
    } catch (_) {
      /* ignore */
    }
};

/**
 * After visiting Shopify checkout, the Storefront Cart often returns empty lines for the same GID
 * (lines are associated with checkout). Session flag = our Checkout click; referrer = return navigation.
 */
const shouldTreatEmptyCartAsPostCheckoutAbandon = function shouldTreatEmptyCartAsPostCheckoutAbandon() {
    try {
      const ts = sessionStorage.getItem(SESSION_KEY_B2B_CHECKOUT_REDIRECT);
      if (ts) {
        const age = Date.now() - Number(ts);
        if (Number.isFinite(age) && age >= 0 && age < CHECKOUT_ABANDON_MAX_MS) return true;
      }
      const ref = document.referrer || '';
      if (/\/cart\/c\/|\/checkouts\/|checkout\.shopify\.com/i.test(ref)) return true;
    } catch (_) {
      /* ignore */
    }
    return false;
};

const preCheckoutSnapshotStorageKey = function preCheckoutSnapshotStorageKey() {
    return withCartPersistenceKey(LOCAL_KEY_B2B_PRE_CHECKOUT_SNAPSHOT_BASE);
};

/** @param {any} cart */
const buildStorefrontLineInputsFromCart = function buildStorefrontLineInputsFromCart(cart) {
    const edges = cart?.lines?.edges || [];
    const lines = [];
    for (let i = 0; i < edges.length; i++) {
      const node = edges[i]?.node;
      const merchandiseId = node?.merchandise?.id;
      const qty = Number(node?.quantity) || 0;
      if (!merchandiseId || qty < 1) continue;
      lines.push({ merchandiseId, quantity: qty });
    }
    return lines;
};

/** @param {any} cart */
const savePreCheckoutCartSnapshotFromCart = function savePreCheckoutCartSnapshotFromCart(cart) {
    try {
      const lines = buildStorefrontLineInputsFromCart(cart);
      if (!lines.length) {
        localStorage.removeItem(preCheckoutSnapshotStorageKey());
        return;
      }
      const payload = {
        ts: Date.now(),
        cartIdTail: cart?.id ? String(cart.id).slice(-18) : '',
        lines,
      };
      localStorage.setItem(preCheckoutSnapshotStorageKey(), JSON.stringify(payload));
    } catch (_) {
      /* ignore */
    }
};

const readPreCheckoutCartSnapshotRaw = function readPreCheckoutCartSnapshotRaw() {
    try {
      const raw = localStorage.getItem(preCheckoutSnapshotStorageKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.lines) || !parsed.lines.length) return null;
      const age = Date.now() - Number(parsed.ts);
      if (!Number.isFinite(age) || age < 0 || age > PRE_CHECKOUT_SNAPSHOT_MAX_MS) return null;
      return parsed;
    } catch (_) {
      return null;
    }
};

const clearPreCheckoutCartSnapshot = function clearPreCheckoutCartSnapshot() {
    try {
      localStorage.removeItem(preCheckoutSnapshotStorageKey());
    } catch (_) {
      /* ignore */
    }
};

const indicatesPostOrderOrThankYouNavigation = function indicatesPostOrderOrThankYouNavigation() {
    try {
      const ref = document.referrer || '';
      if (/thank[-_]you|thank_you|order[-_]confirmation|\/orders\/|\/thank_you/i.test(ref)) return true;
      if (/checkout\.shopify\.com.*\/thank_you/i.test(ref)) return true;
      const qs = window.location.search || '';
      if (/\b(order_id|checkout[_-]?complete|ordered|purchase)\b/i.test(qs)) return true;
    } catch (_) {
      /* ignore */
    }
    return false;
};

/**
 * The order note + discount code are localStorage-persisted (scope-keyed) so they survive drawer
 * repaints, and are pushed onto the Storefront cart at checkout. Once the cart they belong to is
 * gone — the order was placed (or checkout was abandoned past recovery) — they must NOT pre-fill the
 * customer's next Private Cellar cart. Call this from every empty/gone-cart return path in the sync
 * orchestrator: it only clears when there is positive evidence the customer just came through
 * checkout (our redirect session flag, a checkout referrer, or a thank-you/order referrer), so a
 * note on an in-progress cart that merely read empty for a moment is never wiped.
 * @returns {boolean} true when this was a post-checkout return and the local state was cleared.
 */
const clearB2BCartAnnotationsIfPostCheckout = function clearB2BCartAnnotationsIfPostCheckout() {
    if (!shouldTreatEmptyCartAsPostCheckoutAbandon() && !indicatesPostOrderOrThankYouNavigation()) {
      return false;
    }
    clearB2BCheckoutRedirectSessionFlag();
    setStoredB2BCartNote('');
    setStoredB2BDiscountCode('');
    return true;
};

const shouldAttemptPreCheckoutSnapshotRestore = function shouldAttemptPreCheckoutSnapshotRestore() {
    if (indicatesPostOrderOrThankYouNavigation()) return false;
    const snap = readPreCheckoutCartSnapshotRaw();
    if (!snap?.lines?.length) return false;
    if (shouldTreatEmptyCartAsPostCheckoutAbandon()) return true;
    const age = Date.now() - Number(snap.ts);
    if (Number.isFinite(age) && age >= 0 && age < PRE_CHECKOUT_SNAPSHOT_RESTORE_GRACE_MS) return true;
    return false;
};

/**
 * @param {number} runId
 * @param {any} opts
 * @param {HTMLElement} root
 * @param {Element | null} drawerBody
 */
const maybeRestoreStorefrontCartFromPreCheckoutSnapshot = async function maybeRestoreStorefrontCartFromPreCheckoutSnapshot(runId, opts, root, drawerBody) {
    if (!shouldAttemptPreCheckoutSnapshotRestore()) {
      if (indicatesPostOrderOrThankYouNavigation()) clearPreCheckoutCartSnapshot();
      return false;
    }
    const snap = readPreCheckoutCartSnapshotRaw();
    if (!snap?.lines?.length) return false;
    try {
      await createStorefrontCartWithLines(snap.lines);
      clearPreCheckoutCartSnapshot();
      clearB2BCheckoutRedirectSessionFlag();
      const newId = getStoredStorefrontCartId();
      const fullCart = newId ? await fetchStorefrontCart(newId) : null;
      if (!fullCart) return false;
      root.setAttribute('data-b2b-cart-checkout-url', fullCart.checkoutUrl || '');
      const effScopeSnap = getEffectiveB2BCatalogScope(getConfig() || {});
      if (effScopeSnap) root.setAttribute('data-b2b-catalog-scope', effScopeSnap);
      const fp = buildCartFingerprint(fullCart);
      cartState.lastRenderedCartFingerprint = fp;
      if (drawerBody) paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), fullCart, opts);
      applyPrivateCellarMinimumCheckoutUi(root, fullCart);
      schedulePrivateCellarMinimumCheckoutUiRetries(root);
      const rq = Number(fullCart.totalQuantity) || 0;
      setB2BCartCount(rq, 'restored-pre-checkout-snapshot');
      return true;
    } catch (err) {
      console.warn('B2B cart: pre-checkout snapshot restore failed:', err);
      b2bCartDebugLog('run#' + runId, 'checkout abandon: snapshot restore failed', err);
      return false;
    }
};

/** @param {any} cart */
const buildCartFingerprint = function buildCartFingerprint(cart) {
    if (!cart) return 'empty';
    const lines = (cart.lines?.edges || []).map((/** @type {any} */ e) => {
      const n = e?.node;
      return `${n?.id || ''}:${n?.quantity || 0}:${n?.cost?.totalAmount?.amount || ''}`;
    });
    return `${cart.id || ''}|${cart.totalQuantity || 0}|${cart.cost?.subtotalAmount?.amount || ''}|${cart.cost?.totalAmount?.amount || ''}|${lines.join(',')}`;
};

/**
 * Transient cart feedback: the order-request-sent toast and the missing-VIP-
 * channel warning (off-channel products that cannot enter the Private Cellar
 * cart). Moved verbatim from legacy.js.
 */

const B2B_ORDER_REQUEST_SENT_TOAST_MS = 12000;

/**
 * Warning toast shown when a product can't enter the Private Cellar cart because it isn't published
 * to the VIP/Headless sales channel. Reuses the order-sent toast styling with a warning accent.
 * @param {string[]} productTitles
 */
const showB2BMissingVipChannelAlert = function showB2BMissingVipChannelAlert(productTitles) {
    const cfg = getConfig() || {};
    const titles = [...new Set((productTitles || []).map((t) => String(t || '').trim()).filter(Boolean))];
    const intro = cfg.alertMissingVipChannel || 'Oops — looks like this product is missing the VIP sales channel:';
    const namesHtml = titles.length ? `<p class="b2b-channel-alert__names">${titles.map((t) => esc(t)).join('<br>')}</p>` : '';
    const html = `<p>${esc(intro)}</p>${namesHtml}`;
    document.querySelectorAll('.b2b-channel-alert-toast').forEach((n) => n.remove());
    const toast = document.createElement('div');
    toast.className = 'b2b-order-sent-toast b2b-channel-alert-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.innerHTML = html;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('b2b-order-sent-toast--visible'));
    window.setTimeout(() => {
      toast.classList.remove('b2b-order-sent-toast--visible');
      window.setTimeout(() => toast.remove(), 280);
    }, 9000);
};

/** @param {string} html */
const showB2BOrderRequestSentToast = function showB2BOrderRequestSentToast(html) {
    const content = (html || '').trim();
    if (!content) return;
    document.querySelectorAll('.b2b-order-sent-toast').forEach((n) => n.remove());
    const toast = document.createElement('div');
    toast.className = 'b2b-order-sent-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = content;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('b2b-order-sent-toast--visible'));
    window.setTimeout(() => {
      toast.classList.remove('b2b-order-sent-toast--visible');
      window.setTimeout(() => toast.remove(), 280);
    }, B2B_ORDER_REQUEST_SENT_TOAST_MS);
};

/**
 * Native (/cart.js) cart helpers used by the VIP merge: locale-prefixed ajax
 * endpoints, variant/selling-plan gid conversion, line aggregation by
 * variant+plan, conversion to Storefront line inputs, and quantity updates on
 * the theme cart. Moved verbatim from legacy.js.
 */

const getAjaxCartChangeUrl = function getAjaxCartChangeUrl() {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_change_url) {
      return Theme.routes.cart_change_url;
    }
    return '/cart/change.js';
};

/** @param {string | number | null | undefined} variantId */
const variantGidFromAjaxId = function variantGidFromAjaxId(variantId) {
    const id = String(variantId || '').replace(/\D/g, '');
    if (!id) return '';
    return `gid://shopify/ProductVariant/${id}`;
};

/** @param {string | number | null | undefined} planId */
const sellingPlanGidFromAjax = function sellingPlanGidFromAjax(planId) {
    if (planId == null || planId === '') return '';
    const raw = String(planId);
    if (raw.startsWith('gid://')) return raw;
    const id = raw.replace(/\D/g, '');
    return id ? `gid://shopify/SellingPlan/${id}` : '';
};

/** @param {any} item Ajax `/cart.js` line */
const ajaxCartLineAggregateKey = function ajaxCartLineAggregateKey(item) {
    if (!item) return '';
    const vid = item.variant_id;
    const planRaw = item.selling_plan_allocation?.selling_plan?.id;
    const planKey = planRaw != null && planRaw !== '' ? String(planRaw) : '';
    return `${vid}::${planKey}`;
};

/** @param {{ variant_id: any, selling_plan_id?: any }} row Row from {@link aggregateAjaxCartItems} */
const ajaxAggregatedRowKey = function ajaxAggregatedRowKey(row) {
    if (!row) return '';
    const vid = row.variant_id;
    const planRaw = row.selling_plan_id;
    const planKey = planRaw != null && planRaw !== '' ? String(planRaw) : '';
    return `${vid}::${planKey}`;
};

/** @param {any[]} items */
const aggregateAjaxCartItems = function aggregateAjaxCartItems(items) {
    const map = new Map();
    for (const item of items || []) {
      const vid = item.variant_id;
      const planRaw = item.selling_plan_allocation?.selling_plan?.id;
      const planKey = planRaw != null && planRaw !== '' ? String(planRaw) : '';
      const key = `${vid}::${planKey}`;
      const qty = parseInt(String(item.quantity), 10) || 0;
      if (qty < 1) continue;
      const prev = map.get(key);
      if (prev) {
        prev.quantity += qty;
      } else {
        map.set(key, { variant_id: vid, quantity: qty, selling_plan_id: planRaw });
      }
    }
    return [...map.values()];
};

/** @param {any[]} rows */
const ajaxRowsToStorefrontLineInputs = function ajaxRowsToStorefrontLineInputs(rows) {
    const lines = [];
    for (const row of rows) {
      const merchandiseId = variantGidFromAjaxId(row.variant_id);
      if (!merchandiseId) continue;
      const qty = parseInt(String(row.quantity), 10) || 0;
      if (qty < 1) continue;
      const sp = sellingPlanGidFromAjax(row.selling_plan_id);
      lines.push(sp ? { merchandiseId, quantity: qty, sellingPlanId: sp } : { merchandiseId, quantity: qty });
    }
    return lines;
};

/**
 * @param {string} lineKey
 * @param {number} quantity
 * @param {number} [lineIndex1]
 */
const changeAjaxCartLineQuantity = async function changeAjaxCartLineQuantity(lineKey, quantity, lineIndex1) {
    const url = getAjaxCartChangeUrl();
    const qty = Math.max(0, quantity);
    const tryBodies = [];
    if (lineKey) tryBodies.push({ id: lineKey, quantity: qty });
    if (lineIndex1 != null && lineIndex1 >= 1) tryBodies.push({ line: lineIndex1, quantity: qty });
    if (!tryBodies.length) throw new Error('Cart line not found');
    let lastErr = null;
    /** @type {any} */
    let data = {};
    let ok = false;
    for (const body of tryBodies) {
      try {
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        data = await res.json().catch(() => ({}));
        const errList = data.errors;
        const hasErr =
          (Array.isArray(errList) && errList.length > 0) ||
          (typeof errList === 'string' && errList.length > 0);
        const looksLikeCart = data.items != null || typeof data.item_count === 'number';
        if (res.ok && !hasErr && (looksLikeCart || qty === 0)) {
          ok = true;
          break;
        }
        lastErr = hasErr
          ? typeof errList === 'string'
            ? errList
            : errList?.[0] || 'Cart update failed'
          : res.statusText || 'Cart update failed';
      } catch (e) {
        lastErr = e;
      }
    }
    if (!ok) throw new Error(lastErr ? String(lastErr) : 'Cart update failed');
    try {
      const itemCount = typeof data.item_count === 'number' ? data.item_count : 0;
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'b2b-vip-merge',
            data: { itemCount },
          },
        })
      );
    } catch (_) {
      /* non-critical */
    }
};

const getAjaxCartUpdateUrl = function getAjaxCartUpdateUrl() {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_update_url) {
      return Theme.routes.cart_update_url;
    }
    return '/cart/update.js';
};

/**
 * Set several theme Ajax cart line quantities in a single `/cart/update.js` call (keyed by line key).
 * Used by the merge to remove all just-merged lines at once instead of one `/cart/change.js` per line.
 * @param {Record<string, number>} updatesByKey
 */
const setAjaxCartLineQuantitiesByKey = async function setAjaxCartLineQuantitiesByKey(updatesByKey) {
    const keys = Object.keys(updatesByKey || {});
    if (!keys.length) return;
    const res = await fetch(getAjaxCartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updatesByKey }),
    });
    const data = await res.json().catch(() => ({}));
    const errList = data && data.errors;
    const hasErr =
      (Array.isArray(errList) && errList.length > 0) || (typeof errList === 'string' && errList.length > 0);
    if (!res.ok || hasErr) {
      throw new Error(hasErr ? (typeof errList === 'string' ? errList : errList[0]) : res.statusText || 'Cart update failed');
    }
};

/**
 * Fee line mutations: keep the insurance (percent of product subtotal) and
 * excise/duty (destination-based) placeholder lines in sync with the cart via
 * the cents-as-quantity trick (fee variants cost 0.01/unit; non-Plus store, no
 * Cart Transform). Returns true when a mutation was made so the caller
 * re-fetches. Moved verbatim from legacy.js.
 */

/**
 * Reconcile the cart's denomination lines for ONE fee type against a target amount (in cents):
 * decompose the target to a single denomination line ({@link decomposeFeeToDenominations}) and
 * add/update/remove cart lines so the fee's lines match it. `targetCents <= 0` removes every line
 * for this fee. Shared by insurance + excise so the two reconcilers can't diverge.
 * @param {string} cartId
 * @param {any} cart
 * @param {Array<{ gid: string, cents: number }>} denominations
 * @param {string[]} gids  all variant GIDs for this fee (ladder)
 * @param {number} targetCents
 * @param {string} tag  mutation-name + warning tag, e.g. 'InsuranceFee' / 'ExciseFee'
 * @returns {Promise<boolean>} true when a mutation was made (caller re-fetches)
 */
const reconcileFeeDenominationLines = async function reconcileFeeDenominationLines(cartId, cart, denominations, gids, targetCents, tag) {
    const edges = cart?.lines?.edges || [];
    const feeLines = edges
      .map((/** @type {any} */ e) => e?.node)
      .filter((/** @type {any} */ n) => {
        const id = normalizeInsuranceFeeVariantGid(n?.merchandise?.id || '');
        return !!id && gids.indexOf(id) !== -1;
      });

    /** @type {Map<string, number>} */
    const targets = new Map();
    const decomposed = decomposeFeeToDenominations(targetCents, denominations);
    for (const part of decomposed) {
      targets.set(part.gid, part.quantity);
    }

    /* Diagnostic: what the code INTENDS (targetCents/decomposed) vs what's actually on the cart
     * (each fee line's real per-unit price + how many lines). Pinpoints a "fee charged ≠ computed"
     * report: a unitPrice ≠ the denomination = market/price-list adjustment on the fee variant;
     * existingCount > 1 of the same fee = stale duplicate lines being summed in the drawer. */
    b2bCartRoutingLog('fee_reconcile', {
      tag,
      intended: (Number(targetCents) / 100).toFixed(2),
      targetCents,
      existingCount: feeLines.length,
      existing: feeLines.map((/** @type {any} */ l) => {
        const q = Number(l?.quantity) || 0;
        const tot = parseFloat(l?.cost?.totalAmount?.amount || '0') || 0;
        return { gidTail: String(l?.merchandise?.id || '').slice(-12), qty: q, total: tot.toFixed(2), unitPrice: q ? (tot / q).toFixed(4) : '0' };
      }),
      decomposed: decomposed.map((/** @type {any} */ d) => ({ gidTail: String(d.gid).slice(-12), denomCents: d.cents, qty: d.quantity })),
    });

    if (!feeLines.length && !targets.size) return false;

    /* Reconcile against the target: keep ONE line per wanted denomination (update its quantity if it
     * drifted) and REMOVE every other fee line — wrong denominations AND any DUPLICATE line of the
     * same variant. A partial earlier sync can leave duplicate/stray fee lines that the drawer would
     * otherwise SUM into one inflated row (the "€7.90 vs €9.72" report). Iterating every line — not a
     * gid-keyed map that silently collapses duplicates — guarantees the extras are swept. */
    /** @type {string[]} */
    const removeIds = [];
    /** @type {Array<{ id: string, quantity: number }>} */
    const updates = [];
    /** @type {Array<{ merchandiseId: string, quantity: number }>} */
    const adds = [];
    for (const ln of feeLines) {
      const gid = normalizeInsuranceFeeVariantGid(ln?.merchandise?.id || '');
      const want = targets.get(gid) || 0;
      if (want > 0) {
        if (ln.quantity !== want) updates.push({ id: ln.id, quantity: want });
        targets.delete(gid); // covered — any further line with this variant is a duplicate → removed
      } else {
        removeIds.push(ln.id); // unwanted denomination, or a duplicate of an already-covered one
      }
    }
    for (const [gid, qty] of targets) {
      if (qty > 0) adds.push({ merchandiseId: gid, quantity: qty });
    }

    if (!removeIds.length && !updates.length && !adds.length) return false;

    if (removeIds.length) {
      const data = await storefrontFetch(
        `mutation Remove${tag}($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart { id totalQuantity }
            userErrors { field message }
          }
        }`,
        { cartId, lineIds: removeIds }
      );
      if (data?.cartLinesRemove?.userErrors?.length) {
        console.warn(`B2B ${tag}: remove error`, data.cartLinesRemove.userErrors);
      }
    }
    if (updates.length) {
      const data = await storefrontFetch(
        `mutation Update${tag}($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart { id totalQuantity }
            userErrors { field message }
          }
        }`,
        { cartId, lines: updates }
      );
      if (data?.cartLinesUpdate?.userErrors?.length) {
        console.warn(`B2B ${tag}: update error`, data.cartLinesUpdate.userErrors);
      }
    }
    if (adds.length) {
      const data = await storefrontFetch(
        `mutation Add${tag}($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart { id totalQuantity }
            userErrors { field message }
          }
        }`,
        { cartId, lines: adds }
      );
      if (data?.cartLinesAdd?.userErrors?.length) {
        console.warn(`B2B ${tag}: add error`, data.cartLinesAdd.userErrors);
      }
    }
    return true;
};

/**
 * Keeps the insurance fee in sync with the cart using a cents-as-quantity approach across a
 * DENOMINATION LADDER: the fee product has variants priced €0.01 / €0.10 / €1 / €10 / €100, and
 * we greedily decompose the fee (in cents) into per-variant quantities. Example: 1% of a €3,600
 * cart = €36.00 = 3,600 cents → 3 × €10 + 6 × €1 (9 line-units) instead of 3,600 × €0.01.
 *
 * This ensures the correct fee TOTAL appears at checkout without requiring a Cart Transform
 * Function (which needs Shopify Plus for custom apps). With only the legacy €0.01 variant
 * configured (no ladder), the decomposition collapses to a single line at qty = cents — identical
 * to the previous behaviour.
 *
 * Returns true when a mutation was made (caller should re-fetch the cart).
 */
/**
 * @param {string} cartId
 * @param {any} cart
 * @returns {Promise<boolean>}
 */
const syncInsuranceFeeLine = async function syncInsuranceFeeLine(cartId, cart) {
    const cfg = getConfig() || {};
    const denominations = getInsuranceFeeDenominations(cfg);
    if (!denominations.length) return false;
    const feePercent = Number(cfg.insuranceFeePercent) || 0;

    /* Compute the percentage on product lines only — never on the insurance or excise/duty fees. */
    const nonFeeEdges = (cart?.lines?.edges || []).filter((/** @type {any} */ e) => !isAnyFeeLine(e?.node, cfg));
    let targetCents = 0;
    if (nonFeeEdges.length && feePercent) {
      const subtotal = nonFeeEdges.reduce(
        (/** @type {number} */ sum, /** @type {any} */ e) => sum + (parseFloat(e?.node?.cost?.totalAmount?.amount || '0') || 0),
        0
      );
      targetCents = getInsuranceFeeTargetQty(subtotal, cfg);
    }

    return reconcileFeeDenominationLines(cartId, cart, denominations, denominations.map((d) => d.gid), targetCents, 'InsuranceFee');
};

/**
 * Keeps the Private Cellar excise/duty placeholder line in sync (VIP scope only), mirroring
 * {@link syncInsuranceFeeLine}. Returns true when a mutation was made (caller re-fetches).
 * @param {string} cartId
 * @param {any} cart
 * @returns {Promise<boolean>}
 */
const syncExciseFeeLine = async function syncExciseFeeLine(cartId, cart) {
    const cfg = getConfig() || {};
    const scope = getEffectiveB2BCatalogScope(cfg);
    const denominations = getExciseFeeDenominations(cfg);
    if (scope !== 'vip' || !denominations.length) {
      b2bCartRoutingLog('excise_fee_skip', {
        reason: scope !== 'vip' ? 'scope_not_vip' : 'no_excise_variant_configured',
        scope,
        country: getPrivateCellarDestinationCountry(cfg) || '(empty)',
        exciseVariantConfigured: !!denominations.length,
      });
      return false;
    }

    const productEdges = (cart?.lines?.edges || []).filter((/** @type {any} */ e) => !isAnyFeeLine(e?.node, cfg));
    const productSubtotal = getB2BCartProductSubtotal(cart, cfg);
    const bottleCount = getB2BCartBottleCount(cart, cfg);
    const targetCents = productEdges.length ? getExciseFeeTargetQty(productSubtotal, bottleCount, cfg) : 0;

    b2bCartRoutingLog('excise_fee_compute', {
      country: getPrivateCellarDestinationCountry(cfg) || '(empty)',
      countrySource: getMaFeesSelectedCountry() ? 'ma_fees' : (cfg.localizationCountry ? 'localization' : 'none'),
      usPercent: Number(cfg.exciseUsPercent) || 0,
      sePerBottle: Number(cfg.exciseSePerBottle) || 0,
      productLineCount: productEdges.length,
      productSubtotal,
      bottleCount,
      targetCents,
      feeAmount: (targetCents / 100).toFixed(2),
    });

    /* Recognise excise lines across BOTH the US and Sweden products (union of GIDs) so a destination
     * switch removes the wrong-product line; ADD from the destination-specific `denominations`. */
    return reconcileFeeDenominationLines(cartId, cart, denominations, getExciseFeeGids(cfg), targetCents, 'ExciseFee');
};

/**
 * VIP merge / strip rules: when the native cart should fold into the Private
 * Cellar Storefront cart, when non-catalog lines must be stripped from the B2B
 * cart, and whether boutique lines are still pending merge (keeps the checkout
 * gate honest). Moved verbatim from legacy.js.
 */

/** Theme editor: Private Cellar storefront cart off → `data-enable-private-cellar-cart="false"` on `<body>`. */
const isPrivateCellarStorefrontCartEnabled = function isPrivateCellarStorefrontCartEnabled() {
    try {
      if (typeof document === 'undefined' || !document.body) return true;
      return document.body.getAttribute('data-enable-private-cellar-cart') !== 'false';
    } catch (_) {
      return true;
    }
};

const isVipCatalogScope = function isVipCatalogScope() {
    return getScopeKeySuffix() === 'vip';
};

const isB2bCatalogScope = function isB2bCatalogScope() {
    return getScopeKeySuffix() === 'b2b';
};

/**
 * Enforce B2B-only lines for the For Professionals Storefront cart (tag = B2B).
 * Never run on VIP / Private Cellar carts: those products are VIP-tagged, not B2B — stripping
 * would remove every line right after add. VIP scope must win over body class alone.
 */
const shouldStripNonB2bLinesFromStorefrontCart = function shouldStripNonB2bLinesFromStorefrontCart() {
    const isPrivateCellarPage =
      typeof document !== 'undefined' && document.body
        ? document.body.classList.contains('private-cellar-nav')
        : false;
    const isB2bProfessionalsPage =
      typeof document !== 'undefined' && document.body
        ? document.body.classList.contains('professionals-b2b-nav')
        : false;
    try {
      if (typeof document === 'undefined' || !document.body) return false;
      if (document.body.classList.contains('private-cellar-nav')) return false;
    } catch (_) {
      return false;
    }
    if (isVipCatalogScope()) return false;
    // VIP win must also honour the stamped catalog scope, not just last-config / body class.
    // The cart-ui is stamped `vip` (and the cart id persisted under `_vip`) whenever any config
    // block is VIP, even if a lower [data-b2b-config] forces scopeKey to `b2b`; stripping then
    // would wipe the VIP cart. The live For Professionals page is never stamped `vip`, so this
    // veto is a no-op there and the body-class clause below remains the authoritative B2B signal.
    try {
      const cartRoot = document.querySelector('[data-b2b-cart-ui]');
      const stamped = cartRoot ? String(cartRoot.getAttribute('data-b2b-catalog-scope') || '').toLowerCase() : '';
      if (stamped === 'vip') return false;
    } catch (_) {
      /* ignore */
    }
    const shouldStrip = (
      isB2bCatalogScope() ||
      (typeof document !== 'undefined' &&
        document.body &&
        document.body.classList.contains('professionals-b2b-nav'))
    );
    b2bCartRoutingLog('strip_non_b2b_lines_decision', {
      shouldStrip,
      scopeSuffix: getScopeKeySuffix(),
      isVipCatalogScope: isVipCatalogScope(),
      isB2bCatalogScope: isB2bCatalogScope(),
      isPrivateCellarPage,
      isB2bProfessionalsPage,
    });
    return shouldStrip;
};

/**
 * Theme Ajax cart → VIP Storefront merge: when catalog scope is `vip`, regular (non-VIP-tagged,
 * non-B2B-tagged) lines from `/cart.js` are moved into the scoped VIP Storefront cart — **including
 * on the main Online Store** (e.g. `/collections/all`) so VIP customers keep one Private Cellar cart
 * for public boutique lines + Cave Privée lines (see test flow in docs / QA).
 *
 * Merge runs **one aggregated variant row at a time**: if one variant is not published to the
 * Headless / Storefront API sales channel (“merchandise … does not exist”), others still merge.
 * Skips For Professionals B2B pages (`professionals-b2b-nav`).
 */
const shouldMergeAjaxCartIntoPrivateCellarStorefrontCart = function shouldMergeAjaxCartIntoPrivateCellarStorefrontCart() {
    // Stamp-aware VIP detection (matches getCartPersistenceScopeSuffix / minimum-order gate): the
    // VIP cart is persisted under `_vip` whenever the cart-ui is stamped `vip`, even if a lower
    // [data-b2b-config] forces scopeKey to `b2b`. Using isVipCatalogScope() (last-config only)
    // would skip the boutique merge in that disagreement case. The professionals-b2b-nav guard
    // below still hard-blocks the live B2B page regardless of how this resolves.
    const isVip = getEffectiveB2BCatalogScope(getConfig() || {}) === 'vip';
    if (!isVip) {
      b2bCartRoutingLog('merge_ajax_to_private_cellar_decision', {
        shouldMerge: false,
        reason: 'scope_not_vip',
        scopeSuffix: getScopeKeySuffix(),
      });
      return false;
    }
    if (!isPrivateCellarStorefrontCartEnabled()) {
      b2bCartRoutingLog('merge_ajax_to_private_cellar_decision', {
        shouldMerge: false,
        reason: 'private_cellar_cart_disabled_theme',
        scopeSuffix: getScopeKeySuffix(),
      });
      return false;
    }
    try {
      if (typeof document === 'undefined' || !document.body) {
        b2bCartRoutingLog('merge_ajax_to_private_cellar_decision', {
          shouldMerge: false,
          reason: 'missing_document_or_body',
          scopeSuffix: getScopeKeySuffix(),
        });
        return false;
      }
      const isPrivateCellarPage = document.body.classList.contains('private-cellar-nav');
      if (document.body.classList.contains('professionals-b2b-nav')) {
        b2bCartRoutingLog('merge_ajax_to_private_cellar_decision', {
          shouldMerge: false,
          reason: 'b2b_professionals_page',
          scopeSuffix: getScopeKeySuffix(),
          isPrivateCellarPage,
          bodyClasses: Array.from(document.body.classList || []),
        });
        return false;
      }
      b2bCartRoutingLog('merge_ajax_to_private_cellar_decision', {
        shouldMerge: true,
        scopeSuffix: getScopeKeySuffix(),
        isPrivateCellarPage,
        bodyClasses: Array.from(document.body.classList || []),
      });
      return true;
    } catch (_) {
      return false;
    }
};

/**
 * Regular boutique lines still sitting in the theme Ajax cart that belong in the single Private
 * Cellar cart. Used to keep the €120 gate honest on the *merged* total: while such lines are
 * pending we must not block checkout on a stale/partial VIP Storefront cart. Off-channel boutique
 * products (which can never enter the Storefront cart) are excluded — counting them would leave
 * checkout falsely enabled with nothing left to merge.
 * @returns {Promise<boolean>}
 */
const hasPendingMergeableThemeCartLines = async function hasPendingMergeableThemeCartLines() {
    if (!shouldMergeAjaxCartIntoPrivateCellarStorefrontCart()) return false;
    try {
      const ajaxCart = await fetchAjaxCartJson();
      const items = Array.isArray(ajaxCart?.items) ? ajaxCart.items : [];
      if (!items.length) return false;
      const emptyTagMap = new Map();
      const mergeable = items.filter((/** @type {any} */ item) =>
        isRegularOnlineStoreLineForVipMerge(item, emptyTagMap)
      );
      if (!mergeable.length) return false;
      const unavailable = await findUnavailableStorefrontVariantGids(
        mergeable.map((/** @type {any} */ item) => variantGidFromAjaxId(item.variant_id))
      );
      return mergeable.some(
        (/** @type {any} */ item) => !unavailable.has(variantGidFromAjaxId(item.variant_id))
      );
    } catch (_) {
      return false;
    }
};

/**
 * For-professionals carts are keyed by scope `b2b` and must only contain products tagged for that
 * catalog. Drops anything else (stale ids, prior cross-scope bugs) so regular boutique lines
 * cannot linger in the B2B Storefront cart.
 */
/**
 * @param {string} cartId
 * @param {any} cart
 */
const removeNonB2BTaggedLinesFromStorefrontCart = async function removeNonB2BTaggedLinesFromStorefrontCart(cartId, cart) {
    if (!cartId || !cart || !shouldStripNonB2bLinesFromStorefrontCart()) return cart;
    const required = getB2bCatalogProductTagLower();
    if (!required) return cart;
    const edges = cart?.lines?.edges || [];
    const lineIds = [];
    for (let i = 0; i < edges.length; i++) {
      const node = edges[i]?.node;
      const tags = node?.merchandise?.product?.tags;
      const lowered = Array.isArray(tags) ? tags.map((t) => String(t).toLowerCase()) : [];
      // Never strip the auto-managed fee lines (insurance + excise/duty) regardless of product tags
      const isFee = isAnyFeeLine(node, getConfig() || {});
      if (!isFee && !lowered.includes(required) && node?.id) lineIds.push(node.id);
    }
    if (!lineIds.length) return cart;
    const mutation = `
      mutation b2bCartStripNonCatalogLines($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            id
            checkoutUrl
            totalQuantity
            cost {
              subtotalAmount { amount currencyCode }
              totalAmount { amount currencyCode }
            }
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  cost { totalAmount { amount currencyCode } }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image { url altText }
                      product {
                        title
                        handle
                        tags
                        featuredImage { url altText }
                      }
                    }
                  }
                }
              }
            }
          }
          userErrors { field message }
        }
      }`;
    try {
      const data = await storefrontFetch(mutation, { cartId, lineIds });
      const errors = data.cartLinesRemove?.userErrors;
      if (errors?.length) {
        console.warn('B2B cart: could not remove non-catalog lines:', errors.map((/** @type {any} */ e) => e.message).join('; '));
        return cart;
      }
      const out = data.cartLinesRemove?.cart;
      if (out?.id) {
        setStoredStorefrontCartId(out.id);
        return out;
      }
    } catch (err) {
      console.warn('B2B cart: strip non-catalog lines failed:', err);
    }
    return cart;
};

/**
 * Optimistic cart-line UI: instant paint of a just-added line (reconciled or
 * rolled back by the next sync), optimistic qty/remove updates, shimmer and
 * per-line error states, and the merged-cart preview built from native lines.
 * Moved verbatim from legacy.js.
 */

/**
 * Build a Storefront-cart-shaped line node from a theme Ajax `/cart.js` line — enough for the drawer
 * renderer to show an optimistic pending row (title, image, options, quantity). The price is an
 * estimate that renders under a shimmer until the authoritative sync confirms it, so we never show a
 * misleading confirmed number.
 * @param {any} item Ajax `/cart.js` line
 * @param {string} currency
 */
const buildOptimisticStorefrontNodeFromAjaxLine = function buildOptimisticStorefrontNodeFromAjaxLine(item, currency) {
    const variantGid = variantGidFromAjaxId(item?.variant_id);
    const qty = parseInt(String(item?.quantity), 10) || 0;
    const lineTotalMajor = (parseFloat(String(item?.final_line_price ?? item?.line_price ?? 0)) || 0) / 100;
    const opts = Array.isArray(item?.options_with_values)
      ? item.options_with_values
          .map((/** @type {any} */ o) => ({ name: String(o?.name || ''), value: String(o?.value || '') }))
          .filter((/** @type {any} */ o) => o.name)
      : [];
    const img = item?.featured_image?.url || item?.image || '';
    return {
      /* Synthetic id — never collides with a real Storefront line id, and lets us target the row for the
       * pending shimmer / control-disabling after paint. */
      id: `b2b-optimistic:${String(item?.variant_id || '')}`,
      quantity: qty,
      cost: { totalAmount: { amount: String(lineTotalMajor), currencyCode: currency } },
      merchandise: {
        id: variantGid,
        title: String(item?.variant_title || ''),
        quantityAvailable: null,
        selectedOptions: opts,
        image: img ? { url: img, altText: String(item?.product_title || item?.title || '') } : null,
        product: {
          title: String(item?.product_title || item?.title || ''),
          handle: String(item?.handle || ''),
          tags: [],
          collections: { edges: [] },
          featuredImage: img ? { url: img } : null,
        },
      },
    };
};

/**
 * Synthesize the cart the Private Cellar drawer will *become* once the just-added theme line merges
 * into the Storefront cart: the last confirmed Storefront cart (incl. fee lines) plus the mergeable
 * theme Ajax lines, summing quantity where a variant is already present. Returns null when there is
 * nothing to add, or when a primeurs/regular mix means the slow path must run its clear-cart modal
 * first (we never optimistically show a cart we're about to force-clear).
 * @param {any} snapshotCart Last confirmed Storefront cart ({@link cartState.b2bLastStorefrontCartForMinUi})
 * @param {any} ajaxCart Theme `/cart.js`
 * @returns {{ cart: any, pendingLineIds: Set<string> } | null}
 */
const buildOptimisticMergedCart = function buildOptimisticMergedCart(snapshotCart, ajaxCart) {
    const cfg = getConfig() || {};
    const emptyTagMap = new Map();
    const ajaxItems = Array.isArray(ajaxCart?.items) ? ajaxCart.items : [];
    const mergeable = ajaxItems.filter((/** @type {any} */ i) => isRegularOnlineStoreLineForVipMerge(i, emptyTagMap));
    if (!mergeable.length) return null;

    /* Mixing en-primeur and regular wines triggers a clear-cart modal in the merge — don't optimistically
     * paint a mixed cart we're about to clear. Detect a conflict against the confirmed cart's composition. */
    const snapComp = getStorefrontCartPrimeursComposition(snapshotCart);
    const incomingPrimeurs = mergeable.some((/** @type {any} */ i) => ajaxLineLooksPrimeurs(i));
    const incomingRegular = mergeable.some((/** @type {any} */ i) => !ajaxLineLooksPrimeurs(i));
    const unionPrimeurs = snapComp.hasPrimeurs || incomingPrimeurs;
    const unionRegular = snapComp.hasRegular || incomingRegular;
    if (unionPrimeurs && unionRegular) return null;

    const currency =
      snapshotCart?.cost?.subtotalAmount?.currencyCode ||
      snapshotCart?.cost?.totalAmount?.currencyCode ||
      cfg.currency ||
      'EUR';

    /* Start from the confirmed Storefront lines (product + fee lines) so fees and prior items carry over. */
    /** @type {any[]} */
    const edges = (snapshotCart?.lines?.edges || []).map((/** @type {any} */ e) => ({ node: e.node }));
    /** @type {Set<string>} ids of lines that are new or whose quantity changed — these get the shimmer. */
    const pendingLineIds = new Set();

    const aggregated = aggregateAjaxCartItems(mergeable);
    for (const row of aggregated) {
      const gid = variantGidFromAjaxId(row.variant_id);
      if (!gid) continue;
      const addQty = parseInt(String(row.quantity), 10) || 0;
      if (addQty < 1) continue;
      const existing = edges.find(
        (/** @type {any} */ e) => !isAnyFeeLine(e.node, cfg) && e.node?.merchandise?.id === gid
      );
      if (existing && existing.node?.id) {
        existing.node = { ...existing.node, quantity: (existing.node.quantity || 0) + addQty };
        pendingLineIds.add(existing.node.id);
      } else {
        const src =
          mergeable.find((/** @type {any} */ i) => variantGidFromAjaxId(i.variant_id) === gid) || {
            variant_id: row.variant_id,
          };
        const node = buildOptimisticStorefrontNodeFromAjaxLine({ ...src, quantity: addQty }, currency);
        edges.push({ node });
        pendingLineIds.add(node.id);
      }
    }
    if (!pendingLineIds.size) return null;

    /* Estimated totals (hidden under the shimmer until the authoritative sync confirms them). */
    let totalQty = 0;
    let estSubtotal = 0;
    for (const e of edges) {
      const n = e.node;
      estSubtotal += parseFloat(n?.cost?.totalAmount?.amount || '0') || 0;
      if (!isAnyFeeLine(n, cfg)) totalQty += n?.quantity || 0;
    }
    const estStr = String(estSubtotal);
    return {
      cart: {
        id: snapshotCart?.id || '',
        checkoutUrl: snapshotCart?.checkoutUrl || '',
        totalQuantity: totalQty,
        cost: {
          subtotalAmount: { amount: estStr, currencyCode: currency },
          totalAmount: { amount: estStr, currencyCode: currency },
        },
        lines: { edges },
      },
      pendingLineIds,
    };
};

/**
 * Paint the just-added theme line into the Private Cellar drawer immediately, before the (slower)
 * Storefront merge + fetch confirm it: pending rows show a shimmering price and disabled controls.
 * Returns true when it painted a pending line — the caller then passes `reconcileOptimistic` to the
 * following {@link requestCartSync}, which repaints the authoritative cart (reconcile) or, on error,
 * rolls back to the last confirmed cart. Best-effort — any failure just returns false so the add
 * falls through to a normal sync and can never be blocked or broken by this.
 * @returns {Promise<boolean>}
 */
const optimisticallyPaintPrivateCellarAdd = async function optimisticallyPaintPrivateCellarAdd() {
    try {
      const root = /** @type {HTMLElement | null} */ (document.querySelector('[data-b2b-cart-ui]'));
      const drawerBody = root?.querySelector('[data-b2b-cart-drawer-body]');
      if (!root || !drawerBody) return false;
      if (getEffectiveB2BCatalogScope(getConfig() || {}) !== 'vip') return false;
      const ajaxCart = await fetchAjaxCartJson();
      const built = buildOptimisticMergedCart(cartState.b2bLastStorefrontCartForMinUi, ajaxCart);
      if (!built) return false;
      /* Force the next authoritative sync to repaint — its real fingerprint will differ from this sentinel. */
      cartState.lastRenderedCartFingerprint = '__b2b_optimistic__';
      paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), built.cart, {});
      for (const id of built.pendingLineIds) {
        const li = drawerBody.querySelector(`[data-b2b-line-id="${id}"]`);
        if (li instanceof HTMLElement) li.classList.add('b2b-updating', 'b2b-cart-line--optimistic');
      }
      setB2BCartTotalLoadingState(root, true);
      const checkoutBtn = root.querySelector('[data-b2b-cart-checkout]');
      if (checkoutBtn instanceof HTMLButtonElement) checkoutBtn.disabled = true;
      return true;
    } catch (e) {
      b2bCartRoutingLog('optimistic_add_paint_failed', {
        error: e && e.message ? String(e.message) : String(e || ''),
      });
      return false;
    }
};

/**
 * Build a Storefront-cart-shaped line node for a DIRECT Storefront add (the catalog's own
 * `data-b2b-buy` buttons add straight to the Storefront cart — there is no native `/cart.js` line to
 * read, unlike the VIP theme-merge path). It carries only what the drawer needs to paint a pending row
 * (title, image, variant label, quantity); the price renders under a shimmer until the authoritative
 * sync confirms it, so case-pack/locale price math is left to the server and we never show a misleading
 * number.
 * @param {string} variantGid
 * @param {number} qty
 * @param {{ title?: string, imageUrl?: string, variantLabel?: string, volumeLabel?: string } | null | undefined} hints
 * @param {string} currency
 */
const buildOptimisticStorefrontNodeFromBuyHints = function buildOptimisticStorefrontNodeFromBuyHints(variantGid, qty, hints, currency) {
    const h = hints || {};
    /** @type {Array<{ name: string, value: string }>} */
    const opts = [];
    if (h.volumeLabel) opts.push({ name: 'Volume', value: String(h.volumeLabel) });
    const img = String(h.imageUrl || '');
    return {
      /* Synthetic id (same scheme as the merge path) — never collides with a real Storefront line id,
       * and lets us target the row for the pending shimmer / control-disabling after paint. */
      id: `b2b-optimistic:${String(variantGid || '')}`,
      quantity: qty,
      cost: { totalAmount: { amount: '0', currencyCode: currency } },
      merchandise: {
        id: String(variantGid || ''),
        title: String(h.variantLabel || ''),
        quantityAvailable: null,
        selectedOptions: opts,
        image: img ? { url: img, altText: String(h.title || '') } : null,
        product: {
          title: String(h.title || ''),
          handle: '',
          tags: [],
          collections: { edges: [] },
          featuredImage: img ? { url: img } : null,
        },
      },
    };
};

/**
 * Synthesize the cart the drawer will *become* once a direct Storefront add lands: the last confirmed
 * Storefront cart (incl. fee lines) with the just-added variant either bumped (already present) or
 * appended as a synthetic pending row. Returns null when there is nothing to add, or when the incoming
 * product would mix en-primeur and regular wines against the confirmed cart — that triggers a clear-cart
 * modal in the slow path, and we never optimistically paint a cart we may be about to force-clear.
 * Mirrors {@link buildOptimisticMergedCart}.
 * @param {any} snapshotCart Last confirmed Storefront cart ({@link cartState.b2bLastStorefrontCartForMinUi})
 * @param {string} variantGid
 * @param {number} addQty
 * @param {{ title?: string, imageUrl?: string, variantLabel?: string, volumeLabel?: string, isPrimeurs?: boolean } | null | undefined} hints
 * @param {B2BConfig | null | undefined} [cfgArg]
 * @returns {{ cart: any, pendingLineIds: Set<string> } | null}
 */
const buildOptimisticDirectAddCart = function buildOptimisticDirectAddCart(snapshotCart, variantGid, addQty, hints, cfgArg) {
    const cfg = cfgArg || getConfig() || {};
    const gid = String(variantGid || '');
    const qty = parseInt(String(addQty), 10) || 0;
    if (!gid || qty < 1) return null;

    /* Best-effort primeurs/regular conflict check from the confirmed cart's composition + the incoming
     * product (DOM-derived flag). The authoritative guard re-checks with a live fetch before the real
     * add, so a wrong guess here only costs an extra paint/rollback, never correctness. */
    const snapComp = getStorefrontCartPrimeursComposition(snapshotCart);
    const incomingPrimeurs = !!(hints && hints.isPrimeurs === true);
    const unionPrimeurs = snapComp.hasPrimeurs || incomingPrimeurs;
    const unionRegular = snapComp.hasRegular || !incomingPrimeurs;
    if (unionPrimeurs && unionRegular) return null;

    const currency =
      snapshotCart?.cost?.subtotalAmount?.currencyCode ||
      snapshotCart?.cost?.totalAmount?.currencyCode ||
      cfg.currency ||
      'EUR';

    /* Start from the confirmed Storefront lines (product + fee lines) so fees and prior items carry over. */
    /** @type {any[]} */
    const edges = (snapshotCart?.lines?.edges || []).map((/** @type {any} */ e) => ({ node: e.node }));
    /** @type {Set<string>} */
    const pendingLineIds = new Set();

    const existing = edges.find(
      (/** @type {any} */ e) => !isAnyFeeLine(e.node, cfg) && e.node?.merchandise?.id === gid
    );
    if (existing && existing.node?.id) {
      existing.node = { ...existing.node, quantity: (existing.node.quantity || 0) + qty };
      pendingLineIds.add(existing.node.id);
    } else {
      const node = buildOptimisticStorefrontNodeFromBuyHints(gid, qty, hints, currency);
      edges.push({ node });
      pendingLineIds.add(node.id);
    }

    /* Estimated totals (hidden under the shimmer until the authoritative sync confirms them). */
    let totalQty = 0;
    let estSubtotal = 0;
    for (const e of edges) {
      const n = e.node;
      estSubtotal += parseFloat(n?.cost?.totalAmount?.amount || '0') || 0;
      if (!isAnyFeeLine(n, cfg)) totalQty += n?.quantity || 0;
    }
    const estStr = String(estSubtotal);
    return {
      cart: {
        id: snapshotCart?.id || '',
        checkoutUrl: snapshotCart?.checkoutUrl || '',
        totalQuantity: totalQty,
        cost: {
          subtotalAmount: { amount: estStr, currencyCode: currency },
          totalAmount: { amount: estStr, currencyCode: currency },
        },
        lines: { edges },
      },
      pendingLineIds,
    };
};

/**
 * Paint the just-added line into the cart drawer immediately for the DIRECT Storefront buy path, before
 * the (slower) `cartLinesAdd` + cart fetch confirm it: the pending row shows a shimmering price and
 * disabled controls. Returns true when it painted — the caller then passes `reconcileOptimistic` to the
 * following {@link requestCartSync}, which repaints the authoritative cart (reconcile) or, on error,
 * rolls back to the last confirmed cart. Works for both VIP and B2B scope. Best-effort — any failure (or
 * a primeurs conflict) just returns false so the add falls through to a normal sync and can never be
 * blocked or broken by this.
 * @param {string} variantGid
 * @param {number} qty
 * @param {{ title?: string, imageUrl?: string, variantLabel?: string, volumeLabel?: string, isPrimeurs?: boolean } | null | undefined} hints
 * @returns {Promise<boolean>}
 */
const optimisticallyPaintDirectStorefrontAdd = async function optimisticallyPaintDirectStorefrontAdd(variantGid, qty, hints) {
    try {
      const root = /** @type {HTMLElement | null} */ (document.querySelector('[data-b2b-cart-ui]'));
      const drawerBody = root?.querySelector('[data-b2b-cart-drawer-body]');
      if (!root || !drawerBody) return false;
      const built = buildOptimisticDirectAddCart(cartState.b2bLastStorefrontCartForMinUi, variantGid, qty, hints, getConfig() || {});
      if (!built) return false;
      /* Force the next authoritative sync to repaint — its real fingerprint will differ from this sentinel. */
      cartState.lastRenderedCartFingerprint = '__b2b_optimistic__';
      paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), built.cart, {});
      for (const id of built.pendingLineIds) {
        const li = drawerBody.querySelector(`[data-b2b-line-id="${id}"]`);
        if (li instanceof HTMLElement) li.classList.add('b2b-updating', 'b2b-cart-line--optimistic');
      }
      setB2BCartTotalLoadingState(root, true);
      const checkoutBtn = root.querySelector('[data-b2b-cart-checkout]');
      if (checkoutBtn instanceof HTMLButtonElement) checkoutBtn.disabled = true;
      return true;
    } catch (e) {
      b2bCartRoutingLog('optimistic_direct_add_paint_failed', {
        error: e && e.message ? String(e.message) : String(e || ''),
      });
      return false;
    }
};

/** @param {HTMLElement | null | undefined} lineNode */
const applyB2BCartLineShimmer = function applyB2BCartLineShimmer(lineNode) {
    if (!lineNode) return;
    lineNode.classList.add('b2b-updating');
    const b2bRoot = /** @type {HTMLElement | null} */ (document.querySelector('[data-b2b-cart-ui]'));
    if (b2bRoot) setB2BCartTotalLoadingState(b2bRoot, true);
};

/**
 * Show a transient inline error message on a B2B cart line, mirroring the
 * `.cart-items__error` pattern used by the regular cart. The error is
 * appended below the quantity selector and auto-clears after a short delay
 * (or when the line successfully updates).
 * @param {HTMLElement | null | undefined} lineNode
 * @param {string} message
 */
const showB2BCartLineError = function showB2BCartLineError(lineNode, message) {
    if (!lineNode) return;
    let err = /** @type {HTMLElement | null} */ (lineNode.querySelector('[data-b2b-cart-line-error]'));
    if (!err) {
      err = document.createElement('div');
      err.setAttribute('data-b2b-cart-line-error', '');
      err.className = 'cart-items__error b2b-cart-line__error';
      err.setAttribute('role', 'alert');
      err.style.cssText = 'grid-column: 1 / -1; color: #c0392b; font-size: var(--cart-font-size--sm, 0.875rem); margin-top: 4px;';
      const row = lineNode.querySelector('.b2b-cart-line__row') || lineNode;
      row.appendChild(err);
    }
    err.textContent = message;
    err.hidden = false;
    // Auto-dismiss after a few seconds so it doesn't linger if the user
    // ignores it and goes on to do something else.
    if (err.dataset.b2bCartLineErrorTimer) {
      window.clearTimeout(parseInt(err.dataset.b2bCartLineErrorTimer, 10));
    }
    const tid = window.setTimeout(() => { if (err) err.hidden = true; }, 4000);
    err.dataset.b2bCartLineErrorTimer = String(tid);
};

/** @param {HTMLElement | null | undefined} lineNode */
const clearB2BCartLineError = function clearB2BCartLineError(lineNode) {
    if (!lineNode) return;
    const err = /** @type {HTMLElement | null} */ (lineNode.querySelector('[data-b2b-cart-line-error]'));
    if (err) err.hidden = true;
};

/**
 * @param {number} stock
 * @returns {string}
 */
const getB2BMaxStockMessage = function getB2BMaxStockMessage(stock) {
    const translations = (typeof window !== 'undefined' && /** @type {any} */ (window).Theme && /** @type {any} */ (window).Theme.translations) || {};
    const template = translations.custom_quantity_exceeds_stock || 'Allocation limit reached';
    return template.replace('__COUNT__', String(stock));
};

/**
 * @param {HTMLElement | null} lineNode
 * @param {number} newQty
 */
const applyB2BCartOptimisticQty = function applyB2BCartOptimisticQty(lineNode, newQty) {
    if (!lineNode) return;
    const input = /** @type {HTMLInputElement | null} */ (lineNode.querySelector('.b2b-cart-line__qty-selector input'));
    if (input) input.value = String(newQty);
    const select = /** @type {HTMLSelectElement | null} */ (lineNode.querySelector('[data-b2b-cart-qty-select]'));
    if (select) {
      const lineStockRaw = lineNode.getAttribute('data-b2b-line-stock');
      const lineStock = parseInt(String(lineStockRaw || '-1'), 10);
      const unlimited = isNaN(lineStock) || lineStock < 0;
      const matching = select.querySelector(`option[value="${String(newQty)}"]`);
      if (matching) {
        select.value = String(newQty);
      } else {
        const stockForVariant = unlimited ? -1 : lineStock;
        if (unlimited || newQty <= (stockForVariant < 0 ? Infinity : stockForVariant)) {
          b2bSetCustomQuantity(select, newQty);
        }
      }
    }
    const variantP = lineNode.querySelector('.cart-items__variant');
    if (variantP) {
      const text = variantP.textContent || '';
      const updated = text.replace(/\s*x\s*\d+/, ` x ${newQty}`);
      if (updated !== text) variantP.textContent = updated;
    }
    lineNode.setAttribute('data-b2b-line-qty', String(newQty));
    applyB2BCartLineShimmer(lineNode);
};

/** @param {HTMLElement | null | undefined} lineNode */
const applyB2BCartOptimisticRemove = function applyB2BCartOptimisticRemove(lineNode) {
    if (!lineNode) return;
    lineNode.style.setProperty('max-height', lineNode.scrollHeight + 'px');
    lineNode.classList.add('b2b-removing');
    const b2bRoot = /** @type {HTMLElement | null} */ (document.querySelector('[data-b2b-cart-ui]'));
    if (b2bRoot) setB2BCartTotalLoadingState(b2bRoot, true);
};

const clearB2BCartShimmers = function clearB2BCartShimmers() {
    const root = document.querySelector('[data-b2b-cart-ui]');
    if (!root) return;
    root.querySelectorAll('.b2b-updating').forEach((/** @type {any} */ el) => el.classList.remove('b2b-updating'));
    root.querySelectorAll('.b2b-shimmer').forEach((/** @type {any} */ el) => el.classList.remove('b2b-shimmer'));
    root.querySelectorAll('text-component[shimmer]').forEach((/** @type {any} */ el) => el.removeAttribute('shimmer'));
    root.querySelectorAll('.b2b-removing').forEach((/** @type {any} */ el) => el.classList.remove('b2b-removing'));
};

/**
 * Same mechanism as assets/component-cart-items.js (`cartTotal?.shimmer()`): {@link TextComponent} `[shimmer]`.
 * @param {HTMLElement | null | undefined} root
 * @param {boolean} loading
 */
const setB2BCartTotalLoadingState = function setB2BCartTotalLoadingState(root, loading) {
    if (!(root instanceof HTMLElement)) return;
    const totals = root.querySelectorAll('.b2b-cart-total-container text-component.cart__total-value');
    totals.forEach((/** @type {Element} */ el) => {
      if (loading) el.setAttribute('shimmer', '');
      else el.removeAttribute('shimmer');
    });
};

/**
 * Order-request form UI: panel HTML, target email resolution, the hidden
 * contact-form iframe and the native Shopify contact-form submit (hCaptcha
 * compatible). Moved verbatim from legacy.js.
 */

/* Scope/key helpers, storage-key constants and stored cart-id/discount/note accessors — moved to ./persistence.js */
const B2B_ORDER_REQUEST_CONTACT_IFRAME_NAME = 'b2b-order-request-contact-iframe';

/** @returns {string} */
const getOrderRequestTargetEmail = function getOrderRequestTargetEmail() {
    const cfg = getConfig() || {};
    return (cfg.orderRequestEmail || cfg.shopEmail || '').trim();
};

/** @param {any} cart */
const renderB2BOrderRequestFormHtml = function renderB2BOrderRequestFormHtml(cart) {
    const cfg = getConfig() || {};
    const targetEmail = getOrderRequestTargetEmail();
    const gids = getInsuranceFeeGids(cfg);
    const lines = cart?.lines?.edges || [];
    // The insurance fee uses a cents-as-quantity trick across a denomination ladder (several
    // €0.01/€1/€10… lines). Render it as ONE read-only priced line (matching the cart drawer)
    // instead of "× 209", and base "has items" on real product lines so an only-fee cart counts as
    // empty. Its amount is already inside the totals.
    const hasItems = lines.some((/** @type {any} */ edge) => !isInsuranceFeeLine(edge?.node, gids));
    const customerName = cfg.customerName || '';
    const customerEmail = cfg.customerEmail || '';
    const discountCode = getStoredB2BDiscountCode();

    const insuranceNodes = lines
      .map((/** @type {any} */ e) => e?.node)
      .filter((/** @type {any} */ n) => isInsuranceFeeLine(n, gids));
    const insuranceTotal = insuranceNodes.reduce(
      (/** @type {number} */ s, /** @type {any} */ n) => s + (parseFloat(n?.cost?.totalAmount?.amount || '0') || 0),
      0
    );
    const insuranceCur = insuranceNodes[0]?.cost?.totalAmount?.currencyCode || cfg.currency || 'EUR';
    const firstInsuranceLineId = insuranceNodes[0]?.id || '';

    const listHtml = hasItems
      ? lines.map((/** @type {any} */ edge) => {
        const line = edge?.node;
        const qty = line?.quantity || 0;
        const productTitle = line?.merchandise?.product?.title || '';
        const variantTitle = line?.merchandise?.title || '';
        if (isInsuranceFeeLine(line, gids)) {
          if ((line?.id || '') !== firstInsuranceLineId) return '';
          const feeTitle = productTitle || variantTitle || '1% Shipping Insurance';
          return `<li style="margin:0;padding:0;line-height:1.45;">${esc(feeTitle)} — ${esc(formatMoney(insuranceTotal, insuranceCur))}</li>`;
        }
        return `<li style="margin:0;padding:0;line-height:1.45;">${esc(productTitle)}${variantTitle ? ` — ${esc(variantTitle)}` : ''} × ${qty}</li>`;
      }).join('')
      : `<li style="margin:0;padding:0;line-height:1.45;">${esc(cfg.orderRequestEmptyLine || '(empty)')}</li>`;

    return `
      <div class="cart-drawer__header b2b-cart-drawer__header">
        <span class="cart-drawer__heading h3"><span class="b2b-cart-drawer__title-cluster"><span class="b2b-cart-drawer__title-text h3">${esc(cfg.orderRequestTitle || 'Order request')}</span></span></span>
        <button type="button" class="button close-button cart-drawer__close-button button-unstyled" data-b2b-cart-close aria-label="${esc(cfg.cartCloseLabel || 'Close')}">
          <span class="svg-wrapper">${ICON_CLOSE}</span>
        </button>
      </div>
      <div class="cart-drawer__content b2b-cart-drawer__content motion-reduce" style="padding: 16px; display:flex; flex-direction:column; gap: 14px;">
        <div style="display:flex; flex-direction:column; gap: 8px;">
          <p style="margin:0; opacity:.85; line-height:1.5;">
            ${esc(cfg.orderRequestIntro || '')}
          </p>
        </div>

        <div style="border: 1px solid rgb(var(--color-foreground-rgb,0 0 0) / .12); border-radius: 12px; padding: 12px;">
          <p style="margin:0 0 8px;"><strong>${esc(cfg.orderRequestSummary || 'Summary')}</strong></p>
          <ul style="margin:0; padding:0 0 0 18px;">${listHtml}</ul>
          ${formatB2BCartTotalsOrderRequestBlockHtml(cart)}
          ${discountCode ? `<p style="margin:6px 0 0; opacity:.75;"><strong>${esc(cfg.orderRequestDiscountLabel || 'Discount code:')}</strong> ${esc(discountCode)}</p>` : ''}
        </div>

        <form data-b2b-order-request-form style="display:flex; flex-direction:column; gap: 10px;">
          <div style="display:grid; grid-template-columns: 1fr; gap: 10px;">
            <label style="display:flex; flex-direction:column; gap: 6px;">
              <span style="font-size:.9rem; opacity:.85;">${esc(cfg.orderRequestCompany || 'Company')}</span>
              <input name="company" type="text" autocomplete="organization" style="min-height:40px;padding:8px 10px;border:1px solid var(--color-border,#d4d6d0);background:transparent" required>
            </label>
            <label style="display:flex; flex-direction:column; gap: 6px;">
              <span style="font-size:.9rem; opacity:.85;">${esc(cfg.orderRequestContactName || 'Contact name')}</span>
              <input name="name" type="text" autocomplete="name" value="${esc(customerName)}" style="min-height:40px;padding:8px 10px;border:1px solid var(--color-border,#d4d6d0);background:transparent" required>
            </label>
            <label style="display:flex; flex-direction:column; gap: 6px;">
              <span style="font-size:.9rem; opacity:.85;">${esc(cfg.orderRequestContactEmail || 'Contact email')}</span>
              <input name="email" type="email" autocomplete="email" value="${esc(customerEmail)}" style="min-height:40px;padding:8px 10px;border:1px solid var(--color-border,#d4d6d0);background:transparent" required>
            </label>
            <label style="display:flex; flex-direction:column; gap: 6px;">
              <span style="font-size:.9rem; opacity:.85;">${esc(cfg.orderRequestPhoneOptional || 'Phone (optional)')}</span>
              <input name="phone" type="tel" autocomplete="tel" style="min-height:40px;padding:8px 10px;border:1px solid var(--color-border,#d4d6d0);background:transparent">
            </label>
            <label style="display:flex; flex-direction:column; gap: 6px;">
              <span style="font-size:.9rem; opacity:.85;">${esc(cfg.orderRequestNotesOptional || 'Notes (optional)')}</span>
              <textarea name="notes" rows="4" style="padding:8px 10px;border:1px solid var(--color-border,#d4d6d0);background:transparent;resize:vertical"></textarea>
            </label>
          </div>

          <div class="cart__ctas" style="display:grid; grid-template-columns: 1fr; gap: 10px;">
            <button type="submit" class="button" ${!hasItems ? 'disabled' : ''}>${esc(cfg.orderRequestSend || 'Send order request')}</button>
            <button type="button" class="button button--secondary" data-b2b-order-request-copy ${(!hasItems) ? 'disabled' : ''}>${esc(cfg.orderRequestCopy || 'Copy request')}</button>
            <button type="button" class="button button--tertiary" data-b2b-order-request-back>${esc(cfg.orderRequestBack || 'Back to cart')}</button>
          </div>
          <div data-b2b-order-request-feedback style="font-size:.85rem; opacity:.85; min-height: 1.1em;"></div>
        </form>
      </div>
    `;
};

/**
 * @param {{ subject: string, body: string, contactEmail: string, contactName: string, phone: string }} param0
 */
const submitB2BOrderRequestViaShopifyContactForm = function submitB2BOrderRequestViaShopifyContactForm({ subject, body, contactEmail, contactName, phone }) {
    const shopifyForm = document.getElementById('B2BOrderRequestContactForm');
    if (!(shopifyForm instanceof HTMLFormElement)) {
      throw new Error((getConfig() || {}).errorFormNotFound || 'Form not found');
    }
    const fullMessage = `${subject}\n\n${body}`;
    setB2BOrderRequestContactField(shopifyForm, 'contact[email]', contactEmail);
    setB2BOrderRequestContactField(shopifyForm, 'contact[name]', contactName);
    setB2BOrderRequestContactField(shopifyForm, 'contact[phone]', phone);
    setB2BOrderRequestContactField(shopifyForm, 'contact[body]', fullMessage);
    ensureB2BOrderRequestContactIframe();
    shopifyForm.setAttribute('target', B2B_ORDER_REQUEST_CONTACT_IFRAME_NAME);
    shopifyForm.requestSubmit();
};

const ensureB2BOrderRequestContactIframe = function ensureB2BOrderRequestContactIframe() {
    const existing = document.querySelector(`iframe[name="${B2B_ORDER_REQUEST_CONTACT_IFRAME_NAME}"]`);
    if (existing instanceof HTMLIFrameElement) return existing;
    const iframe = document.createElement('iframe');
    iframe.name = B2B_ORDER_REQUEST_CONTACT_IFRAME_NAME;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(iframe);
    return iframe;
};

/**
 * Remaining line-level cart mutations: add line(s) to an existing Storefront
 * cart, persist the order note onto the cart, checkout-redirect bookkeeping and
 * the discount-applied checkout URL. Moved verbatim from legacy.js.
 */

/**
 * @param {string} checkoutUrl
 * @param {string} discountCode
 */
const buildCheckoutUrlWithDiscount = function buildCheckoutUrlWithDiscount(checkoutUrl, discountCode) {
    if (!checkoutUrl) return '';
    if (!discountCode) return checkoutUrl;
    try {
      const url = new URL(checkoutUrl);
      url.searchParams.set('discount', discountCode);
      return url.toString();
    } catch (_) {
      const sep = checkoutUrl.includes('?') ? '&' : '?';
      return `${checkoutUrl}${sep}discount=${encodeURIComponent(discountCode)}`;
    }
};

/**
 * @param {string} cartId
 * @param {Array<{ merchandiseId: string, quantity: number, sellingPlanId?: string }>} lineInputs
 */
const addLinesToExistingStorefrontCart = async function addLinesToExistingStorefrontCart(cartId, lineInputs) {
    if (!cartId || !lineInputs?.length) throw new Error('Cart or lines missing');
    const mutation = `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { id checkoutUrl totalQuantity }
          userErrors { field message }
        }
      }`;
    const variables = { cartId, lines: lineInputs };
    const data = await storefrontFetch(mutation, variables);
    const errors = data.cartLinesAdd?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const cart = data.cartLinesAdd?.cart;
    if (!cart?.id) throw new Error('Could not update cart');
    setStoredStorefrontCartId(cart.id);
    return cart;
};

const markB2BRedirectedToStorefrontCheckout = function markB2BRedirectedToStorefrontCheckout() {
    try {
      sessionStorage.setItem(SESSION_KEY_B2B_CHECKOUT_REDIRECT, String(Date.now()));
    } catch (_) {
      /* ignore */
    }
};

/**
 * @param {string} cartId
 * @param {string} variantId
 * @param {number} quantity
 */
const addLineToExistingStorefrontCart = async function addLineToExistingStorefrontCart(cartId, variantId, quantity) {
    const mutation = `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { id checkoutUrl totalQuantity }
          userErrors { field message }
        }
      }`;
    const variables = {
      cartId,
      lines: [{ merchandiseId: variantId, quantity }],
    };
    const data = await storefrontFetch(mutation, variables);
    const errors = data.cartLinesAdd?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const cart = data.cartLinesAdd?.cart;
    if (!cart?.id) throw new Error('Could not update cart');
    setStoredStorefrontCartId(cart.id);
    return cart;
};

/**
 * Write the customer's order note onto the Storefront cart so it carries into the Headless checkout
 * and onto the order. Called at checkout time with the locally-stored note.
 * @param {string} cartId
 * @param {string} note
 */
const updateStorefrontCartNote = async function updateStorefrontCartNote(cartId, note) {
    if (!cartId) return null;
    /* `note` is `String!` (non-null) on cartNoteUpdate in the current Storefront API — declaring the
     * variable as nullable `String` fails query validation ("Nullability mismatch on variable $note
     * and argument note (String / String!)") and silently drops the note. We always pass a string
     * (`note || ''`), so non-null is safe and an empty string clears the note. */
    const mutation = `
      mutation cartNoteUpdate($cartId: ID!, $note: String!) {
        cartNoteUpdate(cartId: $cartId, note: $note) {
          cart { id }
          userErrors { field message }
        }
      }`;
    const data = await storefrontFetch(mutation, { cartId, note: note || '' });
    const errors = data?.cartNoteUpdate?.userErrors;
    if (errors?.length) throw new Error(errors.map((/** @type {any} */ e) => e.message).join('; '));
    const cart = data?.cartNoteUpdate?.cart;
    if (cart?.id) setStoredStorefrontCartId(cart.id);
    return cart;
};

/**
 * Window-exposed diagnostics: filter debug info, the cart-persistence snapshot
 * and the lazy bubble tracer loader. Moved verbatim from legacy.js.
 */

/**
 * Set `window.__B2B_DEBUG_CART__ = true` before load (or in console) to log why cart scope / localStorage
 * can differ between pages (e.g. homepage has two [data-b2b-config] blocks: theme vip + section b2b).
 */
/** @param {string} label */
const b2bCartPersistenceDebugSnapshot = function b2bCartPersistenceDebugSnapshot(label) {
    if (typeof window === 'undefined') return;
    if (!window.__B2B_DEBUG_CART__ && !b2bCartRoutingDebugEnabled()) return;
    try {
      const cfgNodes = typeof document !== 'undefined' ? document.querySelectorAll('[data-b2b-config]') : [];
      /** @type {Array<{ index: number; scope: string }>} */
      const scopes = [];
      cfgNodes.forEach((n, i) => {
        const node = /** @type {HTMLElement} */ (n);
        scopes.push({
          index: i,
          scope: (node.dataset && node.dataset.b2bScope) || node.getAttribute('data-b2b-scope') || '?',
        });
      });
      /** @type {Record<string, string>} */
      const cartKeys = {};
      if (typeof localStorage !== 'undefined') {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.indexOf(STOREFRONT_CART_ID_KEY_BASE) === 0) {
            const v = localStorage.getItem(k);
            cartKeys[k] = v ? String(v).slice(-20) : '';
          }
        }
      }
      const cartRoot =
        typeof document !== 'undefined' ? /** @type {HTMLElement | null} */ (document.querySelector('[data-b2b-cart-ui]')) : null;
      /** Persistence helpers may not exist yet during very early calls; guard by name. */
      let persistenceSuffix = '';
      let storedCartIdTail = '';
      try {
        persistenceSuffix =
          typeof getCartPersistenceScopeSuffix === 'function' ? getCartPersistenceScopeSuffix() : '';
        const pk =
          typeof withCartPersistenceKey === 'function'
            ? withCartPersistenceKey(STOREFRONT_CART_ID_KEY_BASE)
            : '';
        if (pk && typeof localStorage !== 'undefined') {
          const id = localStorage.getItem(pk) || '';
          storedCartIdTail = id ? String(id).slice(-24) : '';
        }
      } catch (_) {
        /* ignore */
      }
      console.log('[b2b-storefront][cart-debug]', label, {
        resolvedConfigScope: getScopeKeySuffix(),
        cartPersistenceScopeSuffix: persistenceSuffix,
        mountedCatalogScope: cartRoot?.getAttribute('data-b2b-catalog-scope') || null,
        persistenceKeyCartIdTail: storedCartIdTail,
        dataB2bConfigBlocks: scopes,
        storefrontCartIdKeysInLocalStorage: cartKeys,
      });
    } catch (e) {
      console.warn('[b2b-storefront][cart-debug] snapshot failed', e);
    }
};

/**
 * Snapshot for on-page filter debugging (?b2b_filter_debug=1 on B2B collection).
 * Explains whether failure is search API (0 products) vs client filterProducts (products but 0 after filter).
 */
/**
 * @param {any} state
 * @param {any[]} products
 * @param {any} regionsData
 */
const getB2BFilterDebugInfo = function getB2BFilterDebugInfo(state, products, regionsData) {
    const st = state || {};
    const productsArr = Array.isArray(products) ? products : [];
    const filtered = filterProducts(productsArr, st);
    const searchQueryString = buildProductQuery(st) || getDefaultProductQuery();
    const hints = [];
    const hasRegion = Array.isArray(st.region) && st.region.length > 0;
    const hasVintage = Array.isArray(st.vintage) ? st.vintage.length > 0 : !!(st.vintage && String(st.vintage).trim());
    const hasProducer = Array.isArray(st.producer) && st.producer.length > 0;

    if (productsArr.length === 0 && (hasRegion || hasVintage || hasProducer)) {
      hints.push(
        'Search API returned 0 products for the current query. Often: metafield clauses are not indexed for Storefront search, or syntax does not match (region/vintage/producer).'
      );
    }
    if (productsArr.length > 0 && filtered.length === 0 && (hasRegion || hasVintage || hasProducer)) {
      hints.push(
        'API returned ' +
          productsArr.length +
          ' product(s) on this page but filterProducts() removed all — compare sample metafields below to selected region/vintage/producer.'
      );
    }

    const samples = productsArr.slice(0, 8).map((/** @type {any} */ p) => ({
      handle: p.handle,
      vintage: (p.vintageMetafield?.value ?? '').toString(),
      region_sub_last_parent_id: (p.regionSubLastParentIdMetafield?.value ?? '').toString(),
      producer_name: (p.producerNameMetafield?.value ?? '').toString(),
      millesimes_producteur: (p.millesimesProducteurMetafield?.value ?? '').toString(),
    }));

    let regionUi = null;
    if (regionsData && Array.isArray(regionsData.topLevelList)) {
      regionUi = regionsData.topLevelList.slice(0, 15).map((/** @type {any} */ r) => ({
        id: (r.id || '').toString(),
        name: (r.name || '').toString(),
      }));
    }

    return {
      searchQueryString,
      productFilters: buildProductFiltersFromState(st),
      hints,
      state: JSON.parse(JSON.stringify(st)),
      apiProductCountOnPage: productsArr.length,
      filteredCount: filtered.length,
      regionFilterOptions: regionUi,
      sampleProductsMetafields: samples,
      normalizedSelectedRegions: Array.isArray(st.region) ? st.region.map((/** @type {any} */ id) => normalizeRegionFilterValue(id)) : [],
    };
};

/**
 * Lazy-load debug tracer so production bundle stays smaller.
 * The tracer implementation lives in `/assets/b2b-cart-bubble-tracer.js`.
 * @returns {Promise<void>}
 */
const installCartBubbleTracer = async function installCartBubbleTracer() {
    if (typeof window === 'undefined') return;
    const tracerGlobal = /** @type {any} */ (window).__B2BCartBubbleTracerInstall;
    if (typeof tracerGlobal === 'function') {
      tracerGlobal();
      return;
    }
    if (!document.querySelector('script[data-b2b-bubble-tracer-loader]')) {
      const s = document.createElement('script');
      s.src = '/assets/b2b-cart-bubble-tracer.js';
      s.async = true;
      s.defer = true;
      s.setAttribute('data-b2b-bubble-tracer-loader', 'true');
      document.head.appendChild(s);
    }
    const started = Date.now();
    while (Date.now() - started < 4000) {
      const fn = /** @type {any} */ (window).__B2BCartBubbleTracerInstall;
      if (typeof fn === 'function') {
        fn();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    b2bCaptureError('bubble-tracer-loader', new Error('Timed out loading /assets/b2b-cart-bubble-tracer.js'));
};

/**
 * Page-injected data readers: product tags from DOM elements, the variant
 * stock-map JSON, sort-key mapping and scope-config presence. Moved verbatim
 * from legacy.js.
 */

const hasAnyB2bScopeConfig = function hasAnyB2bScopeConfig() {
    try {
      return document.querySelectorAll('[data-b2b-config][data-b2b-scope="b2b"]').length > 0;
    } catch (_) {
      return false;
    }
};

/**
 * @param {HTMLElement | null} el
 * @returns {string[]}
 */
const readProductTagsFromElement = function readProductTagsFromElement(el) {
    if (!(el instanceof HTMLElement)) return [];
    const raw = el.getAttribute('data-product-tags') || '';
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((tag) => String(tag || '').trim().toLowerCase()).filter(Boolean);
    } catch (_) {
      return [];
    }
};

/** Map UI sortBy to Storefront API sortKey + reverse. */
/** @param {string} sortBy */
const getSortKeyAndReverse = function getSortKeyAndReverse(sortBy) {
    /** @type {Record<string, {sortKey: string, reverse: boolean}>} */
    const map = {
      title_asc: { sortKey: 'TITLE', reverse: false },
      title_desc: { sortKey: 'TITLE', reverse: true },
      price_asc: { sortKey: 'PRICE', reverse: false },
      price_desc: { sortKey: 'PRICE', reverse: true },
      created_asc: { sortKey: 'CREATED_AT', reverse: false },
      created_desc: { sortKey: 'CREATED_AT', reverse: true },
      best_selling: { sortKey: 'BEST_SELLING', reverse: false },
    };
    return map[sortBy] || { sortKey: 'ID', reverse: false };
};

/**
 * Read the JSON variant stock map serialised on the container.
 * @param {Element | null} container
 * @returns {Record<string, number>}
 */
const b2bReadVariantStockMap = function b2bReadVariantStockMap(container) {
    if (!container) return {};
    const raw = /** @type {any} */ (container).dataset?.variantStock || '';
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch (_) {
      return {};
    }
};

/**
 * Resolve the stock for the currently-selected variant within a B2B
 * purchase container, falling back to the data attribute on the buy button
 * (which always carries the variant id even when there is no variant select).
 * @param {Element} container
 * @returns {number}
 */
const b2bResolveCurrentVariantStock = function b2bResolveCurrentVariantStock(container) {
    const map = b2bReadVariantStockMap(container);
    const variantSelect = /** @type {HTMLSelectElement | null} */ (container.querySelector('[data-b2b-variant-select]'));
    const buyBtn = /** @type {any} */ (container.querySelector('[data-b2b-buy]'));
    const variantId = (variantSelect && variantSelect.value) || (buyBtn && buyBtn.dataset.b2bBuy) || '';
    if (!variantId) return -1;
    const raw = map[String(variantId)];
    if (raw === undefined || raw === null) return -1;
    const n = parseInt(String(raw), 10);
    return isNaN(n) ? -1 : n;
};

/**
 * Theme cart (/cart.js) line count — matches Liquid `cart.item_count` on first paint.
 * Used when there is no Storefront cart yet so we do not overwrite the header bubble with 0.
 */
const getThemeAjaxCartItemCount = async function getThemeAjaxCartItemCount() {
    try {
      const ajaxCart = await fetchAjaxCartJson();
      if (ajaxCart && typeof ajaxCart.item_count === 'number') return ajaxCart.item_count;
    } catch (err) {
      b2bCartDebugLog('getThemeAjaxCartItemCount failed', err);
    }
    return null;
};

/**
 * For Professionals the header bubble is the B2B Storefront cart only. Theme Ajax /cart.js can
 * hold regular products — never mirror that count on the B2B icon (drawer would show 0).
 * @param {number|null|undefined} ajaxCount from getThemeAjaxCartItemCount
 * @returns {number|null} value for setB2BCartCount, or null to skip updating the bubble
 */
const storefrontHeaderBubbleFromAjaxFallback = function storefrontHeaderBubbleFromAjaxFallback(ajaxCount) {
    try {
      if (typeof document !== 'undefined' && document.body && document.body.classList.contains('professionals-b2b-nav')) {
        return 0;
      }
    } catch (_) {
      /* ignore */
    }
    if (ajaxCount == null) return null;
    return ajaxCount;
};

/**
 * B2B Storefront API Client (entrypoint/orchestrator).
 *
 * Purpose:
 * - Fetches B2B/VIP catalog data from Storefront API (not `/products.json`).
 * - Renders collection/product UI, filters, and B2B cart drawer behavior.
 * - Exposes `window.B2BStorefront` API used by Liquid sections/snippets.
 *
 * Files relying on this bundle:
 * - Liquid entry/loading points: `layout/theme.liquid`, `sections/b2b-collection.liquid`,
 *   `sections/b2b-product.liquid`.
 * - Config contract: `snippets/b2b-storefront-config-attrs.liquid` (`data-b2b-config` attrs).
 * - Consumers of exported/global behavior: `snippets/b2b-filters-sidebar.liquid`,
 *   `snippets/product-details-info-b2b.liquid`, `snippets/b2b-product-recommendations.liquid`,
 *   `snippets/producer-details.liquid`, `assets/cart-icon.js`, `snippets/header-actions.liquid`.
 * - Type/runtime globals: `assets/global.d.ts`.
 *
 * Split-out companion assets loaded by this file:
 * - `assets/b2b-cart-ui.css` (drawer/fab styles; loaded via `ensureB2BCartUiStyles()`).
 * - `assets/b2b-cart-bubble-tracer.js` (debug tracer; lazy-loaded by `installCartBubbleTracer()`).
 * - Extracted modules (imported below) live in src/b2b/{core,api,catalog,cart}/;
 *   tests in tests/. core: config/debug/cache/utils/html/money. api: queries/
 *   storefront-client/client. catalog: case-pack/pricing/normalize/product-display/
 *   filtering/display-price. cart: fees/merge-policy/persistence.
 *
 * Important maintenance notes:
 * - This bundle can be included from multiple templates; keep the singleton guard
 *   (`window.__B2B_STOREFRONT_BUNDLE__`) intact.
 * - Keep `window.B2BStorefront` method names backward-compatible unless all Liquid callers are updated.
 */

(function () {
  'use strict';
  /**
   * Incremental typing target: keep this broad for now, then split by feature
   * (cart, drawer, product card, filters) in later passes.
   * @typedef {{ [key: string]: any }} B2BConfig
   */
  /** @typedef {{ mode: 'single' | 'ht_ttc', amount: number, currency: string, totalLabel: string | null, htAmount: number, ttcAmount: number }} B2BCartCostSummary */

  /* The bundle is often included twice (layout/theme.liquid + sections/b2b-*.liquid). A second run would duplicate document listeners and race parallel syncs — correct count flashes, then clears. */
  if (typeof window !== 'undefined' && window.__B2B_STOREFRONT_BUNDLE__) {
    return;
  }
  if (typeof window !== 'undefined') {
    window.__B2B_STOREFRONT_BUNDLE__ = true;
  }

  let vipCartMergeListenerAttached = false;

  /* Magical Fees watch/auto-confirm/destination-prompt machinery — moved to ./cart/mf-watch.js */

  /** Variant gids we've already shown the "missing VIP channel" alert for, so it fires once, not every sync. */
  const b2bAlertedOffChannelVariants = new Set();

  /** Serializes sync so VIP merge + fetch are not interleaved with a second sync (e.g. cart:update while merge runs). */
  let b2bCartSyncChain = Promise.resolve();

  let b2bCartSyncRunId = 0;




  /* Cart drawer + header bubble + €120 gate UI — moved to ./cart/drawer-ui.js */



  /* Debug switches + unified logging (b2bLog, b2bCaptureError, cart-routing/min-ui loggers) — moved to ./debug.js */








  /* Primeurs cart-conflict guard — moved to ./cart/primeurs-guard.js */



  /* VIP merge/strip decision rules — moved to ./cart/merge-rules.js */

  /* Page-data readers (tags, stock map, sort keys, ajax counts) — moved to ./catalog/page-data.js */











  /* Diagnostics (filter debug, persistence snapshot, bubble tracer) — moved to ./core/diagnostics.js */

  /* Order-request form UI + contact-form submit — moved to ./cart/order-request-ui.js */
  /* Order-sent toast + missing-VIP-channel alert — moved to ./cart/toasts.js */
  /* Pre-checkout snapshot/restore + checkout-return detection — moved to ./cart/checkout-flow.js */
  /* sessionStorage TTL cache (CACHE_TTL, getCached, setCache) — moved to ./cache.js */
  /* esc() + ICON_* SVG strings — moved to ./html.js */

  /* ------------------------------------------------------------------ */
  /*  Config (injected via data attributes from Liquid)                  */
  /* ------------------------------------------------------------------ */

  /* Config parsing (getConfig, taxToggleEnabledFromPage, localePrefixedStorePath, formatDiscountFeedback) — moved to ./config.js */




  /* ------------------------------------------------------------------ */
  /*  Insurance / service fee                                             */
  /*  The Cart Transform Function (insurance-fee-transform) handles       */
  /*  price computation server-side. The JS here only manages the        */
  /*  placeholder line: qty 1 present ↔ cart has product lines.          */
  /* ------------------------------------------------------------------ */

  /* Fee predicates/computations + PC destination resolution — moved to ./fees.js */



  /* Cart cost summaries, €120 minimum, fee-line finders, totals HTML — moved to ./cart/cost.js */

  /* Insurance/excise fee-line sync mutations — moved to ./cart/fee-sync.js */

  /* ------------------------------------------------------------------ */
  /*  Private Cellar excise / duty fee                                   */
  /*  Same €0.01 cents-as-quantity placeholder trick as the insurance    */
  /*  fee, but the amount depends on the destination country (from the   */
  /*  store localization picker): US = % of product subtotal, Sweden =   */
  /*  fixed € per bottle (case packs count as their bottle count).       */
  /*  Baked into the cart before the checkout redirect — non-Plus, so    */
  /*  there is no checkout/cart Function to compute or correct it.        */
  /* ------------------------------------------------------------------ */




















  /* Card/grid/pagination rendering — moved to ./catalog/render.js */



  /* Order-request email + contact-form helpers + sent flags — moved to ./cart/order-request.js */






















  /* Cart data access + low-level mutations — moved to ./cart/api.js */






  /* VIP merge policy + catalog-tag resolvers — moved to ./merge-policy.js */


  /* Filtering / sorting / faceting + search query builders — moved to ./filtering.js */

  /** @type {Record<string, number>} */
  /* Shop money-format template rendering (CURRENCY_DECIMALS, formatMoneyWithShopFormat)
     now imported from ./money.js. */

  /* ------------------------------------------------------------------ */
  /*  Storefront API fetch with retries                                  */
  /* ------------------------------------------------------------------ */

  /* storefrontFetch / storefrontFetchPartial — the shared client from ./client.js */

  /* ------------------------------------------------------------------ */
  /*  Cache helpers                                                      */
  /* ------------------------------------------------------------------ */



  /* ------------------------------------------------------------------ */
  /*  Fetch all B2B products                                             */
  /* ------------------------------------------------------------------ */

  /* PRODUCTS_QUERY — moved to ./queries.js */

  // NOTE: For recommendations we use `search` instead of `products(query: ...)`.
  // In practice, `products(query: ...)` has been behaving like it only filters by tag/title,
  // ignoring `metafields.custom.*` clauses, which breaks producer/vintage similarity.
  /* PRODUCTS_SEARCH_QUERY — moved to ./queries.js */

  /* PRODUCTS_SEARCH_CARD_QUERY — moved to ./queries.js */

  const FIRST_PAGE_SIZE = 48;
  /* Catalog fetchers (products/regions/producers/media + mpp pagination) — moved to ./catalog/fetch.js */

  /** Search API query to get total count (simple query so search endpoint accepts it). */
  /* PRODUCTS_COUNT_QUERY — moved to ./queries.js */














  /* ------------------------------------------------------------------ */
  /*  Fetch single B2B product                                           */
  /* ------------------------------------------------------------------ */

  /* PRODUCT_QUERY — moved to ./queries.js */


  /* ------------------------------------------------------------------ */
  /*  Fetch region metaobjects (map_shown = true) for filter             */
  /* ------------------------------------------------------------------ */

  /* REGIONS_QUERY — moved to ./queries.js */






  /* ------------------------------------------------------------------ */
  /*  Producer metaobjects (for B2B producer-details)                   */
  /* ------------------------------------------------------------------ */

  /* PRODUCERS_QUERY — moved to ./queries.js */

  const PRODUCERS_CACHE_TTL = 30 * 60 * 1000;

  /* MEDIA_IMAGE_URL_QUERY — moved to ./queries.js */








  /* ------------------------------------------------------------------ */
  /*  Price formatting                                                   */
  /* ------------------------------------------------------------------ */

  /* Case-pack parsing + per-unit math (normalizeFormatOptionValue, getCaseQtyFromString,
     getCaseQty, dividePriceByCaseQty) now imported from ./case-pack.js — a faithful port
     of snippets/variant-option-case-qty.liquid plus the price.liquid cents rounding. */

  /* Product display text (titles, subtitles, variant labels, card description) — moved to ./product-display.js */











  /* Display price formatting (formatMoney, getDisplayPrice, getDisplayCompareAtPrice, getVariantMajorAmountPerDisplayedUnit) — moved to ./display-price.js */



  /* Numeric per-unit price helpers (getVariantPriceNum, getVariantUnitPriceNum,
     getSortPrice, getPriceRange) now imported from ./pricing.js — per DISPLAYED
     unit, matching the cards (the case-pack filter/sort/slider fix). */

  /* ------------------------------------------------------------------ */
  /*  Title / subtitle (mirror product-title-value & product-subtitle-value) */
  /* ------------------------------------------------------------------ */

  /* Catalog normalizers + product predicates — moved to ./catalog.js */









  /* ------------------------------------------------------------------ */
  /*  Client-side filter & sort                                          */
  /* ------------------------------------------------------------------ */









  /* ------------------------------------------------------------------ */
  /*  Pagination HTML (matches collection-pagination snippet)            */
  /* ------------------------------------------------------------------ */




  /* ------------------------------------------------------------------ */
  /*  HTML helpers                                                       */
  /* ------------------------------------------------------------------ */








  /* Filters sidebar UI (buildB2BFilters + facet cache) — moved to ./catalog/filters-ui.js */

  /* ------------------------------------------------------------------ */
  /*  Render a product card (matches theme product-card.liquid output)   */
  /* ------------------------------------------------------------------ */




  /* Quantity selector options / stock limits / custom-qty dialog — moved to ./catalog/quantity.js */












  /* ------------------------------------------------------------------ */
  /*  Render the product grid (matches product-grid.liquid)              */
  /* ------------------------------------------------------------------ */


  /* ------------------------------------------------------------------ */
  /*  B2B custom filters UI (client-side; works with API-loaded products) */
  /* ------------------------------------------------------------------ */





  /* ------------------------------------------------------------------ */
  /*  Add to cart via Storefront API (B2B products aren't on Online      */
  /*  Store channel, so we use Storefront cart → checkout redirect)      */
  /* ------------------------------------------------------------------ */







  /* Line add / note / checkout-url mutations — moved to ./cart/mutations.js */

  /* ------------------------------------------------------------------ */
  /*  VIP scope: merge regular Online Store (Ajax) lines into the VIP      */
  /*  Storefront cart on main storefront + Private Cellar (see             */
  /*  shouldMergeAjaxCartIntoPrivateCellarStorefrontCart). Per-variant     */
  /*  merge avoids one Headless-channel miss failing the whole batch.     */
  /* ------------------------------------------------------------------ */






  /* Native /cart.js helpers (aggregation, gid conversion, qty updates) — moved to ./cart/ajax-cart.js */



















  /**
   * In-flight VIP merge promise. The merge is NOT re-entrant: two concurrent runs would both
   * read the same theme cart lines and double-add them (or, with no stored cart id yet, create
   * two Storefront carts and orphan one). Syncs can overlap (add event + location change +
   * checkout click), so concurrent callers share the one in-flight run instead.
   * @type {Promise<any> | null}
   */
  let b2bVipMergeInFlightPromise = null;

  /**
   * Moves regular (non-VIP, non-B2B–tagged) Ajax cart lines into the VIP Storefront cart and removes them from the theme cart.
   */
  /**
   * @param {{ allowPrimeursConflictModal?: boolean }} [opts]
   */
  function mergeRegularOnlineCartIntoVipStorefrontCart(opts) {
    if (b2bVipMergeInFlightPromise) {
      b2bCartRoutingLog('vip_merge_skip', { reason: 'merge_already_in_flight_sharing_result' });
      return b2bVipMergeInFlightPromise;
    }
    const run = mergeRegularOnlineCartIntoVipStorefrontCartInner(opts).finally(() => {
      b2bVipMergeInFlightPromise = null;
    });
    b2bVipMergeInFlightPromise = run;
    return run;
  }

  /**
   * @param {{ allowPrimeursConflictModal?: boolean }} [opts]
   */
  async function mergeRegularOnlineCartIntoVipStorefrontCartInner(opts) {
    const allowPrimeursConflictModal = opts?.allowPrimeursConflictModal === true;
    if (!shouldMergeAjaxCartIntoPrivateCellarStorefrontCart()) {
      b2bCartRoutingLog('vip_merge_skip', { reason: 'should_merge_guard_false' });
      return { ranFullMerge: false };
    }
    const ajaxCart = await fetchAjaxCartJson();
    const ajaxItems = Array.isArray(ajaxCart?.items) ? ajaxCart.items : [];
    b2bCartRoutingLog('vip_merge_ajax_cart_snapshot', {
      ajaxItemCount: ajaxItems.length,
      ajaxItemCountField: typeof ajaxCart?.item_count === 'number' ? ajaxCart.item_count : null,
      sample: ajaxItems.slice(0, 5).map((/** @type {any} */ item) => ({
        key: item?.key || '',
        productId: item?.product_id || '',
        variantId: item?.variant_id || '',
        quantity: item?.quantity || 0,
        title: item?.title || '',
        productTitle: item?.product_title || '',
        productTags: Array.isArray(item?.product_tags)
          ? item.product_tags
          : typeof item?.product_tags === 'string'
            ? item.product_tags
            : '',
      })),
    });
    if (!ajaxItems.length) {
      b2bCartRoutingLog('vip_merge_skip', { reason: 'ajax_cart_empty' });
      return { ranFullMerge: false };
    }

    let tagMap = new Map();
    /* Ajax `/cart.js` often includes product_tags — skip Storefront nodes query when every line has tags (one fewer round-trip). */
    const skipStorefrontTagFetch = ajaxItems.every((/** @type {any} */ i) => getAjaxLineProductTagsLower(i).length > 0);
    if (skipStorefrontTagFetch) {
      b2bCartRoutingLog('vip_merge_tag_lookup_skip', { reason: 'ajax_product_tags_present' });
    } else {
      try {
        tagMap = await fetchStorefrontProductTagsMap(ajaxItems.map((/** @type {any} */ i) => i.product_id));
        b2bCartRoutingLog('vip_merge_tag_lookup_ok', { productTagMapSize: tagMap.size });
      } catch (e) {
        console.warn('VIP merge: product tag lookup failed, using Ajax cart fields only:', e);
        b2bCartRoutingLog('vip_merge_tag_lookup_failed', {
          error: e && e.message ? String(e.message) : String(e || ''),
        });
      }
    }

    const decisions = ajaxItems.map((/** @type {any} */ item) => {
      const tags = tagsForAjaxLineItem(item, tagMap);
      const include = isRegularOnlineStoreLineForVipMerge(item, tagMap);
      let reason = 'regular_line';
      if (isMagicalFeesAjaxLine(item)) reason = 'excluded_magical_fees_fee_line';
      else if (!tags.length) reason = 'no_tags_fallback_include';
      else if (tags.includes('vip')) reason = 'excluded_vip_tag';
      else {
        const b2b = getB2bCatalogProductTagLower();
        if (b2b && tags.includes(b2b)) reason = 'excluded_b2b_tag';
      }
      return {
        key: item?.key || '',
        title: item?.title || item?.product_title || '',
        productId: item?.product_id || '',
        variantId: item?.variant_id || '',
        quantity: item?.quantity || 0,
        tags,
        include,
        reason,
      };
    });
    const mergeableItems = ajaxItems.filter((/** @type {any} */ item) => isRegularOnlineStoreLineForVipMerge(item, tagMap));
    b2bCartRoutingLog('vip_merge_filter_result', {
      totalAjaxItems: ajaxItems.length,
      mergeableCount: mergeableItems.length,
      excludedCount: ajaxItems.length - mergeableItems.length,
      decisions: decisions.slice(0, 10),
    });
    if (!mergeableItems.length) {
      b2bCartRoutingLog('vip_merge_skip', { reason: 'no_mergeable_items' });
      return { ranFullMerge: false };
    }

    let incomingHasPrimeurs = false;
    let incomingHasRegular = false;
    for (let i = 0; i < mergeableItems.length; i++) {
      const tags = tagsForAjaxLineItem(mergeableItems[i], tagMap);
      if (hasPrimeursTag(tags)) incomingHasPrimeurs = true;
      else incomingHasRegular = true;
      if (incomingHasPrimeurs && incomingHasRegular) break;
    }

    const existingCartId = getStoredStorefrontCartId();
    /** @type {{ hasPrimeurs: boolean, hasRegular: boolean }} */
    let existingComposition = { hasPrimeurs: false, hasRegular: false };
    if (existingCartId) {
      try {
        const existingCart = await fetchStorefrontCart(existingCartId);
        existingComposition = getStorefrontCartPrimeursComposition(existingCart);
      } catch (e) {
        b2bCartRoutingLog('vip_merge_primeurs_existing_cart_read_failed', {
          cartIdTail: String(existingCartId).slice(-18),
          error: e && e.message ? String(e.message) : String(e || ''),
        });
      }
    }

    const hasPrimeursConflict =
      (incomingHasPrimeurs && existingComposition.hasRegular) || (incomingHasRegular && existingComposition.hasPrimeurs);
    b2bCartRoutingLog('vip_merge_primeurs_conflict_check', {
      allowPrimeursConflictModal,
      incomingHasPrimeurs,
      incomingHasRegular,
      existingHasPrimeurs: existingComposition.hasPrimeurs,
      existingHasRegular: existingComposition.hasRegular,
      hasPrimeursConflict,
      mergeableCount: mergeableItems.length,
    });
    if (hasPrimeursConflict) {
      if (!allowPrimeursConflictModal) {
        b2bCartRoutingLog('vip_merge_skip', { reason: 'primeurs_conflict_passive_sync' });
        return { ranFullMerge: false };
      }
      const shouldClear = await showPrimeursClearCartModal();
      b2bCartRoutingLog('vip_merge_primeurs_conflict_modal_result', { shouldClear });
      if (!shouldClear) {
        b2bCartRoutingLog('vip_merge_skip', { reason: 'primeurs_conflict_user_cancelled' });
        return { ranFullMerge: false };
      }
      if (existingCartId) {
        try {
          const existingCart = await fetchStorefrontCart(existingCartId);
          await removeAllLinesFromB2BStorefrontCart(existingCart);
        } catch (e) {
          b2bCartRoutingLog('vip_merge_primeurs_clear_existing_failed', {
            cartIdTail: String(existingCartId).slice(-18),
            error: e && e.message ? String(e.message) : String(e || ''),
          });
        }
      }
    }

    const aggregated = aggregateAjaxCartItems(mergeableItems);
    const lineInputs = ajaxRowsToStorefrontLineInputs(aggregated);
    b2bCartRoutingLog('vip_merge_line_inputs', {
      mergeableCount: mergeableItems.length,
      aggregatedCount: aggregated.length,
      lineInputsCount: lineInputs.length,
      lineInputs: lineInputs.slice(0, 10),
    });
    if (!lineInputs.length) {
      b2bCartRoutingLog('vip_merge_skip', { reason: 'line_inputs_empty_after_aggregation' });
      return { ranFullMerge: false };
    }

    const cartId = getStoredStorefrontCartId();
    /** @type {string} */
    let workingCartId = cartId || '';
    /** @type {Set<string>} */
    const ajaxKeysToRemove = new Set();

    /*
     * Never move a boutique line whose product is not published to the Storefront/Headless channel:
     * the Storefront API accepts the add but then can't read the cart back (`ProductVariant.product`
     * is null), poisoning the whole drawer. Skip such variants here — they stay in the theme cart so
     * they are not lost — and surface them so they can be published to the channel.
     */
    const unavailableVariantGids = await findUnavailableStorefrontVariantGids(
      aggregated.map((/** @type {any} */ r) => variantGidFromAjaxId(r.variant_id))
    );
    if (unavailableVariantGids.size) {
      b2bCartRoutingLog('vip_merge_skip_off_channel_variants', {
        count: unavailableVariantGids.size,
        variantGidTails: [...unavailableVariantGids].map((g) => String(g).slice(-12)),
      });
      /*
       * Tell the customer the product can't go in their Private Cellar order because it isn't on the
       * VIP channel — but only once per variant (the line stays in the theme cart and is re-checked
       * on every sync, which would otherwise re-alert endlessly).
       */
      const newlyOffChannelTitles = [];
      for (const item of mergeableItems) {
        const gid = variantGidFromAjaxId(item.variant_id);
        if (!unavailableVariantGids.has(gid) || b2bAlertedOffChannelVariants.has(gid)) continue;
        b2bAlertedOffChannelVariants.add(gid);
        newlyOffChannelTitles.push(item.product_title || item.title || '');
      }
      if (newlyOffChannelTitles.length) {
        try {
          showB2BMissingVipChannelAlert(newlyOffChannelTitles);
        } catch (_) {
          /* never let the alert break the merge */
        }
      }
    }

    let mergedAggregateRows = 0;
    let failedAggregateRows = 0;
    let skippedOffChannelRows = 0;

    /* Partition: off-channel rows stay in the theme cart (can never enter the Storefront cart); the
     * rest are expected-good (off-channel was already probed above) and go up in ONE mutation. */
    /** @type {Array<{ row: any, inputs: any[], rowKey: string }>} */
    const addableRows = [];
    for (let i = 0; i < aggregated.length; i++) {
      const row = aggregated[i];
      const singleInputs = ajaxRowsToStorefrontLineInputs([row]);
      if (!singleInputs.length) continue;
      if (unavailableVariantGids.has(variantGidFromAjaxId(row.variant_id))) {
        skippedOffChannelRows += 1;
        continue; // leave it in the theme cart; do not mark for removal
      }
      addableRows.push({ row, inputs: singleInputs, rowKey: ajaxAggregatedRowKey(row) });
    }

    /** @param {string} rowKey */
    const markRowKeysForRemoval = (rowKey) => {
      for (const item of mergeableItems) {
        const lineKey = item.key || '';
        if (!lineKey) continue;
        if (ajaxCartLineAggregateKey(item) === rowKey) ajaxKeysToRemove.add(lineKey);
      }
    };

    if (addableRows.length) {
      /* One Storefront round-trip for every on-channel row instead of one per row. On failure we leave
       * the lines in the theme cart (never half-remove) so the next sync — or the merge that a checkout
       * click forces — retries them; this also avoids double-adding a request that may have committed
       * server-side before the response was lost. */
      const batchInputs = addableRows.flatMap((r) => r.inputs);
      try {
        if (!workingCartId) {
          b2bCartRoutingLog('vip_merge_storefront_target', {
            action: 'create_cart_with_lines',
            lineInputsCount: batchInputs.length,
            rows: addableRows.length,
            batched: true,
          });
          await createStorefrontCartWithLines(batchInputs);
          workingCartId = getStoredStorefrontCartId() || '';
          if (!workingCartId) throw new Error('Cart create returned no id');
        } else {
          b2bCartRoutingLog('vip_merge_storefront_target', {
            action: 'add_lines_to_existing_cart',
            cartIdTail: String(workingCartId).slice(-18),
            lineInputsCount: batchInputs.length,
            rows: addableRows.length,
            batched: true,
          });
          await addLinesToExistingStorefrontCart(workingCartId, batchInputs);
        }
        mergedAggregateRows = addableRows.length;
        for (const r of addableRows) markRowKeysForRemoval(r.rowKey);
      } catch (e) {
        failedAggregateRows = addableRows.length;
        console.warn(
          'VIP merge: batched add to Storefront cart failed; leaving lines in the theme cart for retry:',
          e
        );
        b2bCartRoutingLog('vip_merge_storefront_batch_failed', {
          error: e && e.message ? String(e.message) : String(e || ''),
          rows: addableRows.length,
          cartIdTail: workingCartId ? String(workingCartId).slice(-18) : '',
        });
      }
    }

    /* Remove every merged line from the theme cart in ONE /cart/update.js call (was one /cart/change.js
     * per line). Setting a line to qty 0 is idempotent, so the per-line fallback below cannot double-act. */
    const removedKeys = [...ajaxKeysToRemove];
    if (removedKeys.length) {
      try {
        await setAjaxCartLineQuantitiesByKey(
          removedKeys.reduce((/** @type {Record<string, number>} */ m, k) => {
            m[k] = 0;
            return m;
          }, {})
        );
      } catch (e) {
        console.warn('VIP merge: batched theme cart removal failed; retrying per line:', e);
        b2bCartRoutingLog('vip_merge_ajax_batch_remove_failed', {
          count: removedKeys.length,
          error: e && e.message ? String(e.message) : String(e || ''),
        });
        for (const key of removedKeys) {
          try {
            await changeAjaxCartLineQuantity(key, 0);
          } catch (e2) {
            b2bCartRoutingLog('vip_merge_ajax_line_remove_failed', {
              key,
              error: e2 && e2.message ? String(e2.message) : String(e2 || ''),
            });
          }
        }
      }
    }
    b2bCartRoutingLog('vip_merge_complete', {
      mergedAggregateRows,
      failedAggregateRows,
      skippedOffChannelRows,
      mergeableAjaxLines: mergeableItems.length,
      aggregatedRows: aggregated.length,
      removedFromAjaxCount: removedKeys.length,
      removedKeysSample: removedKeys.slice(0, 10),
      batched: true,
    });
    /* Hot-path signal for syncB2BCartUI: only rows that *failed* to merge are still pending-mergeable
     * (off-channel rows can never merge and must not keep the €120 gate open). Lets the caller skip the
     * redundant /cart.js + channel re-probe that hasPendingMergeableThemeCartLines() would do. */
    return {
      ranFullMerge: true,
      mergedAggregateRows,
      failedAggregateRows,
      skippedOffChannelRows,
      pendingMergeable: failedAggregateRows > 0,
    };
  }


  /* CART_QUERY — moved to ./queries.js */


  /** Cart line ids + their variant gid, WITHOUT `product` — never poisons (see {@link healPoisonedStorefrontCart}). */
  /* SAFE_CART_LINES_QUERY — moved to ./queries.js */

  /** Probe whether each variant's product is readable on the Storefront/Headless channel. */
  /* VARIANT_PRODUCT_AVAIL_QUERY — moved to ./queries.js */





  /**
   * Fetches the Storefront cart for the stored id. Does not adopt another scope's cart — vip vs b2b
   * must stay isolated for customers who use both catalogs.
   */
  /** @param {string} preferredCartId */
  async function resolveStorefrontCartForSync(preferredCartId) {
    const cart = await fetchStorefrontCart(preferredCartId);
    if (!cart) {
      return { cart: null, resolvedCartId: preferredCartId };
    }
    const lineEdges = cart?.lines?.edges || [];
    const stQty = Number(cart.totalQuantity) || 0;
    if (lineEdges.length > 0 || stQty > 0) {
      return { cart, resolvedCartId: preferredCartId };
    }
    return { cart, resolvedCartId: preferredCartId };
  }














  /* Optimistic line paint / shimmer / error UI — moved to ./cart/optimistic.js */








  /**
   * @param {HTMLElement | null | undefined} lineNode
   * @param {number} newQty
   */
  /**
   * Shared helper used by the B2B cart drawer's dropdown + and -/+ buttons to
   * push a new quantity through the Storefront API. Performs the optimistic
   * UI update and rolls back via `syncB2BCartUI` if the mutation fails.
   * @param {HTMLElement | null} lineNode
   * @param {string} lineId
   * @param {number} nextQty
   * @param {string} debugLabel
   */
  async function b2bApplyCartLineQtyChange(lineNode, lineId, nextQty, debugLabel) {
    if (!lineNode || !lineId) return;
    const safeQty = Math.max(1, parseInt(String(nextQty), 10) || 1);
    const root = document.querySelector('[data-b2b-cart-ui]');
    applyB2BCartOptimisticQty(lineNode, safeQty);
    cartState.lastRenderedCartFingerprint = '';
    try {
      await updateStorefrontCartLine(lineId, safeQty);
      await syncB2BCartUI(undefined);
      if (root?.querySelector('[data-b2b-cart-drawer]')) setB2BCartDrawerOpen(true);
    } catch (err) {
      b2bCaptureError(debugLabel || 'cart-line-qty-change', err, { lineId, nextQty: safeQty });
      clearB2BCartShimmers();
      cartState.lastRenderedCartFingerprint = '';
      await syncB2BCartUI(undefined);
    }
  }

  /** Tracks the last known good value for each cart-line dropdown, so the
   *  custom-amount dialog can revert on cancel.
   *  @type {WeakMap<HTMLSelectElement, string>} */
  const b2bLastValidCartLineQtyValue = new WeakMap();


















  /** @param {any} [syncOpts] */
  function syncB2BCartUI(syncOpts) {
    const opts = syncOpts && typeof syncOpts === 'object' ? syncOpts : {};
    /* This run reconciles an optimistic add-paint: on success its authoritative repaint replaces the
     * pending line; on error it rolls the drawer back to the last confirmed cart. Bound to this specific
     * invocation (not a global flag) so an unrelated in-flight sync can't clear it out from under us. */
    const reconcileOptimistic = opts.reconcileOptimistic === true;
    const run = async () => {
      const runId = ++b2bCartSyncRunId;
      const root = /** @type {HTMLElement | null} */ (document.querySelector('[data-b2b-cart-ui]'));
      if (!root) {
        return;
      }
      try {
        /* A drawer-open refresh re-syncs a cart the user is already looking at; shimmering the total
         * on every open reads as lag. Skip the loading shimmer here — the repaint (if the cart changed)
         * is still fingerprint-guarded and atomic. Real mutations (add / qty / remove) set their own
         * shimmer via the optimistic helpers before calling sync, so this only affects passive refreshes. */
        if (!opts.fromDrawerOpen) setB2BCartTotalLoadingState(root, true);
        b2bCartPersistenceDebugSnapshot('sync run#' + runId);
        /** @type {{ ranFullMerge?: boolean, pendingMergeable?: boolean } | null} */
        let mergeResult = null;
        if (shouldMergeAjaxCartIntoPrivateCellarStorefrontCart()) {
          try {
            mergeResult = await mergeRegularOnlineCartIntoVipStorefrontCart({
              allowPrimeursConflictModal: opts.allowPrimeursConflictModal === true,
            });
          } catch (err) {
            console.warn('VIP merge from theme cart failed:', err);
          }
        }
        /*
         * Any boutique lines the merge couldn't move yet still count toward this one Private Cellar
         * cart: track them so the €120 gate below judges the merged total, not the VIP cart alone.
         * When the merge ran to completion it already knows exactly what stayed behind (only failed
         * rows are still pending; off-channel rows can never merge), so trust that and skip the extra
         * /cart.js read + channel re-probe. Cold paths (guard off, early return, throw) fall back to the scan.
         */
        if (mergeResult && mergeResult.ranFullMerge) {
          cartState.b2bHasPendingMergeableThemeLines = mergeResult.pendingMergeable === true;
        } else {
          cartState.b2bHasPendingMergeableThemeLines = await hasPendingMergeableThemeCartLines();
        }
        const drawerBody = root.querySelector('[data-b2b-cart-drawer-body]');
        const cartId = getStoredStorefrontCartId();
        if (!cartId) {
          const fp = 'empty';
          const ajaxCount = await getThemeAjaxCartItemCount();
          if (ajaxCount !== null) {
          } else {
          }
          /* No Storefront cart at all: if this is a post-checkout/thank-you return, drop the per-cart
           * order note + discount so they don't pre-fill the next Private Cellar cart (no-ops otherwise). */
          clearB2BCartAnnotationsIfPostCheckout();
          if (cartState.lastRenderedCartFingerprint !== fp || opts.orderJustSent) {
            cartState.lastRenderedCartFingerprint = fp;
            if (drawerBody) paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), null, opts);
          }
          {
            const bubble = storefrontHeaderBubbleFromAjaxFallback(ajaxCount);
            if (bubble !== null) {
              setB2BCartCount(bubble, 'no-cartId:ajax');
            }
          }
          applyPrivateCellarMinimumCheckoutUi(root, null);
          return;
        }

        let { cart, resolvedCartId } = await resolveStorefrontCartForSync(cartId);
        if (cart && shouldStripNonB2bLinesFromStorefrontCart()) {
          cart = await removeNonB2BTaggedLinesFromStorefrontCart(resolvedCartId, cart);
        }
        if (!cart) {
          setStoredStorefrontCartId('');
          const fp = 'empty';
          const ajaxCount = await getThemeAjaxCartItemCount();
          if (ajaxCount !== null) {
          } else {
          }
          /* Stored cart id no longer resolves — after a completed order the Storefront cart token is
           * gone, so this is the usual post-checkout landing. Drop the per-cart order note + discount
           * so they don't pre-fill the next Private Cellar cart (no-ops if not a post-checkout return). */
          clearB2BCartAnnotationsIfPostCheckout();
          if (cartState.lastRenderedCartFingerprint !== fp || opts.orderJustSent) {
            cartState.lastRenderedCartFingerprint = fp;
            if (drawerBody) paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), null, opts);
          }
          {
            const bubble = storefrontHeaderBubbleFromAjaxFallback(ajaxCount);
            if (bubble !== null) {
              setB2BCartCount(bubble, 'invalid-cartId:ajax');
            }
          }
          applyPrivateCellarMinimumCheckoutUi(root, null);
          return;
        }
        const lineEdges = cart?.lines?.edges || [];
        const stQty = Number(cart.totalQuantity) || 0;

        /*
         * Stale localStorage: Storefront cart id points at an empty B2B cart while the theme Ajax cart
         * still has items — previously we set the bubble to 0 here and hid the real count.
         */
        if (lineEdges.length === 0 && stQty === 0) {
          const ajaxCount = await getThemeAjaxCartItemCount();
          if (ajaxCount !== null && ajaxCount > 0) {
            setStoredStorefrontCartId('');
            const fp = 'empty';
            if (cartState.lastRenderedCartFingerprint !== fp || opts.orderJustSent) {
              cartState.lastRenderedCartFingerprint = fp;
              if (drawerBody) paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), null, opts);
            }
            setB2BCartCount(storefrontHeaderBubbleFromAjaxFallback(ajaxCount) || 0, 'stale-empty-storefront:ajax');
            applyPrivateCellarMinimumCheckoutUi(root, null);
            return;
          }
          const restoredFromSnapshot = await maybeRestoreStorefrontCartFromPreCheckoutSnapshot(runId, opts, root, drawerBody);
          if (restoredFromSnapshot) return;
          if (shouldTreatEmptyCartAsPostCheckoutAbandon()) {
            setStoredStorefrontCartId('');
            /* Order completed (or the cart is gone past recovery): drop the per-cart local state so the
             * Special Instructions note + discount code don't pre-fill the customer's NEXT Private Cellar
             * cart. Both are localStorage-persisted (scope-keyed) and outlive the emptied Storefront cart. */
            clearB2BCartAnnotationsIfPostCheckout();
            const fp = 'empty';
            if (cartState.lastRenderedCartFingerprint !== fp || opts.orderJustSent) {
              cartState.lastRenderedCartFingerprint = fp;
              if (drawerBody) paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), null, opts);
            }
            {
              const bubble = storefrontHeaderBubbleFromAjaxFallback(ajaxCount);
              if (bubble !== null) {
                setB2BCartCount(bubble, ajaxCount != null ? 'post-checkout-return:ajax' : 'post-checkout-return:empty');
              } else if (ajaxCount == null) {
                setB2BCartCount(0, 'post-checkout-return:empty');
              }
            }
            applyPrivateCellarMinimumCheckoutUi(root, null);
            return;
          }
        }

        root.setAttribute('data-b2b-cart-checkout-url', cart.checkoutUrl || '');
        const effScope = getEffectiveB2BCatalogScope(getConfig() || {});
        if (effScope) root.setAttribute('data-b2b-catalog-scope', effScope);

        // DIAGNOSTIC (temporary): on VIP pages with no Magical Fees destination set, dump the app's
        // trigger API so we can open its popup from here (the app only auto-shows it on b2c add).
        if (getEffectiveB2BCatalogScope(getConfig() || {}) === 'vip' && isMaFeesDestinationUnset()) {
          probeMaFeesTriggerApi();
        }

        // Sync the auto-managed fee placeholders (cents-as-quantity on a €0.01 variant): insurance
        // first, then the Private Cellar excise/duty. Both compute on product lines only, so the
        // stale `cart` between mutations is fine; re-fetch once after either changed.
        try {
          let feesMutated = false;
          if (await syncInsuranceFeeLine(resolvedCartId, cart)) feesMutated = true;
          if (await syncExciseFeeLine(resolvedCartId, cart)) feesMutated = true;
          if (feesMutated) {
            const refreshed = await fetchStorefrontCart(resolvedCartId);
            if (refreshed) cart = refreshed;
            /* A fee line was just added/updated/removed — force the repaint below so the visible line
             * list always matches the new total (otherwise the fee shows in the total but not as a row). */
            cartState.lastRenderedCartFingerprint = '';
          }
        } catch (feeErr) {
          console.warn('B2B fee sync threw', feeErr);
        }
        const fp = buildCartFingerprint(cart);
        /* While a line removal is in flight, hold the optimistic view — don't repaint the line back in
         * (and don't advance the fingerprint, so the removal's final sync still paints the real state). */
        const b2bDidPaint = (cartState.lastRenderedCartFingerprint !== fp || opts.orderJustSent) && cartState.b2bLineRemoveInFlight === 0;
        if (b2bDidPaint) {
          cartState.lastRenderedCartFingerprint = fp;
          if (drawerBody) paintB2BCartDrawerBody(/** @type {HTMLElement} */ (drawerBody), cart, opts);
        }
        if (cartState.b2bPaintLogBudget > 0 && getEffectiveB2BCatalogScope(getConfig() || {}) === 'vip') {
          cartState.b2bPaintLogBudget -= 1;
          const cfgPaint = getConfig() || {};
          b2bCartRoutingLog('pc_paint', {
            didPaint: b2bDidPaint,
            cartTotal: cart?.cost?.totalAmount?.amount,
            cartSubtotal: cart?.cost?.subtotalAmount?.amount,
            lines: (cart?.lines?.edges || []).map((/** @type {any} */ e) => ({
              title: String(e?.node?.merchandise?.product?.title || '').slice(0, 22),
              isFee: isAnyFeeLine(e?.node, cfgPaint),
              amount: e?.node?.cost?.totalAmount?.amount,
            })),
          });
        }
        applyPrivateCellarMinimumCheckoutUi(root, cart);
        schedulePrivateCellarMinimumCheckoutUiRetries(root);
        {
          const cfgForCount = getConfig() || {};
          const hasFeeConfigured =
            !!getConfiguredInsuranceFeeGid(cfgForCount) || !!getConfiguredExciseFeeGid(cfgForCount);
          /* Fee lines use cents-as-quantity (e.g. qty 7500 = €75) — never count them in the bubble. */
          const displayQty = hasFeeConfigured
            ? (cart?.lines?.edges || []).reduce(
                (/** @type {number} */ n, /** @type {any} */ e) => (isAnyFeeLine(e?.node, cfgForCount) ? n : n + (e?.node?.quantity || 0)),
                0
              )
            : stQty;
          setB2BCartCount(displayQty, 'storefront:totalQuantity');
          /* Remember this authoritative paint so the next page load can show the badge + drawer
           * instantly (see the optimistic replay in mountB2BCartUI) before this sync re-runs. */
          setStoredStorefrontCartSnapshot(displayQty, cart);
        }
      } catch (err) {
        console.error('B2B cart sync error:', err);
        b2bCartDebugLog('run#' + runId, 'sync error', err);
        /* Do not clear data-b2b-private-cellar-below-min here: transient errors would strip dimming while the drawer still shows the previous (disabled) checkout state. */
        /* If this run was reconciling an optimistic add and errored before the authoritative paint,
         * roll the drawer back to the last confirmed cart so a ghost line can't linger. */
        if (reconcileOptimistic) {
          try {
            const db = root.querySelector('[data-b2b-cart-drawer-body]');
            if (db) {
              cartState.lastRenderedCartFingerprint = '';
              paintB2BCartDrawerBody(/** @type {HTMLElement} */ (db), cartState.b2bLastStorefrontCartForMinUi || null, opts);
            }
          } catch (_) {
            /* ignore — best-effort rollback */
          }
        }
      } finally {
        /* Clear the pending shimmer only on the run that owns the optimistic add, so a concurrent
         * line-qty or removal shimmer isn't wiped out from under its own operation. */
        if (reconcileOptimistic) clearB2BCartShimmers();
        setB2BCartTotalLoadingState(root, false);
      }
    };
    b2bCartSyncChain = b2bCartSyncChain.then(run, () => {});
    return b2bCartSyncChain;
  }

  function mountB2BCartUI() {
    if (!document.querySelector('[data-b2b-config]')) return;
    if (document.querySelector('[data-b2b-cart-ui]')) return;
    try {
      const stamped = inferStampedCatalogScopeForCartUi();
      if (
        stamped === 'vip' &&
        !isPrivateCellarStorefrontCartEnabled() &&
        !hasAnyB2bScopeConfig()
      ) {
        return;
      }
    } catch (_) {
      /* proceed */
    }
    const cfg = getConfig() || {};
    const useHeaderTrigger = !!document.querySelector('[data-b2b-header-cart]');
    const wrap = document.createElement('div');
    wrap.setAttribute('data-b2b-cart-ui', 'true');
    wrap.setAttribute('data-b2b-catalog-scope', inferStampedCatalogScopeForCartUi());
    /* Storefront cart line/subtotal money is ex-VAT; tax-toggle must not divide by VAT again. */
    wrap.setAttribute('data-cart-taxes-included', 'false');
    if (cfg.sectionColorScheme) {
      wrap.classList.add('color-' + cfg.sectionColorScheme);
    }
    wrap.innerHTML = `
      ${useHeaderTrigger
        ? ''
        : `<button type="button" class="button b2b-cart-fab" data-b2b-cart-open>
        <span class="svg-wrapper b2b-cart-fab__icon">${ICON_CART}</span>
        <span class="b2b-cart-fab__label">${esc(cfg.cartTitleLabel || 'B2B Cart')}</span>
        <span class="b2b-cart-fab__count" aria-hidden="true">(<span data-b2b-cart-count>0</span>)</span>
      </button>`}
      <div class="b2b-cart-backdrop" data-b2b-cart-backdrop style="display:none;"></div>
      <aside class="b2b-cart-drawer cart-drawer__dialog dialog-modal dialog-drawer" data-b2b-cart-drawer style="display:none;">
        <div data-b2b-cart-drawer-body></div>
      </aside>
    `;
    document.body.appendChild(wrap);
    ensurePrivateCellarCheckoutPaintObserver(wrap);

    if (!document.getElementById('b2b-cart-ui-styles')) {
      const style = document.createElement('style');
      style.id = 'b2b-cart-ui-styles';
      style.textContent = `
        .button.b2b-cart-fab{position:fixed;right:16px;bottom:16px;z-index:70;display:inline-flex;align-items:center;justify-content:center;gap:0.35em;max-width:calc(100vw - 32px);min-width:0;width:auto;white-space:nowrap}
        .b2b-cart-fab__icon{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0}
        .b2b-cart-fab__icon svg{width:1.25rem;height:1.25rem;display:block}
        .b2b-cart-fab__label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .b2b-cart-fab__count{flex-shrink:0}
        /* Backdrop + drawer fade/slide animations mirror snippets/cart-drawer.liquid (see .dialog-modal[open] / .dialog-drawer[open] in base.css). */
        .b2b-cart-backdrop{position:fixed;inset:0;background:rgb(var(--backdrop-color-rgb,0 0 0) / var(--backdrop-opacity,.5));z-index:80;opacity:0;transition:opacity var(--animation-speed,.2s) var(--animation-easing,ease)}
        .b2b-cart-backdrop.b2b-cart-backdrop--open{opacity:1}
        .b2b-cart-drawer{position:fixed;top:0;right:0;width:min(420px,92vw);height:100dvh;background:rgb(var(--color-background-rgb,255 255 255));z-index:90;display:flex;flex-direction:column;overflow:hidden;transform:translateX(100%);box-shadow:var(--shadow-popover,0 12px 40px rgba(0,0,0,.14))}
        .b2b-cart-drawer.b2b-cart-drawer--open{animation:slideInLeft var(--animation-speed,.2s) var(--animation-easing,ease) forwards}
        .b2b-cart-drawer.b2b-cart-drawer--closing{animation:slideOutLeft var(--animation-speed,.2s) var(--animation-easing,ease) forwards}
        @media (prefers-reduced-motion: reduce){
          .b2b-cart-backdrop{transition:none}
          .b2b-cart-drawer.b2b-cart-drawer--open,
          .b2b-cart-drawer.b2b-cart-drawer--closing{animation:none}
          .b2b-cart-drawer.b2b-cart-drawer--open{transform:translateX(0)}
        }
        [data-b2b-cart-drawer-body]{display:flex;flex-direction:column;height:100%;min-height:0}
        .b2b-cart-drawer__content{display:flex;flex-direction:column;flex:1;min-height:0}
        .b2b-cart-drawer__items{display:flex;flex-direction:column;flex:1;min-height:0;overflow-y:auto;padding-inline:var(--cart-drawer-padding)}
        @media screen and (min-width: 750px){.b2b-cart-drawer__items{padding-inline:var(--cart-drawer-padding-desktop)}}
        .b2b-cart-lines{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;flex:1;min-height:0}
        /* Same rhythm as snippets/cart-products.liquid + respect --cart-items-gap (overrides header-actions drawer gap-xl on rows). */
        [data-b2b-cart-ui] .cart-drawer__items.b2b-cart-drawer__items ul.cart-items.b2b-cart-lines.cart-items--dividers > .b2b-cart-line > .cart-items__table-row{
          padding-bottom:var(--cart-items-gap);
          margin-bottom:var(--cart-items-gap);
          border-bottom:1px solid var(--color-border);
        }
        [data-b2b-cart-ui] .cart-drawer__items.b2b-cart-drawer__items ul.cart-items.b2b-cart-lines.cart-items--dividers > .b2b-cart-line:last-child > .cart-items__table-row{
          border-bottom:none;
          padding-block-end:0;
          margin-block-end:0;
        }
        /* Theme: .cart-drawer__summary .cart__summary-totals:not(:has(.cart__original-total-container:empty)) adds border-block-start; B2B has no that node so the line appeared above Reduction. */
        .cart-drawer__summary.b2b-cart-drawer__footer .cart__summary-totals:not(:has(.cart__original-total-container:empty)){border-block-start:none;padding-block-start:0}
        .b2b-cart-line{padding:0 16px}
        .b2b-cart-drawer__header{display:flex;align-items:center;justify-content:space-between;width:100%;min-width:0;padding:16px;gap:12px}
        .b2b-cart-drawer__header .cart-drawer__heading{display:flex;align-items:center;justify-content:flex-start;min-width:0;flex:1 1 auto}
        .b2b-cart-drawer__title-cluster{display:flex;align-items:center;gap:var(--gap-xs,8px);min-width:0;max-width:100%;flex:0 1 auto}
        .b2b-cart-drawer__title-text{min-width:0;flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:var(--font-h3--size,1.5rem)}
        /* Same structure as snippets/cart-bubble.liquid; sizing from base.css + snippets/cart-drawer.liquid (.cart-drawer__heading .cart-bubble) */
        .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble{flex-shrink:0}
        [data-b2b-cart-ui][data-b2b-catalog-scope="b2b"] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__background{background-color:var(--color-main-light-b2b,#E2CFC4)}
        [data-b2b-cart-ui][data-b2b-catalog-scope="vip"] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__background{background-color:var(--color-main-light-vip,#ded6d5)}
        [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble{color:var(--color-foreground)}
        [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__text-count{color:var(--color-foreground)}
        [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble{
          width:1.9rem;
          height:1.9rem;
          min-width:1.9rem;
          min-height:1.9rem;
          max-width:1.9rem;
          max-height:1.9rem;
          border-radius:999px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          overflow:hidden;
          aspect-ratio:1/1;
        }
        [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__background{border-radius:999px}
        [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__text{
          width:100%;
          height:100%;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          text-align:center;
          line-height:1;
        }
        [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__text-count{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-width:0;
          padding:0;
          font-size:.68rem;
          line-height:1;
          letter-spacing:-0.01em;
          white-space:nowrap;
        }
        body.professionals-b2b-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__background{background-color:var(--color-main-light-b2b,#E2CFC4)!important}
        body.professionals-b2b-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble,
        body.professionals-b2b-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__text,
        body.professionals-b2b-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__text-count{color:var(--color-foreground)!important}
        body.private-cellar-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__background{background-color:var(--color-main-light-vip,#ded6d5)!important}
        body.private-cellar-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble,
        body.private-cellar-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__text,
        body.private-cellar-nav [data-b2b-cart-ui] .b2b-cart-drawer__header .cart-drawer__heading .cart-bubble .cart-bubble__text-count{color:var(--color-foreground)!important}
        /* Same grid topology as snippets/cart-products.liquid (details + qty in middle column, line total upper-right spanning rows) */
        [data-b2b-cart-ui] .b2b-cart-lines .b2b-cart-line:not(.b2b-cart-line--fee) > .cart-items__table-row{
          --cart-item-price-width:6rem;
          grid-template-columns:clamp(2.25rem,12.5cqi,5.125rem) minmax(0,1fr) minmax(var(--cart-item-price-width),auto);
          grid-template-areas:
            'media details price'
            'media lineprice price'
            'media quantity price';
          align-items:start;
        }
        [data-b2b-cart-ui] .b2b-cart-lines .b2b-cart-line.b2b-cart-line--fee > .cart-items__table-row{
          --cart-item-price-width:6rem;
          grid-template-columns:clamp(2.25rem,12.5cqi,5.125rem) minmax(0,1fr) minmax(var(--cart-item-price-width),auto);
          grid-template-areas:'media details price';
          align-items:start;
        }
        /* Match cart-products typography/rhythm (.cart-items__table * reset + sibling gaps); drawer list has no .cart-items__table wrapper */
        [data-b2b-cart-ui] .b2b-cart-lines.cart-items > .b2b-cart-line .cart-items__table-row *{margin:0}
        [data-b2b-cart-ui] .b2b-cart-lines.cart-items > .b2b-cart-line .cart-items__details > * + *{
          margin-block-start:var(--margin-2xs);
        }
        .b2b-cart-line .cart-items__media.b2b-cart-line__thumb-wrap{background:transparent;display:flex;align-items:flex-start;justify-content:center;align-self:start;width:100%}
        .b2b-cart-line:not(.b2b-cart-line--fee) .cart-items__media-image.b2b-cart-line__thumb{object-fit:cover}
        .b2b-cart-line--fee .b2b-cart-line__thumb-wrap{width:54px;min-width:54px;height:82px;justify-content:flex-start}
        .b2b-cart-line--fee .b2b-cart-line__thumb{max-width:46px;max-height:64px;margin-left:8px}
        .b2b-cart-line--fee .cart-items__media-image.b2b-cart-line__thumb{object-fit:contain}
        .b2b-cart-line__content{min-width:0;width:100%}
        .b2b-cart-line__actions{display:flex;align-items:center;gap:8px}
        [data-b2b-cart-ui] .b2b-cart-lines .cart-items__price.b2b-cart-line__price{font-variant-numeric:tabular-nums}
        [data-b2b-cart-ui] .b2b-cart-lines .b2b-cart-line__inline-price{
          grid-area:lineprice;
          min-width:0;
          width:100%;
          box-sizing:border-box;
          font-size:var(--cart-font-size--sm);
          font-weight:var(--cart-primary-font-weight);
          font-style:var(--cart-primary-font-style);
          font-family:var(--cart-primary-font-family);
          color:rgb(var(--color-foreground-rgb) / var(--opacity-70));
          font-variant-numeric:tabular-nums;
          line-height:1.25;
          padding-block:var(--margin-2xs);
        }
        [data-b2b-cart-ui] .b2b-cart-lines a.cart-items__title,
        [data-b2b-cart-ui] .b2b-cart-lines .cart-items__title{
          display:block;
          width:100%;
          min-width:0;
          box-sizing:border-box;
          font-size:1rem;
          color:var(--color-foreground);
          text-transform:var(--product-title-case);
        }
        /* Drawer: compact qty row vs PDP (--height-buy-buttons). Stretch +/- to row; avoids min-touch min-height puffing height. */
        [data-b2b-cart-ui] .b2b-cart-lines .cart-items__quantity .b2b-cart-line__qty-selector{
          box-sizing:border-box;
          display:inline-flex;
          flex:0 1 var(--quantity-selector-width);
          font-size:var(--cart-font-size--sm);
          height:clamp(32px,2.125rem,36px);
          min-height:0;
          align-items:center;
        }
        [data-b2b-cart-ui] .b2b-cart-lines .cart-items__quantity .b2b-cart-line__qty-selector :is(.quantity-minus,.quantity-plus){
          box-sizing:border-box;
          min-height:0;
          margin:0;
          height:100%;
          align-self:stretch;
        }
        [data-b2b-cart-ui] .b2b-cart-lines .cart-items__quantity .b2b-cart-line__qty-selector input[type='number']{
          box-sizing:border-box;
          align-self:center;
          height:auto;
          max-height:100%;
          margin-block:0!important;
          padding-block:2px!important;
          line-height:1.15;
          pointer-events:none;
        }
        [data-b2b-cart-ui] .b2b-cart-lines .cart-items__quantity .b2b-cart-line__qty-selector .quantity-selector__select{
          box-sizing:border-box;
          align-self:center;
          height:auto;
          max-height:100%;
          margin-block:0!important;
          padding-block:2px!important;
          line-height:1.15;
          font-size:inherit;
        }
        .b2b-cart-line .cart-items__variant{opacity:.75}
        .cart-items__remove{background-color:transparent;color:var(--color-foreground);width:var(--minimum-touch-target,36px);height:var(--minimum-touch-target,36px);justify-content:center;box-shadow:none;padding:0}
        .remove-icon-bottom,.remove-icon-top{transition:transform var(--animation-speed,.2s) var(--animation-easing,ease)}
        .cart-items__remove:hover .remove-icon-top{transform:translate(calc(-1 * var(--icon-stroke-width,1px)), var(--icon-stroke-width,1px)) rotate(-15deg)}
        .cart-items__remove:is(:hover,:active) .remove-icon-bottom{transform:translateY(var(--icon-stroke-width,1px))}
        .b2b-cart-drawer__footer{margin-top:auto;display:flex;flex-direction:column;gap:12px;padding:16px;border-top:none;position:sticky;bottom:0;background:rgb(var(--color-background-rgb,255 255 255));z-index:1}
        .cart-actions{display:flex;flex-direction:column;align-items:stretch;gap:var(--gap-sm);border-block:1px solid var(--color-border,#d4d6d0);padding-block:var(--padding-sm)}
        .b2b-cart-discount-toggle{cursor:pointer;color:var(--color-foreground);display:flex;align-items:center;justify-content:space-between;padding-block:var(--padding-sm);width:100%;border:none;background:transparent;font:inherit;text-align:start}
        .b2b-cart-discount-toggle:is(:hover,:focus-visible){color:rgb(var(--color-foreground-rgb) / var(--opacity-subdued-text,.65))}
        .b2b-cart-discount-toggle[aria-expanded=true] .horizontal{rotate:90deg}
        .b2b-cart-discount-toggle .horizontal{transform-box:fill-box;transform-origin:center}
        .b2b-cart-discount-toggle .svg-wrapper svg{aspect-ratio:1;width:var(--icon-size-xs)}
        @media (prefers-reduced-motion:no-preference){.b2b-cart-discount-toggle .horizontal{transition:rotate var(--surface-transition-duration,.2s) var(--surface-transition-timing,ease)}}
        .b2b-cart-discount-panel[hidden]{display:none}
        .b2b-cart-discount-panel{width:100%}
        .b2b-cart-discount-actions{margin-top:8px;display:flex;align-items:center;justify-content:flex-start;gap:8px}
        .b2b-cart-discount-feedback{font-size:var(--cart-font-size--xs,.78rem);opacity:.85}
        .b2b-cart-note-panel{margin-top:8px}
        /* Match the theme cart note textarea (.cart-note__instructions in cart-products.liquid): same
         * input surface/border/shadow vars so the field background matches the rest of the store. */
        .b2b-cart-note-input{width:100%;box-sizing:border-box;resize:vertical;min-height:5.5rem;font:inherit;font-size:var(--cart-font-size--sm);line-height:1.4;color:var(--color-input-text);background-color:var(--color-input-background);border-style:solid;border-width:var(--style-border-width-inputs);border-color:var(--color-input-border);border-radius:var(--style-border-radius-inputs,4px);box-shadow:var(--input-box-shadow);transition:box-shadow var(--animation-speed) ease;padding:max(8px,calc(var(--style-border-radius-inputs,4px) * (1 - cos(45deg))))}
        /* Note + discount are both .cart-actions, whose own padding-block stacks on top of each toggle
         * button's padding-block. At their shared boundary, drop the note's bottom wrapper padding and the
         * discount's top wrapper padding + border, so it's a single divider with even spacing from the
         * toggle buttons alone — not an empty band. */
        .b2b-cart-note{padding-block-end:0}
        .b2b-cart-note + .cart-actions{border-block-start:none;padding-block-start:0}
        .cart__total-container{display:flex;flex-direction:column;gap:10px;padding-top:12px}
        .cart__summary-item.cart__total{display:flex;align-items:flex-end;justify-content:space-between}
        .cart__total-label{font-size:var(--cart-font-size--sm,.95rem)}
        .cart__total-value{font-size:clamp(1.7rem,2.8vw,2.1rem);font-weight:500}
        .b2b-tax-note{font-size:var(--cart-font-size--xs,.78rem);opacity:.85}
        .cart__ctas{display:grid;grid-template-columns:1fr}
        .b2b-pc-checkout-button{width:100%;height:clamp(38px,var(--height-buy-buttons,48px),55px)}
        [data-b2b-cart-ui][data-b2b-private-cellar-below-min="true"] .b2b-pc-checkout-button.button:disabled,
        [data-b2b-cart-ui] .b2b-pc-checkout-button.button[data-b2b-private-cellar-checkout-blocked="true"]{
          opacity:0.45!important;
          cursor:not-allowed!important;
          pointer-events:none!important;
        }
        .b2b-cart-drawer__header--empty{justify-content:flex-end}
        .b2b-cart-empty-state{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;text-align:center;padding:16px}
        .b2b-cart-line.b2b-removing{overflow:hidden;animation:b2b-line-remove .3s ease forwards}
        .b2b-cart-line--optimistic .b2b-cart-line__actions{opacity:.4;pointer-events:none}
        @keyframes b2b-line-remove{to{max-height:0;padding-top:0;padding-bottom:0;opacity:0;margin:0}}
        /* Pending price: shimmer the digits themselves via background-clip:text, mirroring the cart
         * total's text-component shimmer (assets/base.css) — not transparent text under a rectangular
         * ::after block. Only the glyphs catch the moving gradient, so it reads as a text effect. */
        .b2b-cart-line.b2b-updating .b2b-cart-line__price,
        .b2b-cart-line.b2b-updating .b2b-cart-line__inline-price{
          color:transparent;-webkit-text-fill-color:transparent;
          background-image:linear-gradient(-85deg,rgb(var(--color-foreground-rgb,0 0 0)/var(--opacity-50,.5)) 10%,rgb(var(--color-foreground-rgb,0 0 0)/var(--opacity-10,.1)) 50%,rgb(var(--color-foreground-rgb,0 0 0)/var(--opacity-50,.5)) 90%);
          background-size:200% 100%;background-position:100% 0;
          -webkit-background-clip:text;background-clip:text;
          animation:text-shimmer var(--shimmer-speed,1.25s) infinite linear;
        }
        .b2b-cart-items-sent-banner{
          margin:12px 16px 4px;
          padding:10px 12px 10px 14px;
          border-radius:8px;
          border-inline-start:3px solid var(--color-success,#2e7d32);
          background:rgb(var(--color-foreground-rgb,0 0 0)/.05);
          font-size:var(--font-body--size,.95rem);
          line-height:1.45;
        }
        .b2b-cart-items-sent-banner p{margin:0 0 .65em}
        .b2b-cart-items-sent-banner p:last-child{margin-bottom:0}
        .b2b-cart-items-sent-banner a,
        .b2b-order-sent-toast a{color:inherit;text-decoration:underline;text-underline-offset:.12em}
        .b2b-order-sent-toast{
          position:fixed;left:50%;bottom:24px;transform:translateX(-50%) translateY(12px);
          z-index:200;max-width:min(480px,calc(100vw - 32px));
          padding:14px 18px;border-radius:var(--style-border-radius-buttons-primary,8px);
          box-shadow:0 8px 28px rgba(0,0,0,.14);
          background:rgb(var(--color-background-rgb,255 255 255));
          border:1px solid rgb(var(--color-foreground-rgb,0 0 0)/.12);
          color:var(--color-foreground);
          font-size:var(--font-body--size,.95rem);line-height:1.45;text-align:left;
          opacity:0;transition:opacity .28s ease,transform .28s ease;pointer-events:auto;
        }
        .b2b-order-sent-toast p{margin:0 0 .5em}
        .b2b-order-sent-toast p:last-child{margin-bottom:0}
        .b2b-order-sent-toast.b2b-order-sent-toast--visible{
          opacity:1;transform:translateX(-50%) translateY(0);
        }
        .b2b-channel-alert-toast{border-left:4px solid #c0392b}
        .b2b-channel-alert-toast .b2b-channel-alert__names{font-weight:600}
      `;
      document.head.appendChild(style);
    }
    /*
     * Optimistic replay: paint the header bubble + drawer body from the last authoritative cart
     * (persisted in setStoredStorefrontCartSnapshot) BEFORE the network sync below, so neither the
     * badge nor the drawer waits on the Storefront round-trip for its first paint. The sync runs
     * immediately after and reconciles; if the live cart matches, the fingerprint guard skips a
     * redundant repaint, so there is no flicker. Purely best-effort — never block the real sync.
     */
    try {
      const snapshot = getStoredStorefrontCartSnapshot();
      if (snapshot && snapshot.count > 0) {
        setB2BCartCount(snapshot.count, 'mount:optimistic-snapshot');
        if (snapshot.cart) {
          const snapBody = wrap.querySelector('[data-b2b-cart-drawer-body]');
          if (snapBody) {
            cartState.lastRenderedCartFingerprint = buildCartFingerprint(snapshot.cart);
            paintB2BCartDrawerBody(/** @type {HTMLElement} */ (snapBody), snapshot.cart, {});
            cartState.b2bLastStorefrontCartForMinUi = snapshot.cart;
            applyPrivateCellarMinimumCheckoutUi(wrap, snapshot.cart);
          }
        }
      } else if (getStoredStorefrontCartEmpty() || !getStoredStorefrontCartId()) {
        /*
         * Known-empty (or no Storefront cart at all): paint the empty state INSTANTLY so opening the
         * drawer doesn't show a blank white panel until the network sync resolves. An emptied cart keeps
         * its id with 0 lines, so "no items snapshot" alone can't distinguish empty from never-seen —
         * the marker (set whenever a sync confirms count<=0 or the id is cleared) does. Fingerprint
         * 'empty' matches the sync's no-id / empty-cart branches, so they skip a redundant repaint when
         * the cart really is still empty (no flicker); a stale marker self-corrects to items on sync.
         */
        setB2BCartCount(0, 'mount:optimistic-empty');
        const emptyBody = wrap.querySelector('[data-b2b-cart-drawer-body]');
        if (emptyBody) {
          cartState.lastRenderedCartFingerprint = 'empty';
          paintB2BCartDrawerBody(/** @type {HTMLElement} */ (emptyBody), null, {});
          cartState.b2bLastStorefrontCartForMinUi = null;
          applyPrivateCellarMinimumCheckoutUi(wrap, null);
        }
      }
    } catch (_) {
      /* optimistic only — the network sync below is the source of truth */
    }
    /* First sync: without a Storefront id, Ajax /cart.js can fill the bubble on non-B2B-nav pages (avoid flashing 0). For Professionals, bubble stays Storefront-only (storefrontHeaderBubbleFromAjaxFallback). */
    void syncB2BCartUI();
    if (!vipCartMergeListenerAttached) {
      vipCartMergeListenerAttached = true;
      document.addEventListener('cart:update', async (/** @type {any} */ e) => {
        if (e.detail?.sourceId === 'b2b-vip-merge') return;
        if (!document.querySelector('[data-b2b-cart-ui]')) return;
        if (!shouldMergeAjaxCartIntoPrivateCellarStorefrontCart()) return;
        const detailData = e.detail?.data || {};
        if (detailData.didError) return;
        /* Theme quick-add / PDP use product-form-component; collection wine cards use wine-card-purchase (snippets/wine-card-purchase.liquid). Open drawer before merge/sync so the UI does not wait on Storefront + Ajax. */
        const openDrawerAfterThemeAdd =
          detailData.source === 'product-form-component' || detailData.source === 'wine-card-purchase';
        if (openDrawerAfterThemeAdd) {
          const root = /** @type {HTMLElement | null} */ (document.querySelector('[data-b2b-cart-ui]'));
          if (root?.querySelector('[data-b2b-cart-drawer]')) setB2BCartDrawerOpen(true);
          /*
           * INSTANT paint, NO /cart.js round-trip. The theme add already landed and the event carries the
           * variant id (wine-card-purchase: data.variantId; product-form-component: sourceId), so paint the
           * new line straight from the cached cart + DOM hints — the just-added regular product appears
           * immediately, matching the VIP direct-buy path. Guarded to the cases that render cleanly: the
           * variant is already in the cached cart (accurate qty bump) OR we have a real title hint, so a
           * brand-new line never flashes blank. The fetch-based paint below then refines exact details.
           */
          const instantVariantGid = variantGidFromAjaxId(detailData.variantId || e.detail?.sourceId || '');
          const instantQty = parseInt(String(detailData.itemCount), 10) || 1;
          const instantHints = readThemeAddDisplayHints(e);
          const snapshotEdges = cartState.b2bLastStorefrontCartForMinUi?.lines?.edges || [];
          const variantInSnapshot =
            !!instantVariantGid &&
            snapshotEdges.some((/** @type {any} */ ed) => ed?.node?.merchandise?.id === instantVariantGid);
          const instantPainted =
            instantVariantGid && (variantInSnapshot || (instantHints.title && instantHints.title.length))
              ? await optimisticallyPaintDirectStorefrontAdd(instantVariantGid, instantQty, instantHints)
              : false;
          /* Refine with the full theme cart (accurate title/image + any other pending boutique lines),
           * then reconcile authoritatively. reconcileOptimistic must be set if EITHER paint ran so the
           * sync clears the shimmer. */
          optimisticallyPaintPrivateCellarAdd().then(
            (painted) => {
              void syncB2BCartUI({ allowPrimeursConflictModal: true, reconcileOptimistic: painted === true || instantPainted });
            },
            () => {
              void syncB2BCartUI({ allowPrimeursConflictModal: true, reconcileOptimistic: instantPainted });
            }
          );
        } else {
          void syncB2BCartUI({ allowPrimeursConflictModal: true });
        }
      });
    }
    attachMaFeesLocationWatch();
    attachMaFeesAutoConfirm();

    /*
     * Back/forward-cache restore (notably hitting "back" from the Storefront checkout) re-shows this
     * page WITHOUT re-running init, so module state survives — including `b2bCheckoutInFlight`, set
     * true on the Pay click. The €120 gate honours that flag, so Pay would stay disabled even though
     * the cart is checkout-able (diagnostic: below:false, minMet/pendingMergeLines true, disabledAfter:
     * true). Clear it on bfcache restore and re-sync so the button + cart reflect the real state.
     */
    window.addEventListener('pageshow', (/** @type {any} */ e) => {
      if (!e || !e.persisted) return;
      cartState.b2bCheckoutInFlight = false;
      try {
        void syncB2BCartUI(undefined);
      } catch (_) {
        /* best-effort: a sync failure must not leave Pay stuck — the flag is already cleared */
      }
    });
  }





  /* ------------------------------------------------------------------ */
  /*  Private Cellar destination prompt                                  */
  /*                                                                     */
  /*  PC-only carts never touch the native /cart, so the Magical Fees    */
  /*  location popup never fires for them (MF exposes no API to open it: */
  /*  no window globals, dialog built inside its module closure). MF     */
  /*  does, however, trust whatever `__ma_fees_selected_location` holds  */
  /*  at init — so this prompt asks the same question ourselves and      */
  /*  writes the exact object shape MF's own confirm handler writes.     */
  /*  The PC excise reacts instantly (Storage.prototype watcher) and MF  */
  /*  won't re-ask on b2c pages (confirmed + mirrored).                  */
  /* ------------------------------------------------------------------ */












  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  async function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    clearB2BOrderRequestSentFlag();
    const cfg = getConfig() || {};
    const cartId = getStoredStorefrontCartId();
    b2bCartRoutingLog('storefront_add_to_cart_start', {
      variantId: String(variantId || ''),
      quantity,
      existingStorefrontCartId: cartId || '',
      scopeSuffix: getScopeKeySuffix(),
      effectiveCatalogScope: getEffectiveB2BCatalogScope(cfg),
    });
    if (!cartId) {
      b2bCartRoutingLog('storefront_add_to_cart_create_cart', {
        reason: 'no_existing_storefront_cart_id_for_scope',
        scopeSuffix: getScopeKeySuffix(),
      });
      return createStorefrontCartWithLine(variantId, quantity);
    }
    try {
      b2bCartRoutingLog('storefront_add_to_cart_update_existing_cart', {
        cartId,
        variantId: String(variantId || ''),
        quantity,
      });
      return await addLineToExistingStorefrontCart(cartId, variantId, quantity);
    } catch (error) {
      b2bCartRoutingLog('storefront_add_to_cart_existing_failed_fallback_create', {
        cartId,
        variantId: String(variantId || ''),
        quantity,
        error: error && error.message ? String(error.message) : String(error || ''),
      });
      return createStorefrontCartWithLine(variantId, quantity);
    }
  }

  /**
   * Best-effort reads off the product card a B2B buy button lives in, used only to paint the optimistic
   * add row instantly. Both are display-only (the authoritative sync replaces them within a moment) and
   * never throw — a missing node just yields '' and the optimistic line falls back to title + quantity.
   * @param {Element} buyBtn
   * @returns {string}
   */
  function readB2BCardImageUrlForOptimisticAdd(buyBtn) {
    try {
      const card = buyBtn.closest('product-card') || buyBtn.closest('[data-product-id]');
      const img = card ? card.querySelector('.product-media-container img, .card-gallery img') : null;
      const raw = img instanceof HTMLImageElement ? img.currentSrc || img.getAttribute('src') || '' : '';
      /* Strip the card's width transform so the drawer renderer appends its own (avoids a double
       * ?width= param); the bare Shopify CDN url still resolves. */
      return raw ? raw.split('?')[0] : '';
    } catch (_) {
      return '';
    }
  }

  /**
   * @param {Element} buyBtn
   * @returns {string}
   */
  function readB2BSelectedVariantLabelForOptimisticAdd(buyBtn) {
    try {
      const container = buyBtn.closest('.wine-purchase-container');
      const select = /** @type {HTMLSelectElement | null} */ (container ? container.querySelector('[data-b2b-variant-select]') : null);
      if (!select || select.selectedIndex < 0) return '';
      const opt = select.options[select.selectedIndex];
      return opt ? String(opt.text || '').trim() : '';
    } catch (_) {
      return '';
    }
  }

  /**
   * Best-effort product display hints for the INSTANT optimistic paint of a regular (boutique) line
   * being merged into the VIP cart. The theme's `cart:update` fires from product-form-component (bubbles,
   * so `e.target` is the form) or wine-card-purchase (dispatched on `document`, identified by
   * `sourceId` = `data-container-id`); we locate the product scope from whichever applies and read its
   * image, title, and primeurs flag. Never throws — a missing node yields a sparse hint that the
   * fetch-based refine then fills in.
   * @param {any} e cart:update event
   * @returns {{ title?: string, imageUrl?: string, isPrimeurs?: boolean }}
   */
  function readThemeAddDisplayHints(e) {
    try {
      const detail = (e && e.detail) || {};
      const data = detail.data || {};
      const esc = (/** @type {string} */ v) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v);
      /** @type {Element | null} */
      let scope = null;
      const containerId = detail.sourceId ? String(detail.sourceId) : '';
      if (containerId) scope = document.querySelector(`[data-container-id="${esc(containerId)}"]`);
      if (!scope && data.productId) {
        const pid = esc(String(data.productId));
        scope = document.querySelector(`.wine-purchase-container[data-product-id="${pid}"], product-card[data-product-id="${pid}"]`);
      }
      if (!scope && e && e.target instanceof Element) {
        scope = e.target.closest('.wine-purchase-container, product-card, product-form-component, .product-information, main') || e.target;
      }
      if (!(scope instanceof Element)) return {};
      const img = scope.querySelector('.product-media-container img, .card-gallery img, .product-media img, .media img, img');
      const imageUrl = img instanceof HTMLImageElement ? (img.currentSrc || img.getAttribute('src') || '').split('?')[0] : '';
      const titleEl = scope.querySelector('[data-product-title], .wine-card-main-title, .product-title, .product__title, .card__heading, h1');
      let title = titleEl ? String(titleEl.textContent || '').trim() : '';
      if (!title && img instanceof HTMLImageElement) title = String(img.getAttribute('alt') || '').trim();
      const primAttr = typeof scope.getAttribute === 'function' ? scope.getAttribute('data-is-primeurs-product') : null;
      const isPrimeurs = primAttr == null ? undefined : primAttr === 'true';
      return { title, imageUrl, isPrimeurs };
    } catch (_) {
      return {};
    }
  }


  /* Persist the Private Cellar order note as the customer types. Delegated (the drawer re-renders via
   * innerHTML), local-only here; it's pushed onto the Storefront cart at checkout. The textarea is
   * repopulated from this value on each render, so typed text survives cart repaints. */
  document.addEventListener('input', (/** @type {any} */ e) => {
    const target = e.target instanceof Element ? e.target : null;
    const ta = target && /** @type {any} */ (target.closest('[data-b2b-note-input]'));
    if (!ta) return;
    setStoredB2BCartNote(ta.value || '');
  });

  /* ------------------------------------------------------------------ */
  /*  Global click handler for B2B buy buttons                           */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', async (/** @type {any} */ e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const openCartBtn = target.closest('[data-b2b-cart-open]');
    if (openCartBtn) {
      e.preventDefault();
      setB2BCartDrawerOpen(true);
      /* Drawer body is already painted (optimistic snapshot on mount, or a prior sync); this is a
       * silent background refresh — no loading shimmer (see fromDrawerOpen in syncB2BCartUI). */
      await syncB2BCartUI({ fromDrawerOpen: true });
      return;
    }

    const closeCartBtn = target.closest('[data-b2b-cart-close], [data-b2b-cart-backdrop]');
    if (closeCartBtn) {
      e.preventDefault();
      setB2BCartDrawerOpen(false);
      return;
    }

      const discountToggleBtn = /** @type {any} */ (target.closest('[data-b2b-discount-toggle]'));
    if (discountToggleBtn) {
      e.preventDefault();
      const panel = discountToggleBtn.parentElement?.querySelector('[data-b2b-discount-panel]');
      const isExpanded = discountToggleBtn.getAttribute('aria-expanded') === 'true';
      discountToggleBtn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      if (panel) /** @type {any} */ (panel).hidden = isExpanded;
      return;
    }

    const noteToggleBtn = /** @type {any} */ (target.closest('[data-b2b-note-toggle]'));
    if (noteToggleBtn) {
      e.preventDefault();
      const panel = noteToggleBtn.parentElement?.querySelector('[data-b2b-note-panel]');
      const isExpanded = noteToggleBtn.getAttribute('aria-expanded') === 'true';
      noteToggleBtn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      if (panel) {
        /** @type {any} */ (panel).hidden = isExpanded;
        if (!isExpanded) {
          const ta = /** @type {any} */ (panel.querySelector('[data-b2b-note-input]'));
          if (ta) try { ta.focus(); } catch (_) { /* ignore */ }
        }
      }
      return;
    }

    const discountApplyBtn = target.closest('[data-b2b-discount-apply]');
    if (discountApplyBtn) {
      e.preventDefault();
      const panel = discountApplyBtn.closest('[data-b2b-discount-panel]');
      const input = /** @type {any} */ (panel?.querySelector('[data-b2b-discount-input]'));
      const feedback = /** @type {any} */ (panel?.querySelector('[data-b2b-discount-feedback]'));
      const code = (input?.value || '').trim();
      setStoredB2BDiscountCode(code);
      if (feedback) {
        feedback.textContent = code ? formatDiscountFeedback(getConfig() || {}, code) : '';
      }
      /* Discount code isn't part of buildCartFingerprint (it's local, not on the Storefront cart), so
       * force the paint — otherwise the applied-code pill never renders until the cart lines change. */
      cartState.lastRenderedCartFingerprint = '';
      await syncB2BCartUI(undefined);
      return;
    }

    const discountClearBtn = target.closest('[data-b2b-discount-clear]');
    if (discountClearBtn) {
      e.preventDefault();
      setStoredB2BDiscountCode('');
      /* The discount code lives in localStorage, not the Storefront cart, so removing it doesn't change
       * buildCartFingerprint — without this reset the paint is skipped and the pill never disappears. */
      cartState.lastRenderedCartFingerprint = '';
      await syncB2BCartUI(undefined);
      return;
    }

      const checkoutCartBtn = /** @type {any} */ (target.closest('[data-b2b-cart-checkout]'));
    if (checkoutCartBtn) {
      e.preventDefault();
      const cfg = getConfig() || {};
      const root = /** @type {any} */ (document.querySelector('[data-b2b-cart-ui]'));
      const drawerBody = /** @type {any} */ (root?.querySelector('[data-b2b-cart-drawer-body]'));

      if (isB2BScope() && cfg.checkoutFlow === 'order_request') {
        try {
          clearB2BOrderRequestSentFlag();
          const cartId = getStoredStorefrontCartId();
          const cart = cartId ? await fetchStorefrontCart(cartId) : null;
          if (drawerBody) drawerBody.innerHTML = renderB2BOrderRequestFormHtml(cart);
          setB2BCartDrawerOpen(true);
        } catch (err) {
          b2bCaptureError('order-request-ui', err);
          alert(err?.message || (getConfig() || {}).alertUnableOpenOrderRequest || 'Unable to open order request.');
        }
        return;
      }

      if (isB2BScope() && cfg.draftOrderEndpoint) {
        const originalLabel = checkoutCartBtn.textContent;
        checkoutCartBtn.disabled = true;
        checkoutCartBtn.textContent = '…';
        try {
          const payload = await createDraftOrderFromB2BCart();
          if (payload?.redirectUrl) {
            window.location.href = payload.redirectUrl;
            return;
          }
          if (drawerBody) drawerBody.innerHTML = renderB2BBankTransferDraftOrderHtml(payload);
          setB2BCartDrawerOpen(true);
        } catch (err) {
          b2bCaptureError('draft-order', err);
          alert(err?.message || (getConfig() || {}).alertUnablePlaceOrderRequest || 'Unable to place order request.');
        } finally {
          checkoutCartBtn.disabled = false;
          checkoutCartBtn.textContent = originalLabel;
        }
        return;
      }

      /*
       * VIP / Private Cellar: the €120 minimum and the checkout itself must run on the single
       * merged cart. Force the boutique→Private Cellar merge (and a re-sync that refreshes the
       * checkout URL + gate, creating the VIP cart if it did not exist) before reading the cart —
       * otherwise a split cart could either block a ≥€120 combined order (boutique items stranded
       * in the theme cart) or wave through a <€120 one.
       */
      /*
       * Mark checkout in flight so the button stays disabled across the merge/sync + redirect delay
       * (renderB2BCartDrawerHtml and the €120 gate both honour cartState.b2bCheckoutInFlight). Disable
       * the clicked button immediately for instant feedback, before the first repaint. The flag is
       * cleared only on paths that do NOT redirect — a successful redirect unloads the page.
       */
      cartState.b2bCheckoutInFlight = true;
      if (checkoutCartBtn instanceof HTMLButtonElement) {
        checkoutCartBtn.disabled = true;
        checkoutCartBtn.setAttribute('aria-busy', 'true');
      }

      if (getEffectiveB2BCatalogScope(cfg) === 'vip') {
        try {
          await syncB2BCartUI({ allowPrimeursConflictModal: true });
        } catch (_) {
          /* fall through and evaluate whatever state we have */
        }
      }

      const checkoutUrl = root?.getAttribute('data-b2b-cart-checkout-url') || '';
      const discountCode = getStoredB2BDiscountCode();
      const nextCheckoutUrl = buildCheckoutUrlWithDiscount(checkoutUrl, discountCode);
      if (nextCheckoutUrl) {
        (async () => {
          try {
            const cid = getStoredStorefrontCartId();
            if (cid) {
              const c = await fetchStorefrontCart(cid);
              if (getEffectiveB2BCatalogScope(cfg) === 'vip' && c && !isPrivateCellarMinimumMet(c, cfg)) {
                /* Still under €120 on the merged cart — release the button, reflect it, and stop. */
                cartState.b2bCheckoutInFlight = false;
                applyPrivateCellarMinimumCheckoutUi(root, c);
                return;
              }
              savePreCheckoutCartSnapshotFromCart(c);
              /* Carry the customer's order note onto the cart so it reaches the Headless checkout +
               * order. Best-effort — a note failure must never block checkout — but log the outcome
               * (note length, cart id, success/error) so a "note missing on order" report is
               * diagnosable: noteLen:0 = empty note; applied:false = the mutation failed; applied:true
               * with noteLen>0 but still absent on the order = a checkout/propagation issue. */
              const noteToApply = getStoredB2BCartNote();
              try {
                await updateStorefrontCartNote(cid, noteToApply);
                b2bCartRoutingLog('checkout_note', {
                  applied: true,
                  noteLen: (noteToApply || '').length,
                  cidTail: String(cid).slice(-16),
                  checkoutUrlTail: String(checkoutUrl).slice(-44),
                });
              } catch (noteErr) {
                b2bCartRoutingLog('checkout_note', {
                  applied: false,
                  noteLen: (noteToApply || '').length,
                  cidTail: String(cid).slice(-16),
                  error: String((noteErr && noteErr.message) || noteErr),
                });
              }
            }
          } catch (_) {
            /* ignore */
          }
          markB2BRedirectedToStorefrontCheckout();
          window.location.href = nextCheckoutUrl;
        })();
      } else {
        /* No checkout URL to redirect to — release the button so the customer can retry. */
        cartState.b2bCheckoutInFlight = false;
        applyPrivateCellarMinimumCheckoutUi(root);
      }
      return;
    }

    const orderRequestBackBtn = target.closest('[data-b2b-order-request-back]');
    if (orderRequestBackBtn) {
      e.preventDefault();
      /* Order request view replaces the drawer HTML but does not update cartState.lastRenderedCartFingerprint; without invalidating, sync skips re-render and the cart never comes back. */
      cartState.lastRenderedCartFingerprint = '';
      await syncB2BCartUI(undefined);
      setB2BCartDrawerOpen(true);
      return;
    }

    const orderRequestCopyBtn = target.closest('[data-b2b-order-request-copy]');
    if (orderRequestCopyBtn) {
      e.preventDefault();
      const root = document.querySelector('[data-b2b-cart-ui]');
      const form = /** @type {HTMLFormElement | null} */ (root?.querySelector('form[data-b2b-order-request-form]'));
      const feedback = /** @type {any} */ (root?.querySelector('[data-b2b-order-request-feedback]'));
      try {
        if (!form) throw new Error((getConfig() || {}).errorFormNotFound || 'Form not found');
        const cartId = getStoredStorefrontCartId();
        const cart = cartId ? await fetchStorefrontCart(cartId) : null;
        const fd = new FormData(form);
        const formData = Object.fromEntries(fd.entries());
        const { subject, body } = buildOrderRequestEmail({ cart, formData });
        const text = `${subject}\n\n${body}`;
        await navigator.clipboard.writeText(text);
        if (feedback) feedback.textContent = (getConfig() || {}).feedbackCopied || '';
      } catch (err) {
        b2bCaptureError('order-request-copy', err);
        if (feedback) feedback.textContent = (getConfig() || {}).feedbackCopyFailed || '';
      }
      return;
    }

    const removeBtn = target.closest('[data-b2b-line-remove]');
    if (removeBtn) {
      e.preventDefault();
      const lineNode = /** @type {HTMLElement | null} */ (removeBtn.closest('.b2b-cart-line'));
      const lineId = lineNode?.getAttribute('data-b2b-line-id') || '';
      if (!lineId) return;
      applyB2BCartOptimisticRemove(lineNode);
      cartState.lastRenderedCartFingerprint = '';
      /* Hold the optimistic (line-hidden) view until the delete lands, so a concurrent sync can't
       * repaint the line back in. The final sync below (in-flight back to 0) paints the real state. */
      cartState.b2bLineRemoveInFlight += 1;
      try {
        await updateStorefrontCartLine(lineId, 0);
      } catch (err) {
        b2bCaptureError('cart-line-remove', err, { lineId });
        clearB2BCartShimmers();
      } finally {
        cartState.b2bLineRemoveInFlight = Math.max(0, cartState.b2bLineRemoveInFlight - 1);
        cartState.lastRenderedCartFingerprint = '';
        await syncB2BCartUI(undefined);
      }
      return;
    }

    const decBtn = target.closest('[data-b2b-line-decrease]');
    if (decBtn) {
      e.preventDefault();
      const lineNode = /** @type {HTMLElement | null} */ (decBtn.closest('.b2b-cart-line'));
      const lineId = lineNode?.getAttribute('data-b2b-line-id') || '';
      if (!lineId) return;
      const currentQty = parseInt(lineNode?.getAttribute('data-b2b-line-qty') || '1', 10) || 1;
      if (currentQty <= 1) return;
      clearB2BCartLineError(lineNode);
      await b2bApplyCartLineQtyChange(lineNode, lineId, currentQty - 1, 'cart-line-decrease');
      return;
    }

    const incBtn = target.closest('[data-b2b-line-increase]');
    if (incBtn) {
      e.preventDefault();
      const lineNode = /** @type {HTMLElement | null} */ (incBtn.closest('.b2b-cart-line'));
      const lineId = lineNode?.getAttribute('data-b2b-line-id') || '';
      if (!lineId) return;
      const currentQty = parseInt(lineNode?.getAttribute('data-b2b-line-qty') || '1', 10) || 1;
      const lineStock = parseInt(lineNode?.getAttribute('data-b2b-line-stock') || '-1', 10);
      const unlimited = isNaN(lineStock) || lineStock < 0;
      const cap = unlimited ? Infinity : lineStock;
      // Increment by 1 directly (no dialog) up to the real stock cap. Past 24
      // we simply add a sticky [data-custom-quantity] option via the optimistic
      // update, matching the regular cart's stepper behaviour.
      if (currentQty >= cap) {
        showB2BCartLineError(lineNode, getB2BMaxStockMessage(unlimited ? -1 : lineStock));
        return;
      }
      clearB2BCartLineError(lineNode);
      await b2bApplyCartLineQtyChange(lineNode, lineId, currentQty + 1, 'cart-line-increase');
      return;
    }

    /**
     * Collapse the expanded purchase options for a product card.
     * @param {Element | null} container
     */
    function collapsePurchaseOptions(container) {
      if (!container) return;
      const options = /** @type {any} */ (container.querySelector('.wine-purchase-options'));
      const closeBtn = /** @type {any} */ (container.querySelector('[data-b2b-close-options]'));
      const purchaseButton = /** @type {any} */ (container.querySelector('[data-b2b-purchase-button]'));
      const addBtn = /** @type {any} */ (container.querySelector('[data-b2b-add-button]'));
      if (options) {
        options.style.maxHeight = '0';
        options.style.opacity = '0';
        options.style.marginBottom = '0';
        options.style.pointerEvents = 'none';
      }
      if (closeBtn) {
        closeBtn.style.maxHeight = '0';
        closeBtn.style.opacity = '0';
        closeBtn.style.marginTop = '0';
        closeBtn.style.padding = '0';
        closeBtn.style.pointerEvents = 'none';
      }
      if (purchaseButton) purchaseButton.style.display = '';
      if (addBtn) addBtn.style.display = 'none';
    }

    const purchaseBtn = /** @type {any} */ (target.closest('[data-b2b-purchase-button]'));
    if (purchaseBtn) {
      e.preventDefault();
      e.stopPropagation();
      const container = purchaseBtn.closest('.wine-purchase-container');
      if (!container || purchaseBtn.disabled) return;
      const options = /** @type {any} */ (container.querySelector('.wine-purchase-options'));
      const closeBtn = /** @type {any} */ (container.querySelector('[data-b2b-close-options]'));
      const addBtn = /** @type {any} */ (container.querySelector('[data-b2b-add-button]'));
      if (options) {
        options.style.maxHeight = '500px';
        options.style.opacity = '1';
        options.style.marginBottom = '12px';
        options.style.pointerEvents = 'auto';
      }
      if (closeBtn) {
        closeBtn.style.maxHeight = '50px';
        closeBtn.style.opacity = '1';
        closeBtn.style.marginTop = '8px';
        closeBtn.style.padding = '8px 16px';
        closeBtn.style.pointerEvents = 'auto';
      }
      purchaseBtn.style.display = 'none';
      if (addBtn) addBtn.style.display = '';
      return;
    }

      const closeOptionsBtn = /** @type {any} */ (target.closest('[data-b2b-close-options]'));
    if (closeOptionsBtn) {
      e.preventDefault();
      e.stopPropagation();
      const container = closeOptionsBtn.closest('.wine-purchase-container');
      if (!container) return;
      collapsePurchaseOptions(container);
      return;
    }

    const btn = /** @type {any} */ (target.closest('[data-b2b-buy]'));
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const cfgBuyGate = getConfig() || {};
    if (
      getEffectiveB2BCatalogScope(cfgBuyGate) === 'vip' &&
      !isPrivateCellarStorefrontCartEnabled()
    ) {
      try {
        const raw =
          typeof document !== 'undefined' && document.body
            ? /** @type {any} */ (document.body.dataset).privateCellarCartDisabledMessage
            : '';
        const msg = String(raw || '').trim() || 'Private cellar checkout is temporarily unavailable.';
        alert(msg);
      } catch (_) {
        alert('Private cellar checkout is temporarily unavailable.');
      }
      return;
    }

    const variantId = btn.dataset.b2bBuy;
    const qtySourceId = btn.getAttribute('data-b2b-qty-source') || 'b2b-qty-select';
    const qtyEl = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (document.getElementById(qtySourceId));
    const quantity = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
    const cfg = getConfig() || {};
    const scopeSuffix = getScopeKeySuffix();
    const activeCartStorageKey = withCartPersistenceKey(STOREFRONT_CART_ID_KEY_BASE);
    const isPrivateCellarPage =
      typeof document !== 'undefined' && document.body ? document.body.classList.contains('private-cellar-nav') : false;
    const isB2bProfessionalsPage =
      typeof document !== 'undefined' && document.body
        ? document.body.classList.contains('professionals-b2b-nav')
        : false;
    const productContainer = btn.closest('[data-product-id]');
    const productMetaContainer = btn.closest('[data-product-tags]') || productContainer;
    const productId = productContainer ? productContainer.getAttribute('data-product-id') || '' : '';
    const productTitle = productMetaContainer ? productMetaContainer.getAttribute('data-product-title') || '' : '';
    const productVendor = productMetaContainer ? productMetaContainer.getAttribute('data-product-vendor') || '' : '';
    const productTags = readProductTagsFromElement(productMetaContainer);
    let incomingIsPrimeurs = hasPrimeursTag(productTags);
    primeursGuardDebug('incoming_product_initial', {
      variantId: String(variantId || ''),
      productId,
      productTitle,
      productVendor,
      tagsFromDom: productTags,
      isPrimeursFromDom: incomingIsPrimeurs,
    });

    /*
     * Instant feedback FIRST. Open the drawer and paint the just-added line from the last confirmed
     * cart + this card's DOM, BEFORE the (networked) primeurs refine/guard, the Storefront add, and the
     * reconcile sync — so the cart appears immediately instead of after several round-trips. The paint
     * is skipped (returns false) on a primeurs↔regular conflict, so we never flash a cart we may force-
     * clear; the DOM-derived primeurs flag is enough here because the guard below re-checks live.
     */
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '…';
    const cartRoot = document.querySelector('[data-b2b-cart-ui]');
    const hasCartDrawer = !!(cartRoot && cartRoot.querySelector('[data-b2b-cart-drawer]'));
    if (hasCartDrawer) setB2BCartDrawerOpen(true);
    const paintedOptimistic = hasCartDrawer
      ? await optimisticallyPaintDirectStorefrontAdd(variantId, quantity, {
          title: productTitle,
          imageUrl: readB2BCardImageUrlForOptimisticAdd(btn),
          variantLabel: readB2BSelectedVariantLabelForOptimisticAdd(btn),
          isPrimeurs: incomingIsPrimeurs,
        })
      : false;
    /* Undo the optimistic paint: repaint the last confirmed cart (clears the pending row + shimmer). */
    const rollbackOptimisticAdd = () => {
      if (!paintedOptimistic) return;
      clearB2BCartShimmers();
      cartState.lastRenderedCartFingerprint = '';
      void syncB2BCartUI(undefined);
    };

    if (!incomingIsPrimeurs) {
      const variantPrimeursStatus = await getVariantPrimeursStatus(variantId);
      if (variantPrimeursStatus != null) incomingIsPrimeurs = variantPrimeursStatus;
    }
    primeursGuardDebug('incoming_product_final', {
      variantId: String(variantId || ''),
      productId,
      incomingIsPrimeurs,
    });
    const isVipScope = getEffectiveB2BCatalogScope(cfg) === 'vip';

    b2bCartRoutingLog('buy_button_click', {
      action: isVipScope ? 'attempt_private_product_add_to_private_cart' : 'attempt_regular_b2b_product_add_to_b2b_cart',
      scopeSuffix,
      effectiveCatalogScope: getEffectiveB2BCatalogScope(cfg),
      activeCartStorageKey,
      existingStorefrontCartId: getStoredStorefrontCartId() || '',
      variantId: String(variantId || ''),
      quantity,
      productId,
      productTitle,
      productVendor,
      productTags,
      incomingIsPrimeurs,
      isPrivateCellarPage,
      isB2bProfessionalsPage,
      bodyClasses: typeof document !== 'undefined' && document.body ? Array.from(document.body.classList || []) : [],
    });
    try {
      const canProceed = await guardPrimeursCartConflict(incomingIsPrimeurs);
      if (!canProceed) {
        rollbackOptimisticAdd();
        btn.textContent = originalText;
        btn.disabled = false;
        return;
      }
      await addToCart(variantId, quantity);
      /* The drawer is already open showing the pending line (painted above); this reconciles it to the
       * authoritative cart and clears the shimmer — or, on error, rolls back to the last confirmed cart
       * (see the reconcileOptimistic branch in syncB2BCartUI). */
      await syncB2BCartUI({ reconcileOptimistic: paintedOptimistic === true });
      if (!paintedOptimistic && hasCartDrawer) setB2BCartDrawerOpen(true);
      /* PC-only adds never touch the native cart, so the MF location popup can't fire — ask
       * ourselves when no destination is confirmed yet (writes the same key MF reads). */
      maybeShowPcDestinationPrompt();
      const purchaseContainer = btn.closest('.wine-purchase-container');
      collapsePurchaseOptions(purchaseContainer);
      const addedLabel = (getConfig() || {}).addedToCart || 'Added';
      btn.textContent = addedLabel;
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1200);
    } catch (err) {
      b2bCaptureError('add-to-cart', err, { variantId: String(variantId || ''), quantity });
      rollbackOptimisticAdd();
      btn.textContent = originalText;
      btn.disabled = false;
      alert(err.message || (getConfig() || {}).alertUnableCreateCheckout || 'Unable to create checkout');
    }
  });

  /** Remember the last numeric selection per quantity select so we can revert on dialog cancel. */
  /** @type {WeakMap<HTMLSelectElement, string>} */
  const b2bLastValidQuantityValue = new WeakMap();

  document.addEventListener('change', (/** @type {any} */ e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const variantSelect = /** @type {HTMLSelectElement | null} */ (target.closest('[data-b2b-variant-select]'));
    if (variantSelect) {
      const container = variantSelect.closest('.wine-purchase-container');
      if (!container) return;
      const addBtn = /** @type {any} */ (container.querySelector('[data-b2b-add-button]'));
      const quantitySelect = /** @type {HTMLSelectElement | null} */ (container.querySelector('[data-b2b-qty-select]'));
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      if (addBtn) {
        addBtn.dataset.b2bBuy = variantSelect.value;
        addBtn.disabled = !!(selectedOption && selectedOption.disabled);
      }
      if (quantitySelect) {
        const stockForVariant = b2bResolveCurrentVariantStock(container);
        b2bRebuildQuantityOptions(quantitySelect, stockForVariant);
        b2bLastValidQuantityValue.set(quantitySelect, quantitySelect.value);
      }
      return;
    }

    const quantitySelect = /** @type {HTMLSelectElement | null} */ (target.closest('[data-b2b-qty-select]'));
    if (quantitySelect) {
      if (quantitySelect.value !== B2B_QUANTITY_CUSTOM_VALUE) {
        b2bLastValidQuantityValue.set(quantitySelect, quantitySelect.value);
        return;
      }
      const container = quantitySelect.closest('.wine-purchase-container');
      if (!container) return;
      const stockForVariant = b2bResolveCurrentVariantStock(container);
      b2bShowCustomQuantityDialog(stockForVariant).then((value) => {
        if (value == null) {
          quantitySelect.value = b2bLastValidQuantityValue.get(quantitySelect) || '1';
          return;
        }
        b2bSetCustomQuantity(quantitySelect, value);
        b2bLastValidQuantityValue.set(quantitySelect, quantitySelect.value);
      });
      return;
    }

    const cartQtySelect = /** @type {HTMLSelectElement | null} */ (target.closest('[data-b2b-cart-qty-select]'));
    if (cartQtySelect) {
      const lineNode = /** @type {HTMLElement | null} */ (cartQtySelect.closest('.b2b-cart-line'));
      const lineId = lineNode?.getAttribute('data-b2b-line-id') || '';
      if (!lineNode || !lineId) return;
      const lineStock = parseInt(lineNode.getAttribute('data-b2b-line-stock') || '-1', 10);
      const stockForVariant = isNaN(lineStock) || lineStock < 0 ? -1 : lineStock;

      if (cartQtySelect.value === B2B_QUANTITY_CUSTOM_VALUE) {
        b2bShowCustomQuantityDialog(stockForVariant).then(async (value) => {
          if (value == null) {
            cartQtySelect.value = b2bLastValidCartLineQtyValue.get(cartQtySelect) || String(parseInt(lineNode.getAttribute('data-b2b-line-qty') || '1', 10) || 1);
            return;
          }
          b2bSetCustomQuantity(cartQtySelect, value);
          b2bLastValidCartLineQtyValue.set(cartQtySelect, cartQtySelect.value);
          await b2bApplyCartLineQtyChange(lineNode, lineId, value, 'cart-line-custom');
        });
        return;
      }

      const parsed = parseInt(cartQtySelect.value, 10);
      if (isNaN(parsed) || parsed < 1) {
        cartQtySelect.value = b2bLastValidCartLineQtyValue.get(cartQtySelect) || '1';
        return;
      }
      b2bLastValidCartLineQtyValue.set(cartQtySelect, cartQtySelect.value);
      b2bApplyCartLineQtyChange(lineNode, lineId, parsed, 'cart-line-select-change');
      return;
    }
  });

  document.addEventListener('submit', async (/** @type {any} */ e) => {
    const form = /** @type {HTMLFormElement | null} */ (e.target instanceof Element ? e.target.closest('form[data-b2b-order-request-form]') : null);
    if (!form) return;
    e.preventDefault();
    const cfg = getConfig() || {};
    const root = /** @type {any} */ (document.querySelector('[data-b2b-cart-ui]'));
    const feedback = /** @type {any} */ (root?.querySelector('[data-b2b-order-request-feedback]'));
    const submitBtn = /** @type {any} */ (form.querySelector('button[type="submit"]'));
    try {
      if (feedback) feedback.textContent = '';
      if (submitBtn) submitBtn.disabled = true;
      const cartId = getStoredStorefrontCartId();
      const cart = cartId ? await fetchStorefrontCart(cartId) : null;
      const fd = new FormData(form);
      const formData = Object.fromEntries(fd.entries());
      const contactEmail = String(formData.email || '').trim();
      if (!contactEmail) {
        if (feedback) feedback.textContent = cfg.feedbackEmailFailed || '';
        return;
      }
      if (feedback) feedback.textContent = cfg.feedbackSendingRequest || '';
      const { subject, body } = buildOrderRequestEmail({ cart, formData });
      // Send the request FIRST. submitB2BOrderRequestViaShopifyContactForm throws synchronously
      // when the Shopify contact form is missing; doing it before clearing the cart means a failed
      // send no longer empties the cart and shows a false "sent" confirmation (the catch below
      // surfaces the error and leaves the cart intact for a retry).
      submitB2BOrderRequestViaShopifyContactForm({
        subject,
        body,
        contactEmail,
        contactName: String(formData.name || '').trim(),
        phone: String(formData.phone || '').trim(),
      });
      await removeAllLinesFromB2BStorefrontCart(cart);
      cartState.lastRenderedCartFingerprint = '';
      markB2BOrderRequestSent();
      await syncB2BCartUI({ orderJustSent: true });
      showB2BOrderRequestSentToast(formatOrderRequestSentFeedbackHtml(cfg));
    } catch (err) {
      b2bCaptureError('order-request-submit', err);
      if (feedback) feedback.textContent = err?.message || cfg.feedbackEmailFailed || '';
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  /* Installed at script execution (not DOMContentLoaded): the guard must be in place before
   * Magical Fees can ever call showModal on its location dialog. */
  setCartSyncHandler(syncB2BCartUI);
  installMaFeesShowModalGuard();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountB2BCartUI);
  } else {
    mountB2BCartUI();
  }

  document.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
    if (e.key !== 'Escape') return;
    setB2BCartDrawerOpen(false);
  });

  /*
   * For Professionals (B2B) header overrides live server-side, not here, so they
   * also apply on pages where this bundle isn't loaded (e.g. for-professionals-sign-in):
   *   - "Pour les professionnels" → "Back to main site" link: snippets/header-left-links.liquid
   *   - Hiding the search icon + menu links: sections/header.liquid (body.professionals-b2b-nav CSS)
   */


  try {
    if (typeof window !== 'undefined' && typeof URLSearchParams !== 'undefined') {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('b2b_bubble_debug') === '1') {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            void installCartBubbleTracer();
          }, { once: true });
        } else {
          void installCartBubbleTracer();
        }
      }
    }
  } catch (_) {
    /* ignore */
  }

  /* ------------------------------------------------------------------ */
  /*  Expose public API                                                  */
  /* ------------------------------------------------------------------ */

  function clearCache() {
    try {
      sessionStorage.removeItem(withScopeKey(CACHE_KEY_BASE));
      sessionStorage.removeItem(withScopeKey(REGIONS_CACHE_KEY_BASE));
      sessionStorage.removeItem(withScopeKey(REGIONS_ALL_CACHE_KEY_BASE));
      sessionStorage.removeItem(withScopeKey(PRODUCERS_CACHE_KEY_BASE));
      resetProducerFacetCache();
    } catch (e) {
      /* ignore */
    }
  }

  window.B2BStorefront = {
    fetchAllProducts: fetchAllProducts,
    fetchProductsPage: fetchProductsPage,
    fetchProductsCount: fetchProductsCount,
    buildProductQuery: buildProductQuery,
    buildProductFiltersFromState: buildProductFiltersFromState,
    fetchProduct: fetchProduct,
    fetchB2BRegionsMapShown: fetchB2BRegionsMapShown,
    fetchAllB2BRegions: fetchAllB2BRegions,
    fetchProducerDetailsByWlId: fetchProducerDetailsByWlId,
    resolveProducerPageHandleForProduct: resolveProducerPageHandleForProduct,
    filterProducts: filterProducts,
    sortProducts: sortProducts,
    getFacets: getFacets,
    buildB2BFilters: buildB2BFilters,
    renderGrid: renderGrid,
    renderCard: renderCard,
    getProductTitleValue: getProductTitleValue,
    getProductSubtitleValue: getProductSubtitleValue,
    getWineCardDescriptionHtml: getWineCardDescriptionHtml,
    addToCart: addToCart,
    buildQuantityOptionsHtml: b2bBuildQuantityOptionsHtml,
    rebuildQuantityOptions: b2bRebuildQuantityOptions,
    setCustomQuantity: b2bSetCustomQuantity,
    showCustomQuantityDialog: b2bShowCustomQuantityDialog,
    getVariantStock: b2bGetVariantStock,
    QUANTITY_CUSTOM_VALUE: B2B_QUANTITY_CUSTOM_VALUE,
    QUANTITY_MAX_OPTIONS: B2B_QUANTITY_MAX_OPTIONS,
    getDisplayPrice: getDisplayPrice,
    getDisplayCompareAtPrice: getDisplayCompareAtPrice,
    getVariantDisplayLabel: getVariantDisplayLabel,
    formatB2BVariantPickerLabel: formatB2BVariantPickerLabel,
    getCaseQty: getCaseQty,
    formatMoney: formatMoney,
    esc: esc,
    getConfig: getConfig,
    isProductInStock: isProductInStock,
    clearCache: clearCache,
    getB2BFilterDebugInfo: getB2BFilterDebugInfo,
    mergeRegularOnlineCartIntoVipStorefrontCart: mergeRegularOnlineCartIntoVipStorefrontCart,
    installCartBubbleTracer: installCartBubbleTracer,
  };
})();

/**
 * Build entry for assets/b2b-storefront.js (see vite.config.js for the contract).
 *
 * Strangler refactor, phase 1: the entire bundle still lives in ./legacy.js,
 * imported verbatim. From phase 2 on, modules get extracted out of legacy.js one
 * at a time and imported here (or by the modules that need them); the built
 * output stays a single classic script the theme already knows how to load.
 */
})();
